/*
 * vms-slanted-wall.js
 * ---------------------------------------------------------------------------
 * The controller for the "TV mount for a slanted wall" page
 * (sections/vms-slanted-wall.liquid). It is the ENHANCEMENT layer: the page is
 * a complete server-rendered article without it, and this file only adds the
 * four-question calculator that turns a slope, a TV and a plate + head choice
 * into a real parts build the shopper can add to the cart.
 *
 * WHAT THIS FILE IS AND IS NOT
 *   - It CALLS window.VMSSlope (assets/vms-slope.js) for every number. It never
 *     reimplements the geometry. If VMSSlope is absent it degrades to nothing
 *     and the server-rendered fallback stays on screen.
 *   - It reads the SAME data layer the ceiling configurator uses
 *     (#vmscfg-data, emitted by snippets/vms-configurator-data.liquid), filtered
 *     client-side to this page's five SKUs plus the fixed pipe pool. Prices and
 *     HEAD offsets are READ FROM THE DATA at runtime, never hardcoded.
 *
 * [PLATE-OFFSET-BY-ANGLE 2026-08-29] THE PLATE OFFSET IS NOT the flat custom.length_offset any more.
 * The swivel hangs from the hinge, so "mounting face P down to the pipe top" GROWS with the slope: the
 * hinge sits pivotToFaceIn below the face perpendicular, which on a plate tilted to the wall angle theta
 * is pivotToFaceIn / cos(theta) straight down. The page computes it in plateOffsetAt (ORTHO_PLATES
 * anchors + owner CAD hingeToSocketBottomIn) and routes EVERY plate-geometry read through the one helper
 * effectivePlateOffset(plate, pitch); a stylized plate with no ortho spec keeps its flat length_offset.
 * The engine (assets/vms-slope.js) contract is UNCHANGED: it still takes plateOffset as an input; the
 * page just hands it the angle-correct number. custom.length_offset on the products is NOT changed.
 * Owner ruling 2026-08-29 (CAD at 63 degrees): 12 in pipe, plate over the top 0.35 in, collar the bottom
 * 0.35 in. See plateOffsetAt and the pipe-draw block.
 *   - Every customer-facing string it builds comes from the #vmsw-copy block
 *     (owner settings) with COPY_DEFAULTS as the fallback. See the section's
 *     header for the three-place rule.
 *
 * [RULE] IT MOUNTS ON #vmsw-calc, NEVER #s1. assets/vms-configurator.js is one
 * IIFE gated on document.getElementById('s1'); this file is gated on
 * document.getElementById('vmsw-calc'), and every id it creates is vmsw-
 * prefixed, so the two controllers can never wake up on each other's page.
 *
 * [RULE] THE STANDOFF IS THE HONEST UNKNOWN. It is read from #vmsw-standoff
 * (owner text settings, blank = not measured yet) and passed straight to
 * VMSSlope.solve() through VMSSlope.num(), so blank flows to reason
 * 'standoff_unknown' and the page shows the vertical answer only. NEVER
 * substitute a default standoff, NEVER let a blank become 0. See the module
 * header of assets/vms-slope.js.
 */
(function () {
  'use strict';

  var ROOT = document.getElementById('vmsw-calc');
  if (!ROOT) return;                     // not our page
  var S = window.VMSSlope;
  if (!S) { console.warn('vmsw: VMSSlope not loaded; leaving the server fallback in place'); return; }

  // =====================================================================
  // COPY (three-place pattern; see the section header)
  // =====================================================================
  // [RULE] THESE DEFAULTS ARE BYTE-IDENTICAL TO THE SCHEMA DEFAULTS in
  // sections/vms-slanted-wall.liquid. The schema default is what renders on a
  // normal install; this map only covers a field the owner has blanked, and a
  // section rendered from an older template JSON. Keep the two copies in step.
  var COPY_DEFAULTS = {
    calc_heading: 'Build your slanted wall mount',
    answer_prompt: 'Fill in the four steps above and your build appears here.',
    // [SUBSTEP LETTERS 2026-09-22] The answer placeholder used ONLY while a TV measurement is still
    // missing (measurementsMissing()), so the sentence can name the three lettered sub-steps of step 1.
    // Owner, relaying a customer: after the three measurement boxes he was hunting for a step 4 and did
    // not know why the picture had gone. Every other not-ready state (no plate picked, no head picked)
    // keeps the generic answer_prompt above, which is still true for those.
    answer_prompt_measure: 'Enter measurements A, B and C in step 1 to see your build.',
    // [ROUND 5 2026-08-30] App-shell + gates copy. Additive, mirrored byte-for-byte in the section schema
    // defaults and the #vmsw-copy pass-through. example_label sits over the result while it shows the example
    // build (before the shopper enters their own measurements); measure_drawer_open labels the mobile button
    // that opens the steps drawer; drawer_close is its close control; tips_tooltip_label names the "?" tips
    // button; plate_gate_angle / head_gate_diag are the card disable-reason lines ([max]/[diag] tokens).
    // [ROUND 6 2026-08-30] example_heading is the answer HEADING while the example shows (was a separate
    // example_label sentence, deleted). fc_tooltip_label / fc_tooltip drive the tooltip on the 11FC summary
    // row explaining the collar; fc_tooltip is site voice (see website-copy-voice.md).
    // [ROUND 17 2026-08-31] fc_tooltip_label is now the VISIBLE "What is this?" link text on the 11FC row
    // trigger (it also stays the popover dialog aria-label). The trigger is a quiet text link, not a "?" pill.
    example_heading: 'Example build',
    fc_tooltip_label: 'What is this?',
    fc_tooltip: 'The 11FC is a collar that tightens onto the pipe above the threaded end. This will allow the mounting head a place to rest, higher than the threaded collar on the bottom of the pipe. Drag the TV up and the 11FC is added automatically.',
    measure_drawer_open: 'Your measurements',
    drawer_close: 'Close',
    tips_tooltip_label: 'Angle finding tips',
    plate_gate_angle: 'Pivots to [max] degrees. Not for this slope.',
    head_gate_diag: 'Sized for TVs up to [max] inches. Your TV measures about [diag] inches.',
    step1_title: 'What angle is the ceiling?',
    step1_angle_label: 'Drag the line to set the angle.',
    step1_angle_help: 'On a keyboard, tab to the handle and use the arrow keys.',
    angle_readout: '[deg] degrees',
    angle_handle_word: 'Drag',
    step1_tips_lead: 'Tips for finding the angle:',
    step1_tips: 'On iPhone, open the built-in Measure app, tap Level, and hold your phone flat against the slope. On Android, search your app store for a bubble level app.\nUse a dedicated angle finder tool. We need a value from 0 to 89 degrees.\nYou can eyeball it, but your final positioning of the TV might not be as exact.\nCall or [contact us] for help.',
    // [COMPACT 2026-08-29] The optional numeric angle entry below the tips in the ANGLE step (rendered as
    // step 2, hence the step2_ key name). A shopper can type the angle instead of dragging; blank leaves
    // the drag value. It stays in sync with the drag (the setPitch single writer moves the handle, and
    // updateAngleSlider mirrors the value back into this box unless the box itself has focus).
    step2_degrees_label: 'Or enter the angle',
    // [COMPACT 2026-08-29] STEP ORDER FLIPPED: the measurements are now STEP 1 and the ceiling angle
    // is STEP 2 (owner "make the whole page more compact"). The copy KEYS keep their old step1_/step2_
    // names (owner ruling: keep the existing help copy keys); only the render ORDER and the two titles
    // moved. So step2_* below is the MEASUREMENTS step, rendered FIRST, titled "Measure your TV"; and
    // step1_* is the ANGLE step, rendered SECOND, keeping "What angle is the ceiling?". Do not rename
    // the keys to match the new positions; the section schema, the pass-throughs and the tests all pin
    // these names.
    step2_title: 'Measure your TV',
    step2_screen_label: 'Height of your TV, in inches',
    step2_screen_help: 'The height of the whole TV, top edge to bottom edge, not the diagonal.',
    step2_tophole_label: 'Distance from the top of your TV to the top mounting hole, in inches',
    step2_tophole_help: 'Measure from the very top of the TV down to the top row of mounting holes on the back.',
    step2_vesa_label: 'Vertical distance between the mounting holes, in inches',
    step2_vesa_help: 'The vertical distance between the top and bottom mounting holes, hole to hole.',
    // [SUBSTEP LETTERS 2026-09-22] The badge on each of the three measurement fields is a LETTER now,
    // not a digit (owner, relaying a customer: the three '1 2 3' badges inside step 1 read as steps of
    // their own). The visible label text is unchanged; only the badge character and its style moved,
    // from a filled accent circle to an outlined one, so a sub-step badge can never be mistaken for a
    // step badge. The same three letters are drawn on the measurement SVG for the phone layout (see
    // .vmsw-measure__svgbadges in sections/vms-slanted-wall.liquid, which reads the same settings).
    measure_letter_a: 'A',
    measure_letter_b: 'B',
    measure_letter_c: 'C',
    step2_diagram_caption: 'We need 3 measurements.',
    mm_converted_note: 'That looks like millimeters. We read [mm] mm as [inches] inches.',
    // [ROUND 8 2026-08-30] The bottom measure-article link is a SENTENCE now (owner), the lead-in then the
    // anchor (measure_link_text) then a period. measure_link_lead ends with "about" (the space and period are
    // added by the render), so the owner can edit either half.
    measure_link_lead: 'Much of this calculator is based on the math explained in our blog post about',
    measure_link_text: 'How to find where the TV will end up',
    // [ROUND 11 2026-08-30] The numbers-table summary (the collapsed <details> under the answer) and the
    // WebMCP configure-build hand-back line the agent echoes to its user.
    numbers_table_summary: 'The numbers behind this build',
    mcp_handback: 'The build is now on screen. Have the user fine-tune it by dragging the three pills on the drawing: the ceiling angle, the screen tilt, and the TV height. The parts list updates as they drag.',
    step3_title: 'Pick your ceiling plate',
    plate_note_11cp006: 'The standard angled plate. It bolts flat to the sloping surface and gives you a threaded 1-1/2 inch pipe fitting to hang from.',
    plate_note_angle: 'A heavier angled plate rated to 200 lb. Choose this if your TV or your slope calls for more capacity.',
    step4_title: 'Pick your mounting head',
    // [COMPACT 2026-08-29] The head card line is TEMPLATED FROM THE HEAD'S METAFIELDS now, never
    // hardcoded per SKU (owner: "I want them to always be driven by the metafield"). Tokens, all read off
    // the shared data snippet's per-head fields: [min]/[max] the screen range (screen_min/screen_max),
    // [lb] the weight_capacity, [tilt] formatted "0 to N deg" from tilt_down (headMaxTilt, with the
    // DEG_SIGN), [vesa] the pattern "WxH" in mm from vesa_width_max x vesa_height_max. Any token whose
    // metafield is missing drops with its ", "-separated clause (fillClauses), so a bracket never renders.
    // The head's custom.picker_blurb, when present, is appended after this line in <strong> (headCardHtml).
    head_card_template: 'Mounting head for [min] to [max] inch screens, [lb] lb capacity, tilt [tilt], max VESA [vesa]',
    // [SUPERSEDED 2026-08-29] head_note_fixed / head_note_slide are NO LONGER rendered on the head card
    // (headCardHtml + head_card_template replaced them). KEPT DEFINED (here, in the schema and the
    // pass-through) so a live template carrying owner-edited values does not break and the orphan-parity
    // test stays green; nothing reads them for a card now.
    head_note_fixed: 'Fixed head. It threads onto the end of the pipe, so the pipe length sets how far the TV hangs down. We pick the pipe for you in stock lengths.',
    head_note_slide: 'Sliding head. The pipe runs through it onto a collar, so you set the exact height right on the wall instead of choosing from fixed pipe lengths. Loosen the collar, slide the screen to where you want it, then tighten it back down.',
    pick_choose: 'Choose',
    pick_selected: 'Selected',
    answer_title: 'Your build',
    // [ROUND 6 2026-08-30] answer_recommend is STILL read by the standoff-unknown answer path (its only
    // surviving reader), so it stays. answer_recommend_tele / answer_finished / answer_holeline were read
    // only by the deleted 'ok' prose stack and are fully removed (schema, pass-through and here).
    answer_recommend: 'Recommended pipe: [pipe], in our stock length.',
    // [SUPERSEDED 2026-08-29] answer_plate_hidden / answer_plate_level / answer_plate_proud are NO LONGER
    // READ by the page: the mount-cover fact now renders through answer_mount_covered / answer_mount_visible
    // (see mountCoverText) for both heads and every option card. These three are KEPT DEFINED only so a
    // live template carrying owner-edited values does not break; do not wire a new reader to them.
    answer_plate_hidden: 'The ceiling plate sits [gap] inches below the top of the screen, so the screen covers it.',
    answer_plate_level: 'The ceiling plate lines up with the top of the screen.',
    answer_plate_proud: 'The ceiling plate stands [gap] inches above the top of the screen, so it stays visible above the TV.',
    // [MOUNT-COVER 2026-08-29] The plate-position sentence, both head types, in the owner's two-message
    // form: either the build covers the mount, or it names how much shows. Driven by the engine's signed
    // plateGap (now carried for the sliding head too). answer_mount_visible takes [in] = |plateGap|.
    answer_mount_covered: 'This build completely covers the mount behind the screen.',
    answer_mount_visible: 'About [in] inches of the mount will show above the top edge of the screen. That is fine if you want room to swing the screen left or right.',
    // [MOUNT-COVER 2026-08-29] SLIDING head only: when the pipe end hangs below the bottom edge of the
    // screen (engine pipeBelowScreen > 0), say so plainly. Hidden when the pipe end is behind the screen.
    answer_pipe_below_screen: 'The pipe runs about [in] inches below the bottom edge of the screen.',
    // [SUPERSEDED 2026-08-29] The longer-pipe OPTION LIST is REMOVED (owner ruling: "get rid of all those
    // different pipe lengths"): the height DRAG on the build image now sets the one required pipe. options_intro
    // is KEPT DEFINED so a live template carrying an owner-edited value does not break, but it is NO LONGER
    // READ (optionsListHTML / wireOptions are gone). Do not wire a new reader to it.
    options_intro: 'Want the screen lower? A longer pipe simply hangs it further down. The plate shows a bit more above the TV, and that is the only trade. Pick any length below and the build updates.',
    option_recommended_tag: 'Recommended',
    answer_price_total: 'Build total: [price]',
    sumrow_plate: 'Ceiling plate',
    sumrow_pipe: 'Pipe',
    sumrow_head: 'Mounting head',
    sumrow_collar: 'External height adjustment collar (11FC)',
    // [RESTING 2026-08-29] the head-rests / optional-11FC copy. Placeholder wording, owner copy
    // pass deferred. answer_rest_note: shown when the head rests (the default). answer_raised_note: shown
    // when the 11FC option is on. opt_11fc_label: the checkbox label offering the upgrade ([in] = headroom
    // inches, [price] = the 11FC price). notice_collar_shows: the only sliding failure now (a short TV).
    answer_rest_note: 'The head rests on the threaded collar at the end of the pipe, so this build does not need the 11FC collar.',
    answer_raised_note: 'With the 11FC collar the head is raised to sit as high as the slope allows.',
    opt_11fc_label: 'Add the 11FC collar to slide the head up to [in] inches higher ([price]).',
    opt_11fc_card: 'With the 11FC, up to [in] in higher.',
    notice_collar_shows: 'This TV is a little short for this sliding head: the threaded collar at the end of the pipe would show below the bottom of the screen, and no pipe length changes that. A taller screen, or a different head or plate, would fix it. Call [phone] and we will finish the fit with you.',
    weight_label: 'Weight rating',
    weight_value: '[weight] lb, limited by [part]',
    weight_unrated: 'At most [weight] lb, [part] has no rating on file',
    slide_install_min: 'Set the head at least [dmin] inches below the ceiling plate. At that drop the screen sits as high as it can go, with its top back corner just clear of the slope. Slide the head any higher and the corner runs into the ceiling; drop it lower and the screen simply hangs further down.',
    slide_floor_note: 'The shortest pipe this head can use is [pipe], because the head needs about [span] inches of pipe to grip.',
    slide_floor_note_reach: 'The shortest pipe for this build is [pipe], because the pipe has to reach from the ceiling plate down to where the head rests, about [drop] inches below it.',
    notice_no_pipe_fits: 'This build needs a pipe between [floor] and [cap] inches, and no single stock pipe is that length. Call [phone] and we can cut a pipe to length and finish the fit with you.',
    notice_standoff_unknown: 'We do not have one measurement on file for this head, so we cannot yet promise the top corner clears the slope. The length below is correct on its own. Call [phone] and we will finish the fit with you.',
    notice_geometry_unknown: 'We need the measurements off your TV before we can work out the pipe. Fill in the screen height and the two mounting hole measurements above, and your build appears here. Or call [phone] and we will finish it with you.',
    notice_measure_check: 'We could not work this build out from these numbers. On a fixed head that usually means one of the TV measurements is missing or does not look right. Check the screen height and the two hole measurements, or call [phone] and we will finish it with you.',
    notice_out_of_range: 'One of your measurements is zero or a negative number. The screen height, the VESA pattern height and the slope each have to be greater than zero. Check the four steps above.',
    notice_sliding_no_fit: 'At this size the sliding head needs a taller screen. Its plate, the pipe the head grips, and the clearance above the screen add up to a minimum screen height of about [minscreen] on this plate and pitch. Call [phone] and we can suggest a plate that sits tighter to the surface, or another combination.',
    cart_add: 'Add this build to cart',
    cart_adding: 'Adding to your cart',
    cart_added: 'Added. Taking you to your cart',
    cart_unavailable: 'One or more parts in this build are currently unavailable. Call [phone] or contact us.',
    cart_error: 'Could not add this to your cart. Call [phone] and we will help.',
    missing_parts: 'The parts list did not load. Call [phone] and we will build this with you over the phone.',
    diagram_heading: 'Your build, to scale',
    diagram_aria: 'A scale side view of this build: the ceiling plate on the slope, the pipe hanging plumb, the mounting head, and your TV with its top back corner just clearing the slope.',
    // [SUPERSEDED 2026-08-29] diagram_wall_label ("Your slope") and diagram_corner_label ("Corner clears") are
    // NO LONGER DRAWN on the build image (owner: remove those two texts). The corner RING and the clearance
    // dimension line stay; the corner-guard logic is untouched. Both keys are KEPT DEFINED so a live template
    // carrying owner-edited values does not break, but nothing reads them for a <text> now.
    diagram_wall_label: 'Your slope',
    diagram_corner_label: 'Corner clears',
    // [DRAG 2026-08-29] The two upright numeric readouts drawn inside the build image, each on TWO rows: a
    // LABEL row (these copy keys) and, directly below it, a VALUE row the JS formats as the whole-degree angle
    // plus the degree sign (DEG_SIGN, e.g. "63 deg"). CEILING is top right (room side, below the ceiling line),
    // TV TILT is bottom right. They render on every ok build and update live during a drag (previewSlope /
    // previewTilt update the value row). These keys are the LABELS only.
    diagram_readout_ceiling: 'Ceiling',
    diagram_readout_tilt: 'TV tilt',
    // [DRAG 2026-08-29] The three draggable-handle accessible labels on the build drawing. Read
    // by assistive tech on the role="slider" handles the page authors inside the build SVG (slope angle, TV
    // tilt, TV height). Owner copy pass deferred.
    diagram_handle_slope: 'Drag to set the ceiling angle',
    diagram_handle_tilt: 'Drag to tilt the screen down',
    diagram_handle_height: 'Drag to set the screen height',
    // [ROUND 14 2026-08-30] The VISIBLE two-line word inside each build-image drag pill (owner: the pills
    // should name their drag). The first word goes on line 1 and the rest on line 2 (so all three read "Drag"
    // over a noun). These replace the single shared angle_handle_word for the build-image pills; the step 1
    // angle handle still uses angle_handle_word. The aria-labels stay the fuller diagram_handle_* sentences.
    pill_slope: 'Drag Ceiling',
    pill_height: 'Drag Length',
    pill_tilt: 'Drag TV Angle',
    // [ROUND 14 2026-08-30] The per-head fallback line in the Q6 VESA chart when a head has no VESA metafields
    // (never a blank cell). Carries the phone number in display format.
    vesa_chart_nodata: 'VESA range not listed. Call 800-585-8795 and we will confirm the fit.',
    // [COMPACT 2026-08-29] The standalone tilt SLIDER is REMOVED (the tilt drag on the build image is the
    // tilt control now). tilt_label and tilt_caption were the slider's label and caption and are NO LONGER
    // rendered; tilt_readout is no longer rendered either (setTiltSlider/commitTilt guard the absent nodes).
    // All three are KEPT DEFINED (here, in the schema and the pass-through) so a live template does not break
    // and the parity tests stay green. The image tilt readout uses diagram_readout_tilt, not these.
    tilt_label: 'Tilt the screen down',
    tilt_readout: 'Screen tilted down [n] degrees',
    tilt_caption: 'Tilt the screen down to see how much higher it can sit. This head tilts down to [max] degrees.',
    tilt_gain_sentence: 'At [n] degrees of downward tilt the screen can sit [gain] inches higher than square.',
    answer_tilt_disclosure: 'This pipe length assumes the screen stays tilted down [n] degrees. Squared up, the top corner would meet the slope, so keep the tilt or choose the square-screen length.',
    answer_tilt_required: 'No stock pipe fits with the screen square. This build needs the screen tilted down [n] degrees, and the pipe length below assumes that tilt. Tilt it less and the top corner hits the slope.'
  };

  // The owner's edits, or {}. Every failure mode lands on shipped copy: a copy
  // edit must never take the calculator down. Same discipline as the configurator.
  var COPY_OVERRIDES = (function () {
    try {
      var node = document.getElementById('vmsw-copy');
      if (!node) return {};
      var parsed = JSON.parse(node.textContent || '{}');
      return (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) ? parsed : {};
    } catch (err) {
      console.warn('vmsw: copy overrides unreadable, using shipped copy', err);
      return {};
    }
  })();

  function copyRaw(key) {
    var v = COPY_OVERRIDES[key];
    return (typeof v === 'string' && v.trim() !== '') ? v : null;
  }
  function COPY(key) {
    var v = copyRaw(key);
    return v != null ? v : (COPY_DEFAULTS[key] || '');
  }

  var PHONE_TEXT = '800-585-8795';
  var PHONE_HTML = '<span class="phone">' + PHONE_TEXT + '</span>';
  var CONTACT_OPEN = '<a href="/pages/contact">';
  var CONTACT_CLOSE = '</a>';

  // Token substitution. A token we have no value for renders as nothing, never a
  // stray bracket, and nothing here can throw. Same contract as the configurator's
  // fill(); see assets/vms-configurator.js.
  function fill(str, map) {
    if (typeof str !== 'string') return '';
    var m = map || {};
    return str.replace(/\[(\/?[a-z_]+)\]/g, function (whole, key) {
      return Object.prototype.hasOwnProperty.call(m, key) ? String(m[key]) : '';
    });
  }
  var COPY_HTML_TOKENS = { phone: PHONE_HTML, contact: CONTACT_OPEN, '/contact': CONTACT_CLOSE };
  function fillHTML(str, map) {
    return fill(str, Object.assign({}, COPY_HTML_TOKENS, map || {}));
  }

  // =====================================================================
  // Small format helpers (same shapes the configurator uses)
  // =====================================================================
  var money = function (p) { return '$' + Number(p).toFixed(2); };
  // [DRAG 2026-08-29] The degree sign, in ONE place. U+00B0 is the one non-keyboard character the house
  // rules allow without asking (degrees). The image readouts append it to the whole-degree number; every
  // owner-editable copy string stays plain ASCII (the sign lives here, not in a copy key).
  var DEG_SIGN = '°';
  // [WARN] nice(null), nice(undefined) and nice(NaN) RETURN null, never the string
  // '0'. The value is routed through S.num(), which rejects a missing or non-finite
  // input BEFORE it can be rounded, so a value we do not have can never masquerade as
  // a confident zero on the page. That is the exact class of bug as Number('') === 0:
  // an absent measurement surfacing as a real number. Every call site below either
  // guards its number (renders no line when nice returns null) or is handed a value
  // that cannot be null; a call site that legitimately wants zero must pass a real 0.
  var nice = function (x) { var n = S.num(x); if (n === null) return null; var v = Math.round(n * 10) / 10; return v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1); };
  var niceIn = function (x) { var s = nice(x); return s === null ? null : s + (s === '1' ? ' inch' : ' inches'); };
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // [STEP1-OVERLAY-R4 2026-08-30] Link a summary-row part number to its product page. Owner: "make the part
  // number link to the product pages." Returns an <a> around the SKU when the part carries a non-empty url
  // (the data snippet emits "url" per component, and CONFIG.companion carries it for the 11FC), and PLAIN
  // escaped text otherwise - never a dead link and never an href of hash. The price and rest of the row stay plain
  // text; the caller wraps only the SKU (for the pipe row, only the SKU inside the parentheses). Same tab
  // (no target=_blank). esc() runs on both the url and the sku, so a hostile field can never break out.
  // [PHOTO PREVIEW 2026-09-06] Owner ask: also show a product photo on hover (and
  // a tap-toggle on mobile). pvUrl swaps width=200 for width=480 (the CDN honours
  // it); a URL with no width param is used unchanged. The preview <img> src is
  // filled from data-src only the first time it is shown (see setupSkuPreview).
  // [PHOTO COLUMN 2026-09-07] Owner follow-up: the inline thumbnails looked messy
  // (a white photo on the orange answer box, one even rendered INSIDE the pipe
  // row's parentheses). So the thumb and preview moved OUT of skuLink into a
  // separate skuPhoto helper, and each row emitter now places skuPhoto(part) in a
  // fixed-width photo column (.vmsw-sumrow__photo) at the FAR LEFT of the row,
  // BEFORE the label; rows with no part (the weight row) emit an empty photo
  // column of the same width so every row's text starts at one x. skuLink is back
  // to returning ONLY the link (or plain escaped text), so the pipe row reads
  // "12 inches (NPT12) - $19.95" with nothing inside the parentheses but the SKU
  // link, exactly as it did before 2026-09-06. The <a class="vmsw-sumrow__sku">
  // stays the ONLY link per row, so getBuild's a.vmsw-sumrow__sku selector still
  // matches exactly one.
  function pvUrl(u) {
    return (typeof u === 'string') ? u.replace(/([?&]width=)\d+/, '$1480') : u;
  }
  function skuLink(part) {
    var sku = (part && part.sku != null) ? part.sku : '';
    var url = (part && typeof part.url === 'string') ? part.url : '';
    var link = url
      ? '<a class="vmsw-sumrow__sku" href="' + esc(url) + '">' + esc(sku) + '</a>'
      : esc(sku);
    return link;
  }
  // Returns the photo-column contents for a part: a thumbnail BUTTON that toggles
  // a hidden preview, or '' when the part carries no image. The thumb and preview
  // sit in the row's .vmsw-sumrow__photo column (see the row emitters); the button
  // is the ONLY interactive element it emits (no <a>), so skuLink keeps the row's
  // single link. sku, image and title are all HTML-escaped so a hostile store
  // field can never break out.
  function skuPhoto(part) {
    var sku = (part && part.sku != null) ? part.sku : '';
    var image = (part && typeof part.image === 'string') ? part.image : '';
    var title = (part && part.title != null) ? part.title : '';
    if (!image) return '';
    // [IMG WARM 2026-09-20] No loading="lazy" on the 32 px answer-row thumb: it is part of the answer the
    // shopper is reading, so it must load with the row, not defer to a scroll (owner report 2026-09-20: a
    // blank white square on first render). The thumb box carries a neutral placeholder background in the CSS
    // (var --vmscfg-border) so the brief pre-load frame is a soft grey, not a stark white square.
    var thumb = '<button type="button" class="vmsw-sumrow__thumb" aria-expanded="false" aria-label="Show photo of ' +
      esc(sku) + '"><img src="' + esc(image) + '" alt="" width="32" height="32" decoding="async"></button>';
    var pv = '<span class="vmsw-sumrow__pv" hidden><img alt="' + esc(title) +
      '" width="180" height="180" data-src="' + esc(pvUrl(image)) + '"></span>';
    return thumb + pv;
  }

  // =====================================================================
  // Input validation and presentation helpers (page layer)
  // All are PURE over S.num() (plus Math and a literal or two), so the Node
  // harness lifts them by name and asserts them directly. None reaches into
  // window.VMSSlope for geometry: the pointer-to-angle map and the clamp below
  // are PRESENTATION (owner brief), and the rise over 12 the build actually uses
  // always comes back through S.pitchFromDegrees at the call site.
  // =====================================================================
  // [A1] A STRICTLY POSITIVE number, or null. S.num('0') returns 0, and the page
  // used to treat that 0 as a measured value: a VESA, a screen height or a pitch of
  // 0 is physically impossible (two mounting holes cannot share a point, a screen or
  // a run of 0 is not a measurement), and a typed 0 produced a dangerously short pipe.
  // This rejects anything at or below 0 the same way S.num rejects a blank. Use it
  // where a value MUST be positive; top-to-top-hole may legitimately be 0 (a hole at
  // the very top edge), so that field takes the allowZero path in readMeasure instead.
  // [G] A PLAIN DECIMAL STRING ONLY. S.num delegates to Number(), which ALSO accepts scientific
  // ('1e3' -> 1000) and hex ('0x10' -> 16) forms, so a shopper who typed '1e3' in a measurement field
  // would silently enter a thousand inches (a millimeter reading, even). The page's input readers gate
  // on this first: optional surrounding whitespace (trimmed), an optional sign, digits with at most one
  // dot. Comma decimals stay rejected exactly as today (Number('1,5') is already NaN; we do not guess a
  // locale). A NON-string (a real number from a test, or a computed value) passes straight through for
  // S.num to judge. Returns the trimmed numeric string, the untouched non-string, or null if the string
  // is not a plain decimal. The engine (assets/vms-slope.js) is left alone; this is a page-layer gate.
  function plainNumeric(v) {
    if (typeof v !== 'string') return v;
    var t = v.trim();
    return /^[+-]?(\d+(\.\d*)?|\.\d+)$/.test(t) ? t : null;
  }

  function posNum(v) {
    var n = S.num(plainNumeric(v));
    return (n === null || n <= 0) ? null : n;
  }

  // [A2] Read one measurement field, converting an obvious millimeter entry to inches.
  // Returns { value, invalid, converted, mm, inches }:
  //   value      inches, or null (blank, or out of range)
  //   invalid    true when a NON-EMPTY entry is out of range (rejected, or <= 0, or
  //              < 0 when zero is allowed), so the caller can show why nothing computed
  //   converted  true when a millimeter entry was detected and divided by 25.4
  // VESA is quoted in millimeters on every spec sheet and on our own linked article, so a
  // shopper who knows their pattern is 400x400 types 400. Real VESA heights in INCHES run
  // about 1.5 to 40 (1000 mm, the largest standard pattern, is 39.4 in); in MILLIMETERS the
  // same patterns are 100 and up, so a threshold of 50 separates them cleanly and is the
  // caller's mmThreshold for VESA. Top-to-top-hole in inches never exceeds about 20 and in mm
  // starts around 25, so it shares the 50 threshold. A screen height in inches reaches about 55
  // on a 110 inch class TV and in mm starts around 250, so its threshold is 100. Every real inch
  // entry sits below its threshold and is untouched; a shopper working in mm is TOLD what we read
  // through mm_converted_note, never converted silently.
  function readMeasure(raw, allowZero, mmThreshold) {
    var MM_PER_INCH = 25.4;   // presentation constant; the build geometry never sees millimeters
    var trimmed = String(raw == null ? '' : raw).trim();
    if (trimmed === '') return { value: null, invalid: false, converted: false, mm: null, inches: null };
    // [G] Plain decimals only: reject '1e3', '0x10', '12abc' before S.num (which via Number() would
    // accept the first two). A non-plain entry is out of range, the same as unparseable text.
    var n = S.num(plainNumeric(trimmed));
    if (n === null) return { value: null, invalid: true, converted: false, mm: null, inches: null };
    if (allowZero ? (n < 0) : (n <= 0)) return { value: null, invalid: true, converted: false, mm: null, inches: null };
    if (n >= mmThreshold) {
      var inches = n / MM_PER_INCH;
      return { value: inches, invalid: false, converted: true, mm: n, inches: inches };
    }
    return { value: n, invalid: false, converted: false, mm: null, inches: null };
  }

  // [TV LOOKUP 2026-10-13] A catalog row in inches. Notes: assets/vms-slanted-wall.notes.md.
  function tvNum(v) { return typeof v === 'number' ? isFinite(v) : (typeof v === 'string' && v.trim() !== '' && isFinite(v)); }
  function tvTierNote(row) {
    var u = tvNum(row.offset_uncertainty_mm) ? row.offset_uncertainty_mm : null;
    var fl = u == null ? null : 'Manufacturer figures, accurate to about +/- ' + u + ' mm. Verify before relying on them.';
    var t = {
      pass_2mm: 'From the manufacturer\'s drawing. Check the hole positions on your TV before drilling.',
      flagged_2_5mm: fl, flagged_5_10mm: fl, flagged_10_12mm: fl,
      photo_measured: u == null ? null : 'Estimated from a product photo, about +/- ' + u + ' mm. Verify on your TV before drilling.',
      calculated_one_side: 'One edge is from the maker\'s drawing or a photo; the other is calculated. Verify on your TV before drilling.',
      customer_measured: 'Your own measurements. Check them on the back of your TV before drilling.'
    };
    return (Object.prototype.hasOwnProperty.call(t, row.offset_tier) && t[row.offset_tier]) || 'Rough estimate, about +/- ' + (u == null ? 25 : u) + ' mm. Verify on your TV before drilling.';
  }
  function tvRowInches(row) {
    if (!row) return null;
    function inch(k, zero) { if (!tvNum(row[k])) return null; var n = Number(row[k]); return (zero ? n >= 0 : n > 0) ? n / 25.4 : null; }
    function str(k) { var v = row[k]; return (v == null || String(v).trim() === '') ? null : String(v).trim(); }
    var h = inch('height_mm'), t = inch('vesa_from_top_mm', true), v = inch('vesa_height_mm');
    var status = row.lookup_status == null ? null : row.lookup_status;
    var hasOffsets = tvNum(row.vesa_from_top_mm) && tvNum(row.vesa_from_bottom_mm);
    var placeable = hasOffsets && h !== null && t !== null && v !== null && t + v / 2 > 0 && t + v / 2 < h && status !== 'proprietary_mount';
    return {
      model: str('model_number'), maker: str('manufacturer'), version: str('catalog_version'), status: status,
      size: tvNum(row.screen_size_in) ? Number(row.screen_size_in) : null,
      height: h, topHole: t, vesa: v, width: inch('width_mm'), depth: inch('depth_mm'),
      hasOffsets: hasOffsets, placeable: placeable,
      why: placeable ? null : (status === 'proprietary_mount' ? 'proprietary' : (hasOffsets && h === null ? 'no_height' : 'no_offsets')),
      tier: row.offset_tier == null ? null : row.offset_tier,
      uncertainty: row.offset_uncertainty_mm == null ? null : row.offset_uncertainty_mm,
      tierNote: tvTierNote(row)
    };
  }
  function tvSameModel(a, b) {
    var k = function (x) { return String(x == null ? '' : x).toUpperCase().replace(/[\s-]+/g, ''); };
    return k(a) !== '' && k(a) === k(b);
  }

  // [B1] PRESENTATION trig for the draggable angle visual (owner brief: pointer-to-angle
  // mapping and the drawing's rotation are presentation and may live here; only GEOMETRY must
  // go through window.VMSSlope). Clamp an angle to the slider's degree range.
  function clampAngle(deg, lo, hi) {
    var d = Number(deg);
    if (!isFinite(d)) return lo;
    return Math.max(lo, Math.min(hi, d));
  }
  // The angle in DEGREES from a hinge to a pointer, measured up from the horizontal. dy is
  // flipped because SVG y grows downward, so a pointer ABOVE the hinge reads as a positive
  // angle. This maps a drag to an angle for the UI; it never sizes a pipe.
  function pointerAngleDeg(px, py, hingeX, hingeY) {
    var dx = px - hingeX;
    var dy = hingeY - py;
    return Math.atan2(dy, dx) * 180 / Math.PI;
  }

  // [B1] PRESENTATION: the drag handle's ROTATED centre in the angle SVG's 480 x 300 viewBox, for
  // placing the level "Drag" label on it. updateAngleSlider rotates the arm group by
  // rotate(-d, 70, 250), and the handle is AUTHORED at (280, 250), i.e. 210 to the right of the
  // hinge (70, 250). Rotating the offset (210, 0) by -d gives the centre: x = 70 + 210*cos(d),
  // y = 250 - 210*sin(d) (y subtracts because SVG y grows downward). The arm was SHORTENED from
  // 240 to 210 so that at the new 89 degree maximum the handle (plus its stroke and focus ring)
  // stays inside the viewBox top with a margin: at 89 the centre sits at about y=40, not y=10, so
  // the handle no longer clips off the top. Positioning a label on a drawing is presentation,
  // allowed to live here; the rise over 12 the build uses never comes from this. The three literals
  // mirror the authored SVG (hinge 70,250; handle 280,250 in the vmsw-angle-diagram template); if
  // that geometry ever changes, change them together. Pure over Math + a literal, so the Node
  // harness lifts it and asserts the (280,250) and 45 degree cases.
  function handleCentreAt(deg) {
    var d = Number(deg);
    if (!isFinite(d)) d = 0;
    var r = d * Math.PI / 180;
    return { x: 70 + 210 * Math.cos(r), y: 250 - 210 * Math.sin(r) };
  }

  // =====================================================================
  // [MOUNT-COVER 2026-08-29] plateGap wording. plateGap is an OUTPUT of the engine (assets/vms-slope.js),
  // not a pass or fail: POSITIVE means the plate sits that far BELOW the top of the screen (the screen
  // covers it), NEGATIVE means it stands that far ABOVE the top (visible). The owner ruled a visible plate
  // is a LOOK, never a failure.
  //
  // [SUPERSEDED 2026-08-29] The old three-key model (plateGapKey / plateGapText -> answer_plate_hidden /
  // answer_plate_level / answer_plate_proud) is REMOVED from the render: it restated the mount-cover fact
  // in a second vocabulary on every option card while the main answer already said it in the owner's new
  // wording (review finding, fixed-head option cards). mountCoverText below is now the ONLY plate-position
  // renderer, used by both the main answer and the option cards. The three answer_plate_* copy keys are
  // KEPT DEFINED in COPY_DEFAULTS and the section schema (a live template may carry owner-edited values,
  // and dropping the keys would break it) but are NO LONGER READ - see the note on them in COPY_DEFAULTS.
  // =====================================================================
  // [MOUNT-COVER 2026-08-29] THE OWNER'S TWO-MESSAGE PLATE-POSITION SENTENCE, both head types, driven by
  // the engine's signed plateGap (now carried for the sliding head too). Owner ruling 2026-08-29: a build
  // either COVERS the mount, or names how much of the mount SHOWS above the screen top (which is fine, it
  // lets the screen swing left or right). POSITIVE-or-zero gap (screen top at or above the plate) reads as
  // covered; NEGATIVE reads as visible, naming |gap| inches. Rounds to tenths to match nice(), so a gap
  // that prints as "0.0" reads as covered, not "0.0 inches showing". Pure (S.num only), so the harness lifts it.
  function mountCoverText(gap) {
    var g = S.num(gap);
    if (g === null) return '';                 // unusable geometry: no line
    var gr = Math.round(g * 10) / 10;
    if (gr < 0) return fill(COPY('answer_mount_visible'), { in: nice(Math.abs(g)) });
    return COPY('answer_mount_covered');
  }
  // [CAP-RESTORED 2026-08-29] SLIDING head only: the pipe-below-screen sentence, shown ONLY when the
  // threaded collar bottom hangs below the bottom edge of the screen (engine pipeBelowScreen > 0, rounded to
  // tenths). With the bottom cap restored this is <= 0 for every feasible build, so the line never renders in
  // practice; the code path is kept so it self-corrects if the cap model ever changes. Returns '' when the
  // collar is at or hidden behind the screen bottom (<= 0) or the number is missing. Pure.
  function pipeBelowScreenText(pbs) {
    var p = S.num(pbs);
    if (p === null) return '';
    var pr = Math.round(p * 10) / 10;
    if (pr <= 0) return '';
    return fill(COPY('answer_pipe_below_screen'), { in: nice(p) });
  }
  // A signed money delta between an option and the recommendation, e.g. "+$2.00".
  // Number formatting, not copy, exactly like the bare prices on the pick cards.
  function deltaText(d) {
    var n = Math.round(Number(d) * 100) / 100;
    if (n > 0) return '+' + money(n);
    if (n < 0) return '-' + money(-n);
    return money(0);
  }

  // =====================================================================
  // THE MID-SCREEN CONVERSION - the whole reason this page exists
  // =====================================================================
  // [RULE] REPORT THE MIDDLE OF THE SCREEN, NOT THE MIDDLE OF THE HOLE PATTERN.
  // The store's existing lengths (and assets/vms-configurator.js) land the mount
  // at the middle of the VESA HOLE PATTERN and CALL it the middle of the screen.
  // On the owner's 70 inch example the two differ by 1.126 in. This page asks for
  // the screen height and both hole measurements, so unlike the configurator it
  // can compute the true figure, and reporting the honest number is exactly what
  // this page was built to do. Do not collapse these back into one figure.
  //
  // The hole centre sits `holeCentre` below the plate. Mid-screen sits
  // (holeMid - screenHeight/2) ABOVE the hole centre, which is NEGATIVE whenever
  // the hole pattern sits above the screen's centre line (it usually does), so
  // mid-screen is that much further DOWN from the plate.
  //
  // [WARN] NULL, NEVER NaN. holeMid and screenHeight are both null while a shopper
  // is mid-entry. Everything is routed through S.num() and a null input yields a
  // null result, so the caller renders NO finished-length line rather than
  // "NaN inches" or a silently wrong number.
  function midScreenFromHoleCentre(holeCentre, holeMid, screenHeight) {
    var hc = S.num(holeCentre), hm = S.num(holeMid), sh = S.num(screenHeight);
    if (hc === null || hm === null || sh === null) return null;
    return hc - (hm - sh / 2);
  }
  // The fixed-head convenience form: the hole centre is (plateOffset + pipe +
  // headOffset) below the plate. This is the signature the coordinator pinned and
  // the one the 14.626 regression asserts.
  function midScreenFromPlate(plateOffset, pipe, headOffset, holeMid, screenHeight) {
    var po = S.num(plateOffset), p = S.num(pipe), ho = S.num(headOffset);
    if (po === null || p === null || ho === null) return null;
    return midScreenFromHoleCentre(po + p + ho, holeMid, screenHeight);
  }
  // [TILT] The finished length (ceiling plate down to the middle of the SCREEN) with the screen
  // tilted DOWN. The mid-screen point (standoff, holeMid - screenHeight/2) rotates about the tilt
  // axis (S.tiltPoint); its rotated HEIGHT above the hole centre is what sits below the plate, so
  // the finished length is holeCentre minus that rotated height. At tiltDeg 0 the rotation is the
  // identity and this is byte-for-byte midScreenFromHoleCentre, so the un-tilted number never moves.
  // NULL, never NaN, on any missing input, exactly like its square-screen sibling.
  function midScreenTilted(holeCentre, holeMid, screenHeight, standoff, tiltDeg, tiltPivot, tiltAxisDrop, tiltTable) {
    var hc = S.num(holeCentre), hm = S.num(holeMid), sh = S.num(screenHeight);
    if (hc === null || hm === null || sh === null) return null;
    // [WARN] ROTATE THE MID-SCREEN EXACTLY ONCE. holeCentre (hc) is ALREADY the rotated hole-centre
    // drop below the plate (holeCentreLen rotates the hole centre). The mid-screen and the hole centre
    // rotate about the SAME axis, so the finished length is hc plus the rotated hole-centre HEIGHT minus
    // the rotated mid-screen height: hc - (rotMid.y - rotHole.y). Subtracting rotMid.y from hc alone
    // (the old form) double-counts the hole-centre rotation baked into hc, which at pivot/drop 0 is
    // harmless (the hole centre lies ON the axis, rotHole.y = 0) but overstated the length by
    // rotHole.y at any real pivot or drop (measured about 0.16 in at tilt 6 on 33HE001). At tiltDeg 0
    // both rotations are the identity (rotHole.y = 0, rotMid.y = hm - sh/2), so this is byte-for-byte
    // midScreenFromHoleCentre and the un-tilted number never moves.
    var rotHole = S.tiltPoint({ x: standoff, y: 0, standoff: standoff, tiltDeg: tiltDeg,
      tiltPivot: tiltPivot, tiltAxisDrop: tiltAxisDrop, tiltTable: tiltTable });
    var rotMid = S.tiltPoint({ x: standoff, y: hm - sh / 2, standoff: standoff, tiltDeg: tiltDeg,
      tiltPivot: tiltPivot, tiltAxisDrop: tiltAxisDrop, tiltTable: tiltTable });
    if (rotHole.y === null || rotMid.y === null) return null;   // standoff unknown at a real tilt; no line
    return hc - (rotMid.y - rotHole.y);
  }

  // [D] The screen-tilt disclosure's GATE and token, kept pure so the harness asserts the "only when
  // tilted, whole degrees" logic without a DOM. [ROUND 6 2026-08-30] The disclosure SENTENCE render was
  // deleted with the rest of the answer prose (the answer_tilt_disclosure string is COPY_DEFAULTS-only now);
  // this gate is retained as the tilt oracle. Returns { show:boolean, n:(string|null) }.
  function tiltDisclosure(tiltDeg) {
    var t = S.num(tiltDeg);
    if (t === null || t <= 0) return { show: false, n: null };
    return { show: true, n: String(Math.round(t)) };
  }

  // =====================================================================
  // Config + standoff (data, not copy)
  // =====================================================================
  function readJSON(id, fallback) {
    try {
      var node = document.getElementById(id);
      if (!node) return fallback;
      var parsed = JSON.parse(node.textContent || 'null');
      return (parsed && typeof parsed === 'object') ? parsed : fallback;
    } catch (e) { return fallback; }
  }
  var CONFIG = readJSON('vmsw-config', {});
  // [ROUND 13 2026-08-30] The MEASURE_URL var is gone: its only reader was the credit sentence at the
  // bottom of the calculator, now removed. The measure-article link lives in Q2 of the bottom Q&A, built
  // server-side in the section from section.settings.url_measure_article (the [measure article] token).
  // The contact page link, owner-editable, expanded into the step 1 tips list (the [contact us]
  // token). Emitted into #vmsw-config as contact_url; a blank falls back to /pages/contact.
  var CONTACT_URL = CONFIG.contact_url || '/pages/contact';
  // The bottom clearance margin, owner-editable (see the section schema). It is the
  // ONLY margin left in the system and applies to the SLIDING head alone: the least
  // distance the bottom end of the pipe must stay above the bottom edge of the TV, so
  // the pipe cannot show below the screen. Default 0 (the owner removed the old 3 inch
  // rule-of-thumb cushion). Passed STRAIGHT INTO solve(), which routes it through its
  // own num() and defaults a blank to its module default, so a blank here is safe.
  // [NOTE] The old top margin is GONE. The "plate must hide behind the screen" upper
  // bound was never a real rule; the plate position is now an OUTPUT (plateGap, signed,
  // positive means hidden), never a constraint, so there is no marginTop to read or pass.
  var MARGIN_BOTTOM = CONFIG.margin_bottom;
  // Standoff per head SKU. Blank stays blank all the way to VMSSlope.num(), which
  // is the whole safety of the unknown-standoff rule; do not coerce here.
  var STANDOFF = readJSON('vmsw-standoff', {});

  // [TILT] The tilt-axis pivot per head SKU, in inches BEHIND the VESA mounting surface
  // (owner CAD pending 2026-08-25). Blank means the axis is assumed AT the mounting surface
  // (pivot 0); the value flows STRAIGHT into VMSSlope.tiltPoint / solve(), whose
  // tiltPivotOrZero() turns a blank into an explicit 0 (never a coerced Number('')). Do not
  // coerce here, exactly like STANDOFF. Emitted by the section into #vmsw-tilt-pivot.
  var PIVOT = readJSON('vmsw-tilt-pivot', {});

  // [TILT] The tilt-axis DROP per head SKU, in inches BELOW the hole centre (owner CAD 2026-08-25;
  // positive = axis below the VESA centre). Blank stays blank to VMSSlope.tiltPoint, which reads it
  // as an explicit 0 (axis at the hole-centre height). Emitted by the section into #vmsw-tilt-axis-drop.
  var DROP = readJSON('vmsw-tilt-axis-drop', {});

  // [TILT-MIGRATE] The 33CE008HEAD is a MIGRATING pivot (a slotted arc whose rotation centre travels),
  // not a single axis. The engine models it from a per-degree VESA-centre displacement table
  // (VMSSlope.MIGRATING_VESA_PATH), passed as tiltTable ONLY for a head that has one; every other head
  // uses the pivot/drop axis. tiltParams(sku) bundles the three so the solve, the length helpers and the
  // drawing all pass the SAME kinematics for a head with no scattered per-call branches.
  var MIGRATE = (S && S.MIGRATING_VESA_PATH) ? S.MIGRATING_VESA_PATH : {};
  function tiltParams(sku) {
    return { tiltPivot: PIVOT[sku], tiltAxisDrop: DROP[sku], tiltTable: MIGRATE[sku] || null };
  }

  // [TILT] The maximum DOWNWARD tilt per head, in degrees. The live value comes from the data
  // snippet's tilt_down on the chosen head (headMaxTilt); this table is the FALLBACK used only
  // when the snippet does not carry it (tilt_down has access.storefront NONE on some heads, see
  // snippets/vms-configurator-data.liquid). Owner figures 2026-08-25: the fixed heads and the
  // 22HE001 tilt down 12 degrees, the 33CE008HEAD sliding head 10. A head absent here AND absent
  // from the snippet has NO tilt data, so the control is hidden for it (headMaxTilt returns null).
  var HEAD_TILT_MAX = { '33HE001': 12, '33HE002': 12, '22HE001': 12, '33CE008HEAD': 10 };
  // The chosen head's maximum downward tilt in degrees, or null when it has none. Prefers the live
  // store datum (comp.tilt_down) and falls back to HEAD_TILT_MAX by SKU. Routed through S.num() so
  // a blank/absent value is null (never a confident 0), the same discipline as the standoff.
  function headMaxTilt(comp) {
    if (!comp) return null;
    var live = S.num(comp.tilt_down);
    if (live !== null) return live;
    var fb = S.num(HEAD_TILT_MAX[comp.sku]);
    return fb;
  }

  // The 11FC External Height Adjustment Collar: the MANDATORY companion line that
  // ships with every 33CE008HEAD (the SLIDING head) build, owner ruling 2026-08-24.
  // It is NOT in the parts pool: it contributes no length of its own (the engine's
  // SLIDING_HEAD_SPAN already models head-plus-collar), so it never touches solve().
  // The section emits it from the store into #vmsw-config as { id, sku, price, title,
  // available }, or null when the product is missing or unavailable. Its price is a
  // dollar string in the SAME shape as the pool (money() reads it with no branch). It
  // ships ONLY with the sliding head, never with 33HE001 or 33HE002; the existing
  // res.slides flag is the single test that decides, threaded through as needsCompanion.
  var COMPANION = (function () {
    var c = CONFIG.companion;
    return (c && typeof c === 'object' && c.id != null) ? c : null;
  })();

  // =====================================================================
  // The parts pool: filter the shared data layer to this page's SKUs
  // =====================================================================
  var PLATE_SKUS = ['11CP006', 'ANGLE-CEILING'];
  var HEAD_SKUS = ['33HE001', '33HE002', '33CE008HEAD', '22HE001'];
  // [VESA GATE] The greatest VESA pattern HEIGHT (top hole to bottom hole) a head can take, in
  // INCHES, as a per-SKU fallback used ONLY when the data snippet does not carry vesa_height_max.
  // 22HE001 fits up to a 200 x 200 mm pattern (owner 2026-08-25: "the 22HE001 only fits up to
  // 200x200mm mounting patterns"), 200 mm = 7.874 in. The snippet DOES emit vesa_height_max (mm)
  // for every component, so headVesaMaxIn prefers that live datum and this literal is the belt-and-
  // suspenders fallback carrying the owner's quote. Keep in step with the test's injected copy.
  var HEAD_VESA_MAX_IN = { '22HE001': 7.874 };
  // [B1] The draggable angle visual. Its DEGREE range and the drawing geometry are
  // PRESENTATION (owner brief 2026-08-28: step 1 is the angle drag ONLY, no degrees box, no pitch
  // box, no chips). [ZERO-DEG 2026-08-29] The range is now 0 to 89 degrees: the owner ruled a 0 degree
  // (true horizontal, parallel to the floor) FLAT ceiling is a valid slope to model, so 0 is the
  // shallowest end. At 90 the run is zero and S.pitchFromDegrees blows up, so 89 is still the hard
  // ceiling. A rise over 12 of 0 (the flat ceiling) is a REAL value, distinct from the UNSET state
  // (state.pitch null before the shopper touches the handle); see setPitch.
  // [COMPACT 2026-08-29] The ceiling angle now DEFAULTS to 45 degrees (owner "make the whole page more
  // compact": present a solution as soon as the three measurements are in, without waiting for a slope
  // entry). ANGLE_DEFAULT_DEG is 45, the handle rests at 45, AND the mount commits that angle so state.pitch
  // is the 45 degree rise over 12 from the start rather than null. A shopper drag or the step 2 degrees box
  // still overrides it through the same S.pitchFromDegrees path. The hinge and viewBox match the inline
  // <template id="vmsw-angle-diagram">.
  var ANGLE_MIN_DEG = 0;
  var ANGLE_MAX_DEG = 89;
  var ANGLE_DEFAULT_DEG = 45;
  var ANGLE_HINGE_X = 70, ANGLE_HINGE_Y = 250;   // pivot corner in the 480 x 300 viewBox
  var ANGLE_VB_W = 480, ANGLE_VB_H = 300;

  var DATA = readJSON('vmscfg-data', null);
  var COMPONENTS = (DATA && Array.isArray(DATA.components)) ? DATA.components : [];
  function bySku(sku) {
    for (var i = 0; i < COMPONENTS.length; i++) { if (COMPONENTS[i].sku === sku) return COMPONENTS[i]; }
    return null;
  }
  // Ordered so the cards render plate-cheap-first / head as listed; missing SKUs
  // are dropped and logged once, never thrown (build brief: degrade gracefully).
  var missing = [];
  function poolFrom(skus) {
    var out = [];
    skus.forEach(function (sku) {
      var c = bySku(sku);
      if (c) out.push(c); else missing.push(sku);
    });
    return out;
  }
  var PLATES = poolFrom(PLATE_SKUS);
  // [ROUND 8 2026-08-30] Head cards are ordered by LIVE PRICE ASCENDING (owner: put the cheapest first),
  // computed from the store price rather than hardcoding an order, the same principle as the configurator's
  // Cheapest pill. Array.prototype.sort is stable in modern engines, so equal prices keep their pool order.
  // The cards render from HEADS.map, so this is the single source for DOM order = visual order = tab order.
  var HEADS = poolFrom(HEAD_SKUS).sort(function (a, b) { return Number(a.price) - Number(b.price); });

  // [RULE, OWNER 2026-09-09] EXCLUDE EVERY SINGLE FIXED PIPE 48 INCHES OR LONGER FROM THE POOL. The main
  // ceiling-mount configurator (assets/vms-configurator.js: its FIXED filter drops nominal_length 48, and
  // the SURCHARGE / SURCHARGE_MIN_LEN constants explain why - "never a 48 inch pipe (UPS oversize)") tops
  // its fixed pool out at NPT42 because one single piece 48 inches or longer ships UPS oversize. This page
  // used to pick NPT48 for the deepest sliding-head builds and merely FLAG the surcharge (the R2 oversize
  // note, an owner call that was pending). The owner has now RULED (2026-09-09 evening): "NPT48 should be
  // excluded on angled wall page." So the constant names and the 48 inch threshold match the configurator,
  // and the single fixed pool tops out at the longest sub-48 stock pipe (NPT42 in today's data).
  // Telescoping units ship collapsed under 48 inches and are NOT single fixed pipes, so the ruling does not
  // touch them (see TELE below); a fixed head that needs a 48 inch length now reaches it with a telescoping
  // unit instead of NPT48, and the sliding head (which only ever rests on a single fixed pipe) is capped at
  // the longest sub-48 stock pipe.
  var SURCHARGE = 15.00;        // UPS oversize surcharge per single piece 48 inches or longer (configurator parity).
  var SURCHARGE_MIN_LEN = 48;   // A single fixed pipe this length or longer is excluded from the pool below, so on
                                // this page the surcharge can never fire and SURCHARGE stays documentary; kept by
                                // the configurator's own name so both pools read the same rule.
  // Fixed pipe pool: every component of type pipe or nipple that carries a
  // nominal_length UNDER SURCHARGE_MIN_LEN. Lengths are READ FROM THE DATA, never hardcoded. Telescoping
  // pipes carry length_min/max not nominal_length, so they fall out naturally.
  var PIPES = COMPONENTS.filter(function (c) {
    return (c.component_type === 'pipe' || c.component_type === 'nipple') &&
      c.nominal_length != null && S.num(c.nominal_length) != null &&
      S.num(c.nominal_length) < SURCHARGE_MIN_LEN;   // [OWNER 2026-09-09] no single fixed pipe 48 inches or longer
  });
  // De-duplicate by length: two SKUs at the same length would double a stock value
  // and let fitStock pick either. Keep the cheapest at each length for the build.
  var PIPE_BY_LEN = {};
  PIPES.forEach(function (c) {
    var len = S.num(c.nominal_length);
    var cur = PIPE_BY_LEN[len];
    if (!cur || Number(c.price) < Number(cur.price)) PIPE_BY_LEN[len] = c;
  });
  var STOCK = Object.keys(PIPE_BY_LEN).map(Number).sort(function (a, b) { return a - b; });
  function pipeAtLength(len) { return PIPE_BY_LEN[len] || null; }

  // [DRAG 2026-08-29] The TELESCOPING pipe pool, for the FIXED-head height drag. When the drag asks for a
  // pipe length no stock FIXED pipe has, a telescoping unit whose [length_min, length_max] range covers it is
  // used instead. The pool follows the main configurator's rule (assets/vms-configurator.js): a component of
  // type 'pipe' that carries length_min, EXCLUDING EXT-4872 (a telescoping pole the configurator drops from
  // its pool). EXT-2033 does not exist in the data. Ascending by length_min. length_max is READ FROM THE
  // DATA, never hardcoded. Empty when the data layer carries no telescoping units (the drag then falls back
  // to the nearest longer fixed stock).
  var TELE = COMPONENTS.filter(function (c) {
    return c.component_type === 'pipe' && c.length_min != null && S.num(c.length_min) != null &&
      c.length_max != null && S.num(c.length_max) != null && c.sku !== 'EXT-4872';
  }).sort(function (a, b) { return S.num(a.length_min) - S.num(b.length_min); });

  if (missing.length) console.warn('vmsw: parts missing from the data layer:', missing.join(', '));

  // If the whole pool failed to load, show the honest notice and stop. The
  // server fallback below stays up too, so the shopper still has the products.
  if (!PLATES.length || !HEADS.length || !STOCK.length) {
    ROOT.innerHTML =
      '<div class="warnbox" role="alert">' + fillHTML(COPY('missing_parts')) + '</div>';
    return;
  }

  // =====================================================================
  // State
  // =====================================================================
  var state = {
    pitch: null,        // riseOver12
    screen: null,       // screen height in
    topHole: null,      // top edge to top hole in
    vesa: null,         // VESA pattern height in
    plateSku: null,
    headSku: null,
    // [TILT] Downward screen tilt in whole degrees, 0 to the chosen head's max. Default 0 (square),
    // so every build is unchanged until the shopper drags the tilt slider. Reset to 0 and re-clamped
    // whenever the head changes (refreshTiltControl); a head with no tilt data keeps it at 0.
    tilt: 0,
    // [AUTO-TILT] True once the shopper has DRAGGED the tilt slider this build. While it is false the
    // calculator is in the "auto" regime: if the square (tilt 0) build does not fit and the head can
    // tilt down, renderAnswer searches for the first tilt that fits, moves the slider to it, and warns.
    // A shopper drag sets this true (their control wins: no auto move). It is cleared back to false, and
    // the tilt reset to 0, whenever a head/plate/TV input changes, so each new build re-derives from
    // square. An auto move does NOT set it (only a real drag does), so the auto regime persists.
    tiltTouched: false,
    // [RESTING 2026-08-29] The optional 11FC External Height Adjustment Collar. FALSE by default: the head
    // RESTS on the threaded collar and the 11FC is not needed. The shopper turns it on (a checkbox shown
    // only when the build has a realizable 11FC rise, res.rise11fc > 0, and the companion is available) to slide
    // the head UP to the corner limit. When on, the 11FC line joins the cart/total/summary, the head is
    // drawn raised by the headroom, and the finished length reflects the raised (headDrop.min) position.
    // Reset to false whenever a head/plate/TV input changes (so each new build starts at rest).
    add11fc: false,
    // [DRAG 2026-08-29] The CONTINUOUS head position set by the height drag, in inches of DROP below the
    // ceiling plate (the hole-centre drop; larger = screen lower). null = the DEFAULT view (rule 5: the head
    // as high as it can go without interfering, i.e. resting on the shortest feasible pipe for a sliding head,
    // the corner-limited shortest pipe for a fixed head). Set by setHeadDrop() (the single writer), which also
    // sets state.pipe and state.add11fc from it. Reset to null (back to the default view) on any head / plate /
    // TV / slope change; a tilt change KEEPS it but re-clamps up to the new corner floor. This REPLACES the old
    // longer-pipe option list (the old optionPipe state), removed with optionsListHTML / wireOptions same date.
    headDrop: null,
    // [DRAG 2026-08-29] The pipe the placed head drop resolves to, or null to use the engine recommendation
    // (the default view). Sliding head: { kind:'fixed', sku, length } (always a stock fixed pipe; the head
    // slides on it). Fixed head: { kind:'fixed'|'tele', sku, length } - a stock fixed pipe when one is exactly
    // the required length, else a telescoping unit whose range covers it. setHeadDrop is the single writer;
    // reset to null alongside state.headDrop on any geometry change.
    pipe: null,
    // [A1/A2] Per-measurement "the shopper typed something out of range" flags. A typed 0 or
    // a negative leaves the state value null (same as blank), so without this the page would
    // fall back to the neutral "fill in the steps" prompt and never explain itself. When any
    // flag is set and the build is not ready, renderAnswer shows notice_out_of_range instead.
    // 'pitch' covers all three pitch inputs (they resolve to one value). Cleared when the
    // field is emptied or a valid value is entered.
    invalid: { screen: false, tophole: false, vesa: false, pitch: false }
  };
  function setInvalid(key, bad) { state.invalid[key] = !!bad; }
  function anyInvalid() {
    return state.invalid.screen || state.invalid.tophole || state.invalid.vesa || state.invalid.pitch;
  }

  // [SUBSTEP LETTERS 2026-09-22] True when one of the three step 1 measurements is still not entered.
  // ready() is false for four different reasons (a measurement, the angle, the plate, the head); only
  // this one is answered by the lettered sub-step prompt, so the other three keep the generic wording.
  function measurementsMissing() {
    return state.screen == null || state.topHole == null || state.vesa == null;
  }

  // [EXAMPLE 2026-08-30] Owner: a configurator should OPEN showing an example of the finished build, then
  // the shopper configures from there. On load the state is seeded with these example values so the real
  // engine renders a complete example build in the result column (drags work on it, so it doubles as a
  // playground). The measurement INPUT BOXES stay EMPTY (a blank is still the real "not measured" state);
  // only state carries the example numbers, and exampleActive gates it. All values live in ONE place here.
  // Chosen because seeding state and rendering the real interactive build is far less intrusive than a
  // parallel read-only render path (renderOk / the elevation figure read state throughout and wire the
  // drags + cart against LIVE state), and it keeps the drags fully working on the example. See the mount.
  var EXAMPLE_DEFAULTS = {
    screen: 28, topHole: 12, vesa: 12,            // a typical mid-size TV (owner example)
    // [ROUND 8 2026-08-30] The example plate is the CHEAPEST plate, 11CP006 (owner). Fine at the example's
    // 45 degrees (its 83 degree pivot gate is far away); it carries ortho art (11CP006-BASE / -SWIVEL) so
    // the drawing renders complete. Head stays 33CE008HEAD (the sliding head, so the height drag can bring
    // the 11FC in for the tooltip demo).
    plateSku: '11CP006', headSku: '33CE008HEAD'
  };
  // TRUE while the result shows the example (before the shopper enters their OWN measurements). The cart is
  // hard-gated to disabled while true (never armed by example data), the example label shows over the
  // result, and the led-through highlight treats the measurements step as not-yet-done. Cleared the first
  // time the shopper types into any measurement box (wireMeasure), which also nulls the other two seeded
  // measurements so ready() falls false until the shopper fills all three themselves. Set false at mount if
  // the example plate/head are not both in this store's pool (degrade to the plain prompt).
  var exampleActive = false;
  // [ROUND 15 2026-08-30] The mobile STEP ACCORDION state (below 990px; desktop keeps the static panel).
  // stepOpen: which step bodies are open. Step 1 (Measure your TV) opens at first paint, 2 to 4 collapsed.
  // autoAdvanced: a per-step latch, so a step auto-collapses and hands off to the next AT MOST ONCE, on its
  // first completion, and never re-collapses a step the shopper reopened by hand (the "do not fight" rule).
  // Completion is SHOPPER-ACTION driven (the example's seeded values never count, round 10 principle):
  // step 1 = the three measurements entered with the example cleared, step 2 = the shopper set the angle
  // (angleTouched, not the 45 default), step 3 = a plate CLICKED (plateTouched, not the example seed),
  // step 4 = a head CLICKED (headTouched). See updateSteps / setupSteps.
  var stepOpen = { 1: true, 2: false, 3: false, 4: false };
  var autoAdvanced = { 1: false, 2: false, 3: false, 4: false };
  var angleTouched = false, plateTouched = false, headTouched = false;
  // [MCP 2026-08-30] A redirect-free cart-add closure for the WebMCP layer, (re)built by wireAddToCart on
  // every addable render and nulled when the build is not addable. The MCP addToCart() guards state first.
  var mcpAddToCart = null;
  var lastRes = null;   // [R3] the last solve, read by the agent snapshot
  // [PAGE TOOLS 2026-09-11] When the reserve gate disables the Add button, mcpAddToCartPublic's own
  // btn.disabled guard would otherwise return a generic reason; this carries the product-naming reserve
  // message so the MCP path relays it. Set in wireAddToCart's reserve branch, cleared on every render.
  var mcpReserveReason = null;
  var mcpCheckedWeight = null;   // [R4] the TV weight the last configure-build checked
  // [TV LOOKUP 2026-10-13] tvSrc 'catalog' (boxes hold an applied row, unedited) or 'manual' (edited from one);
  // tvApplied that row; tvHeld the last explicit row the page cannot place; tvSel the widget's selection.
  var tvSrc = null, tvApplied = null, tvHeld = null, tvSel = null, pendingTv = null, tvLost = null, tvNoteKey = '';
  // tvTyped: boxes the customer typed into (only those are re-read with the mm thresholds); tvIntent 'explicit' from
  // Enter / Look it up / Use this TV / a pick until its result shows; tvAgain during Search again; tvCleared drives the note.
  var tvTyped = {}, tvIntent = null, tvAgain = false, tvCleared = false;

  // =====================================================================
  // Weight rating: the weakest link across the chosen plate and head.
  // Ported EXACTLY from assets/vms-configurator.js (ratingOf/buildWeightRating).
  // [WARN] Number(null) and Number('') are both 0, not NaN. A minimum taken with
  // an isNaN() guard alone would crown a value-less part the weakest link at ZERO
  // pounds, on a rating that hangs a TV over people. Three states per slot:
  //   null    not chosen, so not in the chain;
  //   NaN     chosen but no rating on file, so UNKNOWN, never zero;
  //   value   chosen and rated.
  // =====================================================================
  function ratingOf(comp) {
    if (!comp) return null;
    var w = comp.weight_capacity;
    if (w == null || w === '') return NaN;   // chosen but unrated: unknown, never zero
    var v = Number(w);
    return isNaN(v) ? NaN : v;
  }
  function buildWeightRating(plate, head) {
    var slots = [
      { part: plate ? plate.sku : null, r: ratingOf(plate) },
      { part: head ? head.sku : null, r: ratingOf(head) }
    ];
    var chosen = slots.filter(function (s) { return s.r !== null; });
    if (!chosen.length) return null;
    var known = chosen.filter(function (s) { return !isNaN(s.r); });
    if (!known.length) return null;
    var min = Infinity;
    known.forEach(function (s) { if (s.r < min) min = s.r; });
    var limiters = known.filter(function (s) { return s.r === min; }).map(function (s) { return s.part; });
    var unrated = chosen.filter(function (s) { return isNaN(s.r); }).map(function (s) { return s.part; });
    return { weight: min, limiters: limiters, unrated: unrated, confident: unrated.length === 0 };
  }

  // =====================================================================
  // Shell: build the four steps once, then update only the answer region on
  // change. Building the inputs once is what keeps a typed field from losing
  // focus mid-keystroke (the trap the configurator's in-place updates avoid).
  // =====================================================================
  function thumb(c) {
    // [DESIGN REVIEW F6 2026-09-22] width/height give the browser the box before the photo loads (no shift).
    // They match the square .thumb box (4.8rem, about 50 css px at the page's 10.5 px root); the CSS still sizes it.
    if (c && c.image) return '<span class="thumb"><img src="' + esc(c.image) + '" alt="" width="50" height="50" loading="lazy" decoding="async"></span>';
    return '<span class="thumb thumb--none" aria-hidden="true"></span>';
  }

  // [B1] The step 1 "tips for finding the angle" list. Owner copy is one tip per line (a textarea);
  // we split on newlines, trim, drop blank lines, ESCAPE each line, THEN swap the exact [contact us]
  // token for a real link. Escaping FIRST and substituting after is what keeps owner text from ever
  // reaching innerHTML raw while still letting the one link through. Rendered as an <ol> the browser
  // numbers, so the copy carries no leading numbers. Empty text yields no list at all.
  function tipsListHTML(tipsText, contactUrl) {
    var lines = String(tipsText == null ? '' : tipsText).split(/\r?\n/);
    var items = [];
    for (var i = 0; i < lines.length; i++) {
      var t = lines[i].trim();
      if (t === '') continue;
      var safe = esc(t).replace('[contact us]', '<a href="' + esc(contactUrl) + '">contact us</a>');
      items.push('<li>' + safe + '</li>');
    }
    if (!items.length) return '';
    return '<ol class="vmsw-tips">' + items.join('') + '</ol>';
  }

  // [SUPERSEDED 2026-08-29] The shipped plate note, used ONLY as the fallback for a plate whose
  // custom.picker_blurb is blank (plateCardText). No longer the primary card line.
  function plateNoteFor(sku) {
    if (sku === '11CP006') return COPY('plate_note_11cp006');
    if (sku === 'ANGLE-CEILING') return COPY('plate_note_angle');
    return '';
  }
  // [SUPERSEDED 2026-08-29] headNoteFor is no longer rendered on the head card (the card is templated
  // from metafields now, see headCardText/headCardHtml). Kept for the fixed/slide copy keys it references,
  // which stay defined; no caller reads it today.
  function headNoteFor(comp) {
    return S.headSlides(comp.length_offset) ? COPY('head_note_slide') : COPY('head_note_fixed');
  }

  // [COMPACT 2026-08-29] The plate card line is DATA-DRIVEN: the plate product's custom.picker_blurb
  // (exposed by snippets/vms-configurator-data.liquid) when it carries one, else the shipped plate note.
  // Plain text; the caller escapes it. Owner 2026-08-29.
  function plateCardText(comp) {
    var b = comp && comp.picker_blurb;
    if (typeof b === 'string' && b.trim() !== '') return b.trim();
    return plateNoteFor(comp.sku);
  }

  // [COMPACT 2026-08-29] Fill a ", "-separated template, DROPPING any clause that references a token with
  // no value (a missing metafield), together with its separator, so a bracket never renders. A clause with
  // every token present is filled through fill(); a clause with any empty/absent token is dropped whole.
  // Pure over fill(); the Node harness lifts it. Used by headCardText for the head card line.
  function fillClauses(tmpl, map) {
    var m = map || {};
    var parts = String(tmpl == null ? '' : tmpl).split(', ');
    var kept = [];
    for (var i = 0; i < parts.length; i++) {
      var seg = parts[i], drop = false, re = /\[([a-z_]+)\]/g, mm;
      while ((mm = re.exec(seg)) !== null) {
        var v = m[mm[1]];
        if (v == null || v === '') { drop = true; break; }
      }
      if (!drop) kept.push(fill(seg, m));
    }
    return kept.join(', ');
  }

  // [COMPACT 2026-08-29] The head card line, TEMPLATED FROM THE HEAD'S METAFIELDS (owner: "always driven by
  // the metafield"). Tokens: [min]/[max] the screen range (screen_min/screen_max), [lb] the weight_capacity,
  // [tilt] "0 to N deg" from tilt_down (headMaxTilt) with the DEG_SIGN, [vesa] "WxH" in mm from
  // vesa_width_max x vesa_height_max. A token whose metafield is absent drops with its clause (fillClauses).
  // Pure over S.num/nice/headMaxTilt/COPY + the DEG_SIGN constant, so the harness lifts it.
  function headCardText(comp) {
    var maxTilt = headMaxTilt(comp);
    var vw = S.num(comp && comp.vesa_width_max), vh = S.num(comp && comp.vesa_height_max);
    var map = {
      min: (S.num(comp && comp.screen_min) === null) ? '' : nice(comp.screen_min),
      max: (S.num(comp && comp.screen_max) === null) ? '' : nice(comp.screen_max),
      lb: (S.num(comp && comp.weight_capacity) === null) ? '' : nice(comp.weight_capacity),
      tilt: (maxTilt === null) ? '' : ('0 to ' + Math.round(maxTilt) + DEG_SIGN),
      vesa: (vw === null || vh === null) ? '' : (Math.round(vw) + 'x' + Math.round(vh))
    };
    return fillClauses(COPY('head_card_template'), map);
  }

  // [COMPACT 2026-08-29] The head card's inner HTML: the templated line (escaped), plus the head's
  // custom.picker_blurb in <strong> when it carries one (escaped). This is the ONLY place the head card
  // note is HTML rather than plain text; pickCard drops it in verbatim, so every dynamic part is escaped
  // here and only the literal <strong> tags are markup.
  function headCardHtml(comp) {
    var html = esc(headCardText(comp));
    var b = comp && comp.picker_blurb;
    if (typeof b === 'string' && b.trim() !== '') html += ' <strong>' + esc(b.trim()) + '</strong>';
    return html;
  }

  // [VESA GATE] A head's maximum VESA pattern HEIGHT in INCHES, or null when it has no stated limit.
  // Prefers the live store datum vesa_height_max (millimetres, emitted by the shared data snippet),
  // falling back to the owner-quoted HEAD_VESA_MAX_IN literal, then to null (no limit -> never gated).
  // Pure over S.num + the literal. Owner 2026-08-25: a head "should not show" when the pattern the
  // shopper enters is taller than the head can take.
  function headVesaMaxIn(comp) {
    var mm = S.num(comp && comp.vesa_height_max);
    if (mm !== null) return mm / 25.4;
    var lit = comp ? HEAD_VESA_MAX_IN[comp.sku] : null;
    return (lit == null) ? null : lit;
  }
  // [VESA GATE] The forgiveness band, in INCHES, added on top of a head's stated VESA-height limit before
  // the head is hidden. Owner 2026-08-25, verbatim: "I entered 8 inches between vesa mounting holes and it
  // did not show the 22HE001 as an option. Many people will measure 200mm as 8 inches. I changed to 7.75
  // inches and it appeared. Need to make sure if people enter 8 inches they get the 22HE001 as an option."
  // 200 mm (the 22HE001 limit) is 7.874 in, so a shopper who rounds 200 mm to a flat 8 in was landing just
  // over the strict limit and losing the head. A quarter-inch band lets that reading through: the gate now
  // fires at 7.874 + 0.25 = 8.124 in, so 8 in shows the head and a genuinely larger pattern still hides it.
  var VESA_GATE_TOLERANCE_IN = 0.25;
  // [VESA GATE] Whether a head must be HIDDEN for a given VESA height (the top-to-bottom-hole distance,
  // in INCHES). True only when the VESA is known AND the head has a limit AND the VESA exceeds the limit
  // PLUS the tolerance band above. An unknown VESA (blank) or a head with no limit never gates (owner's
  // 200 mm rule applies to 22HE001; 12 in = 304.8 mm hides it, 8 in shows it). Pure over S.num.
  function headGatedByVesa(comp, vesaIn) {
    var v = S.num(vesaIn), max = headVesaMaxIn(comp);
    if (v === null || max === null) return false;
    return v > max + VESA_GATE_TOLERANCE_IN;
  }

  // [PLATE ANGLE GATE 2026-08-30] The greatest ceiling angle a plate can pivot to, in DEGREES, per SKU.
  // Owner-measured 2026-08-30: the 11CP006 pivots 0 to 83 degrees (a hard mechanical limit, in no spec
  // sheet or metafield, do not go hunting for one). The ANGLE-CEILING adapter is DISTINCT (0 to 94 degrees)
  // and is deliberately absent here so it is never gated. A plate not listed has no angle limit.
  var PLATE_MAX_ANGLE_DEG = { '11CP006': 83 };
  // Gate strictly ABOVE the limit: 83.0 itself is allowed, 84 is not. The drag commits whole degrees, so a
  // tiny epsilon absorbs the float round-trip through pitchDegrees(pitchFromDegrees(83)).
  var ANGLE_GATE_TOLERANCE_DEG = 1e-6;
  function plateMaxAngle(comp) {
    return (comp && PLATE_MAX_ANGLE_DEG[comp.sku] != null) ? PLATE_MAX_ANGLE_DEG[comp.sku] : null;
  }
  // Whether a plate must be DISABLED for the committed slope. True only when the plate has a limit AND the
  // committed angle is known AND it exceeds the limit. An unset pitch (null) or a plate with no limit never
  // gates. Pure over S.pitchDegrees (riseOver12 -> degrees).
  function plateGatedByAngle(comp, pitch) {
    var max = plateMaxAngle(comp);
    if (max === null) return false;
    var deg = S.pitchDegrees(pitch);
    if (deg === null) return false;
    return deg > max + ANGLE_GATE_TOLERANCE_DEG;
  }

  // [DIAG GATE 2026-08-30] Derive the TV's diagonal screen size from the entered overall HEIGHT and gate a
  // head that is sized for smaller TVs. Owner formula 2026-08-30: a 16:9 screen's height is diagonal x
  // 0.4903, and real TVs run about 1 inch taller than the bare screen, so diagonal ~ (height - 1) / 0.4903.
  // Example: height 28 -> (28 - 1) / 0.4903 ~ 55.07 in, so the 22HE001 (screen_max 43) should not be
  // recommended even though its 200 mm VESA pattern fits. Both dials are owner-adjustable.
  var DIAG_BEZEL_ALLOWANCE_IN = 1;     // TVs run about 1 inch taller than the 16:9 screen (owner 2026-08-30)
  var DIAG_HEIGHT_RATIO = 0.4903;      // 16:9 height = diagonal x 0.4903 (owner 2026-08-30)
  // The derivation is approximate, so a head is gated only when the derived diagonal exceeds screen_max by
  // more than this band. Owner-adjustable dial; 2 inches leaves a genuine larger TV gated while absorbing
  // the estimate's slop (e.g. height 28 -> 55.07 still gates the 43 in 22HE001 with room to spare).
  var DIAG_GATE_TOLERANCE_IN = 2;
  // The derived diagonal in inches, or null when the height is blank / out of range (posNum rejects a 0 or
  // a negative the same as blank), so a missing height derives no diagonal and gates nothing.
  function derivedDiagIn(screenHeightIn) {
    var h = posNum(screenHeightIn);
    if (h === null) return null;
    return (h - DIAG_BEZEL_ALLOWANCE_IN) / DIAG_HEIGHT_RATIO;
  }
  // Whether a head must be DISABLED because the TV (derived from the entered height) is bigger than the head
  // is rated for. True only when the height derives a diagonal AND the head has a screen_max AND the derived
  // diagonal exceeds screen_max plus the tolerance. A blank height, or a head with no screen_max, never
  // gates. screen_min is deliberately NOT gated here (the small-side limit is an open owner call).
  function headGatedByDiag(comp, screenHeightIn) {
    var diag = derivedDiagIn(screenHeightIn);
    var max = S.num(comp && comp.screen_max);
    if (diag === null || max === null) return false;
    return diag > max + DIAG_GATE_TOLERANCE_IN;
  }
  // [VESA GATE] Presentation: hide or show each head card for the current state.vesa, and if the
  // SELECTED head just became gated, DESELECT it so the answer falls back to the "fill in the steps"
  // prompt (ready() then returns false). A hidden card is display:none via .vmsw-hidden, so it leaves
  // the tab order and the a11y tree (owner: "hidden ... not merely disabled"). The card DOM is built
  // once in buildShell; this only toggles classes and never re-renders the picker, so a shown-again
  // head keeps its place. Called at the top of renderAnswer, the single funnel for every input change.
  function refreshHeadGate() {
    var box = el('vmsw-heads');
    if (!box) return;
    box.querySelectorAll('.vmsw-pick-card').forEach(function (card) {
      var comp = bySku(card.dataset.head);
      // [VESA GATE] HIDES the card (owner ruling: hidden, not merely disabled). [ROUND 10 2026-08-30]
      // The measurement-driven gates (VESA hide and the diagonal disable below) are INERT while the example
      // is showing: the example seeds vesa=12 / screen=28, which must NOT rule any head out. So on fresh
      // load the head list is the FULL price-sorted pool (22HE001 first). The angle gate (refreshPlateGate)
      // stays live. The moment the shopper enters a real measurement (exampleActive clears) these run.
      var vesaGated = (!exampleActive && comp) ? headGatedByVesa(comp, state.vesa) : false;
      card.classList.toggle('vmsw-hidden', vesaGated);
      card.hidden = vesaGated;
      // [DIAG GATE 2026-08-30] A still-visible head is DISABLED with a reason when the TV derived from the
      // entered overall height is bigger than the head is rated for (screen_max). A VESA-hidden head skips
      // this (it is already gone). This is the disable-plus-reason treatment, distinct from the VESA hide.
      var diagGated = (!exampleActive && !vesaGated && comp) ? headGatedByDiag(comp, state.screen) : false;
      card.classList.toggle('vmsw-disabled', diagGated);
      card.setAttribute('aria-disabled', diagGated ? 'true' : 'false');
      var gnote = card.querySelector('.vmsw-pick__gate');
      if (gnote) {
        if (diagGated) {
          gnote.textContent = fill(COPY('head_gate_diag'), {
            max: nice(comp.screen_max),
            diag: String(Math.round(derivedDiagIn(state.screen)))
          });
          gnote.hidden = false;
        } else { gnote.textContent = ''; gnote.hidden = true; }
      }
      // Deselect a now-ineligible SELECTED head (VESA-hidden OR diagonal-disabled) so the answer falls back
      // to its needs-a-head state (ready() then returns false). Same reset the head cards already used.
      if ((vesaGated || diagGated) && state.headSku === card.dataset.head) {
        state.headSku = null;             // deselect the now-ineligible head
        state.headDrop = null; state.pipe = null;   // [DRAG] the head changed: back to the default position
        state.add11fc = false;               // [RESTING] the 11FC toggle is per-build; a new build starts at rest
        card.classList.remove('sel');
        card.setAttribute('aria-pressed', 'false');
        var pick = card.querySelector('[data-pick]');
        if (pick) pick.textContent = COPY('pick_choose');
      }
    });
  }

  // [PLATE ANGLE GATE 2026-08-30] Presentation twin of refreshHeadGate for the PLATE cards, but the plate
  // is DISABLED with a reason line (not hidden): when the committed slope exceeds a plate's pivot limit
  // (11CP006 above 83 degrees) its card takes .vmsw-disabled + aria-disabled and shows the reason, and if it
  // was the SELECTED plate it is deselected (mirroring the head gate) so the answer falls back to its
  // needs-a-plate state. Called at the top of renderAnswer alongside refreshHeadGate.
  function refreshPlateGate() {
    var box = el('vmsw-plates');
    if (!box) return;
    box.querySelectorAll('.vmsw-pick-card').forEach(function (card) {
      var comp = bySku(card.dataset.plate);
      var gated = comp ? plateGatedByAngle(comp, state.pitch) : false;
      card.classList.toggle('vmsw-disabled', gated);
      card.setAttribute('aria-disabled', gated ? 'true' : 'false');
      var gnote = card.querySelector('.vmsw-pick__gate');
      if (gnote) {
        if (gated) {
          gnote.textContent = fill(COPY('plate_gate_angle'), { max: nice(plateMaxAngle(comp)) });
          gnote.hidden = false;
        } else { gnote.textContent = ''; gnote.hidden = true; }
      }
      if (gated && state.plateSku === card.dataset.plate) {
        state.plateSku = null;             // deselect the now-ineligible plate
        state.headDrop = null; state.pipe = null;
        state.add11fc = false;
        card.classList.remove('sel');
        card.setAttribute('aria-pressed', 'false');
        var pick = card.querySelector('[data-pick]');
        if (pick) pick.textContent = COPY('pick_choose');
      }
    });
  }

  // [TILT] Keep state.tilt inside the current head's bounds. [COMPACT 2026-08-29] The standalone tilt
  // SLIDER is gone (the build-image tilt drag is the control now), so this no longer touches any DOM: it
  // is a STATE-ONLY clamp. The tilt is forced to 0 unless the build is ready() AND a head is chosen AND
  // that head has tilt data with a positive max, so a head without tilt data can never leave a stale angle
  // in the solve. On a head with a smaller maximum than the held tilt, it re-clamps down. Called at the
  // top of renderAnswer alongside refreshHeadGate. Whole degrees.
  function refreshTiltControl() {
    var head = state.headSku ? bySku(state.headSku) : null;
    var maxTilt = headMaxTilt(head);
    var maxWhole = (maxTilt === null) ? null : Math.floor(maxTilt);
    var show = !!(ready() && head && maxWhole !== null && maxWhole > 0);
    if (!show) {
      state.tilt = 0;                          // a no-tilt head never feeds tilt into solve()
      return;
    }
    // Re-clamp: the head may have changed to one with a smaller maximum since the last render.
    if (state.tilt > maxWhole) state.tilt = maxWhole;
    if (state.tilt < 0) state.tilt = 0;
  }

  // A pick card, reusing the configurator's .plate card look. The whole card is
  // the button; a nested button would be invalid, so the Choose / Selected
  // affordance is a span, updated in place on selection.
  // [COMPACT 2026-08-29] noteHtml is ALREADY-SAFE HTML: the caller escapes every dynamic part. The plate
  // caller passes esc(plateCardText(...)); the head caller passes headCardHtml(...) (escaped text plus a
  // literal <strong> for the picker_blurb). pickCard drops it in verbatim, so all escaping lives in the
  // callers, never here.
  function pickCard(comp, noteHtml, kind) {
    var sku = comp.sku;
    return '<button type="button" class="plate vmsw-pick-card" data-' + kind + '="' + esc(sku) +
      '" aria-pressed="false">' +
      thumb(comp) +
      '<span class="pbody">' +
      '<span class="pt">' + esc(sku) + '</span>' +
      '<span class="pn">' + noteHtml + '</span>' +
      // [GATE 2026-08-30] The disable reason line, empty and hidden until a gate fires (plate angle gate or
      // head diagonal gate). refreshPlateGate / refreshHeadGate fill its textContent and toggle hidden; the
      // VESA gate hides the whole card instead and never uses this. role=note so AT announces the reason.
      '<span class="vmsw-help-line vmsw-pick__gate" role="note" hidden></span>' +
      '</span>' +
      '<span class="vmsw-pickcol">' +
      '<span class="pp">' + money(comp.price) + '</span>' +
      '<span class="vmsw-pick" data-pick>' + esc(COPY('pick_choose')) + '</span>' +
      '</span>' +
      '</button>';
  }

  // [ROUND 5 2026-08-30] data-step-n keys the step for the led-through highlight (updateStepHighlight adds
  // .vmsw-step--active to the first incomplete step). headExtra is optional trailing header HTML (the angle
  // step passes the "?" tips-tooltip button so it sits beside the heading). Kept ALREADY-SAFE by the caller.
  // [ROUND 15 2026-08-30] Each step is a collapsible accordion block below 990px (desktop keeps every body
  // open, so the panel looks exactly as before). The header is a real <button> (44px tap target, aria-expanded
  // / aria-controls, a CSS-triangle cue), so it toggles by keyboard and pointer; the body carries the id the
  // button controls. headExtra (the angle step's tips button) stays a SIBLING of the toggle inside the header
  // (a button cannot nest inside a button). data-step-n keys both the highlight and the accordion.
  function stepBlock(n, titleKey, inner, headExtra) {
    return '<section class="vmsw-step" data-step-n="' + n + '">' +
      '<h3 class="vmsw-step__h" id="vmsw-s' + n + 'h">' +
      '<button type="button" class="vmsw-step__toggle" id="vmsw-s' + n + 'btn" aria-expanded="true" aria-controls="vmsw-s' + n + 'body">' +
      '<span class="vmsw-step__n" aria-hidden="true">' + n + '</span>' +
      '<span class="vmsw-step__title">' + esc(COPY(titleKey)) + '</span>' +
      '<span class="vmsw-step__cue" aria-hidden="true"></span>' +
      '</button>' +
      (headExtra || '') +
      '</h3>' +
      '<div class="vmsw-step__body" id="vmsw-s' + n + 'body" role="region" aria-labelledby="vmsw-s' + n + 'btn">' + inner + '</div>' +
      '</section>';
  }

  function buildShell() {
    // STEP 1 is the draggable angle visual, and ONLY that (owner ruling 2026-08-28). The step
    // shows the title, the drag label, a short a11y help line, the SVG stage, the live degrees
    // readout, and a "tips for finding the angle" list. The old degrees box, pitch box, ratio
    // chips, pitch measuring diagram and sub-9-in-12 notice are all gone. The drag (and its
    // keyboard) is the sole way to set the slope and feeds the one canonical state.pitch through
    // setPitch(). Drag range 0 to 89 degrees (0 = a flat, horizontal ceiling, owner ruling 2026-08-29).
    //
    // The angle SVG is authored inline in the section as a hidden <template> (the same pattern as
    // the step 2 measurement diagram), so it renders with the page web fonts and can host the
    // focusable drag handle. If that template is absent (an older template JSON) the visual drops
    // out and, with no other pitch input left, the shopper cannot set the slope; the section always
    // ships the template, so this is a theory-only degradation.
    var angleTpl = document.getElementById('vmsw-angle-diagram');
    var angleSvg = angleTpl ? angleTpl.innerHTML : '';

    // The draggable angle. The handle inside the SVG carries role="slider" and its accessible
    // name/description come from the label + help here via aria-labelledby / aria-describedby.
    // Below it: the live readout (aria-hidden, the slider handle conveys the value to assistive
    // tech via aria-valuetext), then the owner tips list, then the optional numeric entry.
    // [COMPACT 2026-08-29] This block renders as STEP 2 now (the measurements are step 1). The optional
    // "Or enter the angle" box (#vmsw-degrees) sits below the tips: typing feeds commitAngle (blank leaves
    // the drag value) and stays in sync with the drag both ways. inputmode decimal, 0 to 89.
    // [ROUND 5 2026-08-30] The drag stage and the "Or enter the angle" box sit in ONE row (owner: same
    // area). The tips list moved OUT of the always-shown flow into a popover (#vmsw-tips-pop) opened by the
    // "?" button in the step heading (setupTips wires it); role=dialog, hidden until opened. The tips COPY
    // (step1_tips, step1_tips_lead) is unchanged, just relocated.
    var step1 =
      '<div class="vmsw-field vmsw-angle">' +
      '<span class="vmsw-lab" id="vmsw-angle-label">' + esc(COPY('step1_angle_label')) + '</span>' +
      '<span class="vmsw-help-line" id="vmsw-angle-help">' + esc(COPY('step1_angle_help')) + '</span>' +
      '<div class="vmsw-angle__row">' +
      (angleSvg ? '<div class="vmsw-angle__stage">' + angleSvg + '</div>' : '') +
      '<div class="vmsw-field vmsw-degrees">' +
      '<label class="vmsw-lab" for="vmsw-degrees">' + esc(COPY('step2_degrees_label')) + '</label>' +
      '<input class="vmsw-input vmsw-degrees__input" id="vmsw-degrees" name="ceiling_angle" type="text" inputmode="decimal" autocomplete="off">' +
      '<p class="vmsw-angle__readout" id="vmsw-angle-readout" aria-hidden="true" hidden></p>' +
      '</div>' +
      '</div>' +
      '<div class="vmsw-tips-pop" id="vmsw-tips-pop" role="dialog" aria-label="' + esc(COPY('tips_tooltip_label')) + '" hidden>' +
      '<p class="vmsw-help-line vmsw-tips-lead">' + esc(COPY('step1_tips_lead')) + '</p>' +
      tipsListHTML(COPY('step1_tips'), CONTACT_URL) +
      '</div>' +
      '</div>';

    // [STEP1-OVERLAY-R2 2026-08-29] The measurements are STEP 1 and the labelled inputs sit ON the TV
    // drawing. Owner round 2: NO rounded boxes (the card was wasted space) - each callout is just the
    // number badge with its description right next to it, then the input below, in bigger type. At 720px
    // and up the three fields are absolutely positioned over the TV panel by CSS, boxless, the HTML badge
    // being the visible number next to the copy (the SVG badges on the drawing are hidden on desktop and
    // serve the phone layout). Callout 1 is at the panel top-left, callouts 2 and 3 by the top and bottom
    // mounting-hole rows. Below 720px the fields stack beneath the picture as a plain form (unchanged phone
    // layout) and the SVG badges show on the drawing above. There is still ONE canonical input set (no
    // second copy, nothing display:none). Each field carries a NUMBER BADGE (1, 2, 3). The per-field help
    // line stays in the DOM wired by aria-describedby but is visually hidden with .vmsw-sr-only (the visible
    // label carries the description now; kept readable by assistive tech, not display:none). The SVG is
    // authored inline in the section as a hidden <template> so it renders with the page web fonts; if it is
    // absent (an older template JSON) the fields fall back to the plain stacked form and the page works.
    // [SUBSTEP LETTERS 2026-09-22] The badge is a LETTER from owner copy (letterKey -> measure_letter_a/b/c),
    // not a hardcoded digit. The visible badge stays aria-hidden (it is decoration beside the text) and a
    // visually hidden "A. " prefix inside the same <label> puts the letter at the FRONT of the input's
    // accessible name, so a screen reader hears "A. Height of your TV, in inches". The sr-only span is
    // absolutely positioned (.vmsw-sr-only), so it adds no width and the label layout is unchanged on
    // screen. The literal space after the badge is kept so the visible gap does not change either.
    // [DESIGN REVIEW F10 2026-09-22] Each input carries a name (FIELD_NAMES) so autofill and assistive tech see a
    // real field name; there is no form submit, so nothing else reads it.
    var FIELD_NAMES = { screen: 'tv_height', tophole: 'top_to_top_hole', vesa: 'hole_to_hole' };
    function measureField(id, letterKey, labelKey, helpKey) {
      var letter = COPY(letterKey);
      return '<div class="vmsw-field vmsw-field--pair vmsw-measure__field vmsw-measure__field--' + id + '">' +
        '<label class="vmsw-lab" for="vmsw-' + id + '">' +
        '<span class="vmsw-measure__badge" aria-hidden="true">' + esc(letter) + '</span> ' +
        '<span class="vmsw-sr-only">' + esc(letter) + '. </span>' + esc(COPY(labelKey)) + '</label>' +
        '<span class="vmsw-help-line vmsw-sr-only" id="vmsw-' + id + '-help">' + esc(COPY(helpKey)) + '</span>' +
        '<input class="vmsw-input" id="vmsw-' + id + '" name="' + FIELD_NAMES[id] + '" type="text" inputmode="decimal" autocomplete="off" aria-describedby="vmsw-' + id + '-help">' +
        // [A2] The millimeter-detected note. Empty and hidden until readMeasure converts a mm
        // entry, so it never adds a focus stop and never shows on a normal inch entry.
        '<span class="vmsw-mm-note" id="vmsw-' + id + '-mmnote" role="status" aria-live="polite" hidden></span>' +
        '</div>';
    }
    // Tab order is DOM order: height (badge A), then top-to-top-hole (B), then VESA (C), the same order the
    // spec pins. The visual position is set by CSS alone and does not reorder the fields.
    // [SUBSTEP LETTERS 2026-09-22] The second argument is the COPY key of the badge letter, not a digit.
    var measureFields =
      measureField('screen', 'measure_letter_a', 'step2_screen_label', 'step2_screen_help') +
      measureField('tophole', 'measure_letter_b', 'step2_tophole_label', 'step2_tophole_help') +
      measureField('vesa', 'measure_letter_c', 'step2_vesa_label', 'step2_vesa_help');
    var diagramTpl = document.getElementById('vmsw-measure-diagram');
    var diagramSvg = diagramTpl ? diagramTpl.innerHTML : '';
    // [ROUND 13 2026-08-30] The "We need 3 measurements." caption over the drawing is removed (owner);
    // the three measurements are described by the labelled callouts on the TV drawing. step2_diagram_caption
    // is COPY_DEFAULTS-only now (schema field + pass-through gone, no render).
    var step2 =
      (diagramSvg
        ? '<div class="vmsw-measure"><div class="vmsw-measure__stage">' + diagramSvg + '</div>' +
          '<div class="vmsw-measure__inputs">' + measureFields + '</div></div>'
        : measureFields);

    // [COMPACT 2026-08-29] The plate card line is the plate's custom.picker_blurb (falling back to the
    // shipped note); the head card line is templated from the head's metafields plus the head's
    // picker_blurb in <strong>. Both callers hand pickCard ALREADY-SAFE HTML (plate: esc()'d text; head:
    // headCardHtml, which escapes every dynamic part).
    var step3 = '<div class="vmsw-cards" id="vmsw-plates">' +
      PLATES.map(function (p) { return pickCard(p, esc(plateCardText(p)), 'plate'); }).join('') +
      '</div>';

    var step4 = '<div class="vmsw-cards" id="vmsw-heads">' +
      HEADS.map(function (h) { return pickCard(h, headCardHtml(h), 'head'); }).join('') +
      '</div>';

    // [COMPACT 2026-08-29] STEP ORDER: 1 = measurements (step2 content, "Measure your TV"), 2 = ceiling
    // angle (step1 content, "What angle is the ceiling?"), 3 = plate, 4 = head. The copy keys keep their
    // old step1_/step2_ names; only the render order and the two titles moved (see the COPY_DEFAULTS note).
    // [COMPACT 2026-08-29] The standalone tilt SLIDER section is REMOVED: the tilt drag on the build image
    // is the tilt control now (tiltTouched comes from that drag through commitTilt). The image keeps its
    // own tilt readout; the auto-tilt search and the tilt disclosure line are unchanged.
    // [ROUND 5/15 2026-08-30] APP SHELL (owner: "more compact, almost like an app"). Two regions inside
    // .vmsw-app: a LEFT steps PANEL (steps 1 to 4) and a RIGHT build RESULT (#vmsw-answer, the elevation
    // drawing + summary + add-to-cart). Desktop (>= 990px) lays them out as two columns with the result
    // sticky. Below 990px (round 15) the panel is a plain inline stack ABOVE the result and each step is a
    // collapsible accordion block (the off-canvas drawer is retired); one DOM node, never a second copy of any
    // input. The tips "?" button rides the angle step heading. Empty text nodes are avoided (Dawn div:empty).
    // [ROUND 8 2026-08-30] Owner: the angle tips trigger reads "Angle finding tips" instead of a bare "?".
    // The VISIBLE text is the accessible name now, so the aria-label is dropped (it would just duplicate it).
    // Styled as a quiet text-link button (vmsw-tip-link), not a pill; same popover mechanics. The 11FC row
    // trigger is a text link too since 2026-08-31 (reads "What is this?"), see [ROUND 17] on that block.
    var tipsBtn = '<button type="button" class="vmsw-tip-link" id="vmsw-tips-btn" aria-expanded="false"' +
      ' aria-controls="vmsw-tips-pop">' + esc(COPY('tips_tooltip_label')) + '</button>';
    ROOT.innerHTML =
      '<div class="vmsw-inner">' +
      '<h2 class="vmsw-h2">' + esc(COPY('calc_heading')) + '</h2>' +
      '<div class="vmsw-app">' +
      // [ROUND 15 2026-08-30] The steps panel renders INLINE now (owner: on mobile the steps sit above the
      // build as collapsed blocks, not behind a button). The off-canvas drawer, its open button, the close
      // button and the backdrop are all retired; the SAME one-DOM panel is a static column on desktop and an
      // inline stack above the result below 990px. The steps are the collapsible blocks (see stepBlock).
      '<div class="vmsw-panel" id="vmsw-panel">' +
      stepBlock(1, 'step2_title', step2) +
      stepBlock(2, 'step1_title', step1, tipsBtn) +
      stepBlock(3, 'step3_title', step3) +
      stepBlock(4, 'step4_title', step4) +
      '</div>' +
      '<div class="vmsw-result">' +
      // [DESIGN REVIEW F5 2026-09-22] #vmsw-answer is NOT a live region any more (it re-renders about 4,000
      // characters on every keystroke, and a screen reader re-read all of it over the slider's own value). The
      // visually hidden #vmsw-status (role=status, outside the re-rendered box) takes ONE short sentence per
      // solve instead (announceSolve, debounced). Notices keep their own role=alert.
      '<section class="vmsw-answer" id="vmsw-answer">' +
      '<p class="vmsw-answer__prompt">' + esc(COPY('answer_prompt')) + '</p>' +
      '</section>' +
      '<p class="vmsw-sr-only" id="vmsw-status" role="status"></p>' +
      '</div>' +
      '</div>' +
      // [ROUND 13 2026-08-30] The "Much of this calculator is based on..." credit sentence is removed
      // (owner); Q2 of the bottom Q&A now carries the measure-article link. measure_link_lead and
      // measure_link_text are COPY_DEFAULTS-only now (schema field + pass-through gone, no render).
      '</div>';

    // The handle's "Drag" word is owner copy like every other string, so set it from COPY() rather
    // than trusting the fallback baked into the template SVG. textContent (never innerHTML) keeps it
    // inert text. Absent template (older template JSON) -> no node, and the slider still works.
    var angleWord = document.getElementById('vmsw-angle-label-word');
    if (angleWord) angleWord.textContent = COPY('angle_handle_word');
  }

  // =====================================================================
  // Wiring
  // =====================================================================
  function el(id) { return document.getElementById(id); }

  // [ROUND 5 2026-08-30] Reflect a plate/head selection onto the cards (sel class, aria-pressed, pick label)
  // WITHOUT a click. Used to pre-select the example plate/head at mount; the click path in wireCards still
  // owns interactive selection. Mirrors that path's per-card update exactly.
  function reflectPick(containerId, dataKey, sku) {
    var box = el(containerId);
    if (!box) return;
    box.querySelectorAll('.vmsw-pick-card').forEach(function (c) {
      var on = c.dataset[dataKey] === sku;
      c.classList.toggle('sel', on);
      c.setAttribute('aria-pressed', on ? 'true' : 'false');
      var pick = c.querySelector('[data-pick]');
      if (pick) pick.textContent = on ? COPY('pick_selected') : COPY('pick_choose');
    });
  }

  // [ROUND 5 2026-08-30] Led-through highlight: add .vmsw-step--active to the FIRST incomplete step so the
  // shopper is drawn to what to do next. Measurements complete when all three are entered AND the example is
  // no longer showing (so the example opening leads to "Measure your TV"); the angle always has a value (the
  // 45 default), so it never blocks; plate/head complete when selected. CSS only from there (an accent left
  // border). No scroll hijack. Called at the end of renderAnswer, the single funnel.
  function updateStepHighlight() {
    var done = {
      1: !exampleActive && state.screen != null && state.topHole != null && state.vesa != null,
      2: state.pitch != null,
      3: !!state.plateSku,
      4: !!state.headSku
    };
    var activeN = null;
    for (var i = 1; i <= 4; i++) { if (!done[i]) { activeN = i; break; } }
    for (var n = 1; n <= 4; n++) {
      var s = document.querySelector('.vmsw-step[data-step-n="' + n + '"]');
      if (s) s.classList.toggle('vmsw-step--active', n === activeN);
    }
  }

  // [ROUND 15 2026-08-30] SHOPPER-ACTION completion for the step accordion (distinct from updateStepHighlight's
  // STATE-based done, which drives the unchanged desktop border): the example's seeded angle/plate/head never
  // count. Step 1 = the three measurements entered with the example cleared; step 2 = the shopper set the angle
  // (angleTouched); step 3 = a plate clicked; step 4 = a head clicked.
  function stepComplete(n) {
    if (n === 1) return !exampleActive && state.screen != null && state.topHole != null && state.vesa != null;
    if (n === 2) return angleTouched;
    if (n === 3) return plateTouched;
    return headTouched;   // n === 4
  }
  // Below 990px the accordion is live (one body open at a time by default, any header toggled by hand); at and
  // above 990px every body is shown (desktop panel unchanged), so reflectStep forces open there.
  function accordionMode() { return !!(window.matchMedia && window.matchMedia('(max-width: 989px)').matches); }
  function reflectStep(n) {
    var sec = document.querySelector('.vmsw-step[data-step-n="' + n + '"]');
    if (!sec) return;
    var btn = document.getElementById('vmsw-s' + n + 'btn');
    var open = accordionMode() ? !!stepOpen[n] : true;   // desktop: bodies always open (stepOpen is left intact)
    sec.classList.toggle('vmsw-step--open', open);
    // [DESIGN REVIEW F7 2026-09-22] At 990 px and up the header does nothing (bodies always open, click inert),
    // so it is neither a tab stop nor announced as expandable: drop aria-expanded and take it out of the tab
    // order. Below 990 both come back (the accordion toggle). Pointer clicks still reach the round 1 listeners.
    if (btn) {
      if (accordionMode()) {
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.removeAttribute('tabindex');
      } else {
        btn.removeAttribute('aria-expanded');
        btn.setAttribute('tabindex', '-1');
      }
    }
    sec.classList.toggle('vmsw-step--done', stepComplete(n));   // quiet done state on the header (CSS)
  }
  function reflectSteps() { for (var n = 1; n <= 4; n++) reflectStep(n); }
  function setStepOpen(n, open) { stepOpen[n] = !!open; reflectStep(n); }
  // [ROUND 15 2026-08-30] Progressive disclosure. On each render a step that has JUST completed (rising edge,
  // latched once in autoAdvanced) collapses and the first still-incomplete step opens. The latch means a step
  // auto-advances at most once ever, so re-editing measurements or reopening a step by hand never triggers a
  // re-collapse (the "do not fight the shopper" rule). No focus move, no scrollIntoView (opening in place is
  // enough); the open/close is instant in CSS, so prefers-reduced-motion has nothing to animate.
  // [DESIGN REVIEW F1/F2 2026-09-22] Below 990px the rising edge is HELD while the shopper is still editing
  // inside that step (editingInStep): typing "11.8" in C used to collapse step 1 after the first "1" (focus
  // fell to BODY, the rest of the keystrokes were lost), and the angle dial lost its arrow keys after one
  // press. The held step stays open and unlatched; the focusout listener in setupSteps re-runs updateSteps
  // once focus has left the step body, so the step advances when the shopper leaves it. leftStep is the step
  // focus is leaving (its hold is skipped, whatever document.activeElement still reports mid-focusout).
  // Desktop (990 and up) never holds, so its latch timing is unchanged.
  function editingInStep(n) {
    var body = document.getElementById('vmsw-s' + n + 'body');
    var a = document.activeElement;
    if (!body || !a || a === body || !body.contains(a)) return false;
    var tag = String(a.tagName || '').toUpperCase();
    // Editing controls only: a text/number box or the role=slider dial. A pick-card button is a one-shot
    // choice, so a card pick still collapses its step on the click exactly as before.
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' ||
      !!(a.getAttribute && a.getAttribute('role') === 'slider');
  }
  function updateSteps(leftStep) {
    var hold = accordionMode();
    for (var n = 1; n <= 4; n++) {
      if (stepComplete(n) && !autoAdvanced[n]) {
        if (hold && n !== leftStep && editingInStep(n)) continue;   // still editing: advance on focusout
        autoAdvanced[n] = true;
        stepOpen[n] = false;                                   // collapse the just-completed step
        var next = null;
        for (var m = 1; m <= 4; m++) { if (!stepComplete(m)) { next = m; break; } }
        if (next) stepOpen[next] = true;                       // open the next incomplete step
      }
    }
    reflectSteps();
  }
  // Wire the four accordion headers: a header click below 990 toggles that step; on desktop the click is inert
  // (bodies are always shown). Manual toggles never touch the autoAdvanced latch. A resize re-reflects so the
  // aria-expanded / collapse state matches the current side of the 990 breakpoint.
  // [DESIGN REVIEW F1/F2 2026-09-22] Each body gets a focusout listener that releases a held step once focus
  // leaves it (moving between fields inside the same step keeps it open). A header tap snapshots the step's
  // open state at pointerdown, BEFORE the tap's own focusout can auto-open that step, so the click toggles
  // what the shopper saw (tapping a closed step 2 header while still in field C opens step 2, not shuts it).
  // A keyboard click (detail 0) has no pointerdown and toggles the current state.
  function setupSteps() {
    var pressOpen = {};
    for (var n = 1; n <= 4; n++) {
      (function (n) {
        var btn = document.getElementById('vmsw-s' + n + 'btn');
        if (!btn) return;
        btn.addEventListener('pointerdown', function () { pressOpen[n] = !!stepOpen[n]; });
        btn.addEventListener('click', function (e) {
          if (!accordionMode()) return;                        // desktop: header click inert (bodies open)
          var was = (e && e.detail > 0 && pressOpen[n] !== undefined) ? pressOpen[n] : !!stepOpen[n];
          pressOpen[n] = undefined;
          setStepOpen(n, !was);
        });
        var body = document.getElementById('vmsw-s' + n + 'body');
        if (!body) return;
        body.addEventListener('focusout', function (e) {
          if (!accordionMode()) return;                        // desktop never holds a step
          if (e.relatedTarget && body.contains(e.relatedTarget)) return;   // next field in the same step
          updateSteps(n);
        });
      })(n);
    }
    window.addEventListener('resize', reflectSteps);
    reflectSteps();                                            // set the initial per-viewport open state
  }

  // [ROUND 5 2026-08-30] The angle-tips TOOLTIP. The "?" button beside the angle heading toggles a popover
  // holding the existing tips (and the [contact us] link). Closes on Esc (focus back to the button), an
  // outside click/tap, and blur-out. aria-expanded tracks state; the popover is role=dialog.
  // [ROUND 5/6 2026-08-30] One accessible tooltip: a "?" button and a role=dialog popover, opened/closed on
  // click, closed on Esc (focus back to the button), an outside click/tap and blur-out. Touch friendly (the
  // click covers tap). Reused for the angle-tips button (wired once in setupTips) AND the 11FC summary-row
  // button (re-armed after each answer render, since that row is rebuilt on every solve). Idempotent enough
  // for the re-arm: a fresh button/pop each render gets its own listeners, closed by default.
  function wireTooltip(btn, pop) {
    if (!btn || !pop) return;
    function onDoc(e) {
      if (pop.contains(e.target) || btn.contains(e.target) || e.target === btn) return;
      close();
    }
    function onKey(e) { if (e.key === 'Escape' || e.key === 'Esc') { close(); btn.focus(); } }
    function open() {
      pop.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onDoc, true);
      document.addEventListener('keydown', onKey, true);
    }
    function close() {
      if (pop.hidden) return;
      pop.hidden = true;
      btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDoc, true);
      document.removeEventListener('keydown', onKey, true);
    }
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (pop.hidden) { open(); } else { close(); }
    });
    // Blur-out: once focus leaves both the popover and the button, close.
    pop.addEventListener('focusout', function () {
      window.setTimeout(function () {
        if (!pop.contains(document.activeElement) && document.activeElement !== btn) close();
      }, 0);
    });
  }
  function setupTips() { wireTooltip(el('vmsw-tips-btn'), el('vmsw-tips-pop')); }

  // [PHOTO PREVIEW 2026-09-06; PHOTO COLUMN 2026-09-07] Delegate the SKU photo
  // preview ONCE on #vmsw-answer. renderAnswer rewrites its innerHTML on every
  // solve, but the element itself persists, so these delegated listeners survive
  // every re-render with no re-wiring (the same reason wireTooltip re-arms per
  // render but this does not). The preview <img> src is filled from data-src the
  // first time a row is shown, never on render. Hover SHOWS (guarded by
  // (hover: hover) so a touch device does not get a hover-open that fights the tap
  // toggle); keyboard focus on the SKU link SHOWS; a tap on the thumb TOGGLES (the
  // phone path) and flips aria-expanded; Escape hides; one preview open at a time.
  // [PHOTO COLUMN 2026-09-07] The unit is now the ROW (.vmsw-sumrow), not a small
  // wrap span: the thumb + preview live in the row's photo column and the preview
  // is anchored to the row (position:relative), so every lookup closest()s to the
  // row. A row holds exactly one thumb and one preview, so row.querySelector finds
  // the right one.
  function setupSkuPreview() {
    var box = el('vmsw-answer');
    if (!box) return;
    var canHover = window.matchMedia ? window.matchMedia('(hover: hover)').matches : true;
    function pvOf(row) { return row ? row.querySelector('.vmsw-sumrow__pv') : null; }
    function loadOnce(pv) {
      var img = pv && pv.querySelector('img');
      if (img && !img.getAttribute('src') && img.dataset.src) img.src = img.dataset.src;
    }
    function hideAll(except) {
      box.querySelectorAll('.vmsw-sumrow__pv:not([hidden])').forEach(function (pv) {
        if (pv === except) return;
        pv.hidden = true;
        var t = pv.closest('.vmsw-sumrow').querySelector('.vmsw-sumrow__thumb');
        if (t) t.setAttribute('aria-expanded', 'false');
      });
    }
    function show(row) {
      var pv = pvOf(row);
      if (!pv) return;
      hideAll(pv);
      loadOnce(pv);
      pv.hidden = false;
      var t = row.querySelector('.vmsw-sumrow__thumb');
      if (t) t.setAttribute('aria-expanded', 'true');
    }
    function hide(row) {
      var pv = pvOf(row);
      if (!pv || pv.hidden) return;
      pv.hidden = true;
      var t = row.querySelector('.vmsw-sumrow__thumb');
      if (t) t.setAttribute('aria-expanded', 'false');
    }
    function closestRow(e) { return (e.target && e.target.closest) ? e.target.closest('.vmsw-sumrow') : null; }
    box.addEventListener('click', function (e) {
      var thumb = e.target.closest ? e.target.closest('.vmsw-sumrow__thumb') : null;
      if (!thumb) return;
      var row = thumb.closest('.vmsw-sumrow');
      var pv = pvOf(row);
      if (pv && pv.hidden) show(row); else hide(row);
    });
    if (canHover) {
      box.addEventListener('pointerover', function (e) {
        // A touchscreen laptop reports hover:hover but also fires pointerover on a TAP; ignoring
        // pointerType 'touch' lets the click toggle own the tap, no hover-open-then-tap-close flicker.
        if (e.pointerType === 'touch') return;
        var t = e.target.closest ? e.target.closest('.vmsw-sumrow__thumb, .vmsw-sumrow__sku') : null;
        if (t) show(t.closest('.vmsw-sumrow'));
      });
      box.addEventListener('pointerout', function (e) {
        var row = closestRow(e);
        if (!row) return;
        if (e.relatedTarget && row.contains(e.relatedTarget)) return;
        hide(row);
      });
    }
    box.addEventListener('focusin', function (e) {
      var link = e.target.closest ? e.target.closest('.vmsw-sumrow__sku') : null;
      if (link) show(link.closest('.vmsw-sumrow'));
    });
    box.addEventListener('focusout', function (e) {
      var row = closestRow(e);
      if (!row) return;
      if (e.relatedTarget && row.contains(e.relatedTarget)) return;
      hide(row);
    });
    // Escape is bound on DOCUMENT, not #vmsw-answer, so it closes a hover-opened preview even when focus is
    // elsewhere on the page. Guarded to act ONLY when a preview is open, and it neither preventDefault nor
    // stopPropagation, so the page's dialogs and the angle/11FC tooltips keep their own Escape handling.
    document.addEventListener('keydown', function (e) {
      if ((e.key === 'Escape' || e.key === 'Esc') && box.querySelector('.vmsw-sumrow__pv:not([hidden])')) hideAll(null);
    });
  }

  // =====================================================================
  // Pitch: ONE canonical state.pitch fed by the angle drag alone (owner ruling
  // 2026-08-28). setPitch is the single writer: it stores the rise over 12 and,
  // for any non-slider source, moves the handle to match. The drag is the only
  // caller today (source 'slider'), which already moved its own handle, so the
  // updateAngleSlider call is a guard for any future non-drag caller.
  // =====================================================================
  function setPitch(rise, source) {
    // [C] THE SINGLE WRITER NEVER STORES A NEGATIVE OR NON-FINITE RISE. The angle drag clamps to
    // 0 to 89 degrees before it reaches here, but guard the writer itself so no future caller can slip
    // a vertical or past-vertical angle in: a rise that is not a finite number >= 0 is treated as
    // "unset" (null), the same as no value yet.
    // [ZERO-DEG 2026-08-29] A rise of EXACTLY 0 is now a VALID stored value (a flat, 0 degree ceiling,
    // owner ruling), NOT "unset": the gate is `r >= 0`, and only null/undefined/non-finite/negative
    // becomes null. state.pitch === 0 means flat; state.pitch === null means the shopper has not set the
    // slope yet. Everything downstream (ready(), the geometry, wallThetaRad) distinguishes the two.
    var r = (rise == null) ? null : rise;
    if (r != null && !(typeof r === 'number' && isFinite(r) && r >= 0)) r = null;
    state.pitch = r;
    state.headDrop = null; state.pipe = null;   // [DRAG] slope changed: back to the default head position
    state.add11fc = false;               // [RESTING] the 11FC toggle is per-build; a new build starts at rest
    // [AUTO-TILT] A slope change is a core geometry change like head/plate/TV: in the auto regime (the
    // shopper has NOT dragged the tilt slider) return to square so the auto search re-runs from tilt 0
    // and DROPS the tilt when a shallower slope no longer needs it. If the shopper HAS dragged the
    // slider (tiltTouched), leave their tilt alone.
    if (!state.tiltTouched) state.tilt = 0;
    if (r != null) state.invalid.pitch = false;   // a real value clears the out-of-range flag
    var deg = (r == null) ? null : S.pitchDegrees(r);
    if (source !== 'slider') { updateAngleSlider(deg); }   // the drag already moved its own handle
    renderAnswer();
  }

  // [B1] Move the angle handle, its aria value and the visual readout to a degree value.
  // Null parks the handle at the resting default with the readout hidden (pitch not set yet).
  // The rotation transform is PRESENTATION; the rise over 12 shown comes through the module.
  function updateAngleSlider(deg) {
    var d = (deg == null) ? ANGLE_DEFAULT_DEG : clampAngle(deg, ANGLE_MIN_DEG, ANGLE_MAX_DEG);
    var arm = el('vmsw-angle-arm');
    if (arm) arm.setAttribute('transform', 'rotate(' + (-d).toFixed(2) + ' ' + ANGLE_HINGE_X + ' ' + ANGLE_HINGE_Y + ')');
    var handle = el('vmsw-angle-handle');
    if (handle) {
      handle.setAttribute('aria-valuenow', String(Math.round(d)));
      handle.setAttribute('aria-valuetext', angleReadoutText(d));
    }
    // [B1] Keep the "Drag" word LEVEL and centred on the handle. The handle rides inside the arm
    // group, which is rotated, so the word lives OUTSIDE that group and we move it to the handle's
    // rotated centre here each update (handleCentreAt). Presentation positioning only.
    var word = el('vmsw-angle-label-word');
    if (word) {
      var wc = handleCentreAt(d);
      word.setAttribute('x', wc.x.toFixed(2));
      word.setAttribute('y', wc.y.toFixed(2));
    }
    var readout = el('vmsw-angle-readout');
    if (readout) {
      if (deg == null) { readout.hidden = true; readout.textContent = ''; }
      else { readout.hidden = false; readout.textContent = angleReadoutText(d); }
    }
    // [COMPACT 2026-08-29] Mirror the angle into the optional degrees box so a drag or a keyboard step
    // keeps it in sync, but NEVER while the box itself has focus (that would clobber a decimal the shopper
    // is mid-typing; the box's own input handler drove this update in that case). A null angle blanks it.
    var degEl = el('vmsw-degrees');
    if (degEl && document.activeElement !== degEl) {
      degEl.value = (deg == null) ? '' : String(Math.round(d));
    }
  }
  // The degrees-only readout string for a degree value: "[deg] degrees".
  function angleReadoutText(d) {
    return fill(COPY('angle_readout'), { deg: String(Math.round(d)) });
  }
  // Commit a new angle (from a drag or a key): clamp, drive the handle, then push the rise over
  // 12 into the shared state as the 'slider' source so the degrees and pitch boxes update too.
  function commitAngle(deg) {
    var d = clampAngle(deg, ANGLE_MIN_DEG, ANGLE_MAX_DEG);
    state.invalid.pitch = false;
    updateAngleSlider(d);
    setPitch(S.pitchFromDegrees(d), 'slider');
  }

  // [ROUND 15 2026-08-30] A SHOPPER (or agent) angle commit: marks step 2 done (angleTouched) so the accordion
  // can advance. The MOUNT default commit (commitAngle(ANGLE_DEFAULT_DEG)) deliberately does NOT go through
  // here, so the 45 default never counts as the shopper setting the angle.
  function userSetAngle(deg) { angleTouched = true; commitAngle(deg); }

  function wire() {
    // The draggable angle visual (mouse, touch, keyboard) is the sole slope input. See wireAngle.
    wireAngle();

    // TV measurements, mm-aware and range-checked. Each resets the option pick: a new number
    // reshapes the longer-pipe options, so any prior choice from the old set no longer applies.
    // screen height and VESA must be positive; top-to-top-hole may be exactly 0 (a hole at the
    // very top edge), so it allows zero. Thresholds separate an inch entry from a mm one.
    wireMeasure('screen', 'screen', false, 100);
    wireMeasure('tophole', 'topHole', true, 50);
    wireMeasure('vesa', 'vesa', false, 50);

    // Plate + head cards. Selection updates class + aria-pressed + the pick label
    // in place, so the tapped card keeps focus (no innerHTML re-render).
    function wireCards(containerId, key, stateKey) {
      var box = el(containerId);
      if (!box) return;
      box.querySelectorAll('.vmsw-pick-card').forEach(function (card) {
        card.addEventListener('click', function () {
          // [GATE 2026-08-30] A disabled card (plate past its angle limit, or head too small for the derived
          // TV) is not selectable: ignore the click so the reason line stays and nothing commits.
          if (card.classList.contains('vmsw-disabled')) return;
          // [review R2 item 6] A real card CLICK completes step 3 (plate) or step 4 (head) for the accordion.
          // The reset list, the card DOM update (reflectPick) and the re-render are the SAME as an agent pick
          // (selectPlate / selectHead), so both go through the one mcpCommitPick funnel: a click and an agent
          // pick can never drift apart. The example seeds state via reflectPick (no click), so its plateTouched
          // / headTouched stay clear and the accordion advances only on the shopper's own pick.
          mcpCommitPick(stateKey, card.dataset[key], key, containerId);
        });
      });
    }
    wireCards('vmsw-plates', 'plate', 'plateSku');
    wireCards('vmsw-heads', 'head', 'headSku');

    // [COMPACT 2026-08-29] The optional numeric angle entry in the angle step. Typing a value in range
    // feeds commitAngle (which moves the drag handle and re-solves through the one setPitch writer); a
    // blank field LEAVES the drag value; an out-of-range or non-numeric entry is rejected (no commit), so
    // a half-typed number never lurches the build. The drag mirrors its value back into this box through
    // updateAngleSlider, except while the box itself has focus (so it does not clobber a decimal mid-type).
    var degEl = el('vmsw-degrees');
    if (degEl) {
      degEl.addEventListener('input', function (e) {
        var raw = String(e.target.value == null ? '' : e.target.value).trim();
        if (raw === '') return;                               // blank leaves the drag value
        var n = S.num(plainNumeric(raw));
        if (n === null || n < ANGLE_MIN_DEG || n > ANGLE_MAX_DEG) return;   // out of range rejected
        userSetAngle(n);
      });
    }

    // [COMPACT 2026-08-29] The screen-tilt SLIDER is removed; the build-image tilt drag is the control now
    // (it calls the public setTilt -> commitTilt). Nothing to wire here for tilt.

    // [ROUND 5/15 2026-08-30] The app-shell controls: the mobile step accordion and the angle-tips tooltip.
    setupSteps();
    setupTips();
    setupSkuPreview();
  }

  // [TILT] The tilt-slider body, factored into ONE function (owner contract: setTilt(deg) routes here too).
  // Clamp to [0, max] whole degrees, mark the slider TOUCHED (the shopper's control wins, so the auto-tilt
  // search stops moving it), move the live readout in place, and re-solve. maxOverride lets the slider pass
  // its own max attribute; when absent (the public setTilt path) the current head's max is used.
  function commitTilt(deg, maxOverride) {
    var maxWhole = (maxOverride === undefined || maxOverride === null)
      ? (function () { var m = headMaxTilt(bySku(state.headSku)); return m === null ? 0 : Math.floor(m); })()
      : S.num(maxOverride);
    var v = S.num(deg);
    if (v === null) v = 0;
    v = Math.round(v);
    if (v < 0) v = 0;
    if (maxWhole !== null && v > maxWhole) v = maxWhole;
    state.tilt = v;
    // [AUTO-TILT] A real shopper move: their control wins from here, so renderAnswer must not auto-move the
    // slider for this build. Cleared (with tilt reset to 0) on the next head/plate/TV change.
    state.tiltTouched = true;
    var slider = el('vmsw-tilt');
    if (slider && slider.value !== String(v)) slider.value = String(v);
    var ro = el('vmsw-tilt-readout');
    if (ro) ro.textContent = fill(COPY('tilt_readout'), { n: String(v) });
    renderAnswer();
  }

  // [A1/A2] One measurement input: parse it mm-aware and range-checked (readMeasure), store
  // the inches value, flag an out-of-range entry so the answer region can say why, and show or
  // clear the millimeter note. Any change resets the option pick, exactly as before.
  // [TV LOOKUP 2026-10-13] The state write is factored into setMeasure, shared with the catalog path (which
  // writes the exact mm / 25.4 figure with converted false, so no mm note). No behaviour change for typing.
  var MEASURE_FIELDS = [['screen', 'screen', false, 100], ['tophole', 'topHole', true, 50], ['vesa', 'vesa', false, 50]];
  function setMeasure(stateKey, id, valueIn, invalid, converted, mm) {
    // [EXAMPLE 2026-08-30] The shopper is entering their OWN measurement, so the example is over. Clear the
    // flag and null the OTHER two seeded measurements (they typed into one box; the others are still
    // empty), so ready() falls false until they fill all three. Their plate/head/angle stay (configure on).
    if (exampleActive) {
      exampleActive = false;
      ['screen', 'topHole', 'vesa'].forEach(function (k) { if (k !== stateKey) state[k] = null; });
    }
    state[stateKey] = valueIn;
    state.headDrop = null; state.pipe = null;   // [DRAG] a TV measurement changed: back to the default position
    state.add11fc = false;               // [RESTING] the 11FC toggle is per-build; a new build starts at rest
    // [AUTO-TILT] A TV measurement change returns to the auto/square regime: clear the touch flag
    // and reset the tilt to 0 so renderAnswer re-derives the smallest tilt that fits for the new size.
    state.tiltTouched = false;
    state.tilt = 0;
    setInvalid(id, invalid);
    var note = el('vmsw-' + id + '-mmnote');
    if (note) {
      if (converted) {
        note.textContent = fill(COPY('mm_converted_note'), { mm: nice(mm), inches: nice(valueIn) });
        note.hidden = false;
      } else {
        note.textContent = '';
        note.hidden = true;
      }
    }
  }
  function wireMeasure(id, stateKey, allowZero, mmThreshold) {
    var input = el('vmsw-' + id);
    if (!input) return;
    input.addEventListener('input', function (e) {
      // [TV LOOKUP] The first edit of a catalog-filled box makes all three the customer's own (re-read as shown).
      if (tvSrc === 'catalog') {
        tvSrc = 'manual';
        MEASURE_FIELDS.forEach(function (f) {
          var b = el('vmsw-' + f[0]);
          if (f[0] === id || !b) return;
          var o = readMeasure(b.value, f[2], tvTyped[f[0]] ? f[3] : Infinity);
          setMeasure(f[1], f[0], o.value, o.invalid, o.converted, o.mm);
        });
      }
      tvTyped[id] = true;
      tvPendingEnd();
      tvLost = null; tvCleared = false;
      var m = readMeasure(e.target.value, allowZero, mmThreshold);
      setMeasure(stateKey, id, m.value, m.invalid, m.converted, m.mm);
      renderAnswer();
    });
  }

  // [TV LOOKUP 2026-10-13] The widget host 'slanted'. Notes: assets/vms-slanted-wall.notes.md.
  var TV_KEYS = { screen: 'height', topHole: 'topHole', vesa: 'vesa' };
  function tvName(r) { return r ? [r.maker, r.model].filter(function (x) { return !!x; }).join(' ') : ''; }
  function tvRoot() { return document.querySelector('#vmsw-tvl [data-vms-tvl]'); }
  function tvPendingEnd() { if (pendingTv) { clearTimeout(pendingTv.timer); pendingTv = null; tvPickLine(); } }
  function tvSetBoxes(r) {
    MEASURE_FIELDS.forEach(function (f) {
      var v = r ? r[TV_KEYS[f[1]]] : null, b = el('vmsw-' + f[0]);
      setMeasure(f[1], f[0], v, false, false, null);
      tvTyped[f[0]] = false;
      if (b) b.value = v == null ? '' : v.toFixed(1);
    });
  }
  function tvApply(r, moveFocus) {
    var was = !!stepOpen[1];
    tvSetBoxes(r);
    tvSrc = 'catalog'; tvApplied = r; tvHeld = null; tvLost = null; tvCleared = false; tvIntent = null;
    renderAnswer();
    if (moveFocus && accordionMode() && was && !stepOpen[1]) {
      for (var n = 2; n <= 4; n++) { if (stepOpen[n]) { var b = el('vmsw-s' + n + 'btn'); if (b) b.focus(); break; } }
    }
  }
  function tvMeasurePath(r) {
    if (tvSrc === 'catalog' || exampleActive) tvSetBoxes(null);
    tvSrc = null; tvApplied = null; tvHeld = r; tvLost = null; tvCleared = false; tvIntent = null;
    renderAnswer();
  }
  function tvUseBtnSync(d) {
    var use = tvRoot() ? tvRoot().querySelector('.vms-tvl__use') : null, m = d ? d.model_number : null;
    if (use) use.hidden = !!((tvSrc === 'catalog' && tvApplied && tvSameModel(tvApplied.model, m)) || (tvHeld && tvSameModel(tvHeld.model, m)));
  }
  function tvOnSelected(e) {
    var d = e.detail || {}, r = tvRowInches(d);
    tvSel = d;
    if (pendingTv && d.explicit !== true && (tvSameModel(pendingTv.model, d.model_number) || tvSameModel(pendingTv.model, d.query))) {
      var tail = pendingTv.tail;
      tvPendingEnd();
      if (r.placeable) { tvApply(r, false); urlRestoreTail(tail); } else tvMeasurePath(r);
    } else if (d.explicit === true) {
      tvPendingEnd();
      if (r.placeable) tvApply(r, true); else tvMeasurePath(r);
    } else tvNoteRender();
    tvUseBtnSync(d);
  }
  // [M1 2026-10-13] The TV is dropped only on purpose: Search again, the model box emptied, or an explicit
  // search ending in none or unavailable (tvOutChanged). A typed suggestion, none or unavailable keeps the build.
  function tvDrop() {
    tvPendingEnd(); tvIntent = null;
    if (tvSrc === 'catalog') { tvSetBoxes(null); tvCleared = true; }
    tvSrc = null; tvApplied = null; tvHeld = null;
    renderAnswer();
  }
  function tvOnCleared() {
    tvSel = null;
    if (tvAgain) return;
    var root = tvRoot(), v = root && root.vmsTvl ? String(root.vmsTvl.input.value || '').trim() : '';
    if (!v) tvDrop(); else tvNoteRender();
  }
  function tvOutChanged() {
    var root = tvRoot();
    if (!root) return;
    var card = root.querySelector('.vms-tvl__card'), list = root.querySelector('.vms-tvl__list'), msg = root.querySelector('.vms-tvl__msg');
    if (!card && !list && (!msg || msg.textContent === 'Looking it up...')) { tvPickLine(); return; }
    if (pendingTv && list && !card) { clearTimeout(pendingTv.timer); pendingTv.list = true; }
    if (tvIntent === 'explicit') { tvIntent = null; if (!card && !list) tvDrop(); }
    tvPickLine();
  }
  function tvPickLine() {
    var root = tvRoot(), line = el('vmsw-tvl-pick');
    if (!root || !line) return;
    line.hidden = !(pendingTv && pendingTv.list && root.querySelector('.vms-tvl__list'));
  }
  function tvOnMeasure() {
    setStepOpen(1, true);
    var a = el('vmsw-screen');
    if (a) a.focus();
  }
  function tvRestoreModel(m, tries) {
    var root = tvRoot();
    if (!root || !pendingTv) return;
    if (root.vmsTvl && pendingTv.saved && root.vmsTvl.renderRow) { root.vmsTvl.renderRow(pendingTv.saved.row, { prefill: true, catalog_version: pendingTv.saved.catalog_version }); return; }
    if (root.vmsTvl) { root.vmsTvl.input.value = m; root.vmsTvl.lookup(m, false, { prefill: true }); return; }
    if ((tries || 0) < 20) setTimeout(function () { tvRestoreModel(m, (tries || 0) + 1); }, 100);
  }
  function tvSeedSaved(sv) {
    if (!sv || !tvRoot()) return;
    pendingTv = { model: String(sv.row.model_number).trim(), tail: {}, saved: sv, timer: setTimeout(function () {
      if (!pendingTv || pendingTv.list) return;
      tvLost = pendingTv.model; pendingTv = null;
      renderAnswer();
    }, 10500) };
    tvRestoreModel(pendingTv.model);
  }
  // [3A 2026-10-18] the saved TV stands in for a link that carries no TV at all; notes: assets/vms-saved-tv.notes.md
  function tvSavedForUrl(owned) {
    var S = window.VMSSavedTv, rec = S && S.get(), none = true;
    ['tvm', 'tva', 'tvb', 'tvc', 'screen', 'tophole', 'vesa'].forEach(function (k) { if (owned[k] != null && owned[k] !== '') none = false; });
    if (!none || !rec || rec.source !== 'catalog') return null;
    var m = String(rec.row.model_number || '').trim();
    return m && m.length <= 64 ? rec : null;
  }
  function tvLine(cls, text) { var p = document.createElement('p'); p.className = cls; p.textContent = text; return p; }
  function mountTvLookup() {
    var holder = el('vmsw-tvl'), body = el('vmsw-s1body');
    if (!holder || !body) return;
    holder.insertBefore(tvLine('vmsw-lab vmsw-tvl-lead', 'Know your TV model? Look it up'), holder.firstChild);
    body.insertBefore(holder, body.firstChild);
    body.insertBefore(tvLine('vmsw-lab vmsw-tvl-alt', 'Or enter your TV\'s measurements:'), holder.nextSibling);
    var note = tvLine('vmsw-tv-src', '');
    note.id = 'vmsw-tv-src'; note.hidden = true; note.tabIndex = -1;
    body.appendChild(note);
    var pick = tvLine('vmsw-tvl-pick', 'Pick your TV from the list to fill in A, B and C.');
    pick.id = 'vmsw-tvl-pick'; pick.hidden = true;
    holder.appendChild(pick);
    note.setAttribute('role', 'status'); note.setAttribute('aria-live', 'polite');
    holder.hidden = false;
    holder.addEventListener('submit', function () { tvIntent = 'explicit'; }, true);
    holder.addEventListener('click', function (e) {
      var t = e.target && e.target.closest ? e.target.closest('.vms-tvl__use, .vms-tvl__pick, .vms-tvl__again') : null;
      if (!t) return;
      if (/vms-tvl__again/.test(t.className)) { tvAgain = true; setTimeout(function () { tvAgain = false; tvDrop(); }, 0); } else tvIntent = 'explicit';
    }, true);
    holder.addEventListener('input', function (e) {
      if (!e.target || !e.target.hasAttribute || !e.target.hasAttribute('data-tvl-input')) return;
      tvIntent = null;
      if (!String(e.target.value || '').trim()) tvDrop();
      else if (pendingTv) { tvPendingEnd(); renderAnswer(); tvPickLine(); }
    });
    var out = holder.querySelector('[data-tvl-out]');
    if (out && window.MutationObserver) new MutationObserver(tvOutChanged).observe(out, { childList: true, subtree: true });
    holder.addEventListener('vms:tv-selected', tvOnSelected);
    holder.addEventListener('vms:tv-cleared', tvOnCleared);
    holder.addEventListener('vms:tv-measure', tvOnMeasure);
  }
  function tvNoteRender() {
    var p = el('vmsw-tv-src');
    if (!p) return;
    var kind = tvLost ? 'lost' : (tvSrc === 'catalog' ? 'catalog' : (tvSrc === 'manual' ? 'edited' : (tvHeld ? tvHeld.why : (tvCleared ? 'cleared' : ''))));
    var back = kind === 'edited' && !!tvApplied && !!tvSel && tvSameModel(tvApplied.model, tvSel.model_number);
    var key = [kind, back, tvLost, tvName(tvApplied), tvName(tvHeld)].join('|');
    if (key === tvNoteKey) return;
    tvNoteKey = key;
    p.textContent = '';
    var add = function (n) { p.appendChild(typeof n === 'string' ? document.createTextNode(n) : n); };
    var link = function () {
      var a = document.createElement('a');
      a.href = CONFIG.measure_url || '/blogs/articles/how-to-determine-where-the-tv-will-end-up';
      a.textContent = 'how to find where the TV will end up';
      return a;
    };
    if (kind === 'catalog') {
      add('We filled in A, B and C from our catalog' + (tvName(tvApplied) ? ' for the ' + tvName(tvApplied) : '') + '. ' + tvApplied.tierNote + ' Measured something different on your TV? Type your numbers over ours.');
    } else if (kind === 'edited') {
      add('Using your measurements. Check them on the back of your TV before drilling. ');
      if (back) {
        var b = document.createElement('button');
        b.type = 'button'; b.className = 'vmsw-tv-src__back'; b.textContent = 'Use the catalog figures again';
        b.addEventListener('click', function () { if (tvApplied) { tvApply(tvApplied, false); p.focus(); } });
        add(b);
      }
    } else if (kind === 'no_offsets' || kind === 'no_height') {
      add(kind === 'no_height' ? 'Our catalog has where this TV\'s mounting holes sit but not its overall height, so measure A, B and C on the back of your TV. Here is how: ' : 'Measure A, B and C on the back of your TV. Here is how: ');
      add(link()); add('.');
    } else if (kind === 'proprietary') {
      var root = tvRoot(), disp = root ? root.getAttribute('data-phone-display') : '', tel = root ? root.getAttribute('data-phone-tel') : '';
      var ph = document.createElement(disp && tel ? 'a' : 'span');
      ph.className = 'phone'; ph.textContent = disp && tel ? disp : PHONE_TEXT;
      if (disp && tel) ph.href = 'tel:' + tel;
      add('This TV uses its maker\'s own mount, so a standard VESA head may not fit it. Call us at '); add(ph); add(' before you order.');
    } else if (kind === 'lost') {
      add('We could not load the TV named in this link. Measure your TV and enter A, B and C above, or look up the model again.');
    } else if (kind === 'cleared') {
      add('We cleared the TV\'s figures. Enter A, B and C, or look up another model.');
    }
    p.hidden = !kind;
  }
  function activeDepth() { return (tvSrc === 'catalog' && tvApplied && tvApplied.depth) ? tvApplied.depth : ELEV_PANEL_DEPTH; }
  function activeWidth() { return (tvSrc === 'catalog' && tvApplied && tvApplied.width) ? tvApplied.width : null; }
  function tvPlacementOut() {
    if (exampleActive) return null;
    var s = state.screen, t = state.topHole, v = state.vesa, all = s != null && t != null && v != null;
    var cat = tvSrc === 'catalog' ? tvApplied : null, r = cat || (all ? null : tvHeld);
    if (!cat && !all && !r) return null;
    var hm = (cat || all) ? S.holeMidFromTop(t, v) : null, need = !cat && !all;
    var H = need ? r.height : s, W = r && r.width ? r.width : (H != null ? H * 16 / 9 : null);
    var D = r && r.depth ? r.depth : (need ? null : ELEV_PANEL_DEPTH);
    return {
      source: r ? 'catalog' : 'manual', placed: need ? false : (cat ? true : (hm > 0 && hm < s)), has_offsets: r ? r.hasOffsets : true,
      model_number: r ? r.model : null, manufacturer: r ? r.maker : null, catalog_version: r ? r.version : null, screen_size_in: r ? r.size : null,
      tier: r ? r.tier : 'customer_measured', tier_note: need ? null : (r ? r.tierNote : tvTierNote({ offset_tier: 'customer_measured' })),
      height_in: H, width_in: W, depth_in: D,
      width_from: r && r.width ? 'catalog' : (W != null ? '16:9' : null), depth_from: r && r.depth ? 'catalog' : (D != null ? 'assumed' : null),
      hole_centre_below_top_in: hm, holes_below_middle_in: hm == null ? null : hm - s / 2,
      top_hole_below_top_in: need ? r.topHole : t, hole_spacing_in: need ? r.vesa : v, needs_measurement: need
    };
  }

  // [B1] Wire the draggable angle: Pointer Events cover mouse AND touch on one path, and the
  // handle is a real role="slider" with a full keyboard story. Pointer-to-angle mapping and
  // the rotation are PRESENTATION (pointerAngleDeg / commitAngle); the rise over 12 always
  // comes back through the module.
  function wireAngle() {
    var svg = el('vmsw-angle-svg');
    var handle = el('vmsw-angle-handle');
    if (!svg || !handle) return;    // older template JSON without the diagram: NO slope input at all (see buildShell)

    // Map a pointer event to a slope angle in degrees, in the SVG's own viewBox coordinates.
    function angleFromEvent(ev) {
      var rect = svg.getBoundingClientRect();
      if (!rect.width || !rect.height) return ANGLE_DEFAULT_DEG;
      var vx = (ev.clientX - rect.left) / rect.width * ANGLE_VB_W;
      var vy = (ev.clientY - rect.top) / rect.height * ANGLE_VB_H;
      return pointerAngleDeg(vx, vy, ANGLE_HINGE_X, ANGLE_HINGE_Y);
    }

    var dragging = false;
    svg.addEventListener('pointerdown', function (ev) {
      ev.preventDefault();               // no text selection / scroll; touch-action:none backs this up
      dragging = true;
      try { svg.setPointerCapture(ev.pointerId); } catch (e) {}
      handle.focus();
      userSetAngle(angleFromEvent(ev));
    });
    svg.addEventListener('pointermove', function (ev) {
      if (!dragging) return;
      userSetAngle(angleFromEvent(ev));
    });
    function endDrag(ev) {
      dragging = false;
      try { svg.releasePointerCapture(ev.pointerId); } catch (e) {}
    }
    svg.addEventListener('pointerup', endDrag);
    svg.addEventListener('pointercancel', endDrag);

    // Keyboard: arrows step 1 degree, PageUp/PageDown step 5, Home/End jump to the ends.
    handle.addEventListener('keydown', function (ev) {
      var cur = Number(handle.getAttribute('aria-valuenow'));
      if (!isFinite(cur)) cur = ANGLE_DEFAULT_DEG;
      var next = cur, handled = true;
      switch (ev.key) {
        case 'ArrowUp': case 'ArrowRight': next = cur + 1; break;
        case 'ArrowDown': case 'ArrowLeft': next = cur - 1; break;
        case 'PageUp': next = cur + 5; break;
        case 'PageDown': next = cur - 5; break;
        case 'Home': next = ANGLE_MIN_DEG; break;
        case 'End': next = ANGLE_MAX_DEG; break;
        default: handled = false;
      }
      if (!handled) return;
      ev.preventDefault();
      userSetAngle(next);
    });
  }

  // =====================================================================
  // The answer
  // =====================================================================
  function ready() {
    return state.pitch != null && state.screen != null &&
      state.topHole != null && state.vesa != null &&
      state.plateSku && state.headSku;
  }

  function noticeHTML(copyKey, tokens) {
    return '<div class="warnbox" role="alert">' + fillHTML(COPY(copyKey), tokens || {}) + '</div>';
  }

  // [CAP-RESTORED 2026-08-29] The no-pipe-fits notice names the build's pipe window [floor, cap] (one
  // decimal each). On a sliding head both are real numbers (the window may be empty on a too-short screen,
  // or fall between stock sizes). A FIXED head's no_pipe_fits is a pathological empty-stock case with null
  // floor/cap, so the tokens fall back to empty and fill() renders those brackets as nothing.
  function noPipeFitsNotice(res) {
    var tokens = (res && res.floor != null && res.cap != null)
      ? { floor: nice(res.floor), cap: nice(res.cap) } : {};
    return noticeHTML('notice_no_pipe_fits', tokens);
  }

  function weightRowHTML(plate, head) {
    var wr = buildWeightRating(plate, head);
    if (!wr) return '';
    var val = wr.confident
      ? fill(COPY('weight_value'), { weight: nice(wr.weight), part: wr.limiters.join(' and ') })
      : fill(COPY('weight_unrated'), { weight: nice(wr.weight), part: wr.unrated.join(' and ') });
    // [PHOTO COLUMN 2026-09-07] The weight row has no part, so its photo column is
    // an EMPTY span of the same fixed width, keeping its label at the same x as the
    // part rows. A span (not a div) is used so Dawn's `div:empty { display:none }`
    // rule cannot hide it; the CSS also gives it an explicit inline-block + width.
    return '<div class="vmsw-sumrow"><span class="vmsw-sumrow__photo"></span><span class="k">' + esc(COPY('weight_label')) +
      '</span><span class="v">' + esc(val) + '</span></div>';
  }

  // The cart contract, matched to assets/vms-configurator.js exactly:
  // one line per part, and on EVERY line _vms_config_id (a fresh uuid, shared
  // across the lines of one build), _vms_config_summary (pipe-separated, the
  // same shape), and _vms_unit_qty (string count of that SKU in one unit).
  function newConfigId() {
    return (window.crypto && crypto.randomUUID) ? crypto.randomUUID()
      : 'vmsw-' + Date.now() + '-' + Math.random().toString(16).slice(2);
  }
  // [BUILD PROPERTY 2026-09-06] The VISIBLE "Build" cart line property, the twin of
  // the configurator's (assets/vms-configurator.js). Owner ask: a build's line items
  // should carry a plain summary into the cart and order, and since a customer may
  // order several custom builds (on either page) we must be able to tell them apart.
  // It rides ALONGSIDE the hidden _vms_* properties, never in place of them:
  // _vms_config_summary keeps its HOLE-CENTRE figure (the shared app contract),
  // while "Build" reports the honest MID-SCREEN finished length the page shows the
  // shopper. buildCode: first 6 hex of the config id with hyphens removed, uppercased
  // (UUID); the first 6 hex of the random tail for the 'vmsw-<ts>-<rand>' fallback id.
  function buildCode(configId) {
    var id = String(configId == null ? '' : configId);
    if (!id) return 'NOCODE';   // never open the Build value with a bare comma
    if (id.indexOf('vmsw-') === 0) {
      var parts = id.split('-');
      var rand = parts[parts.length - 1] || '';
      return rand.slice(0, 6).toUpperCase();
    }
    return id.replace(/-/g, '').slice(0, 6).toUpperCase();
  }
  // buildProperty(configId, opts): the visible "Build" value. Plain keyboard
  // characters only, no pipes and no em/en dashes. Every number is the same one the
  // page already shows the shopper (opts.midScreenLen is lastNumbers'
  // finished_length_to_mid_screen_in; screen and pitch are state), never recomputed.
  // Shape: <CODE>, angled ceiling page, <size or "no TV size given">, <head> head on
  //   <plate> plate, <pipe> pipe[, <collar> collar], <length> in to mid-screen,
  //   wall pitch <pitch> in 12
  function buildProperty(configId, opts) {
    var code = buildCode(configId);
    var o = opts || {};
    // Defensive guards, mirroring numbersDetailsHTML's graceful omission: a null
    // mid-screen length or pitch DROPS its whole clause (no double space, no empty
    // number), and every SKU falls back to "unknown". Normal-case string unchanged.
    var skuOf = v => (v != null && v !== '') ? v : 'unknown';
    var size = (o.screen != null && o.screen !== '' && !isNaN(Number(o.screen)) && Number(o.screen) > 0)
      ? nice(Number(o.screen)) + ' in TV' : 'no TV size given';
    var collar = o.companionSku ? ', ' + o.companionSku + ' collar' : '';
    var len = nice(o.midScreenLen);   // null when missing
    var pitch = nice(o.pitch);        // null when missing
    var s = code + ', angled ceiling page, ' + size + ', '
      + skuOf(o.headSku) + ' head on ' + skuOf(o.plateSku) + ' plate, '
      + skuOf(o.pipeSku) + ' pipe' + collar;
    if (len !== null) s += ', ' + len + ' in to mid-screen';
    if (pitch !== null) s += ', wall pitch ' + pitch + ' in 12';
    return s;
  }
  // [WARN] THIS PROPERTY REPORTS THE HOLE-CENTRE FIGURE, ON PURPOSE, EVEN THOUGH
  // THE VISIBLE COPY ABOVE REPORTS MID-SCREEN. _vms_config_summary is an app
  // contract SHARED with the ceiling configurator (assets/vms-configurator.js),
  // which computes the hole-centre length. The owner has an OPEN decision on
  // whether to move the configurator's own reference to mid-screen; until he
  // rules, making the two pages disagree in the cart would be worse than the
  // mislabel. So the shopper SEES the honest mid-screen number on the page, and
  // the cart line stays byte-identical to the configurator's. Pass the
  // hole-centre length here. Do not change the " | " separator or the wording;
  // both are the contract.
  function configSummary(head, plate, holeCentreLen) {
    // holeCentreLen is null only on unusable geometry, which the upstream 'ok' gate
    // already excludes before the cart is wired. Guard anyway: nice() now returns null
    // for a missing value, and an unguarded null would concatenate as the literal
    // 'null' into the app-contract string. A real length always prints unchanged.
    var L = nice(holeCentreLen);
    return 'Head: ' + head.sku + ' | Plate: ' + plate.sku + ' | Finished length: ' + (L === null ? '' : L) + ' in';
  }
  // The hole-centre length (ceiling plate down to the middle of the VESA hole
  // pattern), the number that goes into the cart summary and into the installer's
  // hole-line copy. Fixed head: plateOffset + pipe + headOffset. Sliding head:
  // the head-drop d' (headDrop.min, the shallowest drop = TV highest). Null on
  // unusable input, so no line renders rather than a wrong number.
  function holeCentreLen(res, plate, head, pipeLen, standoff, tiltDeg, tiltPivot, tiltAxisDrop, tiltTable, pitch, placedDrop) {
    if (res.slides && res.headDrop) {
      // [RESTING 2026-08-29] The head is installed at the PLACED drop, passed in by the caller: the resting
      // drop (headDrop.max) by default, or the raised drop (headDrop.min) when the optional 11FC is on. When
      // no placedDrop is supplied (the Node harness's legacy call), fall back to the RESTING drop headDrop.max.
      var dmin = (placedDrop != null) ? S.num(placedDrop) : S.num(res.headDrop.max);
      if (dmin === null) return null;
      // [TILT-E] The head sits at d'min, but under a tilting/migrating head the hole centre itself
      // DISPLACES (the 33CE008HEAD's per-degree dyUp rise), so the holes land dyUp higher than the raw
      // drop. Subtract the rotated hole-centre height exactly as the fixed-head branch below does; at
      // tiltDeg 0, and on the legacy call with no tilt args, the rotation is the identity (rotS.y = 0)
      // and this is dmin unchanged, so the cart contract and every zero-tilt figure are preserved.
      var rotS = S.tiltPoint({ x: standoff, y: 0, standoff: standoff, tiltDeg: tiltDeg,
        tiltPivot: tiltPivot, tiltAxisDrop: tiltAxisDrop, tiltTable: tiltTable });
      return (rotS.y === null) ? dmin : dmin - rotS.y;
    }
    // [PLATE-OFFSET-BY-ANGLE] The plate offset is angle-dependent now (effectivePlateOffset); pass the
    // pitch so the finished length / cart hole-centre reflect the real plate depth. A spec-less plate, or a
    // call with no pitch (the pure tilt harness), falls back to the flat custom.length_offset, unchanged.
    var po = effectivePlateOffset(plate, pitch), ho = S.num(head.length_offset), p = S.num(pipeLen);
    if (po === null || ho === null || p === null) return null;
    var base = po + p + ho;
    // [TILT] Rotate the hole centre about the tilt axis. When the pivot and drop are 0 (axis AT the
    // mounting surface and the hole centre) the hole centre lies ON the axis and does not move, so
    // this is exactly base; a real pivot/drop moves it. Identity at tiltDeg 0. A missing standoff
    // (or the old 4-arg call) leaves it at base, the unchanged cart contract.
    var rot = S.tiltPoint({ x: standoff, y: 0, standoff: standoff, tiltDeg: tiltDeg,
      tiltPivot: tiltPivot, tiltAxisDrop: tiltAxisDrop, tiltTable: tiltTable });
    if (rot.y === null) return base;
    return base - rot.y;
  }
  // The 11FC companion, resolved for ONE build. needsCompanion is the existing
  // res.slides flag (the sliding 33CE008HEAD is the only head that carries the collar),
  // and companion is the module-level COMPANION from the store. Returns the companion
  // ONLY when it is genuinely addable on a build that requires it: a fixed head returns
  // null (no collar at all), and a sliding head with a missing/unavailable companion
  // ALSO returns null, which is the signal wireAddToCart reads to refuse the cart rather
  // than sell an incomplete package. This single gate drives the row, the total and the
  // cart line, so they can never disagree.
  function companionLine(needsCompanion, companion) {
    if (!needsCompanion) return null;
    if (!companion || companion.id == null || companion.available === false) return null;
    return companion;
  }
  // [RESTING 2026-08-29] The optional 11FC upgrade, resolved PER PIPE. The head's resting drop, and so the
  // realizable rise, depend on the pipe LENGTH (a longer pipe rests lower and gives more rise), so every
  // helper takes the plate offset and the pipe:
  //   restingDropForPipe   where the head rests on pipe L: plateOffset + L - HOLE_TO_PIPE_END_RESTING.
  //   rise11fcForPipe      the REALIZABLE rise from adding the 11FC on pipe L: (restingDrop - COLLAR_11FC) -
  //                        headDrop.min. The 11FC is 0.875 in tall, so with it in the stack the head sits no
  //                        lower than restingDrop - 0.875; the rise is from there up to the corner/travel limit
  //                        headDrop.min (= max(cornerFloor, dMin), pipe-independent). It CAN be negative (the
  //                        owner case 12 in is -0.424): the raw gap is only 0.451 and the collar's own 0.875 in
  //                        swallows it. Offered only when rise > 0.
  //   fcOfferedForPipe     rise > 0 AND the 11FC is a real, available part -> show the checkbox / card line.
  //   fcOnForPipe          offered AND the shopper ticked it (state.add11fc).
  //   placedHeadDropForPipe  the drop the head is installed/drawn/priced at on pipe L: the RESTING drop by
  //                        default, or headDrop.min (the corner limit) when the 11FC is on.
  // These drive the copy, the cart, the total and the drawing per the CHOSEN pipe, so they cannot disagree.
  var FC_EPS = 1e-9;
  function restingDropForPipe(plateOff, pipeLen) {
    var po = S.num(plateOff), L = S.num(pipeLen);
    return (po === null || L === null) ? null : po + L - S.HOLE_TO_PIPE_END_RESTING;
  }
  function rise11fcForPipe(res, plateOff, pipeLen) {
    if (!res || !res.slides || !res.headDrop) return null;
    var dr = restingDropForPipe(plateOff, pipeLen), hi = S.num(res.headDrop.min);
    return (dr === null || hi === null) ? null : (dr - S.COLLAR_11FC) - hi;
  }
  function fcOfferedForPipe(res, plateOff, pipeLen) {
    var rise = rise11fcForPipe(res, plateOff, pipeLen);
    return !!(rise != null && rise > FC_EPS && COMPANION && COMPANION.id != null && COMPANION.available !== false);
  }
  function fcOnForPipe(res, plateOff, pipeLen) {
    // [DRAG 2026-08-29] When the shopper dragged the height handle, the drag OWNS the 11FC: setHeadDrop set
    // state.add11fc from the placed drop (D < dRest(L)), bypassing the offered gate (the gate is for the
    // default checkbox affordance). Otherwise keep the offered-and-ticked model of the default view.
    if (state.headDrop != null) return res && res.slides && state.add11fc === true;
    return fcOfferedForPipe(res, plateOff, pipeLen) && state.add11fc === true;
  }
  function placedHeadDropForPipe(res, plateOff, pipeLen) {
    if (!res || !res.slides || !res.headDrop) return null;
    // [DRAG 2026-08-29] When the shopper has dragged the height handle (state.headDrop set), the drag OWNS the
    // placed drop: it is the continuous value the drag committed, not the binary resting / 11FC-corner-limit
    // this returned before. The default view (state.headDrop null) keeps the resting-or-checkbox model.
    if (state.headDrop != null) return S.num(state.headDrop);
    return fcOnForPipe(res, plateOff, pipeLen) ? S.num(res.headDrop.min) : restingDropForPipe(plateOff, pipeLen);
  }

  // =====================================================================
  // [DRAG 2026-08-29] THE HEIGHT DRAG: continuous head position -> the ONE required pipe.
  // Owner rulings 2026-08-29 (see SPEC-build-view-drags.md): the longer-pipe option list is gone; the height
  // handle on the build image sets the pipe. Two pure, liftable free functions do the selection (the Node
  // harness lifts them by name), and setHeadDrop() is the single writer that calls them and commits state.
  // =====================================================================
  // [DRAG] slidingPipeForDrop(D, ctx): for the SLIDING head (33CE008HEAD), pick the pipe + placed drop for a
  // requested hole-centre drop D (inches below the plate; larger = screen lower). ctx carries the pure inputs:
  //   { plateOffset, screen, holeMid, cornerFloor, Dmin, htpeResting, collar11fc, collarBelow, stock }
  // where cornerFloor = the drop at which the (tilted) top back corner just clears (D must be >= it), and
  // Dmin = max(cornerFloor, dMinTravel) = the highest the head can sit (res.headDrop.min).
  //
  // THE MODEL (owner rule 3). The head RESTS on the threaded collar at the pipe end at dRest(L) = plateOffset
  // + L - htpeResting (the lowest position on pipe L, the default). The optional 11FC (collar11fc = 0.875 in
  // tall) lets it slide UP the pipe into the raised band [max(Dmin, collarFloorD(L)), dRest(L) - collar11fc],
  // where collarFloorD(L) = plateOffset + L + collarBelow - (screen - holeMid) is the highest the head can go
  // on L before the threaded collar shows below the screen bottom (the "collar about to show" limit). Between
  // dRest(L) - collar11fc and dRest(L) is a 0.875 in DEAD gap (the 11FC's own height) - a D there snaps to
  // the resting detent. Drag DOWN past a pipe's resting drop -> the next LONGER stock rests deeper; drag UP
  // past the collar/corner limit -> the next SHORTER stock, head raised. Selection order for a clamped D:
  //   1) an exact resting detent (D == dRest(L)); 2) a dead-gap snap to the nearest resting detent;
  //   3) an exact raised placement (shortest such pipe); 4) the nearest feasible placement overall.
  // Returns { length, dRest, headDrop (the APPLIED drop, clamped/snapped), add11fc } or null.
  //
  // [NOTE - REPORTED 2026-08-29] On the owner's own case (63 deg, holeMid 18, screen 28, plate 5.419) this
  // model does NOT produce a "12 -> 10 pipe step on the way up": pipe 10's whole drop range (8.383 raised to
  // 9.258 resting) lies ABOVE the corner floor 10.807, so it can never host a corner-legal position; and on
  // pipe 12 the corner floor (10.807) sits inside the 0.875 in dead gap below resting (10.383 to 11.258), so
  // the 11FC offers no legal raise (engine rise11fc(12) = -0.424) and the head simply rests at 11.258. Going
  // up therefore cannot rise above 11.258 and the pipe never shortens to 10. The collar-rule pipe-shortening
  // DOES occur among the longer pipes (e.g. 24 -> 20 -> 18 on the way up), where the collar limit binds before
  // the corner. The "12 -> 18 resting step on the way down" is reproduced exactly.
  function slidingPipeForDrop(D, ctx) {
    if (!ctx) return null;
    var po = S.num(ctx.plateOffset), screen = S.num(ctx.screen), holeMid = S.num(ctx.holeMid);
    var cornerFloor = S.num(ctx.cornerFloor), Dmin = S.num(ctx.Dmin);
    var HTPE_R = S.num(ctx.htpeResting), C11 = S.num(ctx.collar11fc), CB = S.num(ctx.collarBelow);
    if (po === null || screen === null || holeMid === null || cornerFloor === null || Dmin === null ||
        HTPE_R === null || C11 === null || CB === null) return null;
    var EPS = 1e-6;
    var span = screen - holeMid;
    var stock = (ctx.stock || []).map(function (x) { return S.num(x); })
      .filter(function (x) { return x !== null; }).sort(function (a, b) { return a - b; });
    if (!stock.length) return null;
    function dRest(L) { return po + L - HTPE_R; }
    function collarFloorD(L) { return po + L + CB - span; }          // highest legal drop on L (collar limit)
    function collarOK(d, L) { return (po + L + CB) <= (d + span) + EPS; }
    function mk(L, d) { var dr = dRest(L); return { length: L, dRest: dr, headDrop: d, add11fc: (d < dr - EPS) }; }
    var gMax = dRest(stock[stock.length - 1]);
    var t = Math.min(Math.max(S.num(D), Dmin), gMax);
    var i, L, dr;
    // 1) an exact resting detent (shortest pipe whose resting drop is t)
    for (i = 0; i < stock.length; i++) { L = stock[i]; dr = dRest(L);
      if (Math.abs(t - dr) <= EPS && dr >= cornerFloor - EPS && collarOK(dr, L)) return mk(L, dr); }
    // 2) dead-gap snap: t strictly inside a pipe's dead gap (dr - C11, dr) -> rest on that pipe. The band
    // endpoints are EXCLUDED here: t == dr is an exact resting detent (step 1), t == dr - C11 is the raised-
    // band top (step 3), so the gap tested is open at both ends.
    for (i = 0; i < stock.length; i++) { L = stock[i]; dr = dRest(L);
      if (t > dr - C11 + EPS && t < dr - EPS && dr >= cornerFloor - EPS && collarOK(dr, L)) return mk(L, dr); }
    // 3) an exact raised placement (shortest pipe whose raised band contains t)
    for (i = 0; i < stock.length; i++) { L = stock[i]; dr = dRest(L);
      var lo = Math.max(Dmin, collarFloorD(L)), hi = dr - C11;
      if (hi >= lo - EPS && t >= lo - EPS && t <= hi + EPS && collarOK(t, L)) return mk(L, t); }
    // 4) the nearest feasible placement overall (drop nearest t; shorter pipe wins a tie)
    var best = null, bestDist = Infinity;
    for (i = 0; i < stock.length; i++) { L = stock[i]; dr = dRest(L);
      var cands = [];
      if (dr >= cornerFloor - EPS && collarOK(dr, L)) cands.push(dr);
      var l2 = Math.max(Dmin, collarFloorD(L)), h2 = dr - C11;
      if (h2 >= l2 - EPS) cands.push(Math.min(Math.max(t, l2), h2));
      for (var k = 0; k < cands.length; k++) {
        var dist = Math.abs(cands[k] - t);
        if (dist < bestDist - EPS) { bestDist = dist; best = mk(L, cands[k]); }
      }
    }
    return best;
  }

  // [DRAG] fixedPipeForLength(L, pools): for a FIXED head, pick the pipe for a required pipe length L (inches).
  // Snap L to whole inches, clamp to [minPipe, maxLen], then choose: a stock FIXED pipe of exactly that length
  // if one exists; else the cheapest TELESCOPING unit whose [length_min, length_max] range contains it; else
  // the nearest LONGER stock fixed pipe. pools = { byLen (length -> comp), stock (ascending lengths), tele
  // (telescoping comps), minPipe, maxLen }. Returns { kind:'fixed'|'tele', sku, length, comp[, collapsed,
  // extended] } or null. Pure over S.num + Math, so the harness lifts it.
  function fixedPipeForLength(L, pools) {
    if (!pools) return null;
    var Ls = S.num(L);
    if (Ls === null) return null;
    Ls = Math.round(Ls);                                    // whole inches
    var minPipe = S.num(pools.minPipe), maxLen = S.num(pools.maxLen);
    if (minPipe !== null && Ls < Math.ceil(minPipe - 1e-9)) Ls = Math.ceil(minPipe - 1e-9);
    if (maxLen !== null && Ls > Math.floor(maxLen + 1e-9)) Ls = Math.floor(maxLen + 1e-9);
    var byLen = pools.byLen || {};
    if (byLen[Ls]) return { kind: 'fixed', sku: byLen[Ls].sku, length: Ls, comp: byLen[Ls] };
    var tele = (pools.tele || []).filter(function (t) {
      var lo = S.num(t.length_min), hi = S.num(t.length_max);
      return lo !== null && hi !== null && lo <= Ls + 1e-9 && hi >= Ls - 1e-9;
    }).sort(function (a, b) { return Number(a.price) - Number(b.price); });
    if (tele.length) {
      var t0 = tele[0];
      return { kind: 'tele', sku: t0.sku, length: Ls, comp: t0,
               collapsed: S.num(t0.length_min), extended: S.num(t0.length_max) };
    }
    var longer = (pools.stock || []).map(function (x) { return S.num(x); })
      .filter(function (x) { return x !== null && x >= Ls - 1e-9; }).sort(function (a, b) { return a - b; });
    if (longer.length && byLen[longer[0]]) return { kind: 'fixed', sku: byLen[longer[0]].sku, length: longer[0], comp: byLen[longer[0]] };
    return null;
  }

  // [DRAG] The longest available pipe length the FIXED-head drag can reach: the longest stock fixed pipe, or a
  // telescoping unit's length_max if any reaches further. Read from the data (STOCK / TELE), never hardcoded.
  function longestAvailableLength() {
    var m = STOCK.length ? STOCK[STOCK.length - 1] : null;
    TELE.forEach(function (t) { var hi = S.num(t.length_max); if (hi !== null && (m === null || hi > m)) m = hi; });
    return m;
  }

  // [DRAG] The corner floor (the drop at which the tilted top back corner just clears the slope), computed on
  // the page exactly as the engine does, so setHeadDrop / getScene share one number. Null while any input is
  // missing. Uses S only (tiltPoint + riseAvailable), so it stays honest about an unknown standoff/pitch.
  function cornerFloorFor(head, holeMid) {
    if (!head) return null;
    var standoff = STANDOFF[head.sku], tk = tiltParams(head.sku);
    var tp = S.tiltPoint({ x: standoff, y: holeMid, standoff: standoff, tiltDeg: state.tilt,
      tiltPivot: tk.tiltPivot, tiltAxisDrop: tk.tiltAxisDrop, tiltTable: tk.tiltTable });
    if (tp.x === null || tp.y === null) return null;
    var ra = S.riseAvailable(tp.x, state.pitch);
    return (ra === null) ? null : tp.y - ra;
  }

  // [DRAG 2026-08-29][CORNER GUARD] The ACTUAL drawn corner clearance (elevationGeometry.rotCornerGap, y-up
  // inches; >= 0 means the tilted top back corner clears the slope) for a candidate build at the CURRENT tilt.
  // Built from the same inputs elevationSVG feeds elevationGeometry, so the guard agrees with the drawing to
  // the inch. Returns null when the geometry is not evaluable (a missing measurement or an unknown standoff),
  // so callers treat "cannot evaluate" as "do not commit". slides selects the head model; placedDrop is the
  // sliding placed drop (ignored for a fixed head, whose hole centre comes from plateOffset + pipe + offset).
  function cornerGapForBuild(slides, plate, head, pipeLen, placedDrop) {
    if (!plate || !head) return null;
    var holeMid = S.holeMidFromTop(state.topHole, state.vesa);
    var tk = tiltParams(head.sku);
    var geo = elevationGeometry({
      pitch: state.pitch, plateOffset: effectivePlateOffset(plate, state.pitch), pipe: pipeLen,
      slides: slides, headOffset: head.length_offset, headDropMin: placedDrop, show11fc: false,
      standoff: STANDOFF[head.sku], holeMid: holeMid, screen: state.screen,
      topHole: state.topHole, vesa: state.vesa, span: S.SLIDING_HEAD_SPAN,
      tiltDeg: state.tilt, tiltPivot: tk.tiltPivot, tiltAxisDrop: tk.tiltAxisDrop,
      tiltTable: (MIGRATE[head.sku] || null)
    });
    return geo ? geo.rotCornerGap : null;
  }

  // [DRAG 2026-08-29][CORNER GUARD] The shortest STOCK FIXED pipe whose drawn corner gap clears at the current
  // tilt, for a fixed head. This is the HARD floor the fixed drag can never go below, computed from the actual
  // gap (not the engine's minPipeForCorner alone, which for a large-offset head like 22HE001 can read tiny or
  // negative and let a too-short pipe through). Returns { length, comp } or null when nothing clears / the gap
  // is not evaluable.
  function shortestFixedPipeClearingCorner(plate, head) {
    var asc = STOCK.slice().sort(function (a, b) { return a - b; });
    for (var i = 0; i < asc.length; i++) {
      var g = cornerGapForBuild(false, plate, head, asc[i], null);
      if (g === null) return null;                 // cannot evaluate (unknown standoff/geometry): do not commit
      if (g >= -1e-6) { var c = pipeAtLength(asc[i]); return { length: asc[i], comp: c }; }
    }
    return null;
  }

  // [DRAG] setHeadDrop(D): THE SINGLE WRITER for the continuous head position. Resolves the requested drop D
  // (hole-centre inches below the plate) to state.pipe + state.add11fc + state.headDrop through the pure helper
  // for the head type, re-renders, and returns the APPLIED (clamped/snapped) drop. Ignored (returns the current
  // stored drop) when the build is not ready. Owner rulings 2 to 5, 2026-08-29.
  function setHeadDrop(D) {
    if (!ready()) return state.headDrop;
    var plate = bySku(state.plateSku), head = bySku(state.headSku);
    if (!plate || !head) return state.headDrop;
    var holeMid = S.holeMidFromTop(state.topHole, state.vesa);
    var po = effectivePlateOffset(plate, state.pitch);
    if (holeMid === null || po === null) return state.headDrop;
    var d = S.num(D);
    if (d === null) return state.headDrop;

    if (S.headSlides(head.length_offset)) {
      var res = solveBuild(plate, head, holeMid, state.tilt);
      if (!res || !res.slides || !res.headDrop) return state.headDrop;
      var cornerFloor = cornerFloorFor(head, holeMid);
      var Dmin = S.num(res.headDrop.min);
      if (cornerFloor === null || Dmin === null) return state.headDrop;
      var pick = slidingPipeForDrop(d, {
        plateOffset: po, screen: state.screen, holeMid: holeMid,
        cornerFloor: cornerFloor, Dmin: Dmin,
        htpeResting: S.HOLE_TO_PIPE_END_RESTING, collar11fc: S.COLLAR_11FC,
        collarBelow: S.THREADED_COLLAR_BELOW_PIPE_IN, stock: STOCK
      });
      if (!pick) return state.headDrop;
      var comp = pipeAtLength(pick.length);
      state.pipe = { kind: 'fixed', sku: comp ? comp.sku : null, length: pick.length, comp: comp };
      state.add11fc = pick.add11fc;
      state.headDrop = pick.headDrop;
      // [CORNER GUARD] slidingPipeForDrop already clamps the drop to Dmin = max(cornerFloor, dMin), so the
      // placed drop clears; this asserts it against the DRAWN gap and snaps the drop up to the corner floor if
      // any residual (a migrating-pivot rounding edge) leaves it negative. Never commit a negative-gap build.
      var sg = cornerGapForBuild(true, plate, head, pick.length, pick.headDrop);
      if (sg !== null && sg < -1e-6) {
        var pick2 = slidingPipeForDrop(cornerFloor, {
          plateOffset: po, screen: state.screen, holeMid: holeMid, cornerFloor: cornerFloor, Dmin: Dmin,
          htpeResting: S.HOLE_TO_PIPE_END_RESTING, collar11fc: S.COLLAR_11FC,
          collarBelow: S.THREADED_COLLAR_BELOW_PIPE_IN, stock: STOCK
        });
        if (pick2) { var c2 = pipeAtLength(pick2.length);
          state.pipe = { kind: 'fixed', sku: c2 ? c2.sku : null, length: pick2.length, comp: c2 };
          state.add11fc = pick2.add11fc; state.headDrop = pick2.headDrop; }
      }
      renderAnswer();
      return state.headDrop;
    }

    // FIXED head. D is the hole-centre drop below the plate = plateOffset + pipe + headOffset (the same length
    // holeCentreLen / midScreenFromPlate use). Inverting for the pipe: pipe = D - plateOffset - headOffset.
    // [REPORTED 2026-08-29] SPEC-build-view-drags.md A3 wrote "L = D + headOffset_fixed - plateOffset"; that
    // inverts the sign of headOffset. The existing fixed formula is holeCentreDrop = plateOffset + pipe +
    // headOffset (see midScreenFromPlate and its test at PO + 4 + HEAD_HE), so the correct inversion is
    // L = D - plateOffset - headOffset. Implemented that way so the height handle sits at the hole centre and
    // tracks the pointer; the spec's sign is a typo.
    var headOffset = S.num(head.length_offset);
    if (headOffset === null) return state.headDrop;
    var standoff = STANDOFF[head.sku], tk2 = tiltParams(head.sku);
    var minPipe = S.minPipeForCorner({ holeMid: holeMid, headOffset: head.length_offset, plateOffset: po,
      standoff: standoff, riseOver12: state.pitch, tiltDeg: state.tilt,
      tiltPivot: tk2.tiltPivot, tiltAxisDrop: tk2.tiltAxisDrop, tiltTable: tk2.tiltTable });
    var Lreq = d - po - headOffset;
    var fpick = fixedPipeForLength(Lreq, { byLen: PIPE_BY_LEN, stock: STOCK, tele: TELE,
      minPipe: minPipe, maxLen: longestAvailableLength() });
    if (!fpick) return state.headDrop;
    // [CORNER GUARD 2026-08-29] THE HARD RULE: never commit a fixed build whose DRAWN top back corner
    // interferes with the slope. minPipeForCorner alone is not enough (owner bug 2026-08-29): for a large-
    // offset head like 22HE001 it can read tiny (0.839 in) or negative (tilted), and a stale/wrong headDropMin
    // from the drag layer can drive Lreq below it, committing a 1.75 in pipe with the corner above the slope.
    // So verify the CHOSEN pipe's actual corner gap here and, if it does not clear (or cannot be evaluated),
    // snap UP to the shortest stock pipe that DOES clear at the current tilt. This is the corner-limited
    // minimum feasible build (owner rule 5's "closest to the ceiling WITHOUT interfering").
    var fgap = cornerGapForBuild(false, plate, head, fpick.length, null);
    if (fgap === null) return state.headDrop;        // cannot evaluate the corner: do not commit a drag
    if (fgap < -1e-6) {
      var floor = shortestFixedPipeClearingCorner(plate, head);
      if (!floor) return state.headDrop;             // nothing clears: leave the current build untouched
      fpick = { kind: 'fixed', sku: floor.comp ? floor.comp.sku : null, length: floor.length, comp: floor.comp };
    }
    state.pipe = { kind: fpick.kind, sku: fpick.sku, length: fpick.length, comp: fpick.comp,
      collapsed: fpick.collapsed, extended: fpick.extended };
    state.add11fc = false;
    state.headDrop = po + fpick.length + headOffset;   // the applied drop from the chosen pipe length
    renderAnswer();
    return state.headDrop;
  }

  // [DRAG] The pipe length the build uses: the drag's chosen pipe when set, else the engine recommendation
  // (the default view, rule 5). Replaces effectivePipe (the old option-list pick).
  function chosenPipeLen(res) {
    if (state.pipe && S.num(state.pipe.length) !== null) return S.num(state.pipe.length);
    return res ? res.recommended : null;
  }
  // [DRAG] The pipe component for the chosen pipe: the telescoping comp when the fixed-head drag picked one,
  // else the stock fixed pipe at that length.
  function chosenPipeComp(pipeLen) {
    if (state.pipe && state.pipe.comp) return state.pipe.comp;
    return pipeAtLength(pipeLen);
  }
  // The cart line items for a solved build, in the contract order (plate, head, pipe),
  // with the 11FC companion appended as a fourth line when present. Pure so the harness
  // can lift it: the companion belongs on a sliding build and is absent on a fixed one.
  function buildCartItems(plate, head, pipeComp, companion) {
    var items = [
      { id: plate.id, quantity: 1 },
      { id: head.id, quantity: 1 },
      { id: pipeComp.id, quantity: 1 }
    ];
    if (companion && companion.id != null) items.push({ id: companion.id, quantity: 1 });
    return items;
  }
  // The build total (plate + head + pipe), plus the 11FC companion price when the
  // companion is present. Both the visible "Build total" and the button aria-label read
  // this, so they match the cart. The companion price is the same dollar-string shape as
  // the pool, so Number() parses it cleanly.
  function buildTotal(plate, head, pipeComp, companion) {
    var t = Number(plate.price) + Number(head.price) + Number(pipeComp.price);
    if (companion && companion.price != null) t += Number(companion.price);
    return t;
  }
  // [D] The cart line properties for a solved build. _vms_config_id and _vms_config_summary are the
  // existing app contract (configSummary is UNCHANGED). _vms_tilt_deg is added ONLY when the screen is
  // tilted, as whole degrees: the tilt shortens the recommended pipe, so the sold build must record the
  // tilt it assumes. Underscore-prefixed, so Shopify hides it in cart and checkout displays by
  // convention while it still rides on the order. buildPayloadItems spreads these onto EVERY line, so
  // the tilt lands on the plate, head, pipe and collar lines alike. Pure, so the harness asserts it.
  function cartLineProps(configId, summary, tiltDeg, buildValue) {
    var props = { _vms_config_id: configId, _vms_config_summary: summary };
    var t = S.num(tiltDeg);
    if (t !== null && t > 0) props._vms_tilt_deg = String(Math.round(t));
    // [BUILD PROPERTY] The visible "Build" summary, spread onto every line by
    // buildPayloadItems exactly like the hidden props. Additive; the _vms_* set is
    // unchanged. Only set when supplied so a legacy caller keeps the old shape.
    if (buildValue) props.Build = buildValue;
    return props;
  }
  function buildPayloadItems(items, properties) {
    return items.map(function (it) {
      var line = { id: it.id, quantity: it.quantity };
      if (properties) {
        line.properties = Object.assign({}, properties, { _vms_unit_qty: String(it.quantity) });
      }
      return line;
    });
  }
  function postCartAdd(items, properties) {
    var payloadItems = buildPayloadItems(items, properties);
    var url = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: payloadItems })
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (data) {
        if (!res.ok || (data && data.status)) {
          var msg = (data && (data.description || data.message)) || ('Add to cart failed (status ' + res.status + ').');
          throw new Error(msg);
        }
        return data;
      });
    });
  }

  // [PAGE TOOLS 2026-09-11] The slanted reserve-gate scan, factored out so a test can drive the
  // shipping code with fixtures (mirrors the configurator's reservedPartTitle). Returns the title of
  // the first chosen part flagged custom.backorder_enabled (reserve mode), else null. The parts array
  // is exactly what buildCartItems assembles: [plate, head, pipeComp] plus the 11FC companion.
  function slantedReservedTitle(parts) {
    for (var ri = 0; ri < parts.length; ri++) {
      if (parts[ri] && parts[ri].backorder_enabled === true) return parts[ri].title || parts[ri].sku || 'This part';
    }
    return null;
  }
  // The customer-safe, product-naming reserve refusal (straight quotes, a contraction apostrophe, no
  // dashes). Shared by the button warnbox and the MCP add-to-cart relay.
  function slantedReserveMessage(title) {
    return '"' + title + '" is on backorder and can\'t be added here. Reserve it from its product page instead.';
  }

  // Attach the Add-to-cart handler for a solved build. summaryLen is the
  // HOLE-CENTRE length (the app-contract figure), not the mid-screen number.
  // needsCompanion is the OPTIONAL 11FC toggle resolved for the chosen pipe (fcOnForPipe):
  // since the resting model (owner 2026-08-29) the sliding 33CE008HEAD carries the 11FC
  // line only when the shopper ticks it and it can really raise the head; never on fixed heads.
  function wireAddToCart(plate, head, pipeComp, summaryLen, total, needsCompanion, midScreen) {
    var btn = el('vmsw-addcart');
    var status = el('vmsw-cart-status');
    if (!btn) return;
    mcpReserveReason = null;   // [PAGE TOOLS 2026-09-11] reset each render; set only in the reserve branch
    // Availability, same rule as the configurator: every part must have a numeric
    // id and not report available === false. On a sliding build the 11FC companion is
    // one of those parts, so a missing or unavailable companion fails this check and
    // routes to the cart_unavailable notice, never a silent incomplete package.
    var companion = companionLine(needsCompanion, COMPANION);
    // [BUILD PROPERTY] The visible "Build" value for THIS build, keyed off the id
    // that becomes _vms_config_id on the click, so its code matches. midScreen is
    // the SAME mid-screen finished length the page shows (passed in by the caller);
    // the collar clause tracks the optional 11FC exactly as the summary row does.
    var buildPropFor = function (cid) {
      return buildProperty(cid, {
        screen: state.screen,
        headSku: head.sku,
        plateSku: plate.sku,
        pipeSku: pipeComp.sku,
        companionSku: companion ? companion.sku : null,
        midScreenLen: midScreen,
        pitch: state.pitch
      });
    };
    var parts = [plate, head, pipeComp];
    if (needsCompanion) parts.push(companion);   // null when the companion is absent -> not addable
    var addable = parts.every(function (p) { return p && p.id && p.available !== false; });
    if (!addable) {
      mcpAddToCart = null;   // [MCP] this build is not addable
      btn.disabled = true;
      if (status) { status.textContent = ''; }
      btn.insertAdjacentHTML('afterend',
        '<div class="warnbox" role="alert">' + fillHTML(COPY('cart_unavailable')) + '</div>');
      return;
    }
    // [PAGE TOOLS 2026-09-11] Reserve gate: a chosen part flagged custom.backorder_enabled
    // (reserve mode) is reserved from its OWN product page and must never enter the cart as part
    // of a build. Checked BEFORE any /cart/add call, so both the Add button and the MCP
    // add-to-cart tool refuse and name the product. The pool components carry the boolean from
    // snippets/vms-configurator-data.liquid; slanted-pool parts are not normally reserved.
    var reservedTitle = slantedReservedTitle(parts);
    if (reservedTitle) {
      var reserveMsg = slantedReserveMessage(reservedTitle);
      mcpReserveReason = reserveMsg;   // read by mcpAddToCartPublic BEFORE its btn.disabled guard
      mcpAddToCart = function () { return Promise.resolve({ ok: false, reason: reserveMsg }); };
      btn.disabled = true;
      if (status) { status.textContent = ''; }
      var wb = document.createElement('div');
      wb.className = 'warnbox';
      wb.setAttribute('role', 'alert');
      wb.textContent = reserveMsg;
      btn.insertAdjacentElement('afterend', wb);
      return;
    }
    // [MCP 2026-08-30] Capture a redirect-free cart-add for the WebMCP layer: the SAME cartLineProps /
    // buildCartItems builders and the SAME _vms_* config summary as the button, but it resolves with the
    // posted items instead of navigating to /cart. The MCP addToCart() guards the example/ready/disabled
    // state before ever calling this.
    mcpAddToCart = function () {
      var cid = newConfigId();
      var summaryStr = configSummary(head, plate, summaryLen);
      var props = cartLineProps(cid, summaryStr, state.tilt, buildPropFor(cid));
      var items = buildCartItems(plate, head, pipeComp, companion);
      // [review S7] The configurator's C8 result shape: each line with its SKU, title, price and variant id
      // (not just id + quantity), the total, the finished length and the Build text. [review S6] Both
      // finished-length references are named: finished_length is MID-SCREEN (what the page shows), and the
      // cart's _vms_config_summary keeps the HOLE-CENTRE figure ON PURPOSE (the shared app contract).
      var infoById = {};
      [plate, head, pipeComp].forEach(function (p) { if (p && p.id != null) infoById[p.id] = p; });
      if (companion && companion.id != null) infoById[companion.id] = companion;
      // The client-side build total, kept for comparison against the server (review R2 item 1).
      var buildTotal = mcpCents(total);
      return postCartAdd(items, props).then(function () {
        // [review R2 item 1] Read the SERVER's prices and quantities from /cart.js, not the request and the
        // page's cached prices. This store's /cart/add.js returns price as a dollar STRING with no
        // final_line_price, which is why we read /cart.js instead (it always returns integer CENTS:
        // final_price, final_line_price). We keep only the lines carrying THIS build's config_id, match them
        // to the request by variant id, and list any requested variant the cart did not return in not_added[].
        // Mirrors the configurator's addToCart exactly.
        return Promise.resolve(fetch('/cart.js', { headers: { 'Accept': 'application/json' } }))
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var lines = (cart && Array.isArray(cart.items))
              ? cart.items.filter(function (it) { return it.properties && it.properties._vms_config_id === cid; }) : [];
            var byId = {};
            lines.forEach(function (l) { byId[String(l.id)] = l; });
            var added = [];
            var not_added = [];
            items.forEach(function (reqIt) {
              var l = byId[String(reqIt.id)];
              var c = infoById[reqIt.id] || {};
              if (l) {
                var unit = (l.final_price != null ? l.final_price : l.price);
                var lineC = (l.final_line_price != null ? l.final_line_price : l.line_price);
                added.push({
                  sku: (l.sku != null && l.sku !== '') ? l.sku : (c.sku != null ? c.sku : null),
                  title: (l.title != null && l.title !== '') ? l.title : (c.title != null ? c.title : null),
                  quantity: l.quantity,
                  price: (unit != null && !isNaN(Number(unit))) ? mcpCents(Number(unit) / 100) : (c.price != null ? mcpCents(Number(c.price)) : null),
                  line_total: (lineC != null && !isNaN(Number(lineC))) ? mcpCents(Number(lineC) / 100) : null,
                  variant_id: reqIt.id,
                  image_url: mcpImg(infoById[reqIt.id])
                });
              } else {
                not_added.push({ sku: (c.sku != null ? c.sku : null), variant_id: reqIt.id, quantity: reqIt.quantity });
              }
            });
            var serverTotal = mcpCents(added.reduce(function (s, x) { return s + (x.line_total || 0); }, 0));
            return { ok: true, added: added, not_added: not_added,
              total: serverTotal, build_total: buildTotal,
              finished_length: (midScreen != null ? nice(midScreen) + ' in to mid-screen' : null),
              finished_length_hole_centre: (summaryLen != null ? nice(summaryLen) + ' in to hole centre' : null),
              length_reference: { finished_length: 'ceiling plate down to the middle of the screen',
                finished_length_hole_centre: 'ceiling plate down to the middle of the mounting holes',
                config_summary: 'ceiling plate down to the middle of the mounting holes' },
              oversize_shipping: mcpOversize(),
              build_property: buildPropFor(cid), summary: summaryStr, config_id: cid,
              cart_url: (window.routes && window.routes.cart_url) || '/cart',
              handback: 'Added to the cart. To take this build back out, call vms-remove-build with config_id ' + cid + '.',
              share_url: shopperShareURL(),
              currency: 'USD' };
          });
      })
        // [review R2 item 2] The configurator's internal .catch: a guard or a thrown fetch comes back as
        // { ok:false, reason }, never a rejected promise, so the MCP layer always gets a result object.
        .catch(function (err) { return { ok: false, reason: (err && err.message) ? err.message : String(err) }; });
    };
    btn.addEventListener('click', function () {
      btn.disabled = true;
      if (status) { status.classList.remove('vmscfg-cart-error'); status.textContent = COPY('cart_adding'); }
      // [D] _vms_tilt_deg is added by cartLineProps when the screen is tilted (state.tilt > 0), on
      // every line via buildPayloadItems. configSummary is unchanged (the shared app contract).
      var cid = newConfigId();
      var props = cartLineProps(cid, configSummary(head, plate, summaryLen), state.tilt, buildPropFor(cid));
      var items = buildCartItems(plate, head, pipeComp, companion);
      postCartAdd(items, props).then(function () {
        if (status) status.textContent = COPY('cart_added');
        var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        window.location.href = cartUrl;
      }).catch(function (err) {
        console.warn('vmsw: add to cart failed', err);
        btn.disabled = false;
        if (status) { status.classList.add('vmscfg-cart-error'); status.innerHTML = fillHTML(COPY('cart_error')); }
      });
    });
  }

  // [SUPERSEDED 2026-08-29] effectivePipe / optionsListHTML / wireOptions are REMOVED. The longer-pipe OPTION
  // LIST is gone (owner ruling: "get rid of all those different pipe lengths"): the height DRAG on the build
  // image now sets the one required pipe. The pipe the build uses is chosenPipeLen(res) (the drag's state.pipe
  // when set, else the engine recommendation) defined above with the drag helpers; the engine may still return
  // res.options, and the page ignores them. options_intro / option_recommended_tag copy keys are KEPT DEFINED
  // (a live template may carry owner values) but are NO LONGER READ.

  // [RESTING 2026-08-29] Wire the optional 11FC checkbox. Toggling it flips state.add11fc and re-renders the
  // whole answer (which re-places the head, re-prices with/without the collar line, and re-draws). The
  // checkbox is a persistent input inside the aria-live answer, so focus is restored to it after the swap.
  function wire11fc() {
    var box = el('vmsw-answer');
    if (!box) return;
    var cb = box.querySelector('#vmsw-add11fc');
    if (!cb) return;
    cb.addEventListener('change', function () {
      state.add11fc = !!cb.checked;
      renderAnswer();
      var again = el('vmsw-answer');
      var back = again && again.querySelector('#vmsw-add11fc');
      if (back) back.focus();
    });
  }

  // =====================================================================
  // [C1] THE ORTHOGRAPHIC BUILD VIEW (owner ask 2026-08-24: "the full
  // orthographic build view showing an angled ceiling at the users angled and
  // all the mount components and a simulated tv"). A to-scale SIDE ELEVATION,
  // generated here as SVG and re-rendered with every solve, so it always shows
  // the CURRENT build: the user's wall angle, the chosen plate, the effective
  // pipe (chosenPipeLen(res)), the head, and a simulated TV at the user's own
  // measurements. The screen hangs plumb, the wall closes in above it, and the
  // top back corner just clears the slope.
  //
  // Everything below is PRESENTATION. It takes the SOLVED geometry (state +
  // chosenPipeLen(res) + the engine result) and draws it. The ONLY geometry it
  // works out is the coordinate FRAME the build brief fixes, documented on
  // elevationGeometry; every STRUCTURAL number it places comes from a
  // measurement, the data snippet or solve(). The inches-to-pixel scale, the
  // viewBox fit, the plate rotation to the wall angle and the wall line drawn
  // from the pitch are the presentation allowances the brief names.
  // =====================================================================
  var ELEV_VB_W = 420, ELEV_VB_H = 520;   // portrait canvas: a build is always taller than it is wide
  var ELEV_MARGIN = 28;                    // px of breathing room inside the viewBox
  // Drawing sizes (NOT measurements): nominal block sizes in INCHES, so they scale with the
  // scene. They set how big the parts LOOK, never where they sit; the coordinates that matter
  // (plate point, pipe axis, hole centre, top corner) are all engine or measurement numbers.
  var ELEV_PANEL_DEPTH = 2.0;   // nominal TV panel thickness, a drawing width, not a spec
  var ELEV_HEAD_HALF_H = 1.9;   // half the mounting-head body height along the pipe
  var ELEV_HEAD_HALF_W = 1.7;   // half the head body width across the pipe
  var ELEV_PLATE_HALF = 2.6;    // half the ceiling-plate length along the wall
  var ELEV_PLATE_THICK = 1.1;   // ceiling-plate thickness off the wall surface
  var ELEV_COLLAR_HALF_W = 1.0; // half the 11FC collar width across the pipe (sliding head)
  var ELEV_COLLAR_H = 0.9;      // the 11FC collar height along the pipe
  var ELEV_PIPE_HALF_W = 0.6;   // half the drawn pipe width (about a 1-1/2 inch pipe body)
  // [B] The angled ceiling is drawn as an INFINITE HALF-PLANE, the whole area behind the mounting-face
  // line shaded, with the line itself kept as the "line for the ceiling" (owner ask 2026-08-25: "make the
  // ceiling fill the entire upper left portion of the view ... it will be infinity thick ... the entire
  // upper left area shaded"). The 6.5 in slab and its CEILING_THICK_IN are GONE: there is no thickness any
  // more, only the wall line and everything on its far (-x) side, clipped to the frame (see ceilingSlab).

  // [C1] The load-bearing coordinate geometry, in INCHES, in the frame the build brief fixes:
  // x positive TOWARD THE ROOM (the viewer of the TV), y positive UP, the ceiling-plate point P
  // at the origin. Pure over S.num + Math so the Node harness lifts and asserts it. Returns null
  // while any required number is missing (the caller draws nothing then). Presentation sizes
  // arrive on g.* so nothing here reaches a module variable.
  //   riseAvailable  standoff * pitch / 12: the height at which the wall reaches x = standoff
  //   wallK          12 / pitch: the wall line is x = wallK * y (draw-and-clip, presentation)
  //   holeCentreY    the VESA hole-centre height, below P (negative). FIXED head threads onto
  //                  the pipe END: -(plateOffset + pipe + headOffset). SLIDING head runs the pipe
  //                  THROUGH itself and is drawn at its HIGHEST install position: -headDropMin.
  //   topY           top of the screen = holeCentreY + holeMid. The TOP BACK CORNER is (standoff,
  //                  topY); it clears the slope exactly when topY <= riseAvailable, the whole point.
  function elevationGeometry(g) {
    var pitch = S.num(g.pitch), plateOffset = S.num(g.plateOffset), pipe = S.num(g.pipe);
    var standoff = S.num(g.standoff), holeMid = S.num(g.holeMid), screen = S.num(g.screen);
    var topHole = S.num(g.topHole), vesa = S.num(g.vesa);
    var panelDepth = S.num(g.panelDepth); if (panelDepth === null) panelDepth = 2.0;
    var headHalfH = S.num(g.headHalfH); if (headHalfH === null) headHalfH = 1.9;
    var headHalfW = S.num(g.headHalfW); if (headHalfW === null) headHalfW = 1.7;
    var span = S.num(g.span);
    var RUN = 12;
    // [ZERO-DEG 2026-08-29] pitch 0 (a flat, horizontal ceiling) is a valid slope to draw: the wall line
    // is horizontal through P, riseAvailable is 0 (the screen top sits at the ceiling), and wallK below is
    // Infinity (12 / 0), which every drawing consumer now handles explicitly (ceilingSlab, plateFaceAnchor,
    // elevationBBox and the stylized-plate branch all special-case a non-finite k as the horizontal wall).
    // The gate is `pitch < 0`; a null (unset) pitch still returns null and draws nothing.
    if (pitch === null || pitch < 0 || plateOffset === null || pipe === null ||
        standoff === null || holeMid === null || screen === null) return null;

    var riseAvailable = standoff * pitch / RUN;
    var pipeTopY = -plateOffset;
    var pipeBotY = -(plateOffset + pipe);

    var slides = !!g.slides, holeCentreY, headTopY, headBotY, collarY = null;
    if (slides) {
      var dmin = S.num(g.headDropMin);
      if (dmin === null) return null;
      holeCentreY = -dmin;                 // [RESTING] the PLACED head drop (resting by default), below P
      headTopY = holeCentreY + headHalfH;  // the pipe runs THROUGH the head
      headBotY = holeCentreY - headHalfH;
      // [RESTING 2026-08-29] The 11FC collar is drawn ONLY when the optional upgrade is on (g.show11fc). At
      // rest the head body bottom sits on the threaded collar and there is no 11FC band. When on, the 11FC
      // grips SLIDING_HEAD_SPAN of pipe below where the pipe enters the head top, as before.
      if (span !== null && g.show11fc) collarY = headTopY - span;
    } else {
      var headOffset = S.num(g.headOffset);
      if (headOffset === null) return null;
      holeCentreY = -(plateOffset + pipe + headOffset);
      headTopY = pipeBotY;                 // threads onto the pipe END
      headBotY = holeCentreY - headHalfH;
    }

    var topY = holeCentreY + holeMid;
    // VESA holes on the TV back, measured DOWN from the top of the screen. Null while mid-entry.
    var hole1Y = (topHole === null) ? null : topY - topHole;
    var hole2Y = (topHole === null || vesa === null) ? null : topY - (topHole + vesa);

    // [TILT] The rotated top back corner AND the rotated hole centre, from the head's kinematics
    // (tiltPivot BEHIND the surface, tiltAxisDrop BELOW the hole centre, or the 33CE008HEAD migrating
    // tiltTable). S.tiltPoint rotates in a hole-centre-origin frame, so its returned y is ABOVE the
    // hole centre and the absolute scene y is holeCentreY + that. At tiltDeg 0 the rotation is the
    // identity, so the rotated corner IS (standoff, topY), the hole centre does not move, and cornerGap
    // is unchanged. The DRAWING rotates the whole TV group about the ORIGINAL hole centre
    // (standoff, holeCentreY) and then TRANSLATES it by the hole-centre displacement, which reproduces
    // S.tiltPoint for the corner exactly (proven in the slope harness): the axis-behind, axis-below and
    // migrating cases all fold into that one rotate-then-translate. hcDisp* is that displacement in
    // scene inches, from tiltPoint of the hole centre (standoff, 0) itself.
    var tiltDeg = S.num(g.tiltDeg); if (tiltDeg === null) tiltDeg = 0;
    var tp = S.tiltPoint({ x: standoff, y: holeMid, standoff: standoff, tiltDeg: tiltDeg,
      tiltPivot: g.tiltPivot, tiltAxisDrop: g.tiltAxisDrop, tiltTable: g.tiltTable });
    var tpHC = S.tiltPoint({ x: standoff, y: 0, standoff: standoff, tiltDeg: tiltDeg,
      tiltPivot: g.tiltPivot, tiltAxisDrop: g.tiltAxisDrop, tiltTable: g.tiltTable });
    var rotCornerX = (tp.x === null) ? standoff : tp.x;
    var rotCornerY = (tp.y === null) ? topY : holeCentreY + tp.y;
    var hcDispX = (tpHC.x === null) ? 0 : tpHC.x - standoff;   // hole-centre horizontal move (scene in)
    var hcDispY = (tpHC.y === null) ? 0 : tpHC.y;              // hole-centre vertical move (scene in, +up)
    // Clearance of the ROTATED corner: the wall height at the corner's x (x * pitch / RUN) minus its y.
    var rotCornerGap = rotCornerX * pitch / RUN - rotCornerY;

    return {
      slides: slides,
      riseAvailable: riseAvailable,
      wallK: RUN / pitch,
      holeCentreY: holeCentreY,
      topY: topY,
      pipeTopY: pipeTopY, pipeBotY: pipeBotY,
      headTopY: headTopY, headBotY: headBotY, headHalfW: headHalfW,
      collarY: collarY,
      tvBackX: standoff, tvFrontX: standoff + panelDepth, tvTopY: topY, tvBotY: topY - screen,
      hole1Y: hole1Y, hole2Y: hole2Y,
      cornerX: standoff, cornerY: topY,
      cornerGap: riseAvailable - topY,         // >= 0 means the top back corner clears the slope
      // [TILT] the rotated corner and its clearance, plus the TV-group transform: rotate about
      // (tvRotCx, tvRotCy) = the ORIGINAL hole centre, then translate by (hcDispX, hcDispY). At
      // tiltDeg 0 hcDisp* == 0, rotCornerX/Y == cornerX/Y and rotCornerGap == cornerGap.
      tiltDeg: tiltDeg, tvRotCx: standoff, tvRotCy: holeCentreY, hcDispX: hcDispX, hcDispY: hcDispY,
      rotCornerX: rotCornerX, rotCornerY: rotCornerY, rotCornerGap: rotCornerGap
    };
  }

  // [C1] The inch-space bounding box of everything DRAWN: the plate block around P (rotated to the
  // wall), the pipe, the head, the collar, the TV, and the anchor points P and the top back corner.
  // Pure. The wall is NOT included: it is clipped to this box at draw time (clipWallToBox), so a
  // shallow pitch cannot stretch the frame with a far-off wall endpoint.
  // extraPts (optional) is an array of {x, y} scene-inch points from any composited ortho image
  // corners, so the fit zooms to CONTAIN the real images and nothing clips. Omitted (the Node
  // harness calls it with the six sizing args only) it is a no-op and the box is the stylized one.
  function elevationBBox(geo, plateHalf, plateThick, collarHalfW, collarH, pipeHalfW, extraPts) {
    var ph = S.num(plateHalf); if (ph === null) ph = 2.6;
    var pt = S.num(plateThick); if (pt === null) pt = 1.1;
    var cw = S.num(collarHalfW); if (cw === null) cw = 1.0;
    var chh = S.num(collarH); if (chh === null) chh = 0.9;
    var pw = S.num(pipeHalfW); if (pw === null) pw = 0.6;
    var xs = [], ys = [];
    function add(x, y) { if (x !== null && y !== null) { xs.push(x); ys.push(y); } }
    // Plate block corners about P: unit along the wall u = (k,1)/|.|, normal off it n = (1,-k)/|.|.
    // [ZERO-DEG 2026-08-29] wallUnitVectors handles a FLAT ceiling (k Infinity): the wall runs horizontal
    // (u = (1,0)) with the plate thickness dropping straight down (n = (0,-1)), the limit of the formula.
    var wu = wallUnitVectors(geo.wallK);
    var ux = wu.ux, uy = wu.uy, nx = wu.nx, ny = wu.ny;
    [[-ph, 0], [ph, 0], [ph, pt], [-ph, pt]].forEach(function (c) {
      add(c[0] * ux + c[1] * nx, c[0] * uy + c[1] * ny);
    });
    add(0, 0);
    add(-pw, geo.pipeTopY); add(pw, geo.pipeBotY);
    add(-geo.headHalfW, geo.headTopY); add(geo.headHalfW, geo.headBotY);
    if (geo.collarY !== null) { add(-cw, geo.collarY - chh / 2); add(cw, geo.collarY + chh / 2); }
    add(geo.tvBackX, geo.tvTopY); add(geo.tvFrontX, geo.tvBotY);
    add(geo.cornerX, geo.cornerY);
    if (Array.isArray(extraPts)) { extraPts.forEach(function (p) { if (p) add(S.num(p.x), S.num(p.y)); }); }
    var xMin = Math.min.apply(null, xs), xMax = Math.max.apply(null, xs);
    var yMin = Math.min.apply(null, ys), yMax = Math.max.apply(null, ys);
    return { xMin: xMin, xMax: xMax, yMin: yMin, yMax: yMax, w: xMax - xMin, h: yMax - yMin };
  }

  // [C1] Uniform-scale fit of an inch bbox into the viewBox, y flipped (inches up maps to px down),
  // the content centred. ONE scale for BOTH axes so the wall angle stays TRUE. Returns the scale s
  // and offsets so that px_x = ox + x * s and px_y = oy - y * s. Pure.
  function elevationFit(bbox, vbW, vbH, margin) {
    var availW = vbW - 2 * margin, availH = vbH - 2 * margin;
    var s = Math.min(availW / bbox.w, availH / bbox.h);
    if (!isFinite(s) || s <= 0) s = 1;
    var contentW = bbox.w * s, contentH = bbox.h * s;
    var ox = (vbW - contentW) / 2 - bbox.xMin * s;
    var oy = (vbH - contentH) / 2 + bbox.yMax * s;
    return { s: s, ox: ox, oy: oy };
  }

  // [C1] Clip the wall line x = k * y to a rect (the inch bbox), returning its two endpoints. The
  // wall passes through P, and x is monotonic in y (k > 0), so the segment runs between the rect's
  // y bounds, each end pulled in to an x bound when it would exit the side. The optional insetIn pulls
  // BOTH endpoints toward the segment centre by that many inches ALONG the wall; omitted, it behaves
  // exactly as before. [B] The ceiling slab now calls this with NO inset for its full-width mounting-face
  // (near) segment (owner 2026-08-24); the inset arg is kept for any other clipped-line use. Pure.
  // [ZERO-DEG 2026-08-29] The wall's unit direction (u, up the slope) and unit normal (n, toward the room)
  // for a wall x = k*y. u = (k, 1)/|.|, n = (1, -k)/|.|. At a FLAT ceiling k is Infinity (12 / 0): the wall
  // is horizontal, so u -> (1, 0) and n -> (0, -1) (thickness drops straight down), the finite limit of the
  // formula. Used by elevationBBox and the stylized-plate polygon so neither divides Infinity by Infinity.
  // Pure over Math.
  function wallUnitVectors(k) {
    if (typeof k === 'number' && !isFinite(k)) return { ux: 1, uy: 0, nx: 0, ny: -1 };
    var len = Math.sqrt(k * k + 1);
    return { ux: k / len, uy: 1 / len, nx: 1 / len, ny: -k / len };
  }
  function clipWallToBox(k, box, insetIn) {
    function pt(y) {
      var x = k * y;
      if (x < box.xMin) { x = box.xMin; y = x / k; }
      else if (x > box.xMax) { x = box.xMax; y = x / k; }
      return { x: x, y: y };
    }
    var a = pt(box.yMin), b = pt(box.yMax);
    var inset = S.num(insetIn);
    if (inset !== null && inset > 0) {
      var dx = b.x - a.x, dy = b.y - a.y, len = Math.sqrt(dx * dx + dy * dy);
      if (len > 2 * inset) {                 // only when there is room; never cross the ends over
        var ux = dx / len, uy = dy / len;
        a = { x: a.x + ux * inset, y: a.y + uy * inset };
        b = { x: b.x - ux * inset, y: b.y - uy * inset };
      }
    }
    return [a, b];
  }
  // [B] Sutherland-Hodgman clip of a convex polygon (an array of {x, y} scene-inch vertices) to an
  // axis-aligned rectangle (xMin/xMax/yMin/yMax), returning the clipped vertex list (possibly empty).
  // Used to cut the ceiling BAND to the drawn frame so BOTH its faces end exactly on the frame edges,
  // not just the near line. Clipped edge by edge (left, right, bottom, top), each intersection found
  // parametrically. Pure over S.num + Math.
  function clipPolyToRect(poly, box) {
    var xMin = S.num(box.xMin), xMax = S.num(box.xMax), yMin = S.num(box.yMin), yMax = S.num(box.yMax);
    function clip(pts, inside, at) {
      var out = [];
      for (var i = 0; i < pts.length; i++) {
        var a = pts[(i + pts.length - 1) % pts.length], b = pts[i];
        var ina = inside(a), inb = inside(b);
        if (inb) { if (!ina) out.push(at(a, b)); out.push(b); }
        else if (ina) { out.push(at(a, b)); }
      }
      return out;
    }
    function atX(a, b, X) { var t = (X - a.x) / (b.x - a.x); return { x: X, y: a.y + t * (b.y - a.y) }; }
    function atY(a, b, Y) { var t = (Y - a.y) / (b.y - a.y); return { x: a.x + t * (b.x - a.x), y: Y }; }
    var p = poly.slice();
    p = clip(p, function (q) { return q.x >= xMin; }, function (a, b) { return atX(a, b, xMin); });
    if (!p.length) return p;
    p = clip(p, function (q) { return q.x <= xMax; }, function (a, b) { return atX(a, b, xMax); });
    if (!p.length) return p;
    p = clip(p, function (q) { return q.y >= yMin; }, function (a, b) { return atY(a, b, yMin); });
    if (!p.length) return p;
    p = clip(p, function (q) { return q.y <= yMax; }, function (a, b) { return atY(a, b, yMax); });
    return p;
  }
  // [B] The ceiling as an INFINITE HALF-PLANE in scene inches (owner ask 2026-08-25: "make the ceiling
  // fill the entire upper left portion of the view. It should no longer be 6.5 inches thick, it will be
  // infinity thick. We will have a 'line for the ceiling' with the entire upper left area shaded"). It is
  // the WHOLE region on the FAR side of the mounting-face line (the wall x = k*y through P, the -x side
  // away from the room, i.e. the upper-left), CLIPPED to the frame rectangle. The old 6.5 in two-face slab
  // is gone: there is no far face any more, only the wall line and everything behind it. Built the same way
  // the slab was, with clipPolyToRect - a far-side quad whose perpendicular reach is effectively infinite
  // (BIG inches, far beyond any frame), extended BIG along the wall in both directions, so the clip returns
  // exactly (far half-plane) n frame. Returns { poly, near, corners } or null: poly is the clipped shaded
  // region (the fill), near is the mounting-face segment (the wall clipped to the frame, zero inset, the
  // full-width "line for the ceiling") for the bold surface line and the label, corners == poly. Pure over
  // S.num + Math.
  function ceilingSlab(k, frameRect) {
    if (!frameRect) return null;
    // [ZERO-DEG 2026-08-29] A FLAT (0 degree) ceiling: wallK is Infinity (12 / 0). The wall line is the
    // horizontal y = 0 through P, the shaded far side is the WHOLE half-plane ABOVE it (y >= 0), and the
    // "line for the ceiling" (near) is the horizontal frame span at y = 0. S.num rejects a non-finite k,
    // so this is handled before it. The fill is the frame region from y = 0 up to the frame top.
    if (typeof k === 'number' && !isFinite(k)) {
      var fyTop = S.num(frameRect.yMax), fxMin = S.num(frameRect.xMin), fxMax = S.num(frameRect.xMax);
      if (fyTop === null || fxMin === null || fxMax === null) return null;
      var top = Math.max(0, fyTop);   // nothing to shade if the frame top is below the ceiling line
      var flatPoly = [{ x: fxMin, y: 0 }, { x: fxMax, y: 0 }, { x: fxMax, y: top }, { x: fxMin, y: top }];
      var flatNear = [{ x: fxMin, y: 0 }, { x: fxMax, y: 0 }];
      return { poly: flatPoly, near: flatNear, corners: flatPoly };
    }
    var kk = S.num(k);
    if (kk === null || kk <= 0) return null;
    var L = Math.sqrt(kk * kk + 1);
    var BIG = 1e4;
    var ux = kk / L, uy = 1 / L;            // unit direction along the wall (up the slope)
    // Wall line x - k*y = 0; the unit normal toward the room (+x side) is (1, -k)/L, so the ceiling grows
    // on the OPPOSITE normal (-1, k)/L, away from the room. Reach BIG inches out (effectively infinite):
    // clipped to the frame this covers the entire far half-plane inside it.
    var ofx = -BIG / L, ofy = BIG * kk / L; // perpendicular offset to the FAR side, effectively infinite
    var n0 = { x: -BIG * ux, y: -BIG * uy };
    var n1 = { x: BIG * ux, y: BIG * uy };
    var f1 = { x: n1.x + ofx, y: n1.y + ofy };
    var f0 = { x: n0.x + ofx, y: n0.y + ofy };
    var poly = clipPolyToRect([n0, n1, f1, f0], frameRect);
    if (!poly || poly.length < 3) return null;
    var near = clipWallToBox(kk, frameRect);   // the mounting-face segment, zero inset (full width)
    return { poly: poly, near: near, corners: poly };
  }

  // Round to 2 dp for compact SVG coordinates (presentation only).
  function elevPx(v) { return Math.round(v * 100) / 100; }
  // [A] Round a 2D-affine SCALE/ROTATION term (a,b,c,d of an image matrix) to 6 dp. elevPx's 2 dp is fine
  // for a coordinate but NOT for a scale coefficient: at scene scale s ~ 16.57, s/94 ~ 0.1763 rounds to
  // 0.18, a ~2% error that multiplies by the image ROW (1631 for a head), shifting a composited part by
  // ~0.35 in on screen (owner saw the 11FC 3.2 in above the head bottom where the anchor says 2.852). The
  // translation terms (e,f) stay at elevPx: they are coordinates, not multiplied by anything. Presentation.
  function elevMx(v) { return Math.round(v * 1e6) / 1e6; }

  // [TILT] THE SVG ROTATION ANGLE for the simulated-TV group. Tilting the screen DOWN swings its top
  // toward the room (+x) and drops it. The drawing maps y UP-in-inches to DOWN-in-pixels (PY negates
  // it), so that top-forward turn reads as CLOCKWISE on screen, which is a POSITIVE SVG rotate angle.
  // Rotating the group by +tiltDeg about the tilt-axis screen point therefore lands the TV rectangle,
  // its VESA holes and the corner ring exactly on the engine's rotated corner (S.tiltPoint), which the
  // harness proves to 1e-9. [NOTE] The 2026-08-25 task brief wrote "-t"; that is the sign of the turn in
  // the engine's y-UP frame, and the drawing's y-flip converts it to +t on screen. -t would swing the
  // top BACKWARD and pull the ring off the true rotated corner, so +t is the correct one. Pure.
  function tvTiltAngle(tiltDeg) {
    var t = S.num(tiltDeg);
    return t === null ? 0 : t;
  }
  // The transform attribute value for the TV group, or '' at square (no group transform at all then,
  // so the zero-tilt drawing is byte-for-byte what it was). The group is drawn at its ZERO-TILT
  // positions and this transform carries it onto the engine's rotated corner. It ROTATES the group by
  // +tiltDeg about the ORIGINAL hole-centre screen point (rotCxPx, rotCyPx), then TRANSLATES by the
  // hole-centre displacement in px (txPx, tyPx). That rotate-then-translate reproduces S.tiltPoint for
  // every head kinematics (axis behind, axis below, and the migrating 33CE008HEAD): tiltPoint(point) =
  // rotate(point about the hole centre) + (hole-centre displacement), proven to 1e-9 in the harness. In
  // SVG the RIGHTMOST transform applies first, so "translate(..) rotate(..)" rotates then translates.
  // With a zero displacement (axis at the hole centre, the shipped default) it is a bare rotate, exactly
  // as before. Pure (elevPx only).
  function tvTiltTransform(tiltDeg, rotCxPx, rotCyPx, txPx, tyPx) {
    var t = tvTiltAngle(tiltDeg);
    if (t === 0) return '';
    var rot = 'rotate(' + elevPx(t) + ' ' + elevPx(rotCxPx) + ' ' + elevPx(rotCyPx) + ')';
    var tx = elevPx(S.num(txPx) || 0), ty = elevPx(S.num(tyPx) || 0);
    return (tx === 0 && ty === 0) ? rot : 'translate(' + tx + ' ' + ty + ') ' + rot;
  }

  // [DRAG 2026-08-29] Author one draggable handle inside the build SVG as a "drag" PILL (owner: the grips must
  // read "drag"). A rounded-rectangle (44 x 20, rx = half height) filled brand blue with a white stroke and a
  // centred white "drag" word (COPY('angle_handle_word'), the same setting the step 1 handle uses). It is a
  // GROUP that carries the role="slider" / tabindex / id / aria / class (unchanged ids: #vmsw-elev-h-slope /
  // -tilt / -height), so the drag layer still finds and captures the pointer on the element by id; the rect
  // and text bubble their pointer events up to the group and the text is pointer-events:none. aria-orientation
  // vertical for the height handle only. extraTransform lets the caller counter-rotate the tilt pill level.
  // Pure (elevPx / esc). See CSS .vmsw-elev__handle / .vmsw-elev__pill for the cursor and focus ring.
  // [ROUND 14 2026-08-30] The pills carry a two-line word now (owner: "Drag Ceiling" / "Drag Length" /
  // "Drag TV Angle"), so the rect GROWS to fit both lines with padding (the touch target only ever grows,
  // never shrinks: one-line stays 44 x 20). Width is sized to the longest line from a per-char estimate
  // (SVG cannot measure text at build time); height is 20 for one line, 34 for two. pillWords splits the
  // label into line 1 = the first word ("Drag") and line 2 = the rest, degrading to a single line for a
  // one-word setting. Font 13 bold white, centred; two lines sit at +/- 7.5 about the pill centre.
  var ELEV_PILL_H = 20, ELEV_PILL_H2 = 34, ELEV_PILL_MINW = 44, ELEV_PILL_CHARW = 7.4, ELEV_PILL_PAD = 18;
  // [DESIGN REVIEW F4a 2026-09-22] The pointer HIT AREA of each pill: a transparent rect drawn first in the handle
  // group (under the visible pill), at least ELEV_HIT_MIN viewBox units on each side and centred on the pill. At a
  // 375 px phone the 420-wide viewBox renders about 306 css px wide (scale 0.729, the two-line pill measured
  // 24.8 css px tall for 34 units), so 64 units is about 46.7 css px, over the 44 px touch target. The visible
  // pill, its word and the drag maths are unchanged: the drag layer listens on the group, and the rect only
  // widens where a pointerdown lands on it.
  var ELEV_HIT_MIN = 64;
  // [ROUND 14 2026-08-30] Left nudge (viewBox units) for the ceiling pill, shared by the render and previewSlope.
  var SLOPE_PILL_NUDGE = 12;
  function pillWords(text) {
    var words = String(text == null ? '' : text).trim().split(/\s+/);
    if (words.length === 0 || words[0] === '') return ['Drag'];
    if (words.length === 1) return [words[0]];
    return [words[0], words.slice(1).join(' ')];
  }
  function handleCircle(id, cxPx, cyPx, label, vmin, vmax, vnow, vtext, vertical, extraTransform, pillText) {
    var lines = pillWords(pillText != null ? pillText : (COPY('angle_handle_word') || 'Drag'));
    var longest = 0;
    lines.forEach(function (ln) { if (ln.length > longest) longest = ln.length; });
    var w = Math.max(ELEV_PILL_MINW, Math.round(longest * ELEV_PILL_CHARW + ELEV_PILL_PAD));
    var h = (lines.length > 1) ? ELEV_PILL_H2 : ELEV_PILL_H;
    var x = elevPx(cxPx - w / 2), y = elevPx(cyPx - h / 2);
    var hw = Math.max(w, ELEV_HIT_MIN), hh = Math.max(h, ELEV_HIT_MIN);   // [F4a] hit area, never smaller than the pill
    var cx = elevPx(cxPx);
    var textEl;
    if (lines.length > 1) {
      textEl = '<text text-anchor="middle" dominant-baseline="central" font-size="13" font-weight="700" fill="#ffffff" pointer-events="none">' +
        '<tspan x="' + cx + '" y="' + elevPx(cyPx - 7.5) + '">' + esc(lines[0]) + '</tspan>' +
        '<tspan x="' + cx + '" y="' + elevPx(cyPx + 7.5) + '">' + esc(lines[1]) + '</tspan></text>';
    } else {
      textEl = '<text x="' + cx + '" y="' + elevPx(cyPx) + '" text-anchor="middle" dominant-baseline="central" ' +
        'font-size="13" font-weight="700" fill="#ffffff" pointer-events="none">' + esc(lines[0]) + '</text>';
    }
    return '<g id="' + id + '" class="vmsw-elev__handle" role="slider" tabindex="0"' +
      (vertical ? ' aria-orientation="vertical"' : '') +
      ' aria-valuemin="' + vmin + '" aria-valuemax="' + vmax + '" aria-valuenow="' + vnow +
      '" aria-valuetext="' + esc(vtext) + '" aria-label="' + esc(label) + '"' +
      (extraTransform ? ' transform="' + extraTransform + '"' : '') + '>' +
      '<rect class="vmsw-elev__hit" x="' + elevPx(cxPx - hw / 2) + '" y="' + elevPx(cyPx - hh / 2) +
      '" width="' + hw + '" height="' + hh + '" fill="transparent" pointer-events="all"/>' +
      '<rect class="vmsw-elev__pill" x="' + x + '" y="' + y + '" width="' + w + '" height="' + h +
      '" rx="' + (h / 2) + '" ry="' + (h / 2) + '" fill="#1a2b88" stroke="#ffffff" stroke-width="2"/>' +
      textEl +
      '</g>';
  }

  // =====================================================================
  // [C1] REAL PRODUCT ORTHO IMAGES composited into the build view (owner ask
  // 2026-08-24: "I'd like the customer to be able to visualize better what they are
  // getting"). Eight mesh-derived LEFT-profile images (Content > Files, 94 ppi, RGBA
  // straight alpha) replace the stylized part shapes IN PLACE, in the same coordinate
  // frame, drawn to scale. The URLs arrive per part in CONFIG (see the section's
  // #vmsw-config); a part whose URL is absent, or whose vertical anchor is not yet
  // delivered, falls back to the stylized shape kept below.
  //
  // [RULE] THE IMAGES DO NOT DRIVE THE BUILD GEOMETRY. The pipe, the plate offset and
  // the head positions come from the ENGINE numbers exactly as before (elevationGeometry);
  // the images are pinned to those numbers by their delivered anchors. length_offset is a
  // calibrated stack that includes thread make-up, so an image anchor lands NEAR an engine
  // point but not exactly on it. Where the swivel socket and the pipe top disagree we draw
  // the pipe first and the pieces over it, so the pipe disappears UP into the socket (honest)
  // rather than leaving a floating gap.
  // =====================================================================
  var ORTHO_PPI = 94;   // every ortho image is exported at 94 pixels per inch

  // [TILT-STILLS 2026-08-25] THE PER-DEGREE HEAD FRAME COORDINATE TABLE, parsed from section 8 of the
  // head-tilt-stills delivery (generated from its CSV, not retyped). All pixels are at 94 ppi, origin
  // top-left. Each frame carries its VESA pattern-centre (vesaCol, vesaRow, the pin), the two endpoints
  // of the TV mounting-surface line (tvTop/tvBot col/row, so the drawing can check the frame face against
  // the drawn TV), and faceDeg (the face-line angle from vertical; the 33 heads start at 0.666 by their
  // plumbed rails, not 0.000, and 22HE001 / 33CE008HEAD start at 0.000). pipe_axis_col is fixed per head
  // (axisCol below); pipe_entry_row is the static socket mouth (pipeEntryRow below).
  var TILT_FRAMES_META = {
    '33HE001': {
      0: { vesaCol: 184.94, vesaRow: 817.49, tvTopCol: 175.47, tvTopRow: 3.37, tvBotCol: 194.41, tvBotRow: 1631.61, faceDeg: 0.666 },
      1: { vesaCol: 184.4, vesaRow: 820.02, tvTopCol: 160.72, tvTopRow: 6.19, tvBotCol: 208.08, tvBotRow: 1633.85, faceDeg: 1.666 },
      2: { vesaCol: 183.91, vesaRow: 822.56, tvTopCol: 146.03, tvTopRow: 9.27, tvBotCol: 221.78, tvBotRow: 1635.85, faceDeg: 2.666 },
      3: { vesaCol: 183.46, vesaRow: 825.11, tvTopCol: 131.39, tvTopRow: 12.6, tvBotCol: 235.52, tvBotRow: 1637.62, faceDeg: 3.666 },
      4: { vesaCol: 183.05, vesaRow: 827.67, tvTopCol: 116.82, tvTopRow: 16.19, tvBotCol: 249.29, tvBotRow: 1639.14, faceDeg: 4.666 },
      5: { vesaCol: 182.69, vesaRow: 830.23, tvTopCol: 102.3, tvTopRow: 20.04, tvBotCol: 263.08, tvBotRow: 1640.43, faceDeg: 5.666 },
      6: { vesaCol: 182.38, vesaRow: 832.8, tvTopCol: 87.86, tvTopRow: 24.13, tvBotCol: 276.9, tvBotRow: 1641.47, faceDeg: 6.666 },
      7: { vesaCol: 182.11, vesaRow: 835.38, tvTopCol: 73.49, tvTopRow: 28.48, tvBotCol: 290.72, tvBotRow: 1642.27, faceDeg: 7.666 },
      8: { vesaCol: 181.93, vesaRow: 837.83, tvTopCol: 59.25, tvTopRow: 32.95, tvBotCol: 304.61, tvBotRow: 1642.71, faceDeg: 8.666 },
      9: { vesaCol: 181.85, vesaRow: 840.16, tvTopCol: 45.14, tvTopRow: 37.55, tvBotCol: 318.56, tvBotRow: 1642.78, faceDeg: 9.666 },
      10: { vesaCol: 181.8, vesaRow: 842.49, tvTopCol: 31.11, tvTopRow: 42.39, tvBotCol: 332.5, tvBotRow: 1642.6, faceDeg: 10.666 },
      11: { vesaCol: 181.8, vesaRow: 844.83, tvTopCol: 17.16, tvTopRow: 47.47, tvBotCol: 346.44, tvBotRow: 1642.18, faceDeg: 11.666 },
      12: { vesaCol: 181.84, vesaRow: 847.16, tvTopCol: 3.31, tvTopRow: 52.8, tvBotCol: 360.36, tvBotRow: 1641.52, faceDeg: 12.666 }
    },
    '33HE002': {
      0: { vesaCol: 184.94, vesaRow: 817.72, tvTopCol: 175.47, tvTopRow: 3.61, tvBotCol: 194.41, tvBotRow: 1631.84, faceDeg: 0.666 },
      1: { vesaCol: 184.44, vesaRow: 820.16, tvTopCol: 160.77, tvTopRow: 6.34, tvBotCol: 208.12, tvBotRow: 1633.99, faceDeg: 1.666 },
      2: { vesaCol: 183.99, vesaRow: 822.61, tvTopCol: 146.11, tvTopRow: 9.32, tvBotCol: 221.87, tvBotRow: 1635.91, faceDeg: 2.666 },
      3: { vesaCol: 183.58, vesaRow: 825.07, tvTopCol: 131.52, tvTopRow: 12.56, tvBotCol: 235.64, tvBotRow: 1637.58, faceDeg: 3.666 },
      4: { vesaCol: 183.21, vesaRow: 827.53, tvTopCol: 116.98, tvTopRow: 16.06, tvBotCol: 249.45, tvBotRow: 1639.01, faceDeg: 4.666 },
      5: { vesaCol: 182.89, vesaRow: 830, tvTopCol: 102.5, tvTopRow: 19.81, tvBotCol: 263.28, tvBotRow: 1640.2, faceDeg: 5.666 },
      6: { vesaCol: 182.61, vesaRow: 832.48, tvTopCol: 88.09, tvTopRow: 23.81, tvBotCol: 277.13, tvBotRow: 1641.15, faceDeg: 6.666 },
      7: { vesaCol: 182.37, vesaRow: 834.96, tvTopCol: 73.76, tvTopRow: 28.06, tvBotCol: 290.99, tvBotRow: 1641.85, faceDeg: 7.666 },
      8: { vesaCol: 182.18, vesaRow: 837.44, tvTopCol: 59.5, tvTopRow: 32.56, tvBotCol: 304.86, tvBotRow: 1642.32, faceDeg: 8.666 },
      9: { vesaCol: 182.03, vesaRow: 839.93, tvTopCol: 45.32, tvTopRow: 37.31, tvBotCol: 318.74, tvBotRow: 1642.54, faceDeg: 9.666 },
      10: { vesaCol: 181.92, vesaRow: 842.42, tvTopCol: 31.22, tvTopRow: 42.31, tvBotCol: 332.62, tvBotRow: 1642.52, faceDeg: 10.666 },
      11: { vesaCol: 181.86, vesaRow: 844.9, tvTopCol: 17.22, tvTopRow: 47.55, tvBotCol: 346.49, tvBotRow: 1642.26, faceDeg: 11.666 },
      12: { vesaCol: 181.84, vesaRow: 847.4, tvTopCol: 3.31, tvTopRow: 53.04, tvBotCol: 360.36, tvBotRow: 1641.75, faceDeg: 12.666 }
    },
    '22HE001': {
      0: { vesaCol: 90.59, vesaRow: 526.46, tvTopCol: 90.59, tvTopRow: 119.37, tvBotCol: 90.59, tvBotRow: 933.55, faceDeg: 0 },
      1: { vesaCol: 90.13, vesaRow: 528.42, tvTopCol: 83.03, tvTopRow: 121.39, tvBotCol: 97.24, tvBotRow: 935.44, faceDeg: 1 },
      2: { vesaCol: 89.71, vesaRow: 530.38, tvTopCol: 75.5, tvTopRow: 123.54, tvBotCol: 103.92, tvBotRow: 937.22, faceDeg: 2 },
      3: { vesaCol: 89.33, vesaRow: 532.35, tvTopCol: 68.02, tvTopRow: 125.82, tvBotCol: 110.63, tvBotRow: 938.88, faceDeg: 3 },
      4: { vesaCol: 88.97, vesaRow: 534.33, tvTopCol: 60.58, tvTopRow: 128.24, tvBotCol: 117.37, tvBotRow: 940.43, faceDeg: 4 },
      5: { vesaCol: 88.66, vesaRow: 536.31, tvTopCol: 53.18, tvTopRow: 130.78, tvBotCol: 124.14, tvBotRow: 941.85, faceDeg: 5 },
      6: { vesaCol: 88.37, vesaRow: 538.3, tvTopCol: 45.82, tvTopRow: 133.45, tvBotCol: 130.93, tvBotRow: 943.16, faceDeg: 6 },
      7: { vesaCol: 88.22, vesaRow: 540.05, tvTopCol: 38.61, tvTopRow: 136, tvBotCol: 137.83, tvBotRow: 944.1, faceDeg: 7 },
      8: { vesaCol: 88.1, vesaRow: 541.79, tvTopCol: 31.44, tvTopRow: 138.67, tvBotCol: 144.76, tvBotRow: 944.92, faceDeg: 8 },
      9: { vesaCol: 88.01, vesaRow: 543.54, tvTopCol: 24.33, tvTopRow: 141.47, tvBotCol: 151.69, tvBotRow: 945.62, faceDeg: 9 },
      10: { vesaCol: 87.95, vesaRow: 545.29, tvTopCol: 17.26, tvTopRow: 144.39, tvBotCol: 158.64, tvBotRow: 946.19, faceDeg: 10 },
      11: { vesaCol: 87.92, vesaRow: 547.04, tvTopCol: 10.24, tvTopRow: 147.43, tvBotCol: 165.59, tvBotRow: 946.65, faceDeg: 11 },
      12: { vesaCol: 87.92, vesaRow: 548.79, tvTopCol: 3.28, tvTopRow: 150.6, tvBotCol: 172.56, tvBotRow: 946.98, faceDeg: 12 }
    },
    '33CE008HEAD': {
      0: { vesaCol: 192.13, vesaRow: 824.45, tvTopCol: 192.13, tvTopRow: 10.08, tvBotCol: 192.13, tvBotRow: 1638.81, faceDeg: 0 },
      1: { vesaCol: 187.49, vesaRow: 822.7, tvTopCol: 173.27, tvTopRow: 8.46, tvBotCol: 201.7, tvBotRow: 1636.94, faceDeg: 1 },
      2: { vesaCol: 182.82, vesaRow: 821.03, tvTopCol: 154.39, tvTopRow: 7.16, tvBotCol: 211.24, tvBotRow: 1634.9, faceDeg: 2 },
      3: { vesaCol: 178.12, vesaRow: 819.45, tvTopCol: 135.5, tvTopRow: 6.2, tvBotCol: 220.74, tvBotRow: 1632.69, faceDeg: 3 },
      4: { vesaCol: 173.39, vesaRow: 817.94, tvTopCol: 116.58, tvTopRow: 5.56, tvBotCol: 230.2, tvBotRow: 1630.32, faceDeg: 4 },
      5: { vesaCol: 168.64, vesaRow: 816.52, tvTopCol: 97.66, tvTopRow: 5.26, tvBotCol: 239.62, tvBotRow: 1627.79, faceDeg: 5 },
      6: { vesaCol: 163.69, vesaRow: 815.54, tvTopCol: 78.56, tvTopRow: 5.63, tvBotCol: 248.81, tvBotRow: 1625.44, faceDeg: 6 },
      7: { vesaCol: 158.72, vesaRow: 814.63, tvTopCol: 59.48, tvTopRow: 6.34, tvBotCol: 257.97, tvBotRow: 1622.93, faceDeg: 7 },
      8: { vesaCol: 153.74, vesaRow: 813.82, tvTopCol: 40.4, tvTopRow: 7.38, tvBotCol: 267.08, tvBotRow: 1620.26, faceDeg: 8 },
      9: { vesaCol: 148.74, vesaRow: 813.09, tvTopCol: 21.35, tvTopRow: 8.75, tvBotCol: 276.14, tvBotRow: 1617.43, faceDeg: 9 },
      10: { vesaCol: 143.74, vesaRow: 812.45, tvTopCol: 2.32, tvTopRow: 10.46, tvBotCol: 285.15, tvBotRow: 1614.44, faceDeg: 10 }
    }
  };
  // Per-head anchors, now on the TILT SEQUENCE canvas (2026-08-25). Each head image is a per-degree
  // frame whose PIPE AXIS is a fixed image column (axisCol; the socket does not move, so it pins to
  // x = 0), and whose VESA pattern-centre (per-frame vesaCol/vesaRow, from TILT_FRAMES_META) pins to
  // the engine's ROTATED hole centre. The pipe axis is NO LONGER the image centre: the canvas grew
  // to the left to hold the swinging VESA arm, so axisCol is well right of W/2. tiltFrames is the map
  // of whole-degree -> file_url the section emits into #vmsw-config as "tilt_<SKU>"; absent, the head
  // falls back to its zero-tilt base URL (orthoHeadURLForTilt), then to the stylized shape.
  // [SLANTED 2D, ROUND 4 SOCKET-DATUM FIX 2026-09-20] axisCol for 33HE001 / 33HE002 corrected from
  // the 2026-08-25 head-tilt-stills delivery value 447.71 (its pipe_axis_col) to 440.0. The delivery
  // datum was 7.7 px = 0.082 in off the actual socket centre, so the fixed socket rendered 0.082 in
  // toward the ROOM of the drawn pipe (owner bug 2026-09-20: "33HE001/33HE002 offset to the right of
  // the pipe"). MEASURED FROM THE PNG frames (products/<KEY>/orthographic/<KEY>-ortho-side-tNN-94ppi.png,
  // alpha > 40, the opaque run containing the datum, socket rows 310 to 370): the socket run is 338 to
  // 542 with centre 440.0 on ALL 13 frames t00 to t12 (mean 439.99, the turned socket OD 2.18 in does
  // not move with tilt). Cross-check: with 440.0 the face-to-axis span (440.0 - vesaCol 184.94)/94 =
  // 2.713 in matches the GLB pipe-axis-to-mounting-surface 1.2354 + 1.4814 = 2.717 in; 447.71 gave
  // 2.795 in. 22HE001's axisCol 311.70 was re-verified CORRECT (socket run 210 to 413, centre 311.5).
  // 33CE008HEAD (536.74) is NOT changed this round (owner-reviewed composites; measured separately).
  var ORTHO_HEADS = {
    '33HE001': { cfg: 'ortho_33HE001', w: 555, h: 1646, axisCol: 440.0, pipeEntryRow: 301.29, tiltFrames: CONFIG['tilt_33HE001'] },
    '33HE002': { cfg: 'ortho_33HE002', w: 555, h: 1646, axisCol: 440.0, pipeEntryRow: 301.29, tiltFrames: CONFIG['tilt_33HE002'] },
    // [C1] 33CE008HEAD is the SLIDING head and the MIGRATING pivot. On the sequence canvas it is
    // 663 x 1642 with the pipe bore at axisCol 536.74 (well right of W/2 = 331.5) and the static socket
    // mouth at pipeEntryRow 638.92. collarSeatRow is RE-DERIVED on the new height (2026-08-25): the 11FC
    // seats COLLAR_11FC_FROM_HEAD_BOTTOM_IN (2.852 in) above the head image BOTTOM, and the bottom did
    // not move, so the row is h - 2.852 * 94 = 1642 - 268.088 = 1373.912. It is a reference figure only;
    // the runtime collar seat reads COLLAR_11FC_FROM_HEAD_BOTTOM_IN against the live h.
    '33CE008HEAD': { cfg: 'ortho_33CE008HEAD', w: 663, h: 1642, axisCol: 536.74, pipeEntryRow: 638.92, collarSeatRow: 1373.912, tiltFrames: CONFIG['tilt_33CE008HEAD'] },
    // [22HE001] Its BASE file was replaced in place with the true ZERO-TILT t00 frame (426 x 950,
    // axisCol 311.70, VESA row 526.46, socket mouth at the image top, pipeEntryRow 3.02). The earlier
    // 442 x 931 / row 523.913 spec and the "12 degree pose / 92.66 ppi" reading are OBSOLETE: the
    // delivery proved yesterday's render was a true 0 degree render (wrong reference height, not a tilt).
    // pipeOverlapIn 0.35 keeps the owner's "overlap the pipe .35 inches" tuck (pipe interface at the top).
    '22HE001': { cfg: 'ortho_22HE001', w: 426, h: 950, axisCol: 311.70, vesaRow: 526.46, pipeEntryRow: 3.02, pipeOverlapIn: 0.35, tiltFrames: CONFIG['tilt_22HE001'] }
  };
  // Per-plate anchors, BASE and SWIVEL (the two pieces are the articulation). Both pieces are
  // padded so the HINGE AXIS is the exact image horizontal centre, so the centring anchor is
  // W / 2 for both and the mirror leaves it fixed. hingeRow is the vertical anchor.
  // pivotToFaceIn is the perpendicular pivot-to-mounting-surface distance in INCHES, OWNER CAD, measured
  // 2026-08-24 (11CP006 "31.793mm from center of pivot to the surface that mounts to the ceiling" =
  // 1.25169 in, authoritative because the delivered 11CP006 hinge coordinate is ~10 px off; ANGLE-CEILING
  // 1.734 in, where owner CAD and the delivered rows agree). The base is anchored by its mounting FACE
  // using this d (see plateFaceAnchor); the delivered hingeRow/hingeCol stay for reference.
  // [PLATE-OFFSET-BY-ANGLE 2026-08-29] hingeToSocketBottomIn is the OWNER CAD 2026-08-29 perpendicular
  // distance from the hinge centre down to the swivel's bottom face (the socket mouth the pipe enters).
  // plateOffsetAt PREFERS it over the image-row derivation (socketMouthRow - hingeRow)/ORTHO_PPI, which is
  // now only a cross-check: ANGLE-CEILING rows give 1.9497 in vs the owner 1.950 (0.0003 off), 11CP006 rows
  // give 3.917 in vs the owner 3.811 (the swivel image is about 0.106 in off). The owner number governs.
  var ORTHO_PLATES = {
    '11CP006': {
      base:   { cfg: 'ortho_11CP006_BASE',   w: 561, h: 157, mountFaceRow: 1.17, hingeCol: 280.58, hingeRow: 108.86, pivotToFaceIn: 1.25169 },
      swivel: { cfg: 'ortho_11CP006_SWIVEL', w: 207, h: 397, hingeCol: 103.72, hingeRow: 27.53, socketMouthRow: 395.74, hingeToSocketBottomIn: 3.811 }   // owner CAD 2026-08-29
    },
    'ANGLE-CEILING': {
      base:   { cfg: 'ortho_ANGLE_CEILING_BASE',   w: 521, h: 292, mountFaceRow: 1.43, hingeCol: 260.50, hingeRow: 164.44, pivotToFaceIn: 1.734 },
      swivel: { cfg: 'ortho_ANGLE_CEILING_SWIVEL', w: 297, h: 256, hingeCol: 148.50, hingeRow: 71.36, socketMouthRow: 254.63, hingeToSocketBottomIn: 1.950 }   // owner CAD 2026-08-29
    }
  };
  // The 11FC set-screw shaft collar (sliding head only, dormant until 33CE008HEAD composites). [A] OWNER'S
  // SECOND COMPOSITE REVIEW 2026-08-24: it MATES DIRECTLY to the head body, its collar TOP edge (collarTopRow
  // 1.38) seated COLLAR_11FC_FROM_HEAD_BOTTOM_IN up from the head image bottom (see the collar-stack block).
  var ORTHO_COLLAR = { cfg: 'ortho_11FC', w: 284, h: 85, collarTopRow: 1.38 };
  // [A] THE THREADED COLLAR (sliding head only): the pipe-thread collar the re-cut 33CE008HEAD render
  // DROPPED, now its own piece (rendering agent 2026-08-24, GenericFile 46026462527766). OWNER'S SECOND
  // COMPOSITE REVIEW 2026-08-24 moved it OFF the head onto the PIPE END: its TOP row (topRow 0) now seats
  // THREADED_COLLAR_OVER_PIPE_IN (0.35 in) ABOVE the engine pipe end (geo.pipeBotY), on the pipe axis
  // (centred, so W/2), so the pipe end lies 0.35 in inside it ("put it over the end of the pipe .35 inches").
  // Its geometric BOTTOM row (bottomRow 94.03 = 1.000 in tall) stays for reference. The engine is the
  // authority for where the pipe ends; the collar just caps it.
  var ORTHO_THREADED_COLLAR = { cfg: 'ortho_33CE008_THREADED_COLLAR', w: 202, h: 97, topRow: 0, bottomRow: 94.03 };
  // [A] The two owner numbers from the second composite review 2026-08-24, verbatim in the quotes above:
  // where the 11FC mates on the head, and how far the threaded collar sits over the pipe end. Named so a
  // re-cut or a re-measure changes one literal, and so the Node harness can assert them by name.
  var COLLAR_11FC_FROM_HEAD_BOTTOM_IN = 2.852;   // 11FC top edge, inches up from the 33CE008HEAD image bottom
  var THREADED_COLLAR_OVER_PIPE_IN = 0.35;       // threaded collar top edge, inches above the engine pipe end
  // [PLATE-OFFSET-BY-ANGLE 2026-08-29] The pipe runs UP into the bottom of the swivel socket by this much
  // (owner ruling 2026-08-29: "the pipe is drawn 0.35 in up into the bottom of the swivel", the part that
  // stays horizontal at every slope angle, never up to the hinge). It is the plate side of the same 0.35 in
  // engagement the threaded collar uses at the pipe's other end (PIPE_ENGAGEMENT in assets/vms-slope.js),
  // and it is what plateOffsetAt subtracts so the drawn pipe top lands exactly 0.35 in inside the socket
  // mouth at any pitch. Named next to the other overlap literals so a re-measure changes one place.
  var PLATE_SWIVEL_PIPE_OVERLAP_IN = 0.35;       // pipe drawn 0.35 in up into the swivel socket mouth

  // [PLATE-OFFSET-BY-ANGLE 2026-08-29] The plate offset, "mounting face P down to the pipe top", as a
  // function of the slope. It is NOT the flat custom.length_offset any more: the swivel hangs from the
  // hinge, and the hinge sits pivotToFaceIn BELOW the mounting face measured perpendicular, so on a plate
  // tilted to the wall angle theta the face-to-hinge drop is pivotToFaceIn / cos(theta) (the SAME drop
  // plateHingeSceneDrop draws). Add the plumb hinge-to-socket-bottom distance, then back off the 0.35 in
  // the pipe runs UP into the socket, and that is where the pipe top sits below P:
  //   plateOffsetAt = pivotToFaceIn / cos(theta) + hingeToSocketBottom - PLATE_SWIVEL_PIPE_OVERLAP_IN
  // theta = atan2(pitch, 12), the SAME atan2 the drawing uses (plateBaseRotationDeg / plateHingeSceneDrop),
  // never a second angle formula. hingeToSocketBottom is the OWNER CAD hingeToSocketBottomIn when the spec
  // carries one (both live plates do, owner CAD 2026-08-29), else the image-row derivation
  // (socketMouthRow - hingeRow) / ORTHO_PPI (a cross-check only now). Owner ruling 2026-08-29, his CAD at
  // 63 degrees: a 12 in pipe, the plate overlapping the top 0.35 in, the threaded collar the bottom 0.35 in.
  // Constants live ONLY on the ORTHO_PLATES anchors and the two overlap literals; nothing is retyped here.
  // Pure over S.num + Math so the Node harness lifts it; null for a missing spec or a non-positive/absent
  // pitch (no slope means no angle to resolve).
  // THE ONE wall-angle helper: theta (radians from horizontal) for a rise over 12, tan theta = pitch / 12.
  // plateOffsetAt, plateBaseRotationDeg and plateHingeSceneDrop all take theta from HERE and nowhere else
  // (review 2026-08-29: three inline copies of atan2(p, 12) were one silent divergence away from a bug).
  // [ZERO-DEG 2026-08-29] A pitch of EXACTLY 0 is now a valid FLAT ceiling: theta = atan2(0, 12) = 0, so
  // plateOffsetAt returns its flat-limit value (cos 0 = 1), the plate draws unrotated, and the hinge drops
  // straight down. The gate is `p < 0` (only a missing or negative pitch returns null); an unset pitch
  // arrives here as null (S.num(null) === null) and still returns null. So 0 = flat, null = not set yet.
  function wallThetaRad(pitch) {
    var p = S.num(pitch);
    return (p === null || p < 0) ? null : Math.atan2(p, 12);
  }
  function plateOffsetAt(plateSpec, pitch) {
    if (!plateSpec || !plateSpec.base || !plateSpec.swivel) return null;
    var theta = wallThetaRad(pitch);
    if (theta === null) return null;
    var cosT = Math.cos(theta);
    var faceToHinge = S.num(plateSpec.base.pivotToFaceIn);
    if (faceToHinge === null) return null;
    var sw = plateSpec.swivel;
    var hingeToSocketBottom = S.num(sw.hingeToSocketBottomIn);   // owner CAD, preferred
    if (hingeToSocketBottom === null) {                          // fall back to the delivered image rows
      var mouth = S.num(sw.socketMouthRow), hinge = S.num(sw.hingeRow);
      if (mouth === null || hinge === null) return null;
      hingeToSocketBottom = (mouth - hinge) / ORTHO_PPI;
    }
    return faceToHinge / cosT + hingeToSocketBottom - PLATE_SWIVEL_PIPE_OVERLAP_IN;
  }
  // [PLATE-OFFSET-BY-ANGLE 2026-08-29] THE ONE helper the whole page uses for a plate's offset. A plate
  // with an ORTHO_PLATES spec gets the angle-dependent plateOffsetAt; a stylized plate with no spec (or a
  // pitch we cannot use) keeps its flat custom.length_offset, unchanged (item 4, the stylized fallback).
  // This is the ONLY place the page reads plate.length_offset for geometry now; every solve / plateGap /
  // minPipeForCorner / elevationGeometry / holeCentreLen call routes through here, so the flat metafield
  // and the angle model can never diverge. custom.length_offset on the products is NOT changed. Pure.
  function effectivePlateOffset(plate, pitch) {
    if (!plate) return null;
    var spec = ORTHO_PLATES[plate.sku];
    if (spec) {
      var po = plateOffsetAt(spec, pitch);
      if (po !== null) return po;
    }
    return S.num(plate.length_offset);
  }

  // The CDN URL for an ortho key, or null when the file is not on the store (file_url yields an
  // empty string then) so the caller falls back to the stylized shape. Same read as MEASURE_URL.
  function orthoURL(cfgKey) {
    var v = CONFIG[cfgKey];
    return (typeof v === 'string' && v.trim() !== '') ? v : null;
  }

  // [TILT] THE PER-DEGREE HEAD-IMAGE HOOK (owner 2026-08-25: head stills at 1 degree steps come later).
  // The head image today is a single zero-tilt render, and the tilt is shown by rotating the SIMULATED
  // TV rectangle, NOT the head. When the rendering agent later delivers per-degree stills, an ORTHO_HEADS
  // entry may carry tiltFrames: { 0: url, 1: url, ... } (frame keys are whole degrees). This helper
  // returns the frame for round(tiltDeg) when a matching frame exists, otherwise the base URL. With NO
  // frames configured (today) it is a pure no-op that always returns baseURL, so nothing changes until
  // the frames arrive. Pure: no DOM, no state. baseURL may be null (falls through as null).
  function orthoHeadURLForTilt(spec, baseURL, tiltDeg) {
    if (!spec || !spec.tiltFrames) return baseURL;
    var t = S.num(tiltDeg); if (t === null) t = 0;
    var key = Math.round(t);
    var frame = spec.tiltFrames[key];
    return (typeof frame === 'string' && frame.trim() !== '') ? frame : baseURL;
  }

  // [IMG WARM 2026-09-20] The head composites as a PER-DEGREE ortho PNG (about 200 KB each, 13 per head)
  // and the plate as two ortho PNGs, all drawn via SVG <image> elements the browser only fetches when the
  // SVG is inserted; nothing on the page warmed them. So a COLD first render showed a blank head until the
  // PNG arrived (owner report 2026-09-20), and a new tilt degree during a drag could flash blank. This warms
  // the image the build WILL draw as the answer renders (high priority), then idle-warms the SELECTED head's
  // neighbouring tilt frames so a tilt drag never blanks. It fetches nothing until there is a drawable build,
  // only what that build draws plus that head's own frames, respects Save-Data, caps idle warms at two in
  // flight, and never blocks the render (every warm is a detached Image with no effect on the SVG).
  var imgWarmCache = {};        // url -> Image, held so an in-flight fetch is not GC'd, and the dedupe key
  var imgWarmQueue = [];        // neighbour-frame urls waiting for idle time
  var imgWarmInFlight = 0;      // idle neighbour warms currently loading
  var IMG_WARM_MAX_INFLIGHT = 2;

  function saveDataOn() {
    try { return !!(navigator.connection && navigator.connection.saveData); } catch (e) { return false; }
  }
  // Start (or reuse) a detached fetch of url. high true asks fetchPriority 'high' (the frame the build
  // draws). Deduped by imgWarmCache: a url is never requested twice. Returns the Image (or null).
  function warmImage(url, high) {
    if (typeof url !== 'string' || url === '') return null;
    if (imgWarmCache[url]) return imgWarmCache[url];
    var img = new Image();
    imgWarmCache[url] = img;                 // hold the reference AND record it so we never refetch
    try { if ('fetchPriority' in img) img.fetchPriority = high ? 'high' : 'low'; } catch (e) {}
    try { img.decoding = 'async'; } catch (e2) {}
    img.src = url;                           // starts the fetch; the SVG <image> uses the same url, so the browser coalesces
    return img;
  }

  // [IMG WARM] PURE: the ordered frame urls to warm for a head at a tilt. frames is the CONFIG.tilt_<sku>
  // map ({ '0': url, '1': url, ... }); tilt is the current whole degree; maxDeg is the head's max downward
  // tilt (null = no cap); saveData true returns [] (skip all warming). Order: the current degree, then +1,
  // -1, +2, -2, then the rest of THAT head's frames ascending. Deduped; skips empty/missing urls and
  // out-of-range degrees. No DOM, so the Node harness lifts and asserts it.
  function headWarmOrder(frames, tilt, maxDeg, saveData) {
    if (saveData || !frames) return [];
    var t = Math.round(S.num(tilt) || 0);
    var m = S.num(maxDeg);
    var order = [], seen = {};
    function push(deg) {
      if (deg < 0) return;
      if (m !== null && deg > m) return;
      var u = frames[String(deg)];
      if (typeof u === 'string' && u !== '' && !seen[u]) { seen[u] = true; order.push(u); }
    }
    push(t); push(t + 1); push(t - 1); push(t + 2); push(t - 2);
    var keys = Object.keys(frames).map(Number).filter(function (n) { return !isNaN(n); }).sort(function (a, b) { return a - b; });
    for (var i = 0; i < keys.length; i++) { push(keys[i]); }
    return order;
  }

  // Idle-drain the neighbour queue, at most IMG_WARM_MAX_INFLIGHT loading at once, respecting Save-Data.
  function drainImgWarmQueue() {
    if (saveDataOn()) { imgWarmQueue = []; return; }
    if (!imgWarmQueue.length || imgWarmInFlight >= IMG_WARM_MAX_INFLIGHT) return;
    var idle = (typeof window !== 'undefined' && window.requestIdleCallback)
      ? function (cb) { window.requestIdleCallback(cb, { timeout: 1000 }); }
      : function (cb) { setTimeout(cb, 200); };
    idle(function () {
      while (imgWarmQueue.length && imgWarmInFlight < IMG_WARM_MAX_INFLIGHT) {
        var url = imgWarmQueue.shift();
        if (!url || imgWarmCache[url]) continue;    // already warmed
        imgWarmInFlight++;
        var img = new Image();
        imgWarmCache[url] = img;
        try { if ('fetchPriority' in img) img.fetchPriority = 'low'; } catch (e) {}
        try { img.decoding = 'async'; } catch (e2) {}
        img.onload = img.onerror = function () { imgWarmInFlight--; drainImgWarmQueue(); };
        img.src = url;
      }
    });
  }

  // [IMG WARM] The render hook: warm the images THIS drawable build draws (the current head frame, the
  // two plate orthos, the mast sprite - all fetched late through the SVG or VMSMastArt) at high priority,
  // then queue the SELECTED head's neighbouring tilt frames for idle warming. Called from renderOk, which
  // only a drawable build reaches. Wrapped so a warming hiccup can never break the render.
  function warmForBuild(head, plate, tilt) {
    try {
      var hspec = head ? ORTHO_HEADS[head.sku] : null;
      if (hspec) { warmImage(orthoHeadURLForTilt(hspec, orthoURL(hspec.cfg), tilt), true); }
      var pspec = plate ? ORTHO_PLATES[plate.sku] : null;
      if (pspec) { warmImage(orthoURL(pspec.base.cfg), true); warmImage(orthoURL(pspec.swivel.cfg), true); }
      if (CONFIG.sprite_url) { warmImage(CONFIG.sprite_url, true); }
      if (hspec && hspec.tiltFrames) {
        var maxDeg = (function () { var mm = headMaxTilt(head); return mm === null ? null : Math.floor(mm); })();
        var order = headWarmOrder(hspec.tiltFrames, tilt, maxDeg, saveDataOn());
        for (var i = 0; i < order.length; i++) {
          if (!imgWarmCache[order[i]] && imgWarmQueue.indexOf(order[i]) < 0) imgWarmQueue.push(order[i]);
        }
        drainImgWarmQueue();
      }
    } catch (e) { /* warming is best-effort; never break the render */ }
  }

  // [C1] PURE helpers (S.num + Math + the ORTHO_PPI literal only), so the Node harness lifts and
  // asserts them. They carry the load-bearing image math: the mirror, the anchor pin, the rotation.

  // Mirror a column about the image's vertical centre: col -> W - col. Every ortho image is the
  // LEFT profile (TV/room side on the image left); the page draws the room on the RIGHT, so each
  // image is drawn mirrored about its own centre.
  function orthoMirrorCol(col, W) {
    var c = S.num(col), w = S.num(W);
    if (c === null || w === null) return null;
    return w - c;
  }
  // Image pixels to inches at the ortho ppi. A row R is R / 94 inches below the image top.
  function orthoInches(px) {
    var n = S.num(px);
    return n === null ? null : n / ORTHO_PPI;
  }
  // [C1] The base-plate rotation in DEGREES to lay its authored-flat mounting face onto the wall:
  // it EQUALS the wall angle from horizontal, atan(pitch / 12). At 12 in 12 the wall is 45 degrees,
  // so the face rotates to 45 degrees; the mirrored frame then lays it flush on the wall's room side.
  // Returns null for a missing or non-positive pitch (no plate rotation without a slope).
  function plateBaseRotationDeg(pitch) {
    var theta = wallThetaRad(pitch);
    return (theta === null) ? null : theta * 180 / Math.PI;
  }
  // [C1] OWNER-REPORTED DEFECT 2026-08-24: "Your ceiling runs through the pivot point not with the
  // top plate that actually mounts to the ceiling which is above the pivot point ... You can't measure
  // straight up if the plate is tilted though, you have to measure from the pivot, up to the angled
  // surface that contacts the ceiling." The engine's plate point P sits on the ceiling SURFACE
  // (plateOffset, riseAvailable and the corner rule all measure from the surface), but the base plate's
  // physical HINGE hangs BELOW that surface. Pinning the hinge at P drew the ceiling straight through
  // the pivot instead of along the mounting face.
  //
  // [C1] The pivot-to-mounting-surface distance d (inches, perpendicular) is OWNER CAD DATA per plate,
  // stored on the anchor map (pivotToFaceIn). The owner re-measured the 11CP006 pivot on 2026-08-24
  // ("it is 31.793mm from center of pivot to the surface that mounts to the ceiling", = 1.25169 in) and
  // that figure is authoritative: the delivered 11CP006 hinge coordinate is off by ~10 px, so d is NOT
  // derived from the rows for that plate. On ANGLE-CEILING the owner CAD (1.734 in) and the delivered
  // rows (1.7342 in) agree.
  //
  // How far BELOW the ceiling surface the base plate's hinge sits, in inches, so its mounting face lands
  // ON the wall. You cannot drop straight down by d: the plate is TILTED to the wall angle (the owner's
  // own correction, that you cannot measure straight up when the plate is tilted). The hinge sits on the
  // pipe axis at (0, -h); the perpendicular distance from (0, -h) to the wall line x = (12 / pitch) * y
  // is h * cos(theta), theta the wall angle from horizontal (tan theta = pitch/12). Setting that equal to
  // d gives h = d / cos(theta). cos(theta) is worked out from the pitch here (presentation trig, never
  // the engine). Pure over S.num + Math.
  function plateHingeSceneDrop(d, pitch) {
    var dd = S.num(d), theta = wallThetaRad(pitch);
    if (dd === null || theta === null) return null;
    return dd / Math.cos(theta);
  }
  // [C1] The scene point the BASE image's MOUNTING-FACE centre pins to: the foot of the perpendicular
  // dropped from the scene hinge H = (0, -h) onto the wall line x = wallK * y. Anchoring the base by its
  // FACE (not by its delivered hinge coordinate) puts the mounting face exactly ON the ceiling even
  // though the delivered 11CP006 hinge coordinate is ~10 px off (owner re-measured the pivot directly,
  // 2026-08-24); the small hinge residual then lands invisibly at the pivot joint rather than as a
  // floating plate. The foot of the perpendicular from (0, -h) to x = wallK*y is
  // (-h*wallK/(wallK^2+1), -h/(wallK^2+1)); its perpendicular distance back to H is h*cos(theta) = d, so
  // the face sits d off the hinge exactly as the CAD says. Pure over S.num + Math.
  // [ZERO-DEG 2026-08-29] At a FLAT (0 degree) ceiling wallK is Infinity (12 / 0): the wall line is the
  // horizontal y = 0, and the foot of the perpendicular from (0, -h) onto it is the point STRAIGHT UP from
  // the hinge, (0, 0). S.num rejects a non-finite wallK, so this is handled explicitly BEFORE S.num, as
  // the limit of the general formula (x -> 0, y -> 0 as k -> Infinity).
  function plateFaceAnchor(h, wallK) {
    var hh = S.num(h);
    if (hh === null) return null;
    if (typeof wallK === 'number' && !isFinite(wallK)) return { x: 0, y: 0 };   // flat ceiling: straight up
    var k = S.num(wallK);
    if (k === null) return null;
    var denom = k * k + 1;
    return { x: -hh * k / denom, y: -hh / denom };
  }
  // [C1] Map an ortho image pixel (col, row) to a SCENE point in inches (x toward the room, y up).
  // The horizontal anchor is anchorCol (the plate pieces and the collar pad their axis to the image
  // centre, so it DEFAULTS to W / 2; the tilt-sequence HEAD frames pin their PIPE AXIS at axisCol,
  // which is NOT the image centre). The formula mirrors about anchorCol: the image-left (room) side
  // maps to +x and the anchor column maps to ax. anchorRow is the vertical anchor; (ax, ay) the scene
  // point it pins to; rotDeg rotates the piece about that anchor (the base plate; 0 otherwise). Pure.
  function orthoPixelToScene(col, row, W, anchorRow, ax, ay, rotDeg, anchorCol) {
    var w = S.num(W);
    var ac = S.num(anchorCol); if (ac === null) ac = w / 2;   // default: the image centre
    var u = (ac - S.num(col)) / ORTHO_PPI;                    // +x toward the room (mirror about anchorCol)
    var v = (S.num(anchorRow) - S.num(row)) / ORTHO_PPI;      // +y up
    var r = (S.num(rotDeg) || 0) * Math.PI / 180;
    var cs = Math.cos(r), sn = Math.sin(r);
    return { x: S.num(ax) + (u * cs - v * sn), y: S.num(ay) + (u * sn + v * cs) };
  }
  // [C1] The VESA pattern-centre row for a head image. OWNER RULING 2026-08-24: the VESA holes are
  // centered top to bottom on every ortho image, so the anchor row is the image height / 2
  // (1631 / 2 = 815.5 for the fixed heads and the sliding head alike, 1.3 px from the mesh CSV
  // number; the owner's convention wins). Derived from the stored height so a re-rendered taller
  // image stays correct. This closed the old TBD: the sliding 33CE008HEAD composites like the
  // fixed heads, so there is no null-anchor runtime state.
  // [22HE001 2026-08-25] THE EXCEPTION: a head whose spec carries an EXPLICIT vesaRow uses it (the
  // 22HE001 holes sit 110 mm up from the image bottom = row 523.913, not centred), height/2 for all
  // the rest. An out-of-range or absent vesaRow falls through to height/2. Pure.
  function orthoHeadVesaRow(h, vesaRow) {
    var vr = S.num(vesaRow);
    if (vr !== null) return vr;
    var n = S.num(h);
    return n === null ? null : n / 2;
  }
  // [TILT-STILLS] The per-degree frame metadata for a head at a whole-degree tilt: its VESA centre
  // (the pin), the TV-face endpoints and the face angle. round()s the tilt, clamps to t0 when the exact
  // frame is missing, and returns null for a head with no table (so the caller falls back to the spec's
  // static vesaRow / the stylized shape). Pure.
  function headFrameMeta(sku, tiltDeg) {
    var m = TILT_FRAMES_META[sku];
    if (!m) return null;
    var t = S.num(tiltDeg); if (t === null) t = 0;
    var key = Math.round(t);
    return m[key] || m[0] || null;
  }
  // [TILT-STILLS] The head image's horizontal pin column: the pipe-axis column (axisCol) when the spec
  // carries one (the tilt-sequence heads, whose canvas is not centred on the pipe), else the image
  // centre W / 2 (the pre-sequence convention). Pure.
  function orthoHeadAxisCol(spec) {
    if (!spec) return null;
    var ac = S.num(spec.axisCol);
    return ac !== null ? ac : (S.num(spec.w) === null ? null : S.num(spec.w) / 2);
  }
  // [C1] Whether a head composites as an image: it needs a URL. The VESA anchor row is always
  // derivable (orthoHeadVesaRow), so a missing URL is the only fallback to the stylized shape. Pure.
  function orthoHeadActive(url) {
    return (typeof url === 'string' && url.trim() !== '');
  }
  // [C1] The 2D affine matrix (a b c d e f) mapping an ortho image's OWN pixel space (0..W, 0..H)
  // straight to SVG px, folding in the inch-to-px fit, the mirror and the rotation, so the drawing
  // and orthoPixelToScene agree by construction (asserted). It composes the same u/v/rotate math as
  // orthoPixelToScene, then px_x = ox + x*s, px_y = oy - y*s. The determinant is negative (a proper
  // mirror). Presentation: it reads the fit but no geometry.
  function orthoImageMatrix(W, anchorRow, ax, ay, rotDeg, fit, anchorCol) {
    var s = fit.s, ox = fit.ox, oy = fit.oy;
    var r = (S.num(rotDeg) || 0) * Math.PI / 180;
    var cs = Math.cos(r), sn = Math.sin(r);
    var w = S.num(W);
    var ac = S.num(anchorCol); if (ac === null) ac = w / 2;   // default: the image centre (plates/collar)
    var U0 = ac / ORTHO_PPI;
    var V0 = S.num(anchorRow) / ORTHO_PPI;
    return {
      a: -s * cs / ORTHO_PPI,
      b:  s * sn / ORTHO_PPI,
      c:  s * sn / ORTHO_PPI,
      d:  s * cs / ORTHO_PPI,
      e: ox + s * (S.num(ax) + U0 * cs - V0 * sn),
      f: oy - s * (S.num(ay) + U0 * sn + V0 * cs)
    };
  }

  // [PLATE-OFFSET-BY-ANGLE 2026-08-29] The old pipeTopDrawY helper is GONE. It special-cased the
  // composited plate to draw the pipe top up to the swivel HINGE (plateHingeY), a calibrated tuck that
  // depended on the plate images. Now the engine plate offset IS angle-dependent (plateOffsetAt), so the
  // engine pipe top geo.pipeTopY = -plateOffset already lands 0.35 in inside the swivel socket mouth at
  // every angle, for both the composited and the stylized plate. The drawing simply uses geo.pipeTopY:
  // no hinge special-case, and the drawn mast span then equals the engine pipe length exactly. See the
  // pipe-draw block below and plateOffsetAt.
  // [C1] The scene y an image ROW maps to for a piece pinned PLUMB by its anchorRow at scene (0, ay):
  // y = ay + (anchorRow - row) / ppi (row grows down, y grows up). [A] OWNER'S SECOND COMPOSITE REVIEW
  // 2026-08-24 re-ordered the collar pieces: the 11FC now MATES DIRECTLY to the head body, seated by
  // applying this helper to the head image itself (row = h - 2.852*94, anchor = the head's VESA row h/2 at
  // holeCentreY), so a re-cut taller head stays right; the threaded collar no longer uses this helper (it
  // caps the pipe end at geo.pipeBotY + 0.35, on the pipe axis - see the collar-stack block). Pure.
  function orthoRowSceneY(row, anchorRow, ay) {
    var r = S.num(row), ar = S.num(anchorRow), a = S.num(ay);
    if (r === null || ar === null || a === null) return null;
    return a + (ar - r) / ORTHO_PPI;
  }

  // =====================================================================
  // [C1] THE COMPOSITED PIPE MAST (owner correction 2026-08-24: the original configurator does not draw
  // a plain pipe, it "assembles them from 1 inch orthographic sections"). The slanted page's drawn pipe
  // rectangle is replaced with the real sprite-composited mast from assets/vms-mast-art.js
  // (window.VMSMastArt), rendered to an offscreen canvas and embedded in the elevation SVG as an
  // <image>. The rectangle stays as the PERMANENT FALLBACK whenever VMSMastArt or the sprite is missing.
  // =====================================================================
  // A monotonic token bumped on every renderAnswer. VMSMastArt.render() is async and renderAnswer
  // rebuilds the whole answer synchronously, so a resolve that lands after a newer answer rendered must
  // not paint into the replaced DOM. The draw captures the token; mastRenderStale compares it back.
  var mastToken = 0;

  // [DRAG 2026-08-29] The last scene captured by elevationSVG (the object getScene returns and the
  // vmsw:rendered detail carries). null whenever the current render is not a drawable build. See elevationSVG.
  var lastScene = null;

  // [C1] The VMSMastArt pieces spec for THIS page: a SINGLE fixed pipe of length L inches. A slanted
  // build is always one straight 1-1/2 inch pipe (no telescoping, no couplers), so the assembly is one
  // entry {kind:'fixed', length:L}. See assets/vms-mast-art.js render()/totalInches for the shape. Pure.
  function mastPiecesForPipe(lengthIn) {
    var L = S.num(lengthIn);
    if (L === null || L <= 0) return null;
    return [{ kind: 'fixed', length: L }];
  }
  // [DRAG 2026-08-29] The mast pieces for the CURRENT build, from state.pipe: a FIXED pipe draws as one piece
  // (mastPiecesForPipe); a TELESCOPING pipe (the fixed-head drag can land on a length only a tele covers) draws
  // as one tele entry { kind:'tele', collapsed:length_min, extended:length_max, current:L } so VMSMastArt shows
  // the inner tube let out to the set length (see assets/vms-mast-art.js render / totalInches). spanIn is the
  // drawn pipe span (== the set length); when state.pipe is a tele it carries the collapsed/extended range.
  function mastPiecesForBuild(spanIn) {
    var L = S.num(spanIn);
    if (L === null || L <= 0) return null;
    if (state.pipe && state.pipe.kind === 'tele') {
      var lo = S.num(state.pipe.collapsed), hi = S.num(state.pipe.extended);
      if (lo !== null && hi !== null && hi > lo) {
        return [{ kind: 'tele', collapsed: lo, extended: hi, current: Math.min(Math.max(L, lo), hi) }];
      }
    }
    return [{ kind: 'fixed', length: L }];
  }
  // [C1] Stale-resolve guard for the async mast render: true when the token captured at render time is
  // no longer the current one (a newer renderAnswer has since replaced the answer DOM). Same idea as the
  // configurator keying its card art so a late resolve cannot repaint a stale card. Pure.
  function mastRenderStale(tokenAtRender, currentToken) {
    return tokenAtRender !== currentToken;
  }

  // [C1] Assemble the elevation SVG for a solved build. Presentation: it may read COPY/esc/state,
  // unlike the pure geometry helpers above. Returns '' when the geometry is not drawable (a guard;
  // renderOk only calls it on reason 'ok', where every number is real).
  function elevationSVG(res, plate, head, pipeLen) {
    var holeMid = S.holeMidFromTop(state.topHole, state.vesa);
    var elevPO = effectivePlateOffset(plate, state.pitch);
    var geo = elevationGeometry({
      pitch: state.pitch,
      plateOffset: elevPO,
      pipe: pipeLen,
      slides: res.slides,
      headOffset: head.length_offset,
      // [RESTING 2026-08-29] Draw the head at the PLACED drop FOR THIS PIPE: the resting drop by default, or
      // the raised drop (headDrop.min) when the optional 11FC is on. Pipe-aware (the resting drop moves with L).
      headDropMin: placedHeadDropForPipe(res, elevPO, pipeLen),
      show11fc: fcOnForPipe(res, elevPO, pipeLen),   // [RESTING] draw the 11FC collar only when the upgrade is on
      standoff: STANDOFF[head.sku],
      holeMid: holeMid,
      screen: state.screen,
      topHole: state.topHole,
      vesa: state.vesa,
      span: S.SLIDING_HEAD_SPAN,
      panelDepth: activeDepth(),
      headHalfH: ELEV_HEAD_HALF_H,
      headHalfW: ELEV_HEAD_HALF_W,
      tiltDeg: state.tilt,               // [TILT] rotates the TV group + corner; 0 unless the shopper tilted
      tiltPivot: PIVOT[head.sku],
      tiltAxisDrop: DROP[head.sku],      // [TILT] axis below the hole centre (owner CAD)
      tiltTable: (MIGRATE[head.sku] || null)   // [TILT-MIGRATE] the 33CE008HEAD migrating path
    });
    if (!geo) return '';

    // [C1] Resolve which parts composite as REAL ORTHO IMAGES this render. Each needs its CONFIG
    // URL; a part with no URL (the file is not on the store, so file_url is empty) keeps its
    // stylized shape. The plate needs BOTH its base and swivel images; either missing falls the
    // whole plate back to the stylized polygon (half a real plate beside half a drawn one is worse
    // than a clean drawn one). The plate rotation is derived from the SAME wallK the wall line uses,
    // so the base's mounting face lies parallel to the drawn wall.
    var headSpec = ORTHO_HEADS[head.sku] || null;
    // [TILT-STILLS] The head image is now the PER-DEGREE frame for the current tilt (orthoHeadURLForTilt
    // returns tilt_<SKU>[round(t)], or the zero-tilt base when a frame is missing). The frame is already
    // rendered tilted, so the head image itself is NOT rotated; it is pinned by its PIPE AXIS column
    // (axisCol -> x = 0, the socket does not move) and its per-frame VESA row (from TILT_FRAMES_META) to
    // the engine's ROTATED hole centre (holeCentreY + geo.hcDispY). vesaCol lands the VESA face at the
    // frame's own baked-in standoff; the drawn TV back is at the engine standoff, and the residual is the
    // calibrated stack, reported not re-derived.
    var headURL = headSpec ? orthoHeadURLForTilt(headSpec, orthoURL(headSpec.cfg), state.tilt) : null;
    var headActive = !!(headSpec && orthoHeadActive(headURL));
    var headFrame = headSpec ? headFrameMeta(head.sku, state.tilt) : null;
    var headVesaRow = headSpec ? (headFrame ? headFrame.vesaRow : orthoHeadVesaRow(headSpec.h, headSpec.vesaRow)) : null;
    var headAxisCol = headSpec ? orthoHeadAxisCol(headSpec) : null;
    var headAnchorY = geo.holeCentreY + geo.hcDispY;   // engine rotated hole-centre Y (== holeCentreY at tilt 0)

    var plateSpec = ORTHO_PLATES[plate.sku] || null;
    var baseURL = plateSpec ? orthoURL(plateSpec.base.cfg) : null;
    var swivelURL = plateSpec ? orthoURL(plateSpec.swivel.cfg) : null;
    var plateActive = !!(plateSpec && baseURL && swivelURL);
    // geo.wallK = 12 / pitch, so 12 / geo.wallK recovers the pitch the wall was drawn from.
    var platePitch = 12 / geo.wallK;
    var plateRotDeg = plateActive ? plateBaseRotationDeg(platePitch) : null;
    // [C1] The base plate's HINGE hangs BELOW the ceiling surface: P is ON the surface, but the pivot is
    // d (owner CAD, base.pivotToFaceIn) below the mounting face, and on a TILTED plate that is
    // h = d / cos(theta) straight down. The SWIVEL pins plumb at the scene hinge (0, -h); the BASE is
    // anchored by its MOUNTING FACE at the foot of the perpendicular from that hinge to the wall
    // (plateFaceAnchor), so the face lands ON the ceiling even though the delivered 11CP006 hinge
    // coordinate is ~10 px off (owner-reported 2026-08-24). Before this the wall cut through the pivot.
    var plateHingeY = null, plateFacePt = null;
    if (plateActive) {
      var plateDrop = plateHingeSceneDrop(plateSpec.base.pivotToFaceIn, platePitch);
      plateHingeY = (plateDrop === null) ? 0 : -plateDrop;
      plateFacePt = (plateDrop === null) ? { x: 0, y: 0 } : plateFaceAnchor(plateDrop, geo.wallK);
    }

    // [A] THE SLIDING HEAD's COLLAR PIECES, RE-ORDERED (owner's second composite review 2026-08-24,
    // verbatim: "The 11FC should be mated to the bottom of the part of the 33CE008HEAD that the pipe
    // passes through ... I think it is 2.852 inches up from the bottom of the orthographic 33CE008HEAD",
    // and, of the threaded collar, "put it over the end of the pipe .35 inches"). The two pieces are now
    // INDEPENDENT (no longer a head-seated stack):
    //   THREADED COLLAR - CAPS THE PIPE END. Its TOP edge sits THREADED_COLLAR_OVER_PIPE_IN (0.35 in)
    //     above the engine pipe end (geo.pipeBotY), on the pipe axis, so the pipe end lies 0.35 in inside
    //     it wherever the pipe ends. It no longer reads the head's collar-seat row.
    //   11FC - MATES DIRECTLY to the head body. Its TOP edge seats COLLAR_11FC_FROM_HEAD_BOTTOM_IN
    //     (2.852 in) up from the head image bottom: the head's own row (h - 2.852*94) pinned by the head
    //     anchor (headVesaRow at holeCentreY), via orthoRowSceneY, so a re-cut taller head stays right.
    // Each needs its own URL. A missing 11FC file falls back to the stylized band below; a missing
    // threaded collar file draws no cap (the pipe end just shows), no other change.
    var threadedSpec = ORTHO_THREADED_COLLAR;
    var threadedURL = orthoURL(threadedSpec.cfg);
    var threadedActive = !!(headActive && geo.slides && threadedURL);
    var threadedTopY = threadedActive ? (S.num(geo.pipeBotY) + THREADED_COLLAR_OVER_PIPE_IN) : null;

    var collarSpec = ORTHO_COLLAR;
    var collarURL = orthoURL(collarSpec.cfg);
    // [RESTING 2026-08-29] The 11FC composites ONLY when the optional upgrade is on (fcOn). At rest the head
    // body sits on the ALWAYS-DRAWN threaded collar (threadedActive above) and no 11FC is shown.
    var collarActive = !!(headActive && geo.slides && collarURL && fcOnForPipe(res, elevPO, pipeLen));
    var collar11fcRow = headActive ? (S.num(headSpec.h) - COLLAR_11FC_FROM_HEAD_BOTTOM_IN * ORTHO_PPI) : null;
    // [TILT-STILLS] The 11FC seats against the head BODY, so it pins to the head's own anchor (the
    // per-frame VESA row at the rotated hole centre, headAnchorY). The migrating dyUp exactly offsets
    // the per-frame VESA-row shift, so the fixed body (socket, 11FC) stays put across tilt.
    var collarSeatY = collarActive ? orthoRowSceneY(collar11fcRow, headVesaRow, headAnchorY) : null;

    // The scene-inch corners of every ACTIVE image, so the fit contains them and nothing clips.
    // anchorCol defaults to W / 2 (plates/collar); the head passes its pipe-axis column.
    function imgCorners(w, h, anchorRow, ax, ay, rotDeg, anchorCol) {
      return [[0, 0], [w, 0], [w, h], [0, h]].map(function (c) {
        return orthoPixelToScene(c[0], c[1], w, anchorRow, ax, ay, rotDeg, anchorCol);
      });
    }
    var extraPts = [];
    if (headActive) extraPts = extraPts.concat(imgCorners(headSpec.w, headSpec.h, headVesaRow, 0, headAnchorY, 0, headAxisCol));
    if (plateActive) {
      extraPts = extraPts.concat(imgCorners(plateSpec.base.w, plateSpec.base.h, plateSpec.base.mountFaceRow, plateFacePt.x, plateFacePt.y, plateRotDeg));
      extraPts = extraPts.concat(imgCorners(plateSpec.swivel.w, plateSpec.swivel.h, plateSpec.swivel.hingeRow, 0, plateHingeY, 0));
    }
    if (threadedActive) extraPts = extraPts.concat(imgCorners(threadedSpec.w, threadedSpec.h, threadedSpec.topRow, 0, threadedTopY, 0));
    if (collarActive) extraPts = extraPts.concat(imgCorners(collarSpec.w, collarSpec.h, collarSpec.collarTopRow, 0, collarSeatY, 0));
    // [TILT] Feed the four ROTATED TV corners into the fit so a tilted panel never clips the frame. At
    // square (geo.tiltDeg 0) the corners are un-rotated and add nothing new. The rotation is about the
    // tilt axis at the hole-centre height; S.tiltPoint returns y ABOVE the hole centre, so add it back.
    if (geo.tiltDeg) {
      var tPar = tiltParams(head.sku);
      [[geo.tvBackX, geo.tvTopY], [geo.tvFrontX, geo.tvTopY], [geo.tvBackX, geo.tvBotY], [geo.tvFrontX, geo.tvBotY]]
        .forEach(function (c) {
          var tp = S.tiltPoint({ x: c[0], y: c[1] - geo.holeCentreY, standoff: geo.tvBackX, tiltDeg: geo.tiltDeg,
            tiltPivot: tPar.tiltPivot, tiltAxisDrop: tPar.tiltAxisDrop, tiltTable: tPar.tiltTable });
          if (tp.x !== null && tp.y !== null) extraPts.push({ x: tp.x, y: geo.holeCentreY + tp.y });
        });
    }

    // The content box (parts + composited images). [B] The slab is NO LONGER fed into the bbox: it is
    // CLIPPED to the frame (below), so it can never exceed the frame and never needs to expand the fit.
    // That drops the old second bbox pass; the fit now depends on the content alone.
    var bbox = elevationBBox(geo, ELEV_PLATE_HALF, ELEV_PLATE_THICK,
      ELEV_COLLAR_HALF_W, ELEV_COLLAR_H, ELEV_PIPE_HALF_W, extraPts);
    var fit = elevationFit(bbox, ELEV_VB_W, ELEV_VB_H, ELEV_MARGIN);
    var s = fit.s;
    function PX(x) { return elevPx(fit.ox + x * s); }
    function PY(y) { return elevPx(fit.oy - y * s); }
    // [B] THE CEILING SLAB FILLS THE FRAME (owner's second composite review 2026-08-24: "run the full
    // width ... from the left side and off the top at steep angles or ... the right side"). The drawn frame
    // is the whole viewBox rectangle; invert it to scene inches through the SAME fit the parts draw with
    // (x = (px - ox)/s, y = (oy - px)/s), then clip the 6.5 in band to it. The slab lands exactly on the box
    // edges, both faces reaching the frame, with zero inset.
    var frameScene = {
      xMin: (0 - fit.ox) / s, xMax: (ELEV_VB_W - fit.ox) / s,
      yMin: (fit.oy - ELEV_VB_H) / s, yMax: (fit.oy - 0) / s
    };
    var slab = ceilingSlab(geo.wallK, frameScene);
    // An ortho image element mapped by orthoImageMatrix (mirror + rotation + fit folded in). The
    // image is placed in its OWN pixel space (0..w, 0..h); preserveAspectRatio="none" is safe here
    // because the matrix scales both axes by the same s / 94, so it is never distorted.
    function imgEl(url, w, h, anchorRow, ax, ay, rotDeg, anchorCol) {
      var m = orthoImageMatrix(w, anchorRow, ax, ay, rotDeg, fit, anchorCol);
      return '<image x="0" y="0" width="' + w + '" height="' + h + '" href="' + esc(url) +
        '" preserveAspectRatio="none" transform="matrix(' +
        [elevMx(m.a), elevMx(m.b), elevMx(m.c), elevMx(m.d), elevPx(m.e), elevPx(m.f)].join(' ') + ')"/>';
    }
    // A rect from two inch corners, mapped and normalised (x/y = top-left, always positive w/h).
    function rectIn(x1, y1, x2, y2, attrs) {
      var ax = PX(x1), bx = PX(x2), ay = PY(y1), by = PY(y2);
      var x = Math.min(ax, bx), y = Math.min(ay, by);
      return '<rect x="' + x + '" y="' + y + '" width="' + elevPx(Math.abs(bx - ax)) +
        '" height="' + elevPx(Math.abs(by - ay)) + '" ' + attrs + '/>';
    }

    var parts = [];
    // White ground, matching the page's other diagrams.
    parts.push('<rect x="0" y="0" width="' + ELEV_VB_W + '" height="' + ELEV_VB_H + '" fill="#ffffff"/>');

    // THE CEILING: a SHADED region above a full-width line (owner 2026-08-29: shade above the line, and keep
    // it shaded while the line moves). The wall line runs through P (top of the ceiling plate) to both frame
    // edges (slab.near = clipWallToBox, zero inset: full-width horizontal at 0 degrees, near-vertical clipped
    // top and bottom at 89). The shaded region is the ceiling-side half-plane clipped to the frame (slab.poly,
    // #eef2f6), drawn UNDER the line (fill only, no stroke - the line is the single edge) and under the plate,
    // pipe, head and TV, which all draw AFTER. previewSlope recomputes BOTH the polygon and the line every
    // frame the same way, so the shading tracks the moving line and the static render and the preview agree.
    if (slab && slab.poly && slab.poly.length >= 3) {
      var slabPoly = slab.poly.map(function (pp) { return PX(pp.x) + ',' + PY(pp.y); }).join(' ');
      parts.push('<polygon id="vmsw-elev-slab" points="' + slabPoly + '" fill="#eef2f6" stroke="none"/>');
    }
    if (slab && slab.near) {
      parts.push('<line id="vmsw-elev-wallline" x1="' + PX(slab.near[0].x) + '" y1="' + PY(slab.near[0].y) + '" x2="' +
        PX(slab.near[1].x) + '" y2="' + PY(slab.near[1].y) + '" stroke="#16232e" stroke-width="2.5"/>');
    }

    // THE PIPE: plumb on the axis x = 0, drawn FIRST so the plate pieces and head sit over it. Its bottom
    // is the engine pipe end (geo.pipeBotY) and its top is the engine plate end (geo.pipeTopY = -plateOffset),
    // for BOTH the composited and the stylized plate. [PLATE-OFFSET-BY-ANGLE 2026-08-29] plateOffset is now
    // angle-dependent (plateOffsetAt via effectivePlateOffset), computed so that -plateOffset lands EXACTLY
    // PLATE_SWIVEL_PIPE_OVERLAP_IN (0.35 in) above the swivel socket mouth at every pitch. So the pipe top
    // tucks 0.35 in up into the socket and the swivel image drawn over it covers the tuck, with no gap at the
    // mouth and nothing poking above the plate. OWNER DEFECT 2026-08-24 (the pipe showing above the 11CP006)
    // is cured by the engine number itself now, not by a drawing special-case; the old "calibrated tuck to
    // the hinge" (pipeTopDrawY) is deleted. OWNER RULING 2026-08-29: the pipe is drawn 0.35 in into the
    // BOTTOM of the swivel (the part that stays horizontal at any slope), never up to the hinge.
    var pipeTopDraw = geo.pipeTopY;
    // [C1] The drawn pipe SPAN in inches (top to bottom). It now equals the engine pipe length EXACTLY: a
    // 12 in pipe draws 12 in, because pipeTopDraw - pipeBotY = (-plateOffset) - (-(plateOffset + pipe)) =
    // pipe. The composited mast reproduces this span at the SCENE scale. The rectangle is the PERMANENT
    // FALLBACK and carries the data wireMast needs to swap the real mast in at exactly this position and
    // scale: pipe-axis px centre, top px, span in inches, and the scene px-per-inch (fit.s). See wireMast.
    var mastSpanIn = pipeTopDraw - geo.pipeBotY;
    parts.push(rectIn(-ELEV_PIPE_HALF_W, pipeTopDraw, ELEV_PIPE_HALF_W, geo.pipeBotY,
      'id="vmsw-elev-pipe" data-mast-cx="' + PX(0) + '" data-mast-top="' + PY(pipeTopDraw) +
      '" data-mast-span-in="' + mastSpanIn + '" data-mast-ppi="' + s +
      '" fill="#d8dee5" stroke="#16232e" stroke-width="1.5"'));

    // THE CEILING PLATE. When both plate images are present it composites as the two REAL pieces
    // that are the whole reason for the articulation: the BASE, anchored by its MOUNTING FACE onto the
    // wall (rotated to the wall angle, face-centre pinned at the foot of the perpendicular from the
    // scene hinge, so the face lands ON the ceiling despite the ~10 px hinge-coordinate error), and the
    // SWIVEL, hanging plumb (not rotated), pinned at the scene hinge (0, plateHingeY) below the surface.
    // The small hinge residual lands invisibly at the pivot joint (owner-reported 2026-08-24). Drawn
    // over the pipe so the swivel receives it. Otherwise it falls back to the stylized block: flush ON
    // the wall at P, rotated to the wall angle (corners P +/- half along the wall, then out along the
    // normal by the thickness).
    if (plateActive) {
      parts.push(imgEl(baseURL, plateSpec.base.w, plateSpec.base.h, plateSpec.base.mountFaceRow, plateFacePt.x, plateFacePt.y, plateRotDeg));
      parts.push(imgEl(swivelURL, plateSpec.swivel.w, plateSpec.swivel.h, plateSpec.swivel.hingeRow, 0, plateHingeY, 0));
    } else {
      // [ZERO-DEG 2026-08-29] wallUnitVectors handles a FLAT ceiling (wallK Infinity): the stylized plate
      // lies horizontal (u = (1,0)) with its thickness dropping straight down (n = (0,-1)).
      var wus = wallUnitVectors(geo.wallK);
      var ux = wus.ux, uy = wus.uy, nx = wus.nx, ny = wus.ny;
      var corner = function (along, out) {
        return PX(along * ux + out * nx) + ',' + PY(along * uy + out * ny);
      };
      parts.push('<polygon points="' +
        corner(-ELEV_PLATE_HALF, 0) + ' ' + corner(ELEV_PLATE_HALF, 0) + ' ' +
        corner(ELEV_PLATE_HALF, ELEV_PLATE_THICK) + ' ' + corner(-ELEV_PLATE_HALF, ELEV_PLATE_THICK) +
        '" fill="#eef2f6" stroke="#16232e" stroke-width="2"/>');
    }

    // THE MOUNTING ARM: from the head across the standoff to the TV back, at the hole centre. Wrapped in an
    // id'd group so previewHeadDrop can translate it with the head assembly during a height drag.
    parts.push('<g id="vmsw-elev-arm"><line x1="' + PX(0) + '" y1="' + PY(geo.holeCentreY) + '" x2="' + PX(geo.tvBackX) +
      '" y2="' + PY(geo.holeCentreY) + '" stroke="#16232e" stroke-width="2"/></g>');

    // THE HEAD. When its image is present it composites as the real PER-DEGREE frame, placed so its
    // per-frame VESA pattern-centre row sits at the scene ROTATED hole centre (headAnchorY) and its
    // PIPE AXIS column (headAxisCol) sits on x = 0, mirrored so the mount face points at the room. The
    // frame is already rendered tilted, so the image is NOT rotated (rotDeg 0); the simulated TV
    // rectangle and its holes rotate instead. Otherwise the stylized head body is drawn as before.
    // Wrapped in an id'd group (previewHeadDrop translates it with the head assembly during a height drag).
    if (headActive) {
      parts.push('<g id="vmsw-elev-head">' + imgEl(headURL, headSpec.w, headSpec.h, headVesaRow, 0, headAnchorY, 0, headAxisCol) + '</g>');
    } else {
      parts.push('<g id="vmsw-elev-head">' + rectIn(-geo.headHalfW, geo.headTopY, geo.headHalfW, geo.headBotY,
        'fill="#ffffff" stroke="#16232e" stroke-width="2"') + '</g>');
    }

    // [A] THE SLIDING HEAD's COLLAR PIECES (sliding head only), owner's second composite review 2026-08-24,
    // drawn AFTER the pipe and the head so each sits over what it mates to:
    //   THREADED COLLAR caps the pipe end (top edge 0.35 in above geo.pipeBotY), over the drawn pipe.
    //   11FC mates the head body (top edge 2.852 in up from the head image bottom), over the head so it
    //     stays visible against the opaque head body.
    // A missing 11FC file falls back to the single stylized solid band on the pipe (the grip span below the
    // entry), as before; a missing threaded collar file draws no cap (the pipe end just shows).
    if (threadedActive) {
      parts.push(imgEl(threadedURL, threadedSpec.w, threadedSpec.h, threadedSpec.topRow, 0, threadedTopY, 0));
    }
    // The 11FC element (image or stylized band) is wrapped in an id'd group so previewHeadDrop can show/hide
    // and translate it during a height drag. Present only when the optional 11FC is on (collarActive / collarY).
    if (collarActive) {
      parts.push('<g id="vmsw-elev-collar">' + imgEl(collarURL, collarSpec.w, collarSpec.h, collarSpec.collarTopRow, 0, collarSeatY, 0) + '</g>');
    } else if (geo.collarY !== null) {
      parts.push('<g id="vmsw-elev-collar">' + rectIn(-ELEV_COLLAR_HALF_W, geo.collarY + ELEV_COLLAR_H / 2,
        ELEV_COLLAR_HALF_W, geo.collarY - ELEV_COLLAR_H / 2, 'fill="#16232e"') + '</g>');
    }

    // THE SIMULATED TV (and its VESA holes and the corner ring) live in ONE group that ROTATES with
    // the screen tilt. [TILT] The group is transformed by tvTiltTransform about the tilt-axis screen
    // point (PX(pivotX), PY(pivotY)); at square the transform is '' so the drawing is byte-identical.
    // Everything inside is drawn at its ZERO-TILT position, so the SVG rotation carries the rectangle,
    // the holes and the ring onto the engine's rotated corner (S.tiltPoint) with no per-point math here.
    // The head image, the pipe, the plate and the wall are OUTSIDE the group: they do not tilt.
    var tvParts = [];
    tvParts.push(rectIn(geo.tvBackX, geo.tvTopY, geo.tvFrontX, geo.tvBotY,
      'fill="#f7f9fb" stroke="#16232e" stroke-width="2"'));
    [geo.hole1Y, geo.hole2Y].forEach(function (hy) {
      if (hy !== null) {
        tvParts.push('<circle cx="' + PX(geo.tvBackX) + '" cy="' + PY(hy) +
          '" r="3.5" fill="#ffffff" stroke="#16232e" stroke-width="1.5"/>');
      }
    });
    // THE CORNER RING, the point of the whole drawing: the TV top back corner. Drawn at the un-rotated
    // corner inside the group so the tilt transform lands it exactly on the rotated corner.
    tvParts.push('<circle cx="' + PX(geo.cornerX) + '" cy="' + PY(geo.cornerY) +
      '" r="7" fill="none" stroke="#1a2b88" stroke-width="2.5"/>');
    // [DRAG 2026-08-29] THE TILT HANDLE PILL, at the TV's bottom FRONT corner, INSIDE the TV group so it rides
    // to the rotated corner. The TV group rotates by tvTiltTransform (rotate by geo.tiltDeg about the hole
    // centre), so the pill is COUNTER-ROTATED by -geo.tiltDeg about its OWN centre to stay LEVEL and readable
    // (chosen over drawing it outside the group, which would need previewTilt to re-place it). During a tilt
    // PREVIEW the group re-rotates to the new angle while this counter-rotate is baked at the committed tilt,
    // so the pill tips slightly mid-drag; commit re-levels it (approximate by design). Hidden when the head
    // has no downward tilt. aria-orientation none (rotary); value in whole degrees.
    var tiltMaxWhole = (function () { var m = headMaxTilt(head); return (m === null) ? 0 : Math.floor(m); })();
    if (tiltMaxWhole > 0) {
      var tiltPillCounter = geo.tiltDeg ? ('rotate(' + elevPx(-geo.tiltDeg) + ' ' + PX(geo.tvFrontX) + ' ' + PY(geo.tvBotY) + ')') : '';
      tvParts.push(handleCircle('vmsw-elev-h-tilt', PX(geo.tvFrontX), PY(geo.tvBotY),
        COPY('diagram_handle_tilt'), 0, tiltMaxWhole, Math.round(geo.tiltDeg),
        String(Math.round(geo.tiltDeg)) + ' degrees', false, tiltPillCounter, COPY('pill_tilt')));
    }
    // [TILT] Rotate the TV group about the ORIGINAL hole centre, then translate by the hole-centre
    // displacement (scene inches -> px: dx*s in x, -dy*s in y). See tvTiltTransform.
    var tvTransform = tvTiltTransform(geo.tiltDeg, PX(geo.tvRotCx), PY(geo.tvRotCy),
      geo.hcDispX * s, -geo.hcDispY * s);
    parts.push('<g class="vmsw-elev__tv"' + (tvTransform ? ' transform="' + tvTransform + '"' : '') +
      '>' + tvParts.join('') + '</g>');

    // THE CLEARANCE DIMENSION + the corner label, drawn UPRIGHT at the ROTATED corner (they must not
    // spin with the TV group). rotCornerGap is the clearance of the rotated corner against the wall;
    // the line runs from that corner up to the wall at the corner's x. At square, rotCorner* == corner*.
    if (geo.rotCornerGap > 0.15) {
      parts.push('<line x1="' + PX(geo.rotCornerX) + '" y1="' + PY(geo.rotCornerY) + '" x2="' +
        PX(geo.rotCornerX) + '" y2="' + PY(geo.rotCornerY + geo.rotCornerGap) + '" stroke="#1a2b88" stroke-width="1.5"/>');
    }

    // [DRAG 2026-08-29] THE TWO UPRIGHT NUMERIC READOUTS, each on TWO ROWS (label + value), right-aligned at
    // the same x. Drawn OUTSIDE any group so they stay upright and static during a height drag; previewSlope /
    // previewTilt update the VALUE rows live. The value row is the whole-degree angle + DEG_SIGN (e.g. "63 deg"
    // rendered with the real degree sign). Label rows keep the ids #vmsw-elev-ro-slope / -tilt; value rows are
    // #vmsw-elev-ro-slope-val / -tilt-val. The old "Your slope" / "Corner clears" texts are gone; the corner
    // ring and clearance line are untouched.
    var roFs = 15, roLine = roFs + 3, roX = ELEV_VB_W - ELEV_MARGIN;
    function roText(id, y, s2) {
      return '<text id="' + id + '" x="' + roX + '" y="' + elevPx(y) +
        '" font-size="' + roFs + '" fill="#54626e" text-anchor="end">' + esc(s2) + '</text>';
    }
    // CEILING, TOP RIGHT, room side (below the ceiling line). Default at the top-right with a margin; if the
    // ceiling line at that x sits BELOW the label row (a shallow slope, so the readout would land in the shaded
    // ceiling), drop BOTH rows to just below the line.
    var roSlopeDeg = Math.round(S.pitchDegrees(state.pitch));
    var wallPyRight = isFinite(geo.wallK) ? (fit.oy - ((roX - fit.ox) / geo.wallK)) : fit.oy;
    var roSlopeLabelY = ELEV_MARGIN + roFs;
    if (roSlopeLabelY < wallPyRight) roSlopeLabelY = Math.min(wallPyRight + roFs + 6, ELEV_VB_H - ELEV_MARGIN - roLine);
    parts.push(roText('vmsw-elev-ro-slope', roSlopeLabelY, COPY('diagram_readout_ceiling')));
    parts.push(roText('vmsw-elev-ro-slope-val', roSlopeLabelY + roLine, String(roSlopeDeg) + DEG_SIGN));
    // TV TILT, BOTTOM RIGHT. The VALUE row sits on the bottom margin; the LABEL row is one line higher, so the
    // value row stays above the bottom margin. Whole degrees; 0 when the head has no downward tilt.
    var roTiltValY = ELEV_VB_H - ELEV_MARGIN;
    parts.push(roText('vmsw-elev-ro-tilt', roTiltValY - roLine, COPY('diagram_readout_tilt')));
    parts.push(roText('vmsw-elev-ro-tilt-val', roTiltValY, String(Math.round(geo.tiltDeg)) + DEG_SIGN));

    // [DRAG 2026-08-29] THE SCENE, in inches (y up, P at 0,0; px = ox + x*s, py = oy - y*s), captured for the
    // public getScene() and the vmsw:rendered detail. Every field is a real engine/geometry number for THIS
    // build; the drag layer reads it to place its handles and map pointer moves. See the DOM + API CONTRACT.
    var slopeDeg = S.pitchDegrees(state.pitch);
    var sceneHeadDrop = -geo.holeCentreY;                 // hole-centre drop below the plate (screen lower = larger)
    var headDropMin = null, headDropMax = null, headDropMaxAll = null, restingDrop = null;
    if (geo.slides) {
      headDropMin = res.headDrop ? S.num(res.headDrop.min) : null;   // highest the head can sit (11FC up / corner)
      restingDrop = restingDropForPipe(elevPO, pipeLen);             // resting drop on the CURRENT pipe
      var maxStock = STOCK.length ? STOCK[STOCK.length - 1] : null;
      headDropMax = (maxStock !== null) ? restingDropForPipe(elevPO, maxStock) : null;  // dRest(longest stock)
      headDropMaxAll = headDropMax;
    } else {
      var hoff = S.num(head.length_offset);
      var soFixed = STANDOFF[head.sku], tkFixed = tiltParams(head.sku);
      var minPipeF = S.minPipeForCorner({ holeMid: holeMid, headOffset: head.length_offset, plateOffset: elevPO,
        standoff: soFixed, riseOver12: state.pitch, tiltDeg: state.tilt,
        tiltPivot: tkFixed.tiltPivot, tiltAxisDrop: tkFixed.tiltAxisDrop, tiltTable: tkFixed.tiltTable });
      var maxLenF = longestAvailableLength();
      headDropMin = (hoff !== null && minPipeF !== null) ? elevPO + minPipeF + hoff : null;
      headDropMax = (hoff !== null && maxLenF !== null) ? elevPO + maxLenF + hoff : null;
      headDropMaxAll = headDropMax;
      restingDrop = sceneHeadDrop;
    }
    var scene = {
      ok: true, slides: geo.slides, pitch: state.pitch, slopeDeg: slopeDeg,
      tiltDeg: geo.tiltDeg, tiltMax: tiltMaxWhole,
      standoff: geo.tvBackX, plateOffset: elevPO, holeCentreY: geo.holeCentreY,
      headDrop: sceneHeadDrop, headDropMin: headDropMin, headDropMax: headDropMax,
      headDropMaxAll: headDropMaxAll, restingDrop: restingDrop,
      pipe: pipeLen, pipeKind: (state.pipe && state.pipe.kind) ? state.pipe.kind : 'fixed',
      pipeTopY: geo.pipeTopY, pipeBotY: geo.pipeBotY,
      // [SLANTED 3D 2026-09-20] Two additive fields for the 3D view; nothing else in getScene
      // changes and the 2D drawing and the drag/MCP layers never read them.
      // collarY = the 11FC collar BOTTOM on the pipe axis (geo.collarY = the head pipe-entry
      // minus SLIDING_HEAD_SPAN, and SLIDING_HEAD_SPAN runs down to the BOTTOM of the 11FC, see
      // vms-slope.js), non-null ONLY on a sliding build with the 11FC upgrade on. The 3D view
      // pins the 11FC GLB's anchor_bottom here. null on every other build (no collar drawn).
      collarY: geo.collarY,
      // tvPose = the hole-centre DISPLACEMENT (scene inches) the tilt applies, so the 3D TV can
      // reproduce the 2D TV exactly: the 2D rotates the TV about the ORIGINAL hole centre by
      // tiltDeg then TRANSLATES by (hcDispX, hcDispY) (tvTiltTransform above), which equals the
      // per-head S.tiltPoint for every corner. The 3D module has the un-rotated rectangle and
      // tiltDeg from the scene but not the per-head tilt axis, so it needs this displacement.
      tvPose: { hcDispX: geo.hcDispX, hcDispY: geo.hcDispY },
      // [SLANTED 3D, HEADS SPLIT 2026-09-20] The engine's resting hole-to-pipe-end figure
      // (S.HOLE_TO_PIPE_END_RESTING = 6.161 in), read from the slope engine's own exported
      // constant (never retyped). For the SLIDING head the engine rests the head with the PHYSICAL
      // hole centre this far above the seated pipe end and places the head picture by that physical
      // hole row, NOT by length_offset (see vms-slope.js [RESTING 2026-08-29]). The 3D split path
      // poses the fixed body so its anchor_top (seated pipe end) lands holeCentreY - this, i.e. the
      // physical hole centre lands on holeCentreY, matching the 2D (the store vesa_center empty is
      // only 6.0 above anchor_top, which sat the body 0.161 in too high). Additive; the 2D drawing
      // and the drag/MCP layers never read it.
      holeToPipeEndRestingIn: S.HOLE_TO_PIPE_END_RESTING,
      cornerX: geo.cornerX, cornerY: geo.cornerY,
      tvTopY: geo.tvTopY, tvBotY: geo.tvBotY, tvBackX: geo.tvBackX, tvFrontX: geo.tvFrontX,
      screen: state.screen, holeMid: holeMid, tvWidth: activeWidth(),
      fit: { s: fit.s, ox: fit.ox, oy: fit.oy }, svgId: 'vmsw-elev-svg',
      // [DRAG 2026-08-29] The scene FRAME in inches (the same rect the render clips the wall line to) and
      // the authored slope-pill centre in px, so previewSlope can recompute the FULL-WIDTH line + pill.
      frame: { xMin: frameScene.xMin, xMax: frameScene.xMax, yMin: frameScene.yMin, yMax: frameScene.yMax },
      slopeHandlePx: null
    };
    lastScene = scene;

    // [DRAG 2026-08-29] THE SLOPE HANDLE, on the drawn slope line 40 percent of the way from P (0,0) to where
    // the line leaves the frame, on the VISIBLE (down-left) side (coordinator note: the drag layer maps the
    // angle of the line through P regardless of side, but the handle must sit ON the drawn line). Uses the near
    // (mounting-face) segment endpoints; the down-left end is the one with the smaller scene y.
    if (slab && slab.near) {
      var e0 = slab.near[0], e1 = slab.near[1];
      var down = (e0.y <= e1.y) ? e0 : e1;               // down-left endpoint (visible wall)
      // [ROUND 14 2026-08-30] Nudge the (now wider, two-line) ceiling pill LEFT by SLOPE_PILL_NUDGE viewBox
      // units (owner: the "Drag Ceiling" bubble might need to move a little left) so it stays fully on-canvas
      // against the full-width ceiling line at shallow angles. The SAME nudge is applied to scene.slopeHandlePx
      // and to previewSlope's target so the pill tracks the line correctly through a drag.
      var slopeCx = PX(0.4 * down.x) - SLOPE_PILL_NUDGE, slopeCy = PY(0.4 * down.y);
      scene.slopeHandlePx = { x: slopeCx, y: slopeCy };   // authored pill centre (for previewSlope)
      parts.push(handleCircle('vmsw-elev-h-slope', slopeCx, slopeCy,
        COPY('diagram_handle_slope'), 0, 89, Math.round(slopeDeg),
        String(Math.round(slopeDeg)) + ' degrees', false, '', COPY('pill_slope')));
    }
    // [DRAG 2026-08-29] THE HEIGHT HANDLE, on the pipe axis (x = 0) at the head's hole centre, drawn ABOVE the
    // head image so it stays grabbable. aria-orientation vertical; value in inches of drop.
    parts.push(handleCircle('vmsw-elev-h-height', PX(0), PY(geo.holeCentreY),
      COPY('diagram_handle_height'),
      (headDropMin === null ? 0 : Math.round(headDropMin * 100) / 100),
      (headDropMax === null ? Math.round(sceneHeadDrop * 100) / 100 : Math.round(headDropMax * 100) / 100),
      Math.round(sceneHeadDrop * 100) / 100, nice(sceneHeadDrop) + ' inches', true, '', COPY('pill_height')));

    // [DRAG 2026-08-29] The SVG is now a role="group" whose focusable children are the three handles (was
    // role="img"): the drag layer reads data-fit-* and data-scene, and touch-action:none is scoped to the
    // handles in the CSS so a phone can still scroll past the diagram.
    return '<svg id="vmsw-elev-svg" class="vmsw-elev__svg" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' +
      ELEV_VB_W + ' ' + ELEV_VB_H + '" role="group" aria-label="' + esc(COPY('diagram_aria')) +
      '" data-fit-s="' + elevMx(fit.s) + '" data-fit-ox="' + elevPx(fit.ox) + '" data-fit-oy="' + elevPx(fit.oy) +
      '" data-scene="' + esc(JSON.stringify(scene)) +
      '" font-family="\'Work Sans\', system-ui, -apple-system, \'Segoe UI\', Roboto, sans-serif">' +
      parts.join('') + '</svg>';
  }

  // The drawing plus its owner-editable short heading, or '' when nothing is drawable.
  function elevationBlockHTML(res, plate, head, pipeLen) {
    var svg = elevationSVG(res, plate, head, pipeLen);
    if (!svg) return '';
    // [ROUND 8 2026-08-30] Owner: remove the "Your build, to scale" heading over the drawing. The <figure>
    // and the drawing (with its drags) stay; only the caption element is gone. The SVG keeps its own aria-label
    // (diagram_aria), so the figure is still labelled for assistive tech. diagram_heading is retired to
    // COPY_DEFAULTS-only (schema field removed).
    return '<figure class="vmsw-elev">' +
      '<div class="vmsw-elev__stage">' + svg + '</div>' +
      '</figure>';
  }

  // [C1] Swap the real sprite-composited mast in for the fallback pipe rectangle, asynchronously.
  // Called right after the answer HTML is in the DOM (like wireOptions / wireAddToCart). It reads the
  // data-mast-* attributes the fallback rectangle carries, renders the mast off-screen at the SCENE
  // scale, then embeds the canvas as an <image> at the rectangle's exact slot and z-order (inserted
  // before the rect, so it paints UNDER the plate and head that follow it) and removes the rectangle.
  // Guarded three ways against a stale or failed paint: the render token, the rect still being
  // connected, and a tainted-canvas throw. Any miss leaves the rectangle, the permanent fallback.
  function wireMast(svg) {
    var MastArt = window.VMSMastArt;
    var spriteUrl = CONFIG.sprite_url;
    if (!MastArt || !spriteUrl || !svg) return;         // no compositor or no sprite: keep the rectangle
    var rect = svg.querySelector('#vmsw-elev-pipe');
    if (!rect) return;
    var spanIn = S.num(rect.getAttribute('data-mast-span-in'));
    var scenePpi = S.num(rect.getAttribute('data-mast-ppi'));
    var cx = S.num(rect.getAttribute('data-mast-cx'));
    var topPx = S.num(rect.getAttribute('data-mast-top'));
    var pieces = mastPiecesForBuild(spanIn);
    if (!pieces || scenePpi === null || cx === null || topPx === null) return;

    var lengthPx = scenePpi * spanIn;                   // px along the mast at the SCENE scale
    var token = mastToken;                              // capture; a newer renderAnswer bumps mastToken
    var canvas = document.createElement('canvas');
    MastArt.render(canvas, pieces, {
      spriteUrl: spriteUrl,
      lengthPx: lengthPx,
      // [C1] maxPpi reconciliation, per assets/vms-elevation-scene.js's [WARN]: render() derives
      // ppi = min(lengthPx / totalIn, maxPpi) and caps it at 22, then sizes the canvas itself. Hand it
      // the ppi our length implies (lengthPx / the spec's own length) so the cap is a no-op and the pipe
      // draws at the SCENE scale, neither fatter nor blurrier. The span IS the spec length here (one
      // fixed pipe of that length), so this equals scenePpi exactly and there is no stretch.
      maxPpi: lengthPx / MastArt.totalInches(pieces),
      vertical: true
    }).then(function (info) {
      if (mastRenderStale(token, mastToken)) return;    // a newer answer replaced the DOM
      if (!rect.isConnected) return;                    // belt and suspenders
      var dataUrl;
      try { dataUrl = canvas.toDataURL('image/png'); } catch (e) { return; }   // tainted: keep the rectangle
      var NS = 'http://www.w3.org/2000/svg';
      var img = document.createElementNS(NS, 'image');
      img.setAttribute('x', elevPx(cx - info.girthPx / 2));
      img.setAttribute('y', elevPx(topPx));
      img.setAttribute('width', elevPx(info.girthPx));
      img.setAttribute('height', elevPx(info.lengthPx));
      img.setAttribute('preserveAspectRatio', 'none');
      img.setAttribute('href', dataUrl);
      img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', dataUrl);
      rect.parentNode.insertBefore(img, rect);          // same z slot as the rect (under the plate + head)
      rect.parentNode.removeChild(rect);
    }).catch(function (err) {
      // A missing or blocked sprite, or a render failure, must never take the drawing down: the
      // rectangle stays and we say so quietly, never to the customer.
      if (window.console) console.warn('vmsw: mast art unavailable, keeping the schematic pipe', err);
    });
  }

  // Render the finished build (reason 'ok'), plus the add-to-cart.
  // [ROUND 11 2026-08-30] The build's numbers, in ONE place, computed from the SAME state/engine outputs the
  // drawing uses and stored on lastNumbers each ok render. Feeds the collapsed numbers <details> under the
  // answer, the embedded #vmsw-build-json block, and getBuild()'s snapshot. Raw numbers here (stable keys);
  // the table formats them and omits any that are null. During the example these are the example's numbers.
  var lastNumbers = null;
  function buildNumbers(res, plate, head, pipeLen, roPO, placedDrop, holeCentre, midScreen) {
    var wr = buildWeightRating(plate, head);
    var cornerGap = cornerGapForBuild(res.slides, plate, head, pipeLen, res.slides ? placedDrop : null);
    var diag = derivedDiagIn(state.screen);
    var r1 = function (v) { return (v == null) ? null : Math.round(S.num(v) * 10) / 10; };
    var r2 = function (v) { return (v == null) ? null : Math.round(S.num(v) * 100) / 100; };
    return {
      entered: {
        screen_height_in: (state.screen == null) ? null : r1(state.screen),
        top_to_top_hole_in: (state.topHole == null) ? null : r1(state.topHole),
        hole_to_hole_in: (state.vesa == null) ? null : r1(state.vesa),
        ceiling_angle_deg: (state.pitch == null) ? null : Math.round(S.pitchDegrees(state.pitch)),
        screen_tilt_deg: state.tilt,
        tv_height_in: r1(placedDrop)
      },
      calculated: {
        derived_diagonal_in: (diag == null) ? null : Math.round(diag),
        plate_offset_in: r2(roPO),
        pipe_length_in: r1(pipeLen),
        hole_centre_below_plate_in: r1(holeCentre),
        finished_length_to_mid_screen_in: r1(midScreen),
        head_travel_window_in: (res.slides && res.headDrop) ? { min: r1(res.headDrop.min), max: r1(res.headDrop.max) } : null,
        corner_clearance_in: r2(cornerGap),
        weight_limit_lb: wr ? { limit: wr.weight, limited_by: (wr.confident ? wr.limiters : wr.unrated).join(' and '), confident: wr.confident } : null
      }
    };
  }
  // The collapsed numbers table (same <details> family as the bottom expandable). ENTERED + CALCULATED rows;
  // any row whose value is null is OMITTED (never an empty row, so Dawn's div:empty never bites). Angles use
  // the degree sign; inches are numbers followed by " in". Reuses weight_label where a copy label exists.
  function numbersDetailsHTML(n) {
    if (!n) return '';
    function row(label, val) { return (val == null || val === '') ? '' : '<div class="vmsw-numbers__row"><span class="k">' + esc(label) + '</span><span class="v">' + esc(val) + '</span></div>'; }
    function inch(v) { return (v == null) ? null : (Math.round(S.num(v) * 10) / 10) + ' in'; }
    function deg(v) { return (v == null) ? null : Math.round(S.num(v)) + DEG_SIGN; }
    var e = n.entered, c = n.calculated;
    var entered = row('Screen height', inch(e.screen_height_in)) + row('Top of TV to top hole', inch(e.top_to_top_hole_in)) +
      row('Hole to hole', inch(e.hole_to_hole_in)) + row('Ceiling angle', deg(e.ceiling_angle_deg)) +
      row('Screen tilt', deg(e.screen_tilt_deg)) + row('TV height', inch(e.tv_height_in));
    var tw = c.head_travel_window_in;
    var calc = row('Derived diagonal', (c.derived_diagonal_in == null) ? null : c.derived_diagonal_in + ' in') +
      row('Plate offset at this angle', inch(c.plate_offset_in)) + row('Pipe length', inch(c.pipe_length_in)) +
      row('Hole centre below the plate', inch(c.hole_centre_below_plate_in)) +
      row('Finished length to mid-screen', inch(c.finished_length_to_mid_screen_in)) +
      row('Head travel window', tw ? (inch(tw.min) + ' to ' + inch(tw.max)) : null) +
      row('Corner clearance', inch(c.corner_clearance_in)) +
      row(COPY('weight_label'), c.weight_limit_lb ? (c.weight_limit_lb.limit + ' lb' + (c.weight_limit_lb.limited_by ? ' (limited by ' + c.weight_limit_lb.limited_by + ')' : '')) : null);
    if (!entered && !calc) return '';
    return '<details class="vmsw-numbers vmsw-more">' +
      '<summary class="vmsw-more__summary vmsw-numbers__summary">' + esc(COPY('numbers_table_summary')) + '</summary>' +
      '<div class="vmsw-more__body">' +
      (entered ? '<p class="vmsw-numbers__group">Entered</p>' + entered : '') +
      (calc ? '<p class="vmsw-numbers__group">Calculated</p>' + calc : '') +
      '</div></details>';
  }

  function renderOk(res, plate, head, pipeLen) {
    // [DRAG 2026-08-29] The pipe component: the telescoping unit the fixed-head drag picked (chosenPipeComp),
    // else the stock fixed pipe at this length. A fixed-head drag can land on a length only a tele covers.
    var pipeComp = chosenPipeComp(pipeLen);
    if (!pipeComp) return noPipeFitsNotice(res);  // defensive; recommended length always has a pipe
    var pipeIsTele = !!(state.pipe && state.pipe.kind === 'tele');

    // [IMG WARM 2026-09-20] This is the first point we KNOW the drawable build's head, plate and tilt, so
    // start fetching the ortho image this render draws (and the selected head's neighbour frames) now,
    // before the SVG naming them is even built and inserted. Detached, non-blocking, best-effort.
    warmForBuild(head, plate, state.tilt);

    // TWO numbers, each labelled as what it is (see midScreenFromHoleCentre).
    //   holeCentre - ceiling plate down to the middle of the hole pattern, the
    //                number an installer marks out and the cart-summary figure.
    //   midScreen  - ceiling plate down to the true middle of the screen, the
    //                honest headline number this page exists to report.
    var holeMid = S.holeMidFromTop(state.topHole, state.vesa);
    // [TILT] standoff + pivot for this head, and the current tilt. All three feed the rotation of the
    // mid-screen point and (only when the pivot is non-zero) the hole centre.
    var soHead = STANDOFF[head.sku];
    var tk = tiltParams(head.sku);            // { tiltPivot, tiltAxisDrop, tiltTable } for this head
    var roPO = effectivePlateOffset(plate, state.pitch);   // [RESTING] plate offset for the pipe-aware helpers
    // [RESTING 2026-08-29] The placed head drop FOR THE CHOSEN PIPE (resting by default, raised when the 11FC
    // is on) feeds the finished length / hole line and the diagram, so they all agree with the cart.
    var placedDrop = placedHeadDropForPipe(res, roPO, pipeLen);
    var holeCentre = holeCentreLen(res, plate, head, pipeLen, soHead, state.tilt, tk.tiltPivot, tk.tiltAxisDrop, tk.tiltTable, state.pitch, placedDrop);
    var midScreen = (holeCentre === null) ? null
      : midScreenTilted(holeCentre, holeMid, state.screen, soHead, state.tilt, tk.tiltPivot, tk.tiltAxisDrop, tk.tiltTable);

    // [RESTING 2026-08-29] The 11FC companion is now OPTIONAL: it joins the build ONLY when the shopper has
    // turned it on (fcOn) - the head rests on the threaded collar by default. companionLine(fcOn(res),...)
    // returns the part only then, so the summary row, the total and the cart line all track the toggle.
    var addFC = fcOnForPipe(res, roPO, pipeLen);
    var companion = companionLine(addFC, COMPANION);
    var total = buildTotal(plate, head, pipeComp, companion);

    // [STEP1-OVERLAY-R4 2026-08-30] Each row's part number links to its product page (skuLink); the price
    // and the rest of the row stay plain text. For the pipe row only the SKU inside the parentheses links.
    // [PHOTO COLUMN 2026-09-07] Each row opens with a fixed-width photo column (.vmsw-sumrow__photo) holding
    // skuPhoto(part): the thumbnail button + its hidden preview, stacked and aligned down the left edge. The
    // thumb no longer sits inline in the value text, so the pipe row's parentheses now hold only the SKU link.
    var rows = '';
    rows += '<div class="vmsw-sumrow"><span class="vmsw-sumrow__photo">' + skuPhoto(plate) + '</span><span class="k">' + esc(COPY('sumrow_plate')) + '</span><span class="v">' +
      skuLink(plate) + ' - ' + money(plate.price) + '</span></div>';
    rows += '<div class="vmsw-sumrow"><span class="vmsw-sumrow__photo">' + skuPhoto(pipeComp) + '</span><span class="k">' + esc(COPY('sumrow_pipe')) + '</span><span class="v">' +
      esc(niceIn(pipeLen)) + ' (' + skuLink(pipeComp) + ') - ' + money(pipeComp.price) + '</span></div>';
    rows += '<div class="vmsw-sumrow"><span class="vmsw-sumrow__photo">' + skuPhoto(head) + '</span><span class="k">' + esc(COPY('sumrow_head')) + '</span><span class="v">' +
      skuLink(head) + ' - ' + money(head.price) + '</span></div>';
    if (companion) {
      // [ROUND 6 2026-08-30] The 11FC row carries a TOOLTIP explaining what the collar does (owner: the
      // one thing worth retaining from the deleted prose). Same accessible popover as the angle tips: a
      // button with aria-expanded/aria-controls and a role=dialog popover; wireTooltip re-arms it after every
      // answer render (the row is rebuilt each solve). It appears only when the 11FC is in the build, which
      // the height drag brings in and out.
      // [ROUND 17 2026-08-31] Owner: the 11FC trigger is a quiet TEXT LINK now, reading "What is this?"
      // (fc_tooltip_label), matching the angle-tips [ROUND 8] conversion. The explanatory clause that used to
      // trail the row label moved into the tooltip copy, so sumrow_collar is now just the part name (11FC).
      // The visible link text is the accessible name, so the button's aria-label is
      // dropped; the popover span keeps its aria-label from fc_tooltip_label unchanged.
      rows += '<div class="vmsw-sumrow vmsw-sumrow--fc"><span class="vmsw-sumrow__photo">' + skuPhoto(companion) + '</span><span class="k">' + esc(COPY('sumrow_collar')) +
        ' <button type="button" class="vmsw-tip-link vmsw-tip-link--fc" id="vmsw-fc-tip-btn" aria-expanded="false"' +
        ' aria-controls="vmsw-fc-tip-pop">' + esc(COPY('fc_tooltip_label')) + '</button>' +
        '<span class="vmsw-tips-pop vmsw-fc-tip-pop" id="vmsw-fc-tip-pop" role="dialog" aria-label="' +
        esc(COPY('fc_tooltip_label')) + '" hidden>' + esc(COPY('fc_tooltip')) + '</span>' +
        '</span><span class="v">' + skuLink(companion) + ' - ' + money(companion.price) + '</span></div>';
    }
    rows += weightRowHTML(plate, head);

    // [C1] The orthographic build view, to scale, for the CHOSEN pipe. It sits after the headline
    // numbers and before the parts list, and re-renders with every solve (option picks included),
    // so it always shows the current build. Empty string only if the geometry is somehow not
    // drawable (guarded; unreachable on reason 'ok').
    var elevBlock = elevationBlockHTML(res, plate, head, pipeLen);

    // [ROUND 6 2026-08-30] Owner: eliminate the whole prose stack between the heading and the parts list
    // (the recommended-pipe / finished-length / tilt / mounting-holes / mount-cover / resting-note lines,
    // the 11FC checkbox row and the shortest-pipe box). The heading, the to-scale build image, the parts
    // list (with the part-number links), the weight row, the total and the add-to-cart STAY. The heading
    // itself carries the example state now (example_heading while exampleActive, else answer_title); the
    // separate example-label sentence is gone. The engine math (tilt search, resting model, shortest-pipe,
    // the 11FC via the height drag) is untouched; only these prose renders are removed.
    // [ROUND 11 2026-08-30] Compute the build numbers once (stored on lastNumbers) for the collapsed table
    // under the answer, the embedded #vmsw-build-json block (populated post-render), and getBuild()'s snapshot.
    lastNumbers = buildNumbers(res, plate, head, pipeLen, roPO, placedDrop, holeCentre, midScreen);
    lastSolve = { total: total, pipe: pipeLen };   // [F5] the one-sentence status (announceSolve)
    return '<h3 class="vmsw-answer__h">' + esc(exampleActive ? COPY('example_heading') : COPY('answer_title')) + '</h3>' +
      elevBlock +
      '<div class="vmsw-summary">' + rows + '</div>' +
      '<p class="vmsw-total">' + esc(fill(COPY('answer_price_total'), { price: money(total) })) + '</p>' +
      '<div class="vmscfg-cartwrap">' +
      // [EXAMPLE 2026-08-30] The cart is NEVER armed by example data: while the example shows, the button is
      // disabled (a disabled button fires no click, so wireAddToCart's handler cannot run). It arms only once
      // the shopper's own measurements produce a real build (exampleActive false).
      '<button type="button" class="button button--primary vmscfg-addcart vmsw-addcart' +
      (exampleActive ? ' vmsw-addcart--example' : '') + '" id="vmsw-addcart"' +
      (exampleActive ? ' disabled aria-disabled="true"' : '') + ' aria-label="' +
      esc(fill(COPY('answer_price_total'), { price: money(total) })) + ', ' + esc(COPY('cart_add')) + '">' +
      esc(COPY('cart_add')) + '</button>' +
      '<div class="vmscfg-cart-status" id="vmsw-cart-status" role="status" aria-live="polite"></div>' +
      '</div>' +
      // [ROUND 11 2026-08-30] The collapsed numbers table (all users) and the embedded build JSON (populated
      // by refreshBuildJson after this HTML is in the DOM, so agents without WebMCP can read the build).
      numbersDetailsHTML(lastNumbers) +
      '<script type="application/json" id="vmsw-build-json"></script>';
  }

  // [AUTO-TILT] One solve for a given plate + head + holeMid at a given tilt, with every other input
  // read from state exactly as renderAnswer's own solve does. Factored so the auto-tilt search can probe
  // other tilts without duplicating the option object (a drift risk). Pure w.r.t. state (reads only).
  function solveBuild(plate, head, holeMid, tiltDeg) {
    return S.solve({
      holeMid: holeMid,
      screenHeight: state.screen,
      headOffset: head.length_offset,
      plateOffset: effectivePlateOffset(plate, state.pitch),
      standoff: STANDOFF[head.sku],       // blank stays blank -> standoff_unknown
      riseOver12: state.pitch,
      marginBottom: MARGIN_BOTTOM,        // owner-editable, sliding head only; blank -> module default in solve()
      tiltDeg: tiltDeg,                   // [TILT] the tilt to test (state.tilt for the live build)
      tiltPivot: PIVOT[head.sku],         // blank -> axis at the mounting surface (engine tiltPivotOrZero)
      tiltAxisDrop: DROP[head.sku],       // blank -> axis at the hole-centre height
      tiltTable: (MIGRATE[head.sku] || null),  // [TILT-MIGRATE] only the 33CE008HEAD has one
      stockLengths: STOCK
    });
  }

  // [AUTO-TILT] The first whole-degree tilt from `fromTilt` up to `maxWhole` (inclusive) at which
  // solveAtTilt(t).reason is 'ok', or null if none fits. PURE: solveAtTilt is passed in, so the search
  // holds no state and the Node harness lifts and tests it against a mock solver and the real engine.
  // The auto regime starts the scan at the current tilt + 1 (search upward for relief); the touched
  // regime starts at 0 (the smallest tilt that would work, to advise). Ascending, so it returns the
  // SMALLEST fitting tilt in range, which is what "tilt down the least that still fits" means.
  function firstFittingTilt(solveAtTilt, fromTilt, maxWhole) {
    var from = fromTilt < 0 ? 0 : fromTilt;
    for (var t = from; t <= maxWhole; t++) {
      if (solveAtTilt(t).reason === 'ok') return t;
    }
    return null;
  }

  // [AUTO-TILT] Move the persistent tilt slider and its live readout to a whole-degree value, in place,
  // WITHOUT setting state.tiltTouched (this is our own auto move, not a shopper drag). The slider is a
  // persistent node outside #vmsw-answer, so it survives the answer re-render; refreshTiltControl still
  // owns its max/clamp and runs at the top of renderAnswer before this.
  function setTiltSlider(deg) {
    var slider = el('vmsw-tilt');
    if (slider && slider.value !== String(deg)) slider.value = String(deg);
    var ro = el('vmsw-tilt-readout');
    if (ro) ro.textContent = fill(COPY('tilt_readout'), { n: String(deg) });
  }

  // [DESIGN REVIEW F5 2026-09-22] One sentence per solve for #vmsw-status. renderAnswer clears lastSolve and
  // re-arms the timer on every render; the build path fills lastSolve (total and pipe length); the sentence is
  // written ANNOUNCE_MS after the LAST render of a burst (typing, a slider drag), so a screen reader hears one
  // short line, not every intermediate solve. Nothing is announced while the example shows (the shopper has not
  // asked for anything yet), for a render with no build (its notice is role=alert on its own), or when the
  // sentence repeats the last one.
  var ANNOUNCE_MS = 500;
  var lastSolve = null, announceTimer = null, lastAnnounced = '';
  function solveSentence(s) {
    if (!s || s.total == null || !isFinite(Number(s.total))) return '';
    var pipe = nice(s.pipe);
    return 'Total ' + money(s.total) + (pipe === null ? '' : ', pipe ' + pipe + ' in') + '.';
  }
  function announceSolve() {
    if (announceTimer) clearTimeout(announceTimer);
    announceTimer = setTimeout(function () {
      announceTimer = null;
      var out = el('vmsw-status');
      if (!out || exampleActive) return;
      var line = solveSentence(lastSolve);
      if (!line || line === lastAnnounced) return;
      lastAnnounced = line;
      out.textContent = line;
    }, ANNOUNCE_MS);
  }

  function renderAnswer() {
    var box = el('vmsw-answer');
    if (!box) return;
    lastSolve = null;      // [F5] filled by the build path below; read when the debounced announcement fires
    announceSolve();
    lastRes = null;
    // [C1] Invalidate any in-flight mast render: this answer is about to be rebuilt, so a resolve for
    // the previous answer must not paint into the DOM we are replacing (see wireMast / mastRenderStale).
    mastToken++;
    // [VESA GATE] Hide any head the shopper's VESA height rules out (and deselect it if it was chosen)
    // BEFORE ready() is read, so a gated-away selection falls straight to the "fill in the steps" prompt.
    refreshHeadGate();
    // [PLATE ANGLE GATE 2026-08-30] Disable any plate the committed slope pivots past (and deselect it if
    // it was chosen) BEFORE ready() is read, so a gated-away plate falls straight to the needs-a-plate state.
    refreshPlateGate();
    // [TILT] Reveal/hide and re-clamp the screen-tilt control for the current head. Runs BEFORE the
    // ready() gate so a hidden control forces state.tilt to 0 even while the build is incomplete.
    refreshTiltControl();
    // [ROUND 5 2026-08-30] Led-through highlight, after the gates may have deselected a plate/head. Runs on
    // every path below (the ready() early-return included), so the active step tracks the live state.
    updateStepHighlight();
    // [ROUND 15 2026-08-30] Drive the mobile step accordion from the same funnel: mark done headers and, on the
    // rising edge of a step's shopper-completion, collapse it and open the next incomplete step.
    updateSteps();
    // [URL STATE 2026-09-23] Arm the debounced address-bar write from the SAME funnel every shopper action ends
    // in (typing, the angle drag / box, a card pick, the tilt and height drags, the 11FC box, the MCP setters),
    // placed after the gates above so a plate or head they just deselected is never written. The URL string is
    // built when the timer FIRES, not here, so the held-drop re-clamp and the auto-tilt below are included.
    // [RULE] Every path must reach this line; the not-ready return right below is why it sits above it.
    tvNoteRender();
    syncURL();
    if (!ready()) {
      // [A1] A typed value that is out of range (a 0 or a negative screen height, VESA or
      // pitch) leaves state null, the same as blank, so tell the shopper WHY nothing computed
      // rather than falling back to the neutral prompt. Only when nothing is out of range do we
      // show the plain "fill in the steps" placeholder.
      // [SUBSTEP LETTERS 2026-09-22] When the reason is a MISSING MEASUREMENT (the state the customer hit
      // after typing into the first box, which nulls the other two), name the three lettered sub-steps so
      // the shopper knows exactly what brings the picture back. Any other not-ready reason keeps the
      // generic prompt. The out-of-range notice above is unchanged.
      box.innerHTML = anyInvalid()
        ? noticeHTML('notice_out_of_range')
        : '<p class="vmsw-answer__prompt">' +
          esc(COPY(measurementsMissing() ? 'answer_prompt_measure' : 'answer_prompt')) + '</p>';
      return;
    }
    var plate = bySku(state.plateSku);
    var head = bySku(state.headSku);
    var holeMid = S.holeMidFromTop(state.topHole, state.vesa);

    var res = solveBuild(plate, head, holeMid, state.tilt);

    // [DRAG 2026-08-29] A tilt change KEEPS a held head drop but re-clamps it: a steeper tilt can raise the
    // corner floor above the held drop, so re-resolve the held drop through the pure selector for the CURRENT
    // solve (which clamps to [Dmin, dRest(longest)]) and update state.pipe / add11fc / headDrop in place (no
    // recursion into renderAnswer; we are mid-render). Only for the sliding head with a held drop and an ok
    // solve; the fixed head's held pipe does not depend on tilt beyond the corner minimum, re-checked below.
    if (state.headDrop != null && res && res.reason === 'ok' && res.slides && res.headDrop) {
      var rcCorner = cornerFloorFor(head, holeMid);
      var rcDmin = S.num(res.headDrop.min);
      if (rcCorner !== null && rcDmin !== null) {
        var rcPick = slidingPipeForDrop(state.headDrop, {
          plateOffset: effectivePlateOffset(plate, state.pitch), screen: state.screen, holeMid: holeMid,
          cornerFloor: rcCorner, Dmin: rcDmin, htpeResting: S.HOLE_TO_PIPE_END_RESTING,
          collar11fc: S.COLLAR_11FC, collarBelow: S.THREADED_COLLAR_BELOW_PIPE_IN, stock: STOCK
        });
        if (rcPick) {
          var rcComp = pipeAtLength(rcPick.length);
          state.pipe = { kind: 'fixed', sku: rcComp ? rcComp.sku : null, length: rcPick.length, comp: rcComp };
          state.add11fc = rcPick.add11fc;
          state.headDrop = rcPick.headDrop;
        }
      }
    }
    // [CORNER GUARD 2026-08-29] A held FIXED pipe after a tilt change: a shallower tilt raises the corner
    // floor, so a pipe that cleared when tilted can interfere squared up. Re-check the held pipe's DRAWN gap
    // at the current tilt and, if it no longer clears, snap up to the shortest stock pipe that does (the
    // corner-limited minimum). Keeps the held-drop render honest without recursing into renderAnswer.
    if (state.headDrop != null && res && res.reason === 'ok' && !res.slides && state.pipe) {
      var fGapHeld = cornerGapForBuild(false, plate, head, state.pipe.length, null);
      if (fGapHeld !== null && fGapHeld < -1e-6) {
        var floorHeld = shortestFixedPipeClearingCorner(plate, head);
        if (floorHeld) {
          var hoHeld = S.num(head.length_offset), poHeld = effectivePlateOffset(plate, state.pitch);
          state.pipe = { kind: 'fixed', sku: floorHeld.comp ? floorHeld.comp.sku : null, length: floorHeld.length, comp: floorHeld.comp };
          if (hoHeld !== null && poHeld !== null) state.headDrop = poHeld + floorHeld.length + hoHeld;
        }
      }
    }

    // [AUTO-TILT] When the build does not fit at the current tilt AND the head can tilt down, look for
    // a downward tilt that DOES fit (owner 2026-08-28). Two regimes, chosen by state.tiltTouched:
    //   AUTO (the shopper has NOT dragged the tilt slider this build): search UP from the current tilt
    //     for the first tilt that solves ok, MOVE the slider to it, render THAT build, and show the
    //     answer_tilt_required warning above the answer. This is what lets a steep slope that needs the
    //     screen tilted still return a real build instead of a bare "no pipe fits".
    //   TOUCHED (the shopper controls the slider): do NOT move it. Find the smallest tilt that would
    //     work and remember it (autoAdviseTilt) so the failure notice can advise it; if none works the
    //     plain no-fit notice shows. The search never runs for a head with no downward tilt (max 0).
    var autoTiltApplied = null;   // the tilt we auto-moved to (drives the warning), or null
    var autoAdviseTilt = null;    // smallest working tilt to advise when the shopper holds the slider
    if (res.reason === 'no_pipe_fits' || res.reason === 'screen_too_short') {
      var maxWholeTilt = headMaxTilt(head);
      maxWholeTilt = (maxWholeTilt === null) ? 0 : Math.floor(maxWholeTilt);
      if (maxWholeTilt > 0) {
        var solveAtTilt = function (t) { return solveBuild(plate, head, holeMid, t); };
        if (!state.tiltTouched) {
          // Auto regime: the first tilt at or above the current one + 1 that fits. Move the slider to it.
          autoTiltApplied = firstFittingTilt(solveAtTilt, state.tilt + 1, maxWholeTilt);
          if (autoTiltApplied !== null) {
            res = solveAtTilt(autoTiltApplied);
            state.tilt = autoTiltApplied;
            setTiltSlider(autoTiltApplied);   // move the persistent slider + readout to match
          }
        } else {
          // Touched regime: the shopper holds the slider; find the smallest tilt that would fit to advise.
          autoAdviseTilt = firstFittingTilt(solveAtTilt, 0, maxWholeTilt);
        }
      }
    }

    lastRes = res;
    var html, chosenPipe = null;
    if (res.reason === 'ok') {
      // [DRAG 2026-08-29] The build is priced and carted against the drag's chosen pipe when set, otherwise the
      // engine recommendation (the default view). See chosenPipeLen (replaces the old effectivePipe).
      chosenPipe = chosenPipeLen(res);
      html = renderOk(res, plate, head, chosenPipe);
      // [AUTO-TILT] When we auto-moved the slider to make this build fit, warn ABOVE the answer that the
      // pipe length below assumes that tilt (answer_tilt_required). [ROUND 6] The in-answer disclosure line
      // is gone with the rest of the prose stack.
      if (autoTiltApplied !== null) {
        html = noticeHTML('answer_tilt_required', { n: String(autoTiltApplied) }) + html;
      }
    } else if (res.reason === 'no_pipe_fits') {
      // [AUTO-TILT] Touched regime (the shopper holds the slider): if some tilt would make it fit, advise
      // that tilt with the same warning copy; otherwise the plain no-fit notice.
      html = (autoAdviseTilt !== null)
        ? noticeHTML('answer_tilt_required', { n: String(autoAdviseTilt) })
        : noPipeFitsNotice(res);
    } else if (res.reason === 'standoff_unknown') {
      // The honest incomplete answer: the vertical budget is real, so quote it and
      // invite a call. Never invent a standoff to force a full answer.
      html = renderStandoffUnknown(res, plate, head);
    } else if (res.reason === 'geometry_unknown') {
      // The standoff IS known but the TV geometry (holeMid) is not, so the engine cannot
      // size the pipe. This is the shopper's blank measurements, not our missing datum, so
      // ask for the numbers plainly. No cart: there is no build to sell yet.
      // [NOTE] ready() gates renderAnswer() on state.topHole AND state.vesa both being set,
      // and holeMid derives from those two, so holeMid is never null once we reach solve()
      // and this branch is UNREACHABLE from the current page. It is kept because vms-slope.js
      // is reusable and the token is real: the controller must not depend on a gate elsewhere
      // for its own correctness. If the ready() gate is ever relaxed, this branch already
      // handles the state honestly instead of falling through to the blank prompt.
      html = noticeHTML('notice_geometry_unknown');
    } else if (res.reason === 'screen_too_short') {
      // [CAP-RESTORED 2026-08-29] screen_too_short comes ONLY from a FIXED head with unusable geometry (a
      // measurement missing or out of range). A sliding head never reaches this branch: a screen too short
      // for the restored bottom cap gives an EMPTY window, which the engine reports as no_pipe_fits (handled
      // by the branch above, with the auto-tilt search and the floor/cap notice). The old sliding-head
      // minimum-screen-height sub-branch stays retired; its copy key is left defined in the section but is
      // not emitted.
      if (autoAdviseTilt !== null) {
        // [AUTO-TILT] Touched regime: the shopper holds the slider at a tilt that fails, but some tilt in
        // range WOULD fit, so advise that tilt with the same warning copy the no_pipe_fits branch uses.
        html = noticeHTML('answer_tilt_required', { n: String(autoAdviseTilt) });
      } else {
        // Fixed head, unusable geometry: ask the shopper to check the measurements.
        html = noticeHTML('notice_measure_check');
      }
    } else if (res.reason === 'collar_shows') {
      // [RESTING 2026-08-29] SLIDING head, TV too short: the threaded collar at the pipe end would show below
      // the bottom edge of the screen at rest (screenHeight - holeMid < 6.811 in), and NO pipe length or tilt
      // fixes it (the resting bottom rule is pipe-length independent and tilt-independent). So this failure is
      // deliberately OUTSIDE the auto-tilt search above; it names the TV, not the pipe.
      html = noticeHTML('notice_collar_shows');
    } else {
      html = '<p class="vmsw-answer__prompt">' + esc(COPY('answer_prompt')) + '</p>';
    }

    box.innerHTML = html;
    // Wire the cart and the 11FC checkbox only when a real build was rendered. The cart summary carries the
    // HOLE-CENTRE length (the app contract; see configSummary), computed for the CHOSEN pipe so the finished
    // length in the cart matches the drag. [DRAG 2026-08-29] wireOptions is gone (the option list is removed).
    if (res.reason === 'ok') {
      var pipeComp = chosenPipeComp(chosenPipe);
      // [RESTING 2026-08-29] The 11FC companion is OPTIONAL now: it joins the cart, total and aria-label
      // ONLY when the shopper turned it on for the CHOSEN pipe (fcOnForPipe). A resting build carts plate +
      // head + pipe alone.
      var wPO = effectivePlateOffset(plate, state.pitch);
      var addFC2 = fcOnForPipe(res, wPO, chosenPipe);
      var companion = companionLine(addFC2, COMPANION);
      var total = buildTotal(plate, head, pipeComp, companion);
      // [TILT] Pass the standoff + pivot so the cart's hole-centre length matches renderOk (the pivot
      // rotation is a no-op at the shipped pivot 0; only a real CAD pivot would shift it).
      var ctk = tiltParams(head.sku);
      var holeCentre = holeCentreLen(res, plate, head, chosenPipe, STANDOFF[head.sku], state.tilt, ctk.tiltPivot, ctk.tiltAxisDrop, ctk.tiltTable, state.pitch, placedHeadDropForPipe(res, wPO, chosenPipe));
      // [BUILD PROPERTY] The mid-screen finished length the visible numbers table shows
      // (lastNumbers, just computed by renderOk for this build); passed so the "Build"
      // property reports the SAME figure the shopper sees, never a recomputed one.
      var buildMidScreen = (lastNumbers && lastNumbers.calculated) ? lastNumbers.calculated.finished_length_to_mid_screen_in : null;
      wireAddToCart(plate, head, pipeComp, holeCentre, total, addFC2, buildMidScreen);
      wire11fc();
      // [ROUND 6 2026-08-30] Re-arm the 11FC summary-row tooltip; it exists only when the collar is in the
      // build (companion present), so wireTooltip no-ops when the row is absent.
      wireTooltip(el('vmsw-fc-tip-btn'), el('vmsw-fc-tip-pop'));
      // [ROUND 11 2026-08-30] Populate the embedded build JSON now the answer HTML (with the #vmsw-build-json
      // node and the parts rows getBuild reads) is in the DOM.
      refreshBuildJson();
      // [C1] Swap the sprite-composited mast in for the fallback pipe rectangle (async; keeps the
      // rectangle if VMSMastArt or the sprite is unavailable). After the answer HTML is in the DOM.
      wireMast(box.querySelector('.vmsw-elev__svg'));
      // [DRAG 2026-08-29] THE RENDER HOOK: the page owns the build SVG, so after each ok render it publishes
      // the freshly captured scene (getScene) on ROOT so the standalone drag layer (assets/vms-slanted-wall-
      // drag.js) can (re-)attach its three handles by id and read the scene. Fired here, right where wireMast
      // ran, so the SVG and its handle elements are in the DOM.
      ROOT.dispatchEvent(new CustomEvent('vmsw:rendered', { detail: getScene() }));
    } else {
      // No drawable build this render (a notice is shown): clear the captured scene so getScene reports it.
      lastScene = null;
    }
  }

  // The vertical-only answer shown while the standoff is unknown. It states the
  // vertical budget (the longest pipe that hides the plate) as an honest,
  // incomplete figure, clearly labelled, and invites a call. No cart button:
  // we do not sell a build we cannot promise clears the slope.
  // [F] The pipe to recommend while the standoff is unknown. Without the standoff we do not know the
  // corner minimum, but minPipe is ALWAYS <= zeroMarginPipe (window.max; riseAvailable is never
  // negative), so ANY stock pipe at or above window.max clears the corner for EVERY possible standoff.
  // Return the SHORTEST such pipe. The old code took the LONGEST pipe BELOW window.max, the one LEAST
  // guaranteed to clear once the real standoff is known, so it could foul. Fixed head only: a sliding
  // head's cap is null while the standoff is unknown, so it never gets a pipe line here. Pure, so the
  // harness asserts the shortest-at-or-above pick.
  function standoffUnknownPipe(res, stock) {
    var floorPipe = (!res || res.slides || !res.window) ? null : res.window.max;
    if (floorPipe == null || floorPipe < 0) return null;
    var fit = S.fitStock({ min: floorPipe, max: null }, stock);
    return fit.shortest;
  }
  function renderStandoffUnknown(res, plate, head) {
    var out = '<h3 class="vmsw-answer__h">' + esc(COPY('answer_title')) + '</h3>';
    out += '<div class="capture">' + fillHTML(COPY('notice_standoff_unknown')) + '</div>';
    // [F] The safe pipe while the standoff is unknown (standoffUnknownPipe): the SHORTEST stock pipe at
    // or above zeroMarginPipe, which clears the corner for EVERY possible standoff. See the helper.
    var uPipe = standoffUnknownPipe(res, STOCK);
    if (uPipe != null) {
      out += '<p class="vmsw-finished vmsw-incomplete">' +
        esc(fill(COPY('answer_recommend'), { pipe: niceIn(uPipe) })) + '</p>';
    }
    out += weightRowHTMLWrapped(plate, head);
    return out;
  }
  function weightRowHTMLWrapped(plate, head) {
    var row = weightRowHTML(plate, head);
    return row ? '<div class="vmsw-summary">' + row + '</div>' : '';
  }

  // =====================================================================
  // [DRAG 2026-08-29] THE PUBLIC PAGE API (window.VMSSlantedPage) and the MOVE-phase previews. The page OWNS
  // the build SVG; the standalone drag layer (assets/vms-slanted-wall-drag.js) reads getScene() and calls the
  // setters (commit) and previews (move). See the DOM + API CONTRACT in SPEC-build-view-drags.md.
  // =====================================================================
  // getScene(): the scene captured at the last drawable render (elevationSVG), or { ok:false } when the current
  // render is a notice (no drawable build). The drag layer places its handles and maps pointer moves from it.
  function getScene() { return lastScene ? lastScene : { ok: false }; }

  // COMMIT setters (each ends in one renderAnswer):
  //   setSlopeDeg(deg) -> commitAngle (clamps 0..89, moves the step 1 handle, setPitch, re-renders).
  //   setTilt(deg)     -> commitTilt (clamps 0..tiltMax whole, marks touched, moves the slider, re-renders).
  //   setHeadDrop(in)  -> the continuous-height single writer above (returns the applied drop).
  function setSlopeDeg(deg) { userSetAngle(deg); }
  function setTilt(deg) { commitTilt(deg); }

  // MOVE-phase previews: cheap LOCAL SVG mutations, NO re-render, approximate by design (no head-frame swap,
  // no re-solve); the matching commit setter makes it exact. Each is a no-op when the SVG is absent.
  function elevSvg() { return el('vmsw-elev-svg'); }
  // previewSlope(deg): rotate the drawn slope line, the shaded half-plane and the slope handle about P by the
  // change from the committed slope angle (SVG rotate is clockwise; a steeper slope rotates the visible
  // down-left line toward vertical). Approximate: the exact wall re-clips on commit.
  function previewSlope(deg) {
    var svg = elevSvg(); if (!svg || !lastScene || !lastScene.frame) return;
    var d = clampAngle(deg, 0, 89);
    // [DRAG 2026-08-29][FULL-WIDTH, owner feedback] Recompute the ceiling line's endpoints each move exactly
    // the way the static render does, so the line STAYS FULL WIDTH (it changes LENGTH with the angle) instead
    // of rotating a fixed segment. wallK from the previewed angle, clipped to the SAME frame the render used
    // (lastScene.frame): ceilingSlab(...).near is the wall through P clipped to both frame edges (horizontal
    // full-width at 0 deg, near-vertical clipped top and bottom at 89). Set x1/y1/x2/y2 absolutely; NO rotate.
    var f = lastScene.fit;
    function px(x) { return f.ox + x * f.s; }
    function py(y) { return f.oy - y * f.s; }
    var pitch = S.pitchFromDegrees(d);
    var wallK = (pitch === 0) ? Infinity : 12 / pitch;   // flat ceiling -> Infinity, handled by ceilingSlab
    var seg = ceilingSlab(wallK, lastScene.frame);
    var near = seg && seg.near;
    // [SHADE 2026-08-29] Recompute the shaded ceiling polygon the SAME way as the line, so the shading tracks
    // the moving line every frame (owner: keep it shaded while the line moves). Set the points attribute; no rotate.
    if (seg && seg.poly && seg.poly.length >= 3) {
      var slab = svg.querySelector('#vmsw-elev-slab');
      if (slab) {
        slab.removeAttribute('transform');
        slab.setAttribute('points', seg.poly.map(function (pp) { return elevPx(px(pp.x)) + ',' + elevPx(py(pp.y)); }).join(' '));
      }
    }
    if (near) {
      var line = svg.querySelector('#vmsw-elev-wallline');
      if (line) {
        line.removeAttribute('transform');
        line.setAttribute('x1', elevPx(px(near[0].x))); line.setAttribute('y1', elevPx(py(near[0].y)));
        line.setAttribute('x2', elevPx(px(near[1].x))); line.setAttribute('y2', elevPx(py(near[1].y)));
      }
      // The slope pill rides to the same 40 percent point along the down-left segment the render uses, UPRIGHT
      // (a pill needs no rotation): translate it from its authored centre to the new centre.
      var down = (near[0].y <= near[1].y) ? near[0] : near[1];
      var pill = svg.querySelector('#vmsw-elev-h-slope');
      if (pill && lastScene.slopeHandlePx) {
        // [ROUND 14 2026-08-30] Target the SAME left-nudged centre the render used (SLOPE_PILL_NUDGE), so the
        // pill tracks the line through a preview drag without drifting back to the un-nudged 40 percent point.
        var dx = (px(0.4 * down.x) - SLOPE_PILL_NUDGE) - lastScene.slopeHandlePx.x;
        var dy = py(0.4 * down.y) - lastScene.slopeHandlePx.y;
        pill.setAttribute('transform', 'translate(' + elevPx(dx) + ' ' + elevPx(dy) + ')');
      }
    }
    // Live-update the ceiling readout value row to the previewed whole-degree angle (same rounding as commit).
    var ro = svg.querySelector('#vmsw-elev-ro-slope-val');
    if (ro) ro.textContent = String(Math.round(d)) + DEG_SIGN;
  }
  // previewTilt(deg): set the TV group transform via tvTiltTransform, rotating about the drawn hole centre
  // (scene.standoff, scene.holeCentreY). Bare rotate (no hole-centre displacement); the migrating head's
  // exact displacement is applied on commit. The tilt handle rides inside the group, so it rotates too.
  function previewTilt(deg) {
    var svg = elevSvg(); if (!svg || !lastScene) return;
    var g = svg.querySelector('.vmsw-elev__tv'); if (!g) return;
    var f = lastScene.fit;
    var cx = f.ox + lastScene.standoff * f.s, cy = f.oy - lastScene.holeCentreY * f.s;
    var t = tvTiltTransform(deg, cx, cy, 0, 0);
    if (t) g.setAttribute('transform', t); else g.removeAttribute('transform');
    // Live-update the tilt readout to the previewed whole-degree angle (same rounding as the commit).
    var ro = svg.querySelector('#vmsw-elev-ro-tilt-val');
    if (ro) ro.textContent = String(Math.round(S.num(deg) || 0)) + DEG_SIGN;
  }
  // previewHeadDrop(inches): translate the head assembly (arm, head, 11FC, TV group, height handle) vertically
  // by (inches - the committed drop) * s px (down = larger drop), and show/hide the 11FC element by the rule
  // (raised when the drop is above the current pipe's resting drop). Approximate: the pipe does not re-length
  // and the head frame does not swap here; commit (setHeadDrop) re-solves and re-draws exactly.
  function previewHeadDrop(inches) {
    var svg = elevSvg(); if (!svg || !lastScene) return;
    var dyPx = (S.num(inches) - lastScene.headDrop) * lastScene.fit.s;   // + = down on screen
    var tr = 'translate(0 ' + (Math.round(dyPx * 100) / 100) + ')';
    ['vmsw-elev-arm', 'vmsw-elev-head', 'vmsw-elev-collar', 'vmsw-elev-h-height'].forEach(function (id) {
      var eln = svg.querySelector('#' + id); if (eln) eln.setAttribute('transform', tr);
    });
    var tv = svg.querySelector('.vmsw-elev__tv');
    if (tv) {
      // Preserve the committed tilt transform, prepend the height translate.
      var f = lastScene.fit, cx = f.ox + lastScene.standoff * f.s, cy = f.oy - lastScene.holeCentreY * f.s;
      var tilt = tvTiltTransform(lastScene.tiltDeg, cx, cy, 0, 0);
      tv.setAttribute('transform', tr + (tilt ? ' ' + tilt : ''));
    }
    var collar = svg.querySelector('#vmsw-elev-collar');
    if (collar) {
      // Raised (11FC visible) when the previewed drop is above the current pipe's resting drop.
      var raised = lastScene.restingDrop != null && (S.num(inches) < lastScene.restingDrop - 1e-6);
      collar.style.display = raised ? '' : 'none';
    }
  }

  // =====================================================================
  // [MCP 2026-08-30] WebMCP support surface. The standalone in-browser-agent layer
  // (assets/vms-slanted-wall-mcp.js) talks to the page ONLY through these public methods, exactly like the
  // drag layer. getBuild() is a plain-data snapshot of the current build; the setters run through the SAME
  // code paths as a shopper typing or clicking, so the example clears, the gates run and the answer
  // re-renders. Nothing here reaches into engine internals; it reads state and the rendered answer DOM.
  // =====================================================================
  // Extract a dollar amount from a rendered string. A parts-row value is "SKU - $20.95" and a total is
  // "Build total: $162.85", so grab the LAST $-prefixed number (never the digits inside a SKU like 11CP006).
  function mcpMoney(s) {
    var m = String(s == null ? '' : s).match(/\$\s*([0-9][0-9,]*(?:\.[0-9]+)?)/g);
    if (!m || !m.length) return null;
    var n = Number(m[m.length - 1].replace(/[^0-9.]/g, ''));
    return isFinite(n) ? n : null;
  }

  // The gate status of every plate and head card, read off the rendered cards so the reason string matches
  // exactly what the shopper sees: a VESA-gated head is 'hidden'; an angle-gated plate or a diagonal-gated
  // head is 'disabled' with the card's own reason note; everything else is 'ok'.
  function mcpCardStatus(containerId, dataKey) {
    var box = el(containerId), out = [];
    if (!box) return out;
    box.querySelectorAll('.vmsw-pick-card').forEach(function (card) {
      var sku = card.dataset[dataKey];
      var status = 'ok', reason = null;
      if (card.classList.contains('vmsw-hidden')) {
        status = 'hidden';
        reason = 'The VESA pattern you entered is larger than this head supports.';
      } else if (card.classList.contains('vmsw-disabled')) {
        status = 'disabled';
        var g = card.querySelector('.vmsw-pick__gate');
        reason = (g && !g.hidden) ? g.textContent : 'Not available for the current build.';
      }
      out.push({ sku: sku, status: status, reason: reason });
    });
    return out;
  }

  // =====================================================================
  // [MCP 2026-09-09, review R2] The agent-facing snapshot helpers. Same vocabulary and shape as the ceiling
  // configurator (assets/vms-configurator.js), so one agent reads both build pages the same way: cents
  // rounding (mcpCents), rich plate / head objects, plate and head choice (selectPlate / selectHead) driving
  // the page's OWN pick UI exactly like a click, auto-cheapest-eligible when nothing was named, clamp
  // reporting, and refusals that leave the page untouched. Nothing here reaches into the solver or the
  // drawing; it CALLS the existing pure gates (plateGatedByAngle / headGatedByVesa / headGatedByDiag) and
  // reads state, exactly as the click handler does.
  // =====================================================================
  // Round a dollar figure to whole cents before it leaves in a tool result (float noise from summing
  // offsets), null passing through as null. Mirrors the configurator's mcpCents.
  function mcpCents(n) { return (n == null || n === '' || isNaN(Number(n))) ? (n == null ? null : n) : Math.round(Number(n) * 100) / 100; }
  // A "lo to hi" string for a numeric pair, or null when either side is missing. No dash, per the copy rule.
  function mcpRange(lo, hi) {
    var a = S.num(lo), b = S.num(hi);
    if (a === null || b === null) return null;
    return a + ' to ' + b;
  }
  // Resolve a SKU to its component OR the 11FC companion (which is NOT in the parts pool, so bySku alone
  // returns null for it and left the 11FC parts-row title null, review S3). Every parts-row lookup uses this.
  function mcpBySku(sku) {
    var c = bySku(sku);
    if (c) return c;
    return (COMPANION && COMPANION.sku === sku) ? COMPANION : null;
  }
  function mcpImg(c) { return (c && typeof c.image === 'string' && c.image) ? pvUrl(c.image) : null; }
  // Case-insensitive plate / head resolution against THIS page's pools (review S1): a shopper who types
  // "11cp006" or "33he001" is not a wrong SKU, and a case difference must never fall through to auto pick.
  function mcpPlateCI(sku) {
    if (sku == null) return null;
    var key = String(sku).trim().toLowerCase();
    for (var i = 0; i < PLATES.length; i++) { if (PLATES[i].sku && PLATES[i].sku.toLowerCase() === key) return PLATES[i]; }
    return null;
  }
  function mcpHeadCI(sku) {
    if (sku == null) return null;
    var key = String(sku).trim().toLowerCase();
    for (var i = 0; i < HEADS.length; i++) { if (HEADS[i].sku && HEADS[i].sku.toLowerCase() === key) return HEADS[i]; }
    return null;
  }
  // The plate object shape in the snapshot: the fields the configurator's plate object carries that this
  // page has metafields for (review S9), plus the plate's angle limit. URL stays root-relative; the MCP
  // layer absolutizes it (the configurator pattern).
  function mcpPlateObj(p) {
    if (!p) return null;
    return {
      sku: p.sku, title: p.title, price: mcpCents(Number(p.price)), url: p.url || null,
      weight_capacity_lb: S.num(p.weight_capacity),
      max_ceiling_angle_deg: (PLATE_MAX_ANGLE_DEG[p.sku] != null) ? PLATE_MAX_ANGLE_DEG[p.sku] : (p.sku === 'ANGLE-CEILING' ? 90 : null),
      angle_note: (p.sku === 'ANGLE-CEILING') ? 'Adjusts 90 degrees from plumb one way and 4 degrees the other; mount it the right way round.'
        : (PLATE_MAX_ANGLE_DEG[p.sku] != null ? 'Pivots to ' + PLATE_MAX_ANGLE_DEG[p.sku] + ' degrees; above that the page picks ANGLE-CEILING.' : null),
      image_url: mcpImg(p)
    };
  }
  // The head object shape in the snapshot: VESA width / height ranges (mm), weight, downward tilt, screen
  // range and the card blurb, read from the SAME metafields the page's VESA chart renders (review S9). This
  // page carries VESA bounds, not a discrete pattern list, so the ranges are what an agent gets, honestly.
  function mcpHeadObj(h) {
    if (!h) return null;
    return {
      sku: h.sku, title: h.title, price: mcpCents(Number(h.price)), url: h.url || null,
      weight_capacity_lb: S.num(h.weight_capacity),
      tilt_down_deg: S.num(headMaxTilt(h)),
      vesa_width_mm: mcpRange(h.vesa_width_min, h.vesa_width_max),
      vesa_height_mm: mcpRange(h.vesa_height_min, h.vesa_height_max),
      screen_range_in: mcpRange(h.screen_min, h.screen_max),
      picker_blurb: (h.picker_blurb == null || h.picker_blurb === '') ? null : h.picker_blurb,
      image_url: mcpImg(h)
    };
  }
  // [review R2 item 6] The ONE commit funnel for a plate or head pick: set state, mark the step touched, reset
  // the per-build head position / 11FC / tilt (a new head's max and pivot differ, so a stale angle must never
  // carry across a head change; a plate change returns to the auto/square regime too), reflect the selection
  // on the cards (reflectPick), and re-render. BOTH the pick-card CLICK handler (wireCards) AND the agent
  // picks (selectPlate / selectHead) call this, so a shopper's click and an agent pick run the exact same
  // code path and can never drift apart.
  function mcpCommitPick(stateKey, sku, dataKey, containerId) {
    state[stateKey] = sku;
    if (stateKey === 'plateSku') plateTouched = true;
    if (stateKey === 'headSku') { headTouched = true; }
    state.headDrop = null; state.pipe = null; state.add11fc = false;
    state.tiltTouched = false; state.tilt = 0;
    reflectPick(containerId, dataKey, sku);
    renderAnswer();
  }
  // selectPlate(sku): pick a ceiling plate as a click would. Refuses (returns false, page unchanged) an
  // unknown SKU or a plate the committed slope pivots past (the same rule the click handler enforces: a
  // gated card is not selectable). Case-insensitive.
  function selectPlate(sku) {
    var comp = mcpPlateCI(sku);
    if (!comp) return false;
    if (plateGatedByAngle(comp, state.pitch)) return false;
    mcpCommitPick('plateSku', comp.sku, 'plate', 'vmsw-plates');
    return true;
  }
  // selectHead(sku): pick a mounting head as a click would. Refuses (page unchanged) an unknown SKU or a
  // head hidden by the VESA gate or disabled by the derived-diagonal gate for the entered measurements
  // (inert while the example is showing, exactly as the card gates are). Case-insensitive.
  function selectHead(sku) {
    var comp = mcpHeadCI(sku);
    if (!comp) return false;
    if (!exampleActive && (headGatedByVesa(comp, state.vesa) || headGatedByDiag(comp, state.screen))) return false;
    mcpCommitPick('headSku', comp.sku, 'head', 'vmsw-heads');
    return true;
  }
  // listPlates() / listHeads(): every pooled plate / head as the snapshot object, with a `fits` flag for the
  // current build (review S1 / S9), so an agent can compare and choose without leaving the page.
  function listPlates() {
    return PLATES.map(function (p) { var o = mcpPlateObj(p); o.fits = !plateGatedByAngle(p, state.pitch); return o; });
  }
  function listHeads() {
    return HEADS.map(function (h) {
      var o = mcpHeadObj(h);
      o.fits = exampleActive ? true : !(headGatedByVesa(h, state.vesa) || headGatedByDiag(h, state.screen));
      return o;
    });
  }
  // The cheapest plate / head ELIGIBLE for the current build (review S1 / S2). A probe past a plate's pivot
  // limit or past a head's VESA maximum never strands the page: the omitted-choice path auto-picks the
  // cheapest one that still fits. Both pools are price-sorted already; re-sort defensively.
  function cheapestEligiblePlate() {
    var e = PLATES.filter(function (p) { return !plateGatedByAngle(p, state.pitch); })
      .slice().sort(function (a, b) { return Number(a.price) - Number(b.price); });
    return e.length ? e[0] : null;
  }
  function mcpGivenAny(g) { return !!g && (g.diag != null || g.w != null || g.lb != null); }
  // [R4] C3 C8 C9 a given diagonal, VESA width or weight against one head: the sentence naming the cause, or null.
  function mcpGivenGate(h, g) {
    if (!h || !g) return null;
    var smax = S.num(h.screen_max), wmax = S.num(h.vesa_width_max), cap = S.num(h.weight_capacity);
    if (g.diag != null && smax !== null && g.diag > smax + DIAG_GATE_TOLERANCE_IN) return h.sku + ' is sized for screens up to ' + nice(smax) + ' inches and this TV measures about ' + Math.round(g.diag) + ' inches.';
    if (g.w != null && wmax !== null && g.w > wmax + VESA_GATE_TOLERANCE_IN * 25.4) return h.sku + ' takes a VESA width up to ' + nice(wmax) + ' mm and this TV\'s is ' + g.w + ' mm.';
    if (g.lb != null && cap !== null && g.lb > cap) return h.sku + ' is rated ' + nice(cap) + ' lb, under the ' + nice(g.lb) + ' lb TV.';
    return null;
  }
  function mcpHeadCause(h, vesaIn, screenIn, g) {
    if (headGatedByVesa(h, vesaIn)) return h.sku + ' takes a VESA height up to ' + Math.round(headVesaMaxIn(h) * 25.4) + ' mm and this TV\'s is ' + ((g && g.h != null) ? g.h : Math.round(S.num(vesaIn) * 25.4)) + ' mm.';
    if (headGatedByDiag(h, screenIn)) return h.sku + ' is sized for screens up to ' + nice(h.screen_max) + ' inches and this TV measures about ' + Math.round(derivedDiagIn(screenIn)) + ' inches.';
    return mcpGivenGate(h, g);
  }
  function cheapestEligibleHead() {
    var e = HEADS.filter(function (h) { return !headGatedByVesa(h, state.vesa) && !headGatedByDiag(h, state.screen); })
      .slice().sort(function (a, b) { return Number(a.price) - Number(b.price); });
    return e.length ? e[0] : null;
  }
  // The heads that fit a given VESA height (inches) and screen height (inches), price ascending. The single
  // source for the auto pick's cheaper-alternatives list and for every named-head refusal's list.
  function mcpHeadFitList(vesaIn, screenIn, g) {
    return HEADS.filter(function (h) { return !headGatedByVesa(h, vesaIn) && !headGatedByDiag(h, screenIn) && !mcpGivenGate(h, g); })
      .slice().sort(function (a, b) { return Number(a.price) - Number(b.price); });
  }
  function mcpHeadFitText(vesaIn, screenIn, g) {
    return mcpHeadFitList(vesaIn, screenIn, g).map(function (h) { return h.sku + ' ' + money(Number(h.price)); }).join(', ');
  }
  // The tail sentence for a head refusal: the fitting heads, or the phone when none fit.
  function mcpHeadFitTail(vesaIn, screenIn, g) {
    var t = mcpHeadFitText(vesaIn, screenIn, g);
    return t ? ('Heads that fit these measurements: ' + t + '.') : ('No head on this page fits these measurements. Call ' + PHONE_TEXT + '.');
  }
  // The effective INCHES for a raw agent measurement, mm-aware with the SAME thresholds wireMeasure uses, so
  // a pre-validation gate check (before any input is touched) sees the number the page would compute.
  function mcpEffIn(raw, allowZero, mmThreshold) {
    if (raw == null) return null;
    return readMeasure(String(raw), allowZero, mmThreshold).value;
  }
  // [review S1] Refusal objects for a NAMED plate or head that cannot be used for the requested build. Each
  // is a LEAN object, NOT a snapshot, and is returned BEFORE any input is touched, so the page is left
  // exactly as the shopper had it (mirrors the configurator's C2 / C7). Each carries the fitting
  // alternatives (rich objects) and the phone.
  function mcpRefusePlateUnknown(sku) {
    return { refusal: 'plate_unknown', plate_sku: String(sku),
      message: String(sku) + ' is not a ceiling plate on this page. The plates you can pick are: ' +
        PLATES.map(function (p) { return p.sku + ' ' + money(Number(p.price)); }).join(', ') + '.',
      alternatives: PLATES.map(mcpPlateObj), phone: PHONE_TEXT };
  }
  function mcpRefusePlateAngle(pl, pitch) {
    var deg = (pitch == null) ? null : Math.round(S.pitchDegrees(pitch));
    var elig = PLATES.filter(function (p) { return !plateGatedByAngle(p, pitch); });
    return { refusal: 'plate_does_not_fit_angle', plate_sku: pl.sku,
      message: pl.sku + ' pivots to ' + nice(plateMaxAngle(pl)) + ' degrees, not ' + (deg != null ? deg + ' degrees' : 'this slope') + '. ' +
        (elig.length ? ('Plates that fit this slope: ' + elig.map(function (p) { return p.sku + ' ' + money(Number(p.price)); }).join(', ') + '.') : ('Call ' + PHONE_TEXT + '.')),
      alternatives: elig.map(mcpPlateObj), phone: PHONE_TEXT };
  }
  function mcpRefuseHeadUnknown(sku, vesaIn, screenIn, g) {
    return { refusal: 'head_unknown', head_sku: String(sku),
      message: String(sku) + ' is not a mounting head on this page. ' + mcpHeadFitTail(vesaIn, screenIn, g),
      alternatives: mcpHeadFitList(vesaIn, screenIn, g).map(mcpHeadObj), phone: PHONE_TEXT };
  }
  function mcpRefuseHeadFit(hd, vesaIn, screenIn, g) {
    var rng = mcpRange(hd.screen_min, hd.screen_max);
    var why = mcpHeadCause(hd, vesaIn, screenIn, g);
    return { refusal: 'head_does_not_fit', head_sku: hd.sku,
      message: (why || (hd.sku + (rng ? ' fits ' + rng + ' inch screens and a smaller VESA pattern' : ' does not fit this TV') + '.')) + ' ' + mcpHeadFitTail(vesaIn, screenIn, g),
      alternatives: mcpHeadFitList(vesaIn, screenIn, g).map(mcpHeadObj), phone: PHONE_TEXT };
  }
  function mcpVesaWH(v) {
    var m = (typeof v === 'string') ? v.toLowerCase().replace(/\s+/g, '').replace(/mm/g, '').match(/^(\d+)x(\d+)$/) : null;
    return m ? [Number(m[1]), Number(m[2])] : null;
  }
  function mcpRefuseVesaMismatch(wh, vesaIn) {
    return { refusal: 'vesa_pattern_mismatch',
      message: 'The VESA pattern ' + wh[0] + 'x' + wh[1] + ' puts the holes ' + nice(wh[1] / 25.4) + ' inches apart top to bottom, but hole_to_hole_in is ' + nice(vesaIn) + ' inches. Check the TV and pass matching values.',
      alternatives: [], phone: PHONE_TEXT };
  }
  // [R4] C3 the diagonal used for the head fit and each head's screen range; reports only.
  function mcpScreenFit(screenIn, g) {
    var est = derivedDiagIn(screenIn), given = (g && g.diag != null) ? g.diag : null;
    var d = (given != null) ? given : (est === null ? null : Math.round(est));
    if (d === null) return null;
    var src = (given == null) ? ('estimated from the ' + nice(screenIn) + ' inch overall height at 16:9')
      : ('given as screen_diagonal_in' + ((est !== null && est > given + DIAG_GATE_TOLERANCE_IN)
        ? '; the page estimates about ' + Math.round(est) + ' inches from the ' + nice(screenIn) + ' inch overall height, and a head that estimate rules out stays out' : ''));
    return { diagonal_in: d, source: src,
      heads: HEADS.filter(function (h) { return mcpRange(h.screen_min, h.screen_max) !== null; }).map(function (h) {
        var lo = S.num(h.screen_min), hi = S.num(h.screen_max);
        return { sku: h.sku, screen_range_in: mcpRange(h.screen_min, h.screen_max), inside_range: (lo === null || d >= lo) && (hi === null || d <= hi) };
      }) };
  }
  // [R4] C8 the TV weight against the whole build rating; the note names the limiter and a heavier plate.
  function mcpWeightNote(plateSku, headSku, rating, lims, lb) {
    var names = (lims || []).map(function (k) { return k + (k === plateSku ? ' plate' : (k === headSku ? ' head' : '')); });
    var msg = 'This build is rated for ' + nice(rating) + ' lb, limited by the ' + (names.length ? names.join(' and ') : 'parts') + '. Your ' + nice(lb) + ' lb TV needs ';
    var better = ((lims || []).indexOf(plateSku) >= 0) ? PLATES.filter(function (p) { var c = S.num(p.weight_capacity); return p.sku !== plateSku && c !== null && c >= lb && !plateGatedByAngle(p, state.pitch); })
      .slice().sort(function (a, b) { return Number(a.price) - Number(b.price); })[0] : null;
    return msg + (better ? ('a heavier plate: ' + better.sku + ' carries ' + nice(better.weight_capacity) + ' lb for ' + money(Number(better.price)) + '. Pass plate_sku ' + better.sku + ', or call ' + PHONE_TEXT + '.')
      : ('more than this build carries. Call ' + PHONE_TEXT + '.'));
  }
  function mcpWeightFit(snap, lb) {
    var wr = snap.weight_rating, rating = (wr && wr.lb != null) ? Number(wr.lb) : null;
    var ok = (rating === null) ? null : lb <= rating, lims = wr ? wr.limited_by.slice() : [];
    return { tv_weight_lb: lb, build_rating_lb: rating, ok: ok, limited_by: lims,
      note: (ok === false) ? mcpWeightNote(snap.plate ? snap.plate.sku : null, snap.head ? snap.head.sku : null, rating, lims, lb) : null };
  }
  // [R4] C9 the given pattern against the head on the build.
  function mcpVesaFit(head, g) {
    var wmax = head ? S.num(head.vesa_width_max) : null, hmax = head ? S.num(head.vesa_height_max) : null;
    return { pattern_mm: g.w + 'x' + g.h, head_max_mm: (wmax !== null && hmax !== null) ? nice(wmax) + 'x' + nice(hmax) : null,
      ok: head ? !(mcpGivenGate(head, { w: g.w }) || headGatedByVesa(head, g.h / 25.4)) : null };
  }
  // [review S3] The reachable TV-height (hole-centre drop) range on the CURRENT plate and head with stock
  // pipes, so an agent can say "the longest we can go on this head is X". Sliding head: highest = the head's
  // top travel (res.headDrop.min), lowest = resting on the longest stock pipe. Fixed head: shortest pipe
  // that clears the corner up to the longest available pipe. Defensive: null on any missing piece, never
  // throws, so it can never block a build. Reads the solver read-only (S.solve is pure).
  function mcpReachableDrop() {
    try {
      if (!ready()) return null;
      var plate = bySku(state.plateSku), head = bySku(state.headSku);
      if (!plate || !head) return null;
      var holeMid = S.holeMidFromTop(state.topHole, state.vesa);
      var po = effectivePlateOffset(plate, state.pitch);
      if (holeMid === null || po === null) return null;
      var lo = null, hi = null;
      if (S.headSlides(head.length_offset)) {
        var res = solveBuild(plate, head, holeMid, state.tilt);
        if (!res || !res.slides || !res.headDrop) return null;
        lo = S.num(res.headDrop.min);
        hi = restingDropForPipe(po, STOCK[STOCK.length - 1]);
      } else {
        var ho = S.num(head.length_offset);
        var sf = shortestFixedPipeClearingCorner(plate, head);
        var longest = longestAvailableLength();
        if (!sf || ho === null || longest === null) return null;
        lo = po + sf.length + ho;
        hi = po + longest + ho;
      }
      if (lo === null || hi === null) return null;
      var a = Math.min(lo, hi), b = Math.max(lo, hi);
      return { min: Math.round(a * 10) / 10, max: Math.round(b * 10) / 10 };
    } catch (e) { return null; }
  }
  // [review S5, OWNER 2026-09-09] The oversize-shipping field. It can NO LONGER apply on this page: the pool
  // now EXCLUDES every single fixed pipe 48 inches or longer (see SURCHARGE_MIN_LEN where the pool is built),
  // matching the configurator, on the owner's ruling of 2026-09-09 ("NPT48 should be excluded on angled wall
  // page"). This retires the old R2 behaviour, where the page kept NPT48 and merely flagged the surcharge as
  // an owner call that was then pending. The field is KEPT for a stable tool shape and now always reports
  // { applies: false } with a note that says single pipes 48 inches and longer are excluded, so an agent can
  // explain WHY no build here draws the surcharge. Telescoping pipes ship collapsed under 48 inches, so they
  // never triggered it either. Null only when there is no drawable build (the caller also gates on drawable).
  function mcpOversize() {
    try {
      if (!lastScene || !lastNumbers || !lastNumbers.calculated) return null;
      return { applies: false,
        note: 'Single pipes 48 inches and longer are excluded from this page, so no build here draws an oversize shipping surcharge.' };
    } catch (e) { return null; }
  }
  // [R5] C4 a flat ceiling is handed to the ceiling configurator.
  var HANDOFF_FLAT_SENTENCE = 'For a flat ceiling, the ceiling configurator, https://videomountstore.com/pages/build-custom-tv-ceiling-mount, has many more options.';
  function mcpFlatHandoff() {
    return { url: 'https://videomountstore.com/pages/build-custom-tv-ceiling-mount',
      tools: 'vms-configure-build on that page (TV size, ceiling type, length, VESA, weight)',
      why: 'For a flat ceiling the ceiling configurator builds the mount with many more options.' };
  }
  // [review S4 / S10] The state-aware hand-back line for configure-build, mirroring the configurator's C9.
  // With a drawable, configured build it names the plate, head, pipe, the mid-screen length and total, then
  // the VESA and weight caveat and the phone; with no drawable build it gives a plain reason in customer
  // words and the phone, never a false "on the page" line.
  function mcpSlantedHandback(snap) {
    if (lastScene && snap && snap.view === 'configured' && snap.plate && snap.head && snap.parts && snap.parts.length) {
      var head = bySku(snap.head.sku);
      var pipePart = null;
      snap.parts.forEach(function (pt) { var c = bySku(pt.sku); if (c && (c.component_type === 'pipe' || c.component_type === 'nipple')) pipePart = pt; });
      var midLen = (snap.numbers && snap.numbers.calculated) ? snap.numbers.calculated.finished_length_to_mid_screen_in : null;
      var maxV = (head && S.num(head.vesa_width_max) !== null && S.num(head.vesa_height_max) !== null)
        ? (nice(head.vesa_width_max) + 'x' + nice(head.vesa_height_max) + ' mm') : null;
      var lb = (snap.weight_rating && snap.weight_rating.lb != null) ? snap.weight_rating.lb : null;
      var s = 'The build is on the page: ' + snap.plate.sku + ' plate, ' + snap.head.sku + ' head';
      if (pipePart) s += ', ' + pipePart.sku + ' pipe';
      if (midLen != null) s += ', ' + nice(midLen) + ' inches to mid-screen';
      if (snap.total != null) s += ', total ' + money(snap.total);
      s += '. ' + COPY('mcp_handback') + ' Check your TV\'s VESA pattern (' + (maxV ? 'this head takes up to ' + maxV : 'confirm the head takes your pattern') +
        ') and weight (' + (lb != null ? 'rated ' + lb + ' lb' : 'confirm the rating') + '). Not sure? Call ' + PHONE_TEXT + '.';
      return s;
    }
    if (!state.headSku) {
      var hs = nice(state.vesa);
      return 'No mounting head on this page takes ' + (hs !== null ? 'a ' + hs + ' inch hole spacing' : 'these measurements') + '. Call ' + PHONE_TEXT + '.';
    }
    if (!state.plateSku) {
      var dg = (state.pitch == null) ? null : Math.round(S.pitchDegrees(state.pitch));
      return 'No ceiling plate on this page fits ' + (dg !== null ? 'a ' + dg + ' degree ceiling' : 'this slope') + '. Call ' + PHONE_TEXT + '.';
    }
    if (snap && snap.no_build_note) return snap.no_build_note;
    return 'We could not complete this build from these numbers. Call ' + PHONE_TEXT + '.';
  }
  // [R3] C1 the length used, the reachable ranges, the stock pipes and the pipe on the build.
  function mcpLengthChoice(n, parts) {
    var c = n && n.calculated;
    if (!c) return null;
    var hole = S.num(c.hole_centre_below_plate_in), mid = S.num(c.finished_length_to_mid_screen_in);
    var off = (hole !== null && mid !== null) ? mid - hole : null;
    var reach = mcpReachableDrop(), d = state.headDrop == null;
    var r1 = function (v) { return Math.round(v * 10) / 10; };
    var pipe = null;
    (parts || []).forEach(function (pt) { var k = bySku(pt.sku); if (k && (k.component_type === 'pipe' || k.component_type === 'nipple')) pipe = k; });
    var tele = !!(pipe && pipe.length_min != null);
    return { defaulted: d, used_hole_centre_in: hole, used_mid_screen_in: mid,
      range_hole_centre_in: reach, range_mid_screen_in: (reach && off !== null) ? { min: r1(reach.min + off), max: r1(reach.max + off) } : null,
      pipe_used: pipe ? { sku: pipe.sku, length_in: (!tele && S.num(pipe.nominal_length) !== null) ? S.num(pipe.nominal_length) : S.num(c.pipe_length_in), telescoping: tele } : null,
      stock_pipes_in: STOCK.slice(),
      telescoping_in: TELE.map(function (t) { return { sku: t.sku, min: S.num(t.length_min), max: S.num(t.length_max) }; }),
      note: (d && reach && hole !== null) ? ('No length was given, so the page used ' + nice(hole) + ' inches from the plate to the hole centre' +
        (mid !== null ? ' (' + nice(mid) + ' to mid-screen)' : '') + '. Any length from ' + nice(reach.min) + ' to ' + nice(reach.max) +
        ' inches to the hole centre is available; ask the shopper which they want.') : null };
  }
  // [R3] C7 why the pipe is the length it is.
  function mcpPipeNote() {
    var res = lastRes;
    if (!res || res.reason !== 'ok') return null;
    var L = S.num(chosenPipeLen(res));
    if (L === null) return null;
    var ln = function (v) { return String(Math.round(S.num(v) * 100) / 100); };
    if (state.headDrop != null) {
      return (state.pipe && state.pipe.kind === 'tele')
        ? ('The ' + state.pipe.sku + ' telescoping pipe, ' + ln(state.pipe.collapsed) + ' to ' + ln(state.pipe.extended) + ' inches, is set to ' + ln(L) + ' inches to reach the length asked for.')
        : ('The ' + ln(L) + ' inch pipe is the stock pipe that reaches the length asked for.');
    }
    if (res.slides) {
      return (res.floorReason === 'reach')
        ? fill(COPY_DEFAULTS.slide_floor_note_reach, { pipe: ln(L) + ' inches', drop: nice(res.headDrop ? res.headDrop.max : null) })
        : fill(COPY_DEFAULTS.slide_floor_note, { pipe: ln(L) + ' inches', span: nice(S.minPipeForSlidingHeadResting()) });
    }
    return 'The ' + ln(L) + ' inch pipe is the shortest stock pipe' + (L === STOCK[0] ? '' : ' that keeps the top back corner of the TV clear of the ceiling') +
      ', so the TV sits as high as this plate and head allow.';
  }
  // [R3] C12 why there is no build, in the words of the notice the page shows.
  function mcpNoBuild(box, drawable) {
    if (drawable || exampleActive) return { reason: null, note: null };
    var reason = ready() ? (lastRes && lastRes.reason !== 'ok' ? lastRes.reason : null) : (anyInvalid() ? 'out_of_range' : null);
    var wb = box ? box.querySelector('.warnbox') : null;
    var note = wb ? String(wb.textContent || '').replace(/\s+/g, ' ').trim() : '';
    return { reason: reason, note: (reason && note) ? note : null };
  }

  // A plain-data snapshot of the current build for an agent. Measurements are the values the build is
  // computed from (during the example these are the seeded example numbers, flagged by is_example); the
  // parts, total and cart-armed flag are read from the rendered answer so they match what is shown.
  function getBuild() {
    var plate = state.plateSku ? bySku(state.plateSku) : null;
    var head = state.headSku ? bySku(state.headSku) : null;
    var box = el('vmsw-answer');
    var parts = [];
    if (box) {
      box.querySelectorAll('.vmsw-sumrow').forEach(function (row) {
        var a = row.querySelector('a.vmsw-sumrow__sku');
        if (!a) return;   // the weight row has no part link
        var v = row.querySelector('.v');
        // [review S3] mcpBySku knows the 11FC companion (which is NOT in the parts pool), so its parts-row
        // title is no longer null. Prices go through mcpCents so a summed line reads back without float noise.
        var comp = mcpBySku(a.textContent);
        parts.push({ sku: a.textContent, title: comp ? comp.title : null,
          url: a.getAttribute('href'), price: mcpCents(v ? mcpMoney(v.textContent) : null), image_url: mcpImg(comp) });
      });
    }
    var totalEl = box ? box.querySelector('.vmsw-total') : null;
    var cartBtn = el('vmsw-addcart');
    var gates = mcpCardStatus('vmsw-plates', 'plate').concat(mcpCardStatus('vmsw-heads', 'head'))
      .filter(function (g) { return g.status !== 'ok'; });
    // [review S10] weight_rating at the top level, in the configurator's {lb, limited_by[], confident}
    // shape, so one number has one name across both build pages. numbers.calculated.weight_limit_lb is kept
    // for continuity.
    var wr = buildWeightRating(plate, head);
    var weight_rating = wr
      ? { lb: wr.weight, limited_by: (wr.confident ? wr.limiters : wr.unrated).slice(), confident: !!wr.confident }
      : null;
    // [review S4] The current render is drawable only when it actually shows a build, i.e. the answer box
    // holds part rows. A notice or the "fill in the steps" prompt has none, so parts is empty; numbers and
    // the oversize note persist from the last drawable render, so they must be gated on this, else a failed
    // probe hands back a stale build's numbers under an "incomplete" view. (The renderAnswer not-ready path
    // returns without touching lastScene, so lastScene alone is not a safe signal.)
    var drawable = parts.length > 0;
    var nb = mcpNoBuild(box, drawable);
    var r1 = function (v) { return (v == null) ? null : Math.round(S.num(v) * 10) / 10; };
    return {
      is_example: exampleActive === true,
      view: exampleActive ? 'example' : (ready() ? 'configured' : 'incomplete'),
      // [review S8] measurements rounded to 0.1 (a mm entry converts to a long decimal otherwise).
      measurements_in: { height: r1(state.screen), top_to_top_hole: r1(state.topHole), hole_to_hole: r1(state.vesa) },
      ceiling_angle_deg: (state.pitch == null) ? null : Math.round(S.pitchDegrees(state.pitch)),
      screen_tilt_deg: state.tilt,
      // [review S9] Rich plate / head objects: the configurator's fields this page has metafields for.
      plate: mcpPlateObj(plate),
      head: mcpHeadObj(head),
      parts: parts,
      total: mcpCents(totalEl ? mcpMoney(totalEl.textContent) : null),
      cart_armed: !!(cartBtn && !cartBtn.disabled),
      gate_reasons: gates,
      eligible_heads: eligibleHeads(),
      // [review S3] The two finished-length numbers each named, so an agent never confuses the hole-centre
      // figure (the cart-summary / app-contract number) with the mid-screen figure the page shows. The cart
      // line's _vms_config_summary keeps the hole-centre figure ON PURPOSE (the shared app contract; see
      // configSummary); this map is how an agent knows which is which.
      length_reference: {
        hole_centre_below_plate_in: 'ceiling plate down to the middle of the mounting holes',
        finished_length_to_mid_screen_in: 'ceiling plate down to the middle of the screen',
        tv_height_in: 'ceiling plate down to the middle of the mounting holes'
      },
      // [review R2 item 5] Gate the reachable range on `drawable` too, like oversize_shipping: with no
      // drawable build there is no pipe fit to report, so it is null (mcpReachableDrop also returns null
      // when no pipe fits), never a stale range carried over from the last drawable render.
      reachable_tv_height_in: drawable ? mcpReachableDrop() : null,
      oversize_shipping: drawable ? mcpOversize() : null,
      weight_rating: weight_rating,
      // [URL STATE 2026-09-23] Replaces the [review S10] null: the page carries the build in the URL now, so
      // this is the same pathname + query the address bar holds (shareURL), which the MCP layer's absUrl turns
      // absolute. It stays null while the example shows or while the shopper has set nothing the link carries,
      // so an agent is never handed a link that restores the example as if it were a shared build.
      share_url: shopperShareURL(),
      numbers: drawable ? lastNumbers : null,
      plates: listPlates(),
      heads: listHeads(),
      length_choice: drawable ? mcpLengthChoice(lastNumbers, parts) : null,
      pipe_note: drawable ? mcpPipeNote() : null,
      no_build_reason: nb.reason,
      no_build_note: nb.note,
      currency: 'USD',
      tv_placement: tvPlacementOut()
    };
  }

  // The heads currently selectable (not hidden, not disabled) other than the one already chosen, with live
  // price, for an agent to suggest an alternative. During the example every pooled head is eligible (round 10).
  function eligibleHeads() {
    var stat = mcpCardStatus('vmsw-heads', 'head');
    function ok(sku) { for (var i = 0; i < stat.length; i++) { if (stat[i].sku === sku) return stat[i].status === 'ok'; } return true; }
    // [review S9] Each eligible head carries the same fit fields as the configurator's (VESA ranges, weight,
    // tilt, screen range, blurb) so an agent can compare heads without leaving the page.
    return HEADS.filter(function (h) { return h.sku !== state.headSku && ok(h.sku); }).map(mcpHeadObj);
  }

  // Enter the three TV measurements THROUGH the real inputs (value + input event), so wireMeasure runs for each:
  // the example clears, mm-detection and range checks apply, and the answer re-renders. Internal now (used by
  // configureBuild); the MCP tool validates the numbers first.
  function setMeasurements(heightIn, topToTopHoleIn, holeToHoleIn) {
    [['vmsw-screen', heightIn], ['vmsw-tophole', topToTopHoleIn], ['vmsw-vesa', holeToHoleIn]].forEach(function (pair) {
      var input = el(pair[0]);
      if (!input || pair[1] == null) return;
      input.value = String(pair[1]);
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });
    return getBuild();
  }

  // [ROUND 11 2026-08-30; review R2 2026-09-09] The WebMCP workhorse: describe the WHOLE build in ONE call,
  // running the SAME code paths as typing / clicking / dragging (example clears, gates run, the answer
  // re-renders), and return the full snapshot plus the flags and the hand-back line the agent echoes.
  // opts: {height_in, top_to_top_hole_in, hole_to_hole_in, ceiling_angle_deg} required, {screen_tilt_deg,
  // tv_height_in, plate_sku, head_sku} optional. Every call is a full description: an omitted plate or head
  // auto-picks the cheapest ELIGIBLE one (review S1 / S2), an omitted tilt resets to square, an omitted
  // tv_height resets to the default (highest) position. A NAMED plate or head that cannot be used is refused
  // BEFORE any input is touched, so the page is left exactly as the shopper had it (review S1, mirroring the
  // configurator's C2 / C7). The MCP configure-build tool validates the value TYPES first; the gates and the
  // clamps in commitTilt / setHeadDrop still apply here.
  function configureBuild(opts) {
    opts = opts || {};
    // The effective inches / pitch for the requested build, so a named-plate / named-head gate check can run
    // against the REQUESTED numbers before the page is touched (mm-aware, the same thresholds wireMeasure uses).
    var vIn = mcpEffIn(opts.hole_to_hole_in, false, 50);
    var sIn = mcpEffIn(opts.height_in, false, 100);
    var reqPitch = (opts.ceiling_angle_deg != null) ? S.pitchFromDegrees(clampAngle(opts.ceiling_angle_deg, ANGLE_MIN_DEG, ANGLE_MAX_DEG)) : null;
    var wh = mcpVesaWH(opts.vesa_pattern);
    var g = { diag: (opts.screen_diagonal_in != null) ? Number(opts.screen_diagonal_in) : null, w: wh ? wh[0] : null, h: wh ? wh[1] : null,
      lb: (opts.tv_weight_lb != null) ? Number(opts.tv_weight_lb) : null };
    if (wh && vIn != null && Math.abs(wh[1] / 25.4 - vIn) > 0.1) return mcpRefuseVesaMismatch(wh, vIn);

    // 0. PRE-VALIDATION, PAGE UNTOUCHED (review S1). A named plate or head that does not fit the requested
    //    build is refused here, before setMeasurements / userSetAngle change anything.
    var plateSku = null, headSku = null;
    if (opts.plate_sku != null) {
      var pl = mcpPlateCI(opts.plate_sku);
      if (!pl) return mcpRefusePlateUnknown(opts.plate_sku);
      if (plateGatedByAngle(pl, reqPitch)) return mcpRefusePlateAngle(pl, reqPitch);
      plateSku = pl.sku;
    }
    if (opts.head_sku != null) {
      var hd = mcpHeadCI(opts.head_sku);
      if (!hd) return mcpRefuseHeadUnknown(opts.head_sku, vIn, sIn, g);
      if (headGatedByVesa(hd, vIn) || headGatedByDiag(hd, sIn) || mcpGivenGate(hd, g)) return mcpRefuseHeadFit(hd, vIn, sIn, g);
      headSku = hd.sku;
    }

    // [review R2c item 3] The belated selectPlate / selectHead re-checks in steps 2 and 3 fire AFTER the page
    // has already been mutated by setMeasurements / userSetAngle. Pre-validation (step 0) refuses the common
    // cases before any mutation, but the rare re-check refusal must leave the page exactly as the shopper had
    // it (the "page untouched on refusal" contract). Snapshot the state those steps touch here, and restore +
    // re-render once on the re-check refusal path.
    var preState = { screen: state.screen, topHole: state.topHole, vesa: state.vesa, pitch: state.pitch,
      exampleActive: exampleActive, plateSku: state.plateSku, headSku: state.headSku, tilt: state.tilt,
      tiltTouched: state.tiltTouched, headDrop: state.headDrop, pipe: state.pipe, add11fc: state.add11fc,
      plateTouched: plateTouched, headTouched: headTouched };
    function restorePreState() {
      state.screen = preState.screen; state.topHole = preState.topHole; state.vesa = preState.vesa;
      state.pitch = preState.pitch; exampleActive = preState.exampleActive;
      state.plateSku = preState.plateSku; state.headSku = preState.headSku;
      state.tilt = preState.tilt; state.tiltTouched = preState.tiltTouched;
      state.headDrop = preState.headDrop; state.pipe = preState.pipe; state.add11fc = preState.add11fc;
      plateTouched = preState.plateTouched; headTouched = preState.headTouched;
      renderAnswer();
    }

    // 1. Measurements + angle through the real inputs (example clears, mm-detection + range checks run).
    setMeasurements(opts.height_in, opts.top_to_top_hole_in, opts.hole_to_hole_in);
    userSetAngle(opts.ceiling_angle_deg);

    // 2. Plate: the named (validated) SKU, else the cheapest plate that fits this slope (review S1).
    var plate_auto = false, plate_committed = false;
    if (plateSku) {
      // [review R2 item 7 / R2c item 3] Pre-validation passed against the REQUESTED numbers, but honor
      // selectPlate's own gate check against the numbers now ON THE PAGE: a false return means the plate did
      // not commit, so restore the page and refuse (with the requested angle) rather than leave it changed.
      if (!selectPlate(plateSku)) { restorePreState(); return mcpRefusePlateAngle(pl, reqPitch); }
      plate_committed = true;
    }
    else { plate_auto = true; var cp = cheapestEligiblePlate(); if (cp) selectPlate(cp.sku); }

    // 3. Head: the named (validated) SKU, else the cheapest head that fits these measurements (review S1 /
    //    S2), so the example's dearer head never silently becomes the customer's.
    var head_auto = false, head_committed = false;
    if (headSku) {
      // [review R2 item 7 / R2c item 3] Same as the plate: a false selectHead return restores the page and
      // refuses (with the requested measurements), never a silently-kept different head.
      if (!selectHead(headSku)) { restorePreState(); return mcpRefuseHeadFit(hd, vIn, sIn, g); }
      head_committed = true;
    }
    else {
      head_auto = true; var ch = mcpGivenAny(g) ? (mcpHeadFitList(state.vesa, state.screen, g)[0] || null) : cheapestEligibleHead();
      if (ch) selectHead(ch.sku);
      else if (mcpGivenAny(g) && state.headSku) { state.headSku = null; state.headDrop = null; state.pipe = null; state.add11fc = false; reflectPick('vmsw-heads', 'head', null); renderAnswer(); }
    }

    // [review R2 item 4] When no head ends up selected (the auto pick found none eligible, so selectHead
    // never ran, or renderAnswer deselected a now-ineligible head), state.tilt / state.tiltTouched would
    // survive from the previous call while head is null. Reset them so a headless build carries no stale
    // tilt (a plate commit already zeroes tilt, but a build with no fitting plate never commits one).
    if (!state.headSku) { state.tilt = 0; state.tiltTouched = false; }

    // 4. Tilt: a named tilt (clamped by commitTilt), else the square default (selectHead reset it to 0).
    // [review R2c item 2] Tilt is a property of the HEAD, so only commit it when a head is on the page. With
    // no head the guard above already reset tilt to 0; running commitTilt here would flip tiltTouched true and
    // clamp against a null head. The no-head clamp is reported as tilt_adjusted below.
    var reqTilt = (opts.screen_tilt_deg != null) ? Number(opts.screen_tilt_deg) : null;
    if (reqTilt != null && state.headSku) commitTilt(reqTilt);

    // 5. TV height: a named drop (setHeadDrop clamps / snaps and RETURNS the applied drop), else the default
    //    view (selectHead reset state.headDrop to null = the head as high as it can go).
    var reqDrop = (opts.tv_height_in != null) ? Number(opts.tv_height_in) : null;
    var appliedDrop = null;
    if (reqDrop != null) appliedDrop = setHeadDrop(reqDrop);

    // 6. The snapshot, plus the auto-selection flags, the clamp reports and the state-aware hand-back line.
    var snap = getBuild();
    if (plate_auto) snap.plate_auto_selected = true;
    if (head_auto) snap.head_auto_selected = true;
    // [review R2 item 7] Flag that a NAMED plate / head was actually committed to the page (its return was
    // checked above), so an agent knows the SKU it asked for is the one on the build, not a silent auto pick.
    if (plate_committed) snap.plate_pick_committed = true;
    if (head_committed) snap.head_pick_committed = true;

    // [review S2] When a NAMED head is dearer than an eligible one, list the cheaper eligible heads so the
    // agent can offer them (the configurator's cheaper_eligible_heads[]).
    if (headSku) {
      var chosen = bySku(headSku);
      if (chosen) {
        var cheaper = mcpHeadFitList(vIn, sIn, g).filter(function (h) { return h.sku !== headSku && Number(h.price) < Number(chosen.price); });
        if (cheaper.length) snap.cheaper_eligible_heads = cheaper.map(mcpHeadObj);
      }
    }

    // [review S3] Report the tilt clamp: commitTilt settles state.tilt to [0, the head's max]. Round the
    // request to whole degrees the same way commitTilt does before comparing.
    if (reqTilt != null) {
      if (!state.headSku) {
        // [review R2c item 2] A tilt was requested but no head landed on the page, so it was not applied
        // (settled 0). Name why, so an agent does not report the requested tilt as if it stuck.
        snap.tilt_adjusted = { requested: reqTilt, settled: 0, reason: 'no head on the page to tilt', unit: 'degrees' };
      } else {
        var maxTilt = headMaxTilt(bySku(state.headSku));
        var maxWhole = (maxTilt === null) ? 0 : Math.floor(maxTilt);
        if (Math.round(reqTilt) !== state.tilt) {
          snap.tilt_adjusted = { requested: reqTilt, settled: state.tilt, max: maxWhole, unit: 'degrees' };
        }
      }
    }

    // [review S3] Report the TV-height clamp: setHeadDrop settled the drop to appliedDrop. Report when it
    // differs from the request by more than the detent rounding, in customer words, with the reachable range.
    if (reqDrop != null && appliedDrop != null) {
      var settled = Math.round(S.num(appliedDrop) * 10) / 10;
      if (Math.abs(S.num(appliedDrop) - reqDrop) >= 0.5) {
        var reason = (settled < reqDrop)
          ? ('the longest stock pipe on this plate and head reaches ' + settled + ' inches to the hole centre')
          : ('this build cannot place the TV higher than ' + settled + ' inches below the plate');
        snap.tv_height_adjusted = { requested: reqDrop, settled: settled, reason: reason, unit: 'inches' };
      }
    }

    // [review S8] Note any measurement the page auto-converted from millimetres, rounded to 0.1.
    var mm = [];
    [['hole_to_hole_in', opts.hole_to_hole_in, false, 50], ['height_in', opts.height_in, false, 100], ['top_to_top_hole_in', opts.top_to_top_hole_in, true, 50]]
      .forEach(function (row) {
        if (row[1] == null) return;
        var m = readMeasure(String(row[1]), row[2], row[3]);
        if (m.converted) mm.push({ field: row[0], entered_mm: Math.round(m.mm * 10) / 10, inches: Math.round(m.inches * 10) / 10 });
      });
    if (mm.length) snap.mm_converted = mm;

    snap.handback = mcpSlantedHandback(snap);
    if (plate_auto && snap.plate) {
      var gated = PLATES.filter(function (p) { return plateGatedByAngle(p, state.pitch); });
      if (gated.length) snap.plate_note = gated.map(function (p) { return p.sku + ' pivots to ' + nice(plateMaxAngle(p)) + ' degrees'; }).join(' and ') +
        ', so ' + snap.plate.sku + ' was picked for this ' + snap.ceiling_angle_deg + ' degree ceiling.';
    }
    snap.screen_fit = mcpScreenFit(sIn, g);
    if (g.lb != null) { snap.weight_fit = mcpWeightFit(snap, g.lb); if (snap.weight_fit.ok === false) snap.handback = snap.weight_fit.note; }
    if (wh) snap.vesa_fit = mcpVesaFit(state.headSku ? bySku(state.headSku) : null, g);
    if (opts.ceiling_angle_deg != null && Number(opts.ceiling_angle_deg) === 0) {
      snap.handoff = mcpFlatHandoff();
      snap.handback = (snap.handback ? snap.handback + ' ' : '') + HANDOFF_FLAT_SENTENCE;
    }
    mcpCheckedWeight = g.lb;
    return snap;
  }

  // [ROUND 11 2026-08-30] Populate the embedded #vmsw-build-json block with the current snapshot after the
  // answer HTML is in the DOM (getBuild reads the rendered parts rows). Compact JSON, refreshed every render;
  // no-op when there is no drawable build (the block is only in the ok answer).
  function refreshBuildJson() {
    var node = document.getElementById('vmsw-build-json');
    if (node) { try { node.textContent = JSON.stringify(getBuild()); } catch (e) { node.textContent = ''; } }
  }

  // Add the CURRENT build to the cart, allowed only when the page's own button would be enabled (never during
  // the example). Returns a promise: { ok:true, added, config_id, cart_url } or { ok:false, reason }. It never
  // navigates the page (unlike the button click), so an agent can keep working; it posts the SAME payload.
  function mcpAddToCartPublic() {
    var btn = el('vmsw-addcart');
    if (exampleActive) return Promise.resolve({ ok: false, reason: 'This is the example build. Enter your own TV measurements first, then it can be added to the cart.' });
    if (!ready()) return Promise.resolve({ ok: false, reason: 'The build is not complete. Enter the three measurements in inches and pick a plate and a head.' });
    // [PAGE TOOLS 2026-09-11] Reserve gate reason precedes the generic btn.disabled guard, so the MCP
    // path relays the product-naming reserve message (the button is also disabled, blocking a click add).
    if (mcpReserveReason) return Promise.resolve({ ok: false, reason: mcpReserveReason });
    var wrA = (mcpCheckedWeight != null) ? buildWeightRating(bySku(state.plateSku), bySku(state.headSku)) : null;
    if (wrA && wrA.weight != null && !isNaN(wrA.weight) && mcpCheckedWeight > wrA.weight) {
      return Promise.resolve({ ok: false, reason: mcpWeightNote(state.plateSku, state.headSku, wrA.weight, wrA.limiters, mcpCheckedWeight) + ' Nothing was added to the cart.' });
    }
    if (!btn || btn.disabled) return Promise.resolve({ ok: false, reason: 'This build cannot be added to the cart right now.' });
    if (typeof mcpAddToCart !== 'function') return Promise.resolve({ ok: false, reason: 'One or more parts in this build are unavailable.' });
    return mcpAddToCart();
  }

  // [MCP 2026-09-09, review R2] removeBuild(configId): take a build back out of the cart, the way out that
  // pairs with add-to-cart. It reads /cart.js, selects every line carrying our _vms_config_id (that one
  // config_id, or ALL of them when configId is omitted), and zeroes exactly those keys in ONE
  // /cart/update.js. It NEVER touches a line without our property, so the shopper's own items are safe (the
  // native update_cart / cancel_cart cover those). Resolves { ok, config_id, removed[], removed_total,
  // cart_after, cart_url, handback }; throws on a failed cart request so the MCP layer can report it.
  function mcpCartLinePrice(it) {
    var cents = (it && it.final_line_price != null) ? it.final_line_price : (it && it.line_price != null ? it.line_price : null);
    return (cents == null || isNaN(Number(cents))) ? 0 : Number(cents) / 100;
  }
  function mcpBuildsRemaining(cart) {
    var seen = {};
    (cart && cart.items ? cart.items : []).forEach(function (it) {
      var cid = it.properties && it.properties._vms_config_id;
      if (cid) seen[String(cid)] = true;
    });
    return Object.keys(seen);
  }
  function mcpCartAfter(cart) {
    return {
      item_count: (cart && cart.item_count != null) ? cart.item_count : null,
      total_price: mcpCents((cart && cart.total_price != null) ? Number(cart.total_price) / 100 : null),
      builds_remaining: mcpBuildsRemaining(cart)
    };
  }
  function removeBuild(configId) {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    var wantAll = (configId == null || String(configId).trim() === '');
    var wantId = wantAll ? null : String(configId);
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart read failed'); return r.json(); })
      .then(function (cart) {
        var matched = (cart.items || []).filter(function (it) {
          var cid = it.properties && it.properties._vms_config_id;
          if (!cid) return false;
          return wantAll ? true : (String(cid) === wantId);
        });
        if (!matched.length) {
          return { ok: true, config_id: wantId, removed: [], removed_total: 0,
            cart_after: mcpCartAfter(cart), cart_url: cartUrl, handback: 'No build lines are in the cart.' };
        }
        var updates = {};
        matched.forEach(function (it) { updates[it.key] = 0; });
        return fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (r) { if (!r.ok) throw new Error('cart update failed'); return r.json(); })
          .then(function () {
            // [review R2 item 11] Re-read /cart.js after the update and PARTITION the targeted lines by whether
            // they survived (a cart the platform did not actually zero), so an agent never says a build was
            // removed when it was not. The fresh read is also the truth for cart_after (item 10).
            return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
              .then(function (r2) { if (!r2.ok) throw new Error('cart read failed'); return r2.json(); })
              .then(function (fresh) {
                // [review R2c item 1] Partition by survival exactly like the configurator's removeBuild: a
                // surviving targeted line goes to not_removed[] ONLY, a gone one to removed[] ONLY, and
                // removed_total is derived from removed[] alone. A survivor never double-counts in both lists
                // or inflates the total.
                var stillKeys = {};
                ((fresh && fresh.items) ? fresh.items : []).forEach(function (it) { stillKeys[it.key] = it; });
                var removed = [];
                var not_removed = [];
                matched.forEach(function (it) {
                  var survivor = stillKeys[it.key];
                  if (survivor) {
                    // [review R2c item 4] not_removed carries the configurator's five fields (key, sku, title,
                    // quantity, config_id), read from the SURVIVOR, plus variant_id.
                    not_removed.push({ key: it.key, sku: (survivor.sku != null ? survivor.sku : null),
                      title: (survivor.title != null ? survivor.title : (survivor.product_title != null ? survivor.product_title : null)),
                      quantity: survivor.quantity, variant_id: (survivor.variant_id != null ? survivor.variant_id : survivor.id),
                      config_id: (survivor.properties && survivor.properties._vms_config_id) || null });
                  } else {
                    // [review R2 item 8] Title precedence it.title then it.product_title (match the configurator).
                    removed.push({ sku: (it.sku != null ? it.sku : null),
                      title: (it.title != null ? it.title : (it.product_title != null ? it.product_title : null)),
                      quantity: it.quantity, variant_id: (it.variant_id != null ? it.variant_id : it.id),
                      line_price: mcpCents(mcpCartLinePrice(it)),
                      config_id: (it.properties && it.properties._vms_config_id) || null });
                  }
                });
                var removedTotal = removed.reduce(function (s, x) { return s + (x.line_price || 0); }, 0);
                var skus = removed.map(function (x) { return x.sku; }).filter(Boolean).join(', ');
                var itemCount = (fresh && fresh.item_count != null) ? fresh.item_count : null;
                // [review R2 item 10] "The cart is now empty." when nothing remains (match the configurator).
                var tail = (itemCount === 0) ? 'The cart is now empty.'
                  : (itemCount != null ? (itemCount + (itemCount === 1 ? ' item remains' : ' items remain') + ' in the cart.') : '');
                var handback = 'Removed ' + removed.length + ' build line' + (removed.length === 1 ? '' : 's') +
                  (skus ? ' (' + skus + ')' : '') + ' from the cart, ' + money(mcpCents(removedTotal)) + '. ' + tail;
                var result = { ok: true, config_id: wantId, removed: removed, removed_total: mcpCents(removedTotal),
                  cart_after: mcpCartAfter(fresh), cart_url: cartUrl, handback: handback };
                if (not_removed.length) {
                  result.not_removed = not_removed;
                  result.handback += ' ' + not_removed.length + ' build line' + (not_removed.length === 1 ? ' is' : 's are') +
                    ' still in the cart; call ' + PHONE_TEXT + '.';
                }
                return result;
              });
          });
      })
      // [review R2 item 9] The configurator's internal .catch: a thrown fetch comes back as { ok:false,
      // reason }, never a rejected promise, so the MCP layer always gets a result object.
      .catch(function (err) { return { ok: false, reason: (err && err.message) ? err.message : String(err) }; });
  }

  // [ROUND 14 2026-08-30] The Q6 VESA chart. A small table of the mounting heads (cheapest first, the same
  // order as step 4), built ONCE at load from the SAME component data the calculator loads (#vmscfg-data ->
  // COMPONENTS), so every number is METAFIELD-DRIVEN, never hardcoded (custom.vesa_width_min/max,
  // vesa_height_min/max, weight_capacity). It renders into the [vesa chart] placeholder the section drops in
  // the Q6 answer; with no placeholder or no heads it does nothing. Column headers are plain hardcoded
  // sentence-case (the round-11 numbers-table precedent); a head with no VESA metafields shows the copy
  // fallback line (vesa_chart_nodata) across the value columns, never a blank cell. Ranges are written "X to Y".
  function vesaRange(minV, maxV) {
    var lo = S.num(minV), hi = S.num(maxV);
    if (lo === null && hi === null) return null;
    if (lo !== null && hi !== null) return lo + ' to ' + hi;
    return String(lo !== null ? lo : hi);
  }
  function renderVesaChart() {
    var host = document.querySelector('[data-vmsw-vesa-chart]');
    if (!host) return;
    if (!HEADS || !HEADS.length) { if (host.parentNode) host.parentNode.removeChild(host); return; }
    var rows = HEADS.map(function (h) {
      var w = vesaRange(h.vesa_width_min, h.vesa_width_max);
      var ht = vesaRange(h.vesa_height_min, h.vesa_height_max);
      var wcap = S.num(h.weight_capacity);
      var nameCell = h.url ? '<a href="' + esc(h.url) + '">' + esc(h.sku) + '</a>' : esc(h.sku);
      if (w === null && ht === null) {
        return '<tr><th scope="row">' + nameCell + '</th><td colspan="3">' + esc(COPY('vesa_chart_nodata')) + '</td></tr>';
      }
      return '<tr><th scope="row">' + nameCell + '</th>' +
        '<td>' + esc(w === null ? '-' : w) + '</td>' +
        '<td>' + esc(ht === null ? '-' : ht) + '</td>' +
        '<td>' + (wcap === null ? '-' : esc(String(wcap))) + '</td></tr>';
    }).join('');
    host.innerHTML =
      '<table class="vmsw-vesa-table">' +
      '<thead><tr><th scope="col">Mounting head</th><th scope="col">VESA width (mm)</th>' +
      '<th scope="col">VESA height (mm)</th><th scope="col">Holds up to (lb)</th></tr></thead>' +
      '<tbody>' + rows + '</tbody></table>';
  }

  // =====================================================================
  // [URL STATE 2026-09-23] SHAREABLE, BOOKMARKABLE, RELOAD-SAFE BUILD LINKS (design review F9, owner approved).
  // Before this the page kept no URL state, so a finished build could not be linked, bookmarked or survive a
  // reload. The pattern is the ceiling configurator's (assets/vms-configurator.js readURLState / shareURL /
  // syncURL), adapted to this page's inputs:
  //   angle    ceiling angle in degrees (the unit #vmsw-degrees takes), from state.pitch through
  //            S.pitchDegrees, 4 decimals trimmed so a drag-set angle restores to the same pitch and a typed
  //            whole number stays whole. Written only once angleTouched (the 45 mount default never is).
  //   tvm      the catalog model number while a catalog row fills the boxes (or a row with no hole positions is
  //            selected and the boxes are empty, or a restored tvm is still loading); then no tva / tvb / tvc.
  //   tva / tvb / tvc (screen / tophole / vesa before 2026-10-13, still read)   the measurement as the shopper TYPED it (a mm entry stays mm, so the receiving
  //            page converts it and shows the same mm note), when it parses to the value in state; else the
  //            inches value. Nothing is written for a blank or out-of-range box.
  //   plate / head   the selected SKUs.
  //   tilt     whole degrees, only when the shopper set it (tiltTouched) and it is nonzero. An AUTO tilt is
  //            never written: the receiving page re-derives the same one from the same build.
  //   drop     state.headDrop (the height drag), only when non-null, 6 decimals (see urlDropText).
  //   fc       '1' only when the 11FC is on.
  // Params the page does not own (utm tags, preview_theme_id, anything else) are carried through BYTE FOR
  // BYTE, ahead of ours, never invented. There is no arrival analytics event: the ceiling's vmsPublish is a
  // private function inside assets/vms-configurator.js, which this page does not load, and this file has no
  // publisher of its own (none added, per the F9 build brief).
  // [RULE] THE SEEDED EXAMPLE IS NOT PART OF THE LINK. While exampleActive is true nothing is written, so a
  // fresh visit never looks like a shared build and a bare URL stays bare. The plate and head the example
  // pre-selected ARE written once the shopper has entered their own measurements (the example is over then,
  // and they are the parts on the build the cart would add; leaving them out would make the reload lose half
  // the build). The 45 degree default angle is not written because the receiving page commits the same 45.
  // =====================================================================
  // [TV LOOKUP 2026-10-13] The TV travels as tvm (a catalog row) or tva / tvb / tvc (measurements), never both.
  // screen / tophole / vesa are read-only now (URL_LEGACY): an old link restores and is rewritten as tva / tvb / tvc.
  var URL_PARAMS = ['angle', 'tvm', 'tva', 'tvb', 'tvc', 'plate', 'head', 'tilt', 'drop', 'fc'];
  var URL_LEGACY = ['screen', 'tophole', 'vesa'];
  // Only these make a link a shared build (the restore skips the example seed for them). tilt, drop and fc
  // alone mean nothing without a build, so a link carrying only those opens on the example as today.
  var URL_BUILD_PARAMS = ['angle', 'tvm', 'tva', 'tvb', 'tvc', 'screen', 'tophole', 'vesa', 'plate', 'head'];
  var URL_SYNC_MS = 400;
  var urlSyncTimer = null;

  // A number as the shortest decimal string at a fixed number of places ("38", "38.3712"). Pure.
  function urlNumText(v, places) {
    var n = Number(v);
    if (!isFinite(n)) return null;
    var f = Math.pow(10, places);
    var r = Math.round(n * f) / f;
    return String(r === 0 ? 0 : r);      // no "-0"
  }
  // [WARN] The drop is written to 6 decimals, not the 2 the height handle shows. slidingPipeForDrop picks the
  // pipe by comparing the drop against each pipe's resting detent with a 1e-6 tolerance, and the drop a detent
  // snap commits IS that detent (a long float). Rounded to 0.01 it can land a hair past the detent, which the
  // selector reads as "drag down past this pipe" and answers with the next LONGER pipe: a link that restores a
  // different build. 6 places keeps the error under 5e-7, inside the tolerance.
  function urlDropText(v) { return urlNumText(v, 6); }

  // Split a location.search string into the params this page owns (decoded, trimmed, first occurrence wins,
  // like URLSearchParams.get) and the raw segments it does not own (kept verbatim for the carry-through).
  // Pure over its argument, so the Node harness lifts it.
  function urlParamsFrom(search) {
    var out = { owned: {}, carried: [] };
    var q = String(search == null ? '' : search);
    if (q.charAt(0) === '?') q = q.slice(1);
    if (!q) return out;
    q.split('&').forEach(function (seg) {
      if (seg === '') return;
      var eq = seg.indexOf('=');
      var key, val;
      try {
        key = decodeURIComponent((eq < 0 ? seg : seg.slice(0, eq)).replace(/\+/g, ' '));
        val = decodeURIComponent((eq < 0 ? '' : seg.slice(eq + 1)).replace(/\+/g, ' '));
      } catch (e) { out.carried.push(seg); return; }   // a malformed escape is not ours: carry it untouched
      if (URL_PARAMS.indexOf(key) === -1 && URL_LEGACY.indexOf(key) === -1) { out.carried.push(seg); return; }
      if (!Object.prototype.hasOwnProperty.call(out.owned, key)) out.owned[key] = val.trim();
    });
    return out;
  }
  // True when the owned map carries at least one param that makes a shared build. Pure.
  function urlHasBuild(owned) {
    return URL_BUILD_PARAMS.some(function (k) { return owned && owned[k] != null && owned[k] !== ''; });
  }
  // The query string (no "?") for a map of already-formatted values plus the carried raw segments: carried
  // first, then ours in the fixed URL_PARAMS order, so the same build always writes the same bytes. Pure.
  function urlQueryFor(vals, carried) {
    var parts = (carried || []).slice();
    URL_PARAMS.forEach(function (k) {
      var v = vals ? vals[k] : null;
      if (v == null || v === '') return;
      parts.push(k + '=' + encodeURIComponent(String(v)));
    });
    return parts.join('&');
  }

  // One measurement's link value: the box text as typed when it parses to the value in state (readMeasure,
  // the same parser wireMeasure uses), else the inches value, else null. The fallback covers state set without
  // the box (configureBuild's restorePreState restores state only), so the link always follows state.
  function urlMeasureText(id, stateKey, allowZero, mmThreshold) {
    var v = state[stateKey];
    if (v == null) return null;
    var box = el('vmsw-' + id);
    var raw = box ? String(box.value == null ? '' : box.value).trim() : '';
    if (raw !== '') {
      var m = readMeasure(raw, allowZero, mmThreshold);
      if (m.value !== null && Math.abs(m.value - v) < 1e-9) return raw;
    }
    return urlNumText(v, 6);
  }
  // The formatted values the link carries for the CURRENT state (see the table at the top of this block).
  // Reads state and the three boxes; writes nothing.
  function urlValues() {
    var v = {};
    if (exampleActive) return v;                 // [RULE] the seeded example is never part of the link
    if (angleTouched && state.pitch != null) {
      var deg = S.pitchDegrees(state.pitch);
      if (deg !== null) v.angle = urlNumText(deg, 4);
    }
    var none = state.screen == null && state.topHole == null && state.vesa == null;
    var m = pendingTv ? pendingTv.model : (tvSrc === 'catalog' && tvApplied ? tvApplied.model : (tvHeld && none ? tvHeld.model : null));
    if (m && m.length <= 64) v.tvm = m;
    else {
      v.tva = urlMeasureText('screen', 'screen', false, 100);
      v.tvb = urlMeasureText('tophole', 'topHole', true, 50);
      v.tvc = urlMeasureText('vesa', 'vesa', false, 50);
    }
    if (state.plateSku) v.plate = state.plateSku;
    if (state.headSku) v.head = state.headSku;
    if (state.headSku && state.tiltTouched && state.tilt) v.tilt = String(Math.round(state.tilt));
    if (state.headDrop != null) v.drop = urlDropText(state.headDrop);
    if (state.add11fc === true) v.fc = '1';
    return v;
  }
  // The link for the current state: pathname + query, or the bare pathname when there is nothing to write.
  // No side effects (the MCP getBuild calls it); syncURL does the history write.
  function shareURL() {
    var qs = urlQueryFor(urlValues(), urlParamsFrom(location.search).carried);
    return location.pathname + (qs ? ('?' + qs) : '');
  }
  // getBuild's share_url: the link only while it carries a shopper build, else null.
  function shopperShareURL() {
    return urlHasBuild(urlValues()) ? shareURL() : null;
  }
  // [WARN] Trailing debounce, 400 ms, same as the ceiling: renderAnswer runs on every keystroke and every drag
  // frame, and Safari rate-limits history.replaceState and then SILENTLY stops applying it. The URL is built
  // when the timer fires (so it reflects the state the render settled on), the write is skipped when nothing
  // changed (a bare or example visit never touches history at all), and the hash is kept (an in-page anchor
  // must survive). replaceState, never pushState: a build edit is not a Back-button step.
  function syncURL() {
    clearTimeout(urlSyncTimer);
    urlSyncTimer = setTimeout(function () {
      try {
        var url = shareURL();
        if (url === location.pathname + location.search) return;
        history.replaceState(history.state, '', url + location.hash);
      } catch (e) {}
    }, URL_SYNC_MS);
  }

  // Restore a shared build at mount, INSTEAD of the example seed, through the same action paths a shopper
  // uses, in dependency order: measurements (the boxes plus an input event, exactly like setMeasurements, so
  // wireMeasure's mm detection, range checks and invalid flags run), the angle (the degrees box rule: plain
  // number 0 to 89, through userSetAngle so step 2 counts done; anything else keeps the 45 default), the plate
  // and head (selectPlate / selectHead, which refuse an unknown SKU, a plate past its angle limit and a head
  // the VESA or diagonal gate rules out, exactly as for an agent), the tilt (commitTilt: clamps to the head's
  // max and marks it shopper-set, as a drag does), the 11FC, then the drop LAST through setHeadDrop so it
  // re-clamps against the corner floor and derives the pipe and the 11FC itself.
  // [RULE] Never poke state directly for a restored value: a link must land in exactly the state the same
  // clicks and typing would, gates included, or a hand-edited link could build what the page would refuse.
  function readURLState(owned) {
    function box(id, v) {
      var input = el(id);
      if (v == null || v === '' || !input) return;
      input.value = v;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    // [TV LOOKUP 2026-10-13] tva / tvb / tvc win over tvm when all three read; else tvm (held until the row lands,
    // the tilt, 11FC and drop with it); else each box on its own: a valid tva / tvb / tvc, else the legacy
    // screen / tophole / vesa as before (M2: a partial link restores the boxes it carries).
    var a = readMeasure(owned.tva, false, 100), b = readMeasure(owned.tvb, true, 50), c = readMeasure(owned.tvc, false, 50);
    var model = String(owned.tvm == null ? '' : owned.tvm).trim(), held = false, saved = tvSavedForUrl(owned);
    if (saved) model = String(saved.row.model_number).trim();
    if (a.value !== null && b.value !== null && c.value !== null) {
      box('vmsw-screen', owned.tva); box('vmsw-tophole', owned.tvb); box('vmsw-vesa', owned.tvc);
    } else if (model && model.length <= 64 && tvRoot()) {
      held = true;
      pendingTv = { model: model, tail: owned, saved: saved, timer: setTimeout(function () {
        if (!pendingTv || pendingTv.list) return;
        tvLost = pendingTv.model; pendingTv = null;
        renderAnswer();
      }, 10500) };
      tvRestoreModel(model);
    } else {
      box('vmsw-screen', a.value !== null ? owned.tva : owned.screen);
      box('vmsw-tophole', b.value !== null ? owned.tvb : owned.tophole);
      box('vmsw-vesa', c.value !== null ? owned.tvc : owned.vesa);
    }
    var ang = (owned.angle == null || owned.angle === '') ? null : S.num(plainNumeric(owned.angle));
    if (ang !== null && ang >= ANGLE_MIN_DEG && ang <= ANGLE_MAX_DEG) userSetAngle(ang);
    else commitAngle(ANGLE_DEFAULT_DEG);
    if (owned.plate) selectPlate(owned.plate);
    if (owned.head) selectHead(owned.head);
    if (!held) urlRestoreTail(owned);
  }
  // The tilt, the 11FC and the drop of a restored link, in that order (each needs a ready build).
  function urlRestoreTail(owned) {
    var tilt = (owned.tilt == null || owned.tilt === '') ? null : S.num(plainNumeric(owned.tilt));
    if (tilt !== null && state.headSku && ready()) commitTilt(tilt);
    // The 11FC box's own change handler minus its focus() (a page load must not pull focus or scroll): only
    // when the box is actually offered on the rendered build, so a link cannot force it onto a build without it.
    if (owned.fc === '1') {
      var ansBox = el('vmsw-answer');
      if (ansBox && ansBox.querySelector('#vmsw-add11fc')) { state.add11fc = true; renderAnswer(); }
    }
    var drop = (owned.drop == null || owned.drop === '') ? null : S.num(plainNumeric(owned.drop));
    if (drop !== null) setHeadDrop(drop);   // no-op unless the build is ready; clamps and snaps like a drag
  }

  window.VMSSlantedPage = {
    getScene: getScene,
    setSlopeDeg: setSlopeDeg,
    setTilt: setTilt,
    setHeadDrop: setHeadDrop,
    previewSlope: previewSlope,
    previewTilt: previewTilt,
    previewHeadDrop: previewHeadDrop,
    // [MCP 2026-08-30; review R2 2026-09-09] the WebMCP support surface. selectPlate / selectHead / listPlates
    // / listHeads drive and read the page's own pick UI (review S1); removeBuild is the way out that pairs
    // with add-to-cart.
    getBuild: getBuild,
    configureBuild: configureBuild,
    addToCart: mcpAddToCartPublic,
    removeBuild: removeBuild,
    selectPlate: selectPlate,
    selectHead: selectHead,
    listPlates: listPlates,
    listHeads: listHeads
  };

  // =====================================================================
  // Mount
  // =====================================================================
  buildShell();
  mountTvLookup();
  wire();
  // [ROUND 14 2026-08-30] Populate the Q6 VESA chart once (the Q&A is server-rendered and static in the DOM).
  renderVesaChart();
  // [EXAMPLE 2026-08-30] Seed the example build so the page OPENS showing a finished result (owner: a
  // configurator starts by showing an example, then you configure from there). Only when both the example
  // plate and head are actually in this store's pool; otherwise leave exampleActive false and the page opens
  // on the plain prompt (graceful degrade). The measurement input boxes are left EMPTY on purpose; only
  // state carries the example numbers. The cards are pre-selected to match (reflectPick).
  // [URL STATE 2026-09-23] A link carrying a shared build skips the example ENTIRELY (exampleActive stays false)
  // and restores through readURLState, which also commits the angle, so the commitAngle below is in the else.
  // A bare URL, or one carrying only params this page does not own (or only tilt / drop / fc), takes the
  // unchanged example path. Parsed once here, BEFORE any render, so the first syncURL has not run yet.
  // [RULE] Keep the restore BEFORE the example seed and never let both run: a restore on top of the seeded
  // example would inherit its plate and head and write them into the link as if the sender had chosen them.
  var urlArrival = urlParamsFrom(location.search);
  if (urlHasBuild(urlArrival.owned)) {
    readURLState(urlArrival.owned);
  } else {
    if (bySku(EXAMPLE_DEFAULTS.plateSku) && bySku(EXAMPLE_DEFAULTS.headSku)) {
      exampleActive = true;
      state.screen = EXAMPLE_DEFAULTS.screen;
      state.topHole = EXAMPLE_DEFAULTS.topHole;
      state.vesa = EXAMPLE_DEFAULTS.vesa;
      state.plateSku = EXAMPLE_DEFAULTS.plateSku;
      state.headSku = EXAMPLE_DEFAULTS.headSku;
      reflectPick('vmsw-plates', 'plate', state.plateSku);
      reflectPick('vmsw-heads', 'head', state.headSku);
    }
    // [COMPACT 2026-08-29] Commit the DEFAULT ceiling angle (45 degrees) at mount so state.pitch is set from
    // the start (the handle rests at 45, the readout and the degrees box show 45), and the page presents a
    // solution as soon as the three measurements plus a plate and head are in, without waiting for a slope
    // entry. commitAngle runs updateAngleSlider + setPitch + renderAnswer, so no separate renderAnswer here.
    // With the example seeded above, this first render paints the example build.
    commitAngle(ANGLE_DEFAULT_DEG);
    tvSeedSaved(tvSavedForUrl(urlArrival.owned));   // [3A 2026-10-18] a saved TV replaces the example measurements; the example plate and head stay
  }  // [URL STATE 2026-09-23] end of the example (no shared build in the link) path
  // Hand the page over: the calculator is up, so hide the no-JS fallback links.
  var fallback = document.getElementById('vmsw-fallback');
  if (fallback) fallback.classList.add('vmsw-hidden');
})();
